import React, { useEffect, useState, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import axios from 'axios';
import '../styles/dashboard.css';
// Must come after dashboard.css — it re-tones those rules for the teal ground.
import '../styles/dashboard-theme.css';
import '../styles/raw-database.css';
import '../styles/ssm-lab.css';
import '../styles/history-lab.css';
import '../styles/create-lab.css';
import '../styles/dashboard-lab.css';
import '../styles/csv-popup-lab.css';
import api from '../services/authService';
import PdfStaticModal from '../components/PdfPreview/PdfStaticModal';
import Icon from '../components/Icon';
import NavigatorChat from '../components/NavigatorChat';
import FormScanPanel from '../components/FormScanPanel';
import websocketService from '../services/websocketService';
// import pdfOverlayConfig from '../configs/pdfOverlayConfig';
import Papa from 'papaparse';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { PDFDocument } from 'pdf-lib';
import { formSectionsByFormat } from '../configs/All_formatConfig/Config_formSection';
import ReactDOM, { flushSync } from 'react-dom';
import { choosing_supplement as qpt_choosing_supplement, supplement_bacteria_map as qpt_supplement_bacteria_map } from '../configs/supplement/qpt_supplement';
import { choosing_supplement_9P, supplement_bacteria_map_9P, supplement_9P_skus } from '../configs/supplement/9P_supplement';
import { newformula_facts_9P } from '../configs/supplement/9P_newformula_facts';
import { choosing_supplement_AMG, supplement_bacteria_map_AMG } from '../configs/supplement/AMG_supplement';
import { choosing_supplement_toxin, supplement_bacteria_map_toxin } from '../configs/supplement/toxin_supplement';
import { choosing_supplement_bwc, supplement_bacteria_map_bwc } from '../configs/supplement/bwc_supplement';

import { getRecommendedSupplements, getAllPanelSupplements, calculateSpeciesLabel, set9PSupplementVersion, panelSupportsNewFormula } from './calculate_selection';
import {
  petAgeYearsToLifeStageLabel,
  petHealthRawFromRow,
  petHealthCsvDisplay
} from '../configs/biopet_professional/Config_personal_biopet_professional';
import { buildQuartileBySpeciesFromGrids } from '../configs/biopet_professional/calculate_biopet_professional';
import { BIOPET_CALIBRATION_SPECIES_COLUMNS } from '../configs/biopet_professional/calibrate_biopet_professional_quartiles';
import { BIOPET_ESSENTIAL_SUPPLEMENT_FORMULA } from '../configs/biopet_professional/calculate_biopet_professional_supplement';
// The 10 Biopet report pages, shared with the server-side PDF render (renderPdfHtml.js).
import BiopetReportPages, {
  computeBiopetReportData
} from '../components/PdfPreview/BiopetReportPages';
import { 
  choosing_supplement_oms, 
  supplement_bacteria_map_oms 
} from '../configs/supplement/oms_supplement';
import { bacteriaMetaConfig } from '../configs/calculate_ctvalue';
import { calculateReportResultRowByRow, isPanelSupported } from '../utils/resultCalculator';
import { addBioGraceIfNeeded as addBioGraceIfNeeded9P } from '../configs/supplement/9P_supplement';

/**
 * html2canvas' `backgroundColor` option is parsed by its own color parser, which does
 * NOT understand `var(...)` — passing one throws
 * `Attempting to parse an unsupported color function "var"` and the whole capture fails.
 * (Element styles are fine: those are read via getComputedStyle, which resolves vars.)
 * Resolve the token off :root here so the canvas keeps the current theme's surface color.
 */
const CAPTURE_BG_FALLBACK = '#ffffff';
function resolveCaptureBackgroundColor(varName = '--bg-surface') {
  try {
    const value = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    // A var that is itself defined as another var() is no better than the original.
    if (value && !value.includes('var(')) return value;
  } catch (_) {}
  return CAPTURE_BG_FALLBACK;
}

function PetHealthCsvPreviewCell({ row, revealUnhealthyRaw = false }) {
  const rawH = petHealthRawFromRow(row);
  const h = petHealthCsvDisplay(rawH);
  const isUnhealthy = h.variant === 'unhealthy';
  const showingRaw = isUnhealthy && revealUnhealthyRaw;
  const displayText = showingRaw ? h.raw : h.label;

  const tip =
    h.variant === 'empty'
      ? 'No health value in CSV'
      : isUnhealthy
        ? revealUnhealthyRaw
          ? 'Raw value from CSV — click the Health column header to show Unhealthy again'
          : 'Unhealthy — click the Health column header to show raw values for all Unhealthy rows'
        : `Raw from CSV: ${h.raw}`;

  const pill =
    h.variant === 'healthy'
      ? {
          backgroundColor: 'var(--success-soft)',
          color: 'var(--success-text)',
          border: '1px solid #81c784'
        }
      : isUnhealthy
        ? {
            backgroundColor: 'var(--danger-soft)',
            color: 'var(--danger-text)',
            border: '1px solid #e57373'
          }
        : { color: 'var(--text-muted)' };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '6px',
        fontWeight: 600,
        fontSize: '13px',
        cursor: h.variant === 'empty' ? 'default' : 'help',
        whiteSpace: showingRaw ? 'normal' : 'nowrap',
        maxWidth: showingRaw ? 260 : undefined,
        wordBreak: showingRaw ? 'break-word' : undefined,
        ...pill
      }}
      title={tip}
    >
      {displayText}
    </span>
  );
}

function createEmptyQuartileGridBySpecies() {
  const cellGrid = () => [
    ['0', '0', '0'],
    ['0', '0', '0'],
    ['0', '0', '0']
  ];
  const o = {};
  for (const sp of BIOPET_CALIBRATION_SPECIES_COLUMNS) {
    o[sp] = cellGrid().map((row) => [...row]);
  }
  return o;
}

function quartileApiResponseToGridBySpecies(data) {
  const fmt = (x) => (Number.isFinite(Number(x)) ? String(Math.round(Number(x) * 100) / 100) : '0');
  const next = createEmptyQuartileGridBySpecies();
  const by = data?.bySpecies && typeof data.bySpecies === 'object' ? data.bySpecies : {};
  const legacy = Array.isArray(data?.values) && data.values.length === 9 ? data.values : null;
  for (const sp of BIOPET_CALIBRATION_SPECIES_COLUMNS) {
    const flat = by[sp];
    if (Array.isArray(flat) && flat.length === 9) {
      next[sp] = [
        [fmt(flat[0]), fmt(flat[1]), fmt(flat[2])],
        [fmt(flat[3]), fmt(flat[4]), fmt(flat[5])],
        [fmt(flat[6]), fmt(flat[7]), fmt(flat[8])]
      ];
    } else if (legacy) {
      next[sp] = [
        [fmt(legacy[0]), fmt(legacy[1]), fmt(legacy[2])],
        [fmt(legacy[3]), fmt(legacy[4]), fmt(legacy[5])],
        [fmt(legacy[6]), fmt(legacy[7]), fmt(legacy[8])]
      ];
    }
  }
  return next;
}

function meanFlat9FromQuartileGridBySpecies(gridBySpecies) {
  const sum = new Array(9).fill(0);
  for (const sp of BIOPET_CALIBRATION_SPECIES_COLUMNS) {
    const g = gridBySpecies[sp];
    if (!g) continue;
    const flat = g.flat().map((x) => {
      const n = parseFloat(x);
      return Number.isFinite(n) ? n : 0;
    });
    for (let i = 0; i < 9; i++) sum[i] += flat[i] || 0;
  }
  const n = BIOPET_CALIBRATION_SPECIES_COLUMNS.length;
  return sum.map((s) => Math.round((s / n) * 100) / 100);
}

/** Today in Asia/Bangkok as yyyymmdd — the app's reporting timezone. */
function todayYyyymmddBangkok() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value || '';
  return `${get('year')}${get('month')}${get('day')}`;
}

const sanitizePdfNamePart = (s) => String(s ?? '').trim().replace(/[^a-zA-Z0-9.-]/g, '_');

/**
 * Report Date → dd_mm_yyyy for filenames. Accepts the formats st_report holds
 * (ISO / MySQL "2026-09-16 00:00:00", "16/09/2026", "16-09-2026", JS Date). Falls back to
 * today (Asia/Bangkok) when the row has no usable date.
 */
function reportDateDdMmYyyy(report) {
  const raw = report?.['Report Date'] ?? report?.report_date ?? report?.['create_time'] ?? '';
  const s = raw instanceof Date ? '' : String(raw ?? '').trim();
  let y, m, d;
  let mm;
  // A DATETIME serialised by the API arrives as a UTC instant ("2026-09-15T17:00:00.000Z");
  // format it in the reporting timezone so it lands on the calendar day the lab entered.
  const fromInstant = (t) => {
    if (Number.isNaN(t.getTime())) return;
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(t);
    const get = (k) => parts.find((p) => p.type === k)?.value || '';
    [y, m, d] = [get('year'), get('month'), get('day')];
  };
  if (raw instanceof Date) {
    fromInstant(raw);
  } else if (/^\d{4}-\d{2}-\d{2}T.*(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
    fromInstant(new Date(s));
  } else if ((mm = s.match(/^(\d{4})-(\d{2})-(\d{2})/))) {
    [, y, m, d] = mm;
  } else if ((mm = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/))) {
    [, d, m, y] = mm;
  } else if (s) {
    fromInstant(new Date(s));
  }
  if (!y) {
    const today = todayYyyymmddBangkok();
    return `${today.slice(6, 8)}_${today.slice(4, 6)}_${today.slice(0, 4)}`;
  }
  return `${String(d).padStart(2, '0')}_${String(m).padStart(2, '0')}_${y}`;
}

/**
 * PDF download name.
 *
 * Biopet (Pet Care Professional): BioPet_MicrobiomeReport_<petName>_<orderNumber>_<yyyymmdd>.pdf
 * — the date is the day the report is generated, not the collection or report date.
 *
 * Everything else: <ReportNumber>_<First>_<Last>_<dd_mm_yyyy>.pdf, e.g.
 * 26080741QU_Jiyarach_Phattaraworranit_16_09_2026.pdf, where the date is the row's Report Date.
 * Kept in sync with reportDownloadName in routes/report.routes.js.
 */
function pdfFileNameFromReportNumber(report) {
  const n = report?.['Report Number'] ?? report?.['Report number'];
  // Same fallback as the server's reportDownloadName: a row with no report number
  // is named by its DB id so both sides agree.
  const base =
    n !== undefined && n !== null && String(n).trim() !== ''
      ? String(n).trim()
      : (report?.id ? `report-${report.id}` : 'report');

  const isBiopet =
    report?._historyCategory === 'biopet' ||
    report?.Panel === 'Pet Care Professional' ||
    report?.Panel === 'Pet Care Essential';

  if (isBiopet) {
    const petName = sanitizePdfNamePart(report?.petname || report?.['Full Name']) || 'Pet';
    // `||` not `??`: a blank order_number must fall through to the report number, which is
    // what the server's reportDownloadName does — the two names have to agree.
    const orderNumber = sanitizePdfNamePart(report?.order_number || base) || 'NA';
    return `BioPet_MicrobiomeReport_${petName}_${orderNumber}_${todayYyyymmddBangkok()}.pdf`;
  }

  const fullName = String(report?.['Full Name'] ?? report?.full_name ?? '').trim();
  // Collapse whitespace runs to a single underscore, strip filesystem-unsafe characters,
  // then squash repeated underscores (non-Latin names would otherwise become "____").
  const namePart = fullName
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  const parts = [base.replace(/[^a-zA-Z0-9._-]/g, '_')];
  if (namePart) parts.push(namePart);
  parts.push(reportDateDdMmYyyy(report));
  return `${parts.join('_')}.pdf`;
}

/**
 * History table: a click anywhere on a row toggles its checkbox, unless the click landed on
 * something that has its own job (buttons, links, the expand icon, inputs) or the user was
 * selecting text.
 */
function isHistoryRowSelectClick(e) {
  const target = e.target;
  if (!target || typeof target.closest !== 'function') return false;
  if (target.closest('button, a, input, select, textarea, label, img, svg, [role="button"], [data-no-row-select]')) {
    return false;
  }
  const sel = typeof window !== 'undefined' && window.getSelection ? window.getSelection() : null;
  if (sel && String(sel.toString()).trim()) return false;
  return true;
}

/** Modify Quartile modal: column order matches age bands (Young &lt;1 y, Adult 1–7 y, Senior &gt;7 y). */
const QUARTILE_MODAL_LIFE_STAGES = ['Young', 'Adult', 'Senior'];

const QUARTILE_MODAL_STAGE_STYLES = {
  Young: {
    headerBg: '#e3f2fd',
    headerBgFocus: 'rgba(25, 118, 210, 0.28)',
    subHeaderBg: '#f0f7ff',
    subHeaderBgFocus: 'rgba(25, 118, 210, 0.18)',
    cellBg: 'rgba(25, 118, 210, 0.07)',
    cellBgFocus: 'rgba(25, 118, 210, 0.14)'
  },
  Adult: {
    headerBg: '#e8f5e9',
    headerBgFocus: 'rgba(46, 125, 50, 0.3)',
    subHeaderBg: '#f1faf2',
    subHeaderBgFocus: 'rgba(46, 125, 50, 0.2)',
    cellBg: 'rgba(46, 125, 50, 0.09)',
    cellBgFocus: 'rgba(46, 125, 50, 0.16)'
  },
  Senior: {
    headerBg: '#f3e5f5',
    headerBgFocus: 'rgba(106, 27, 154, 0.26)',
    subHeaderBg: '#faf5fc',
    subHeaderBgFocus: 'rgba(106, 27, 154, 0.18)',
    cellBg: 'rgba(106, 27, 154, 0.08)',
    cellBgFocus: 'rgba(106, 27, 154, 0.15)'
  }
};


// Helper function to apply BioGrace age-based filtering for history display (9P panel only)
function applyBioGraceFiltering(supplements, dateOfBirth, panel) {
  if (!supplements || !Array.isArray(supplements) || !dateOfBirth) {
    return supplements;
  }

  // Only apply BioGrace for 9P panel
  if (panel === '9P' || panel === 'Q9' || panel?.toLowerCase() === '9p' || panel?.toLowerCase() === 'q9') {
    return addBioGraceIfNeeded9P(supplements, dateOfBirth);
  }

  // For all other panels, return supplements as-is (BioGrace not available)
  return supplements;
}

/**
 * Decode CSV file text with Thai-friendly encodings. Excel (Thai locale) often exports
 * as Windows-874; browsers read File as UTF-8 by default, which mojibakes those files.
 * Also handles UTF-8 BOM and UTF-16 LE/BE BOM.
 */
async function readCsvFileAsUtf8OrThaiWindows(file) {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  if (bytes.length === 0) return '';

  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder('utf-8').decode(bytes.subarray(3));
  }
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder('utf-16le').decode(bytes.subarray(2));
  }
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder('utf-16be').decode(bytes.subarray(2));
  }

  const asUtf8 = new TextDecoder('utf-8').decode(bytes);
  const replUtf8 = (asUtf8.match(/\uFFFD/g) || []).length;
  const replacementRatio = asUtf8.length ? replUtf8 / asUtf8.length : 0;

  const tryDecode = (label) => {
    try {
      return new TextDecoder(label).decode(bytes);
    } catch {
      return null;
    }
  };

  if (replacementRatio > 0.005) {
    const as874 = tryDecode('windows-874');
    if (as874 != null) {
      const r874 = (as874.match(/\uFFFD/g) || []).length / (as874.length || 1);
      if (r874 < replacementRatio) return as874;
    }
    const asIso = tryDecode('iso-8859-11');
    if (asIso != null) {
      const rIso = (asIso.match(/\uFFFD/g) || []).length / (asIso.length || 1);
      if (rIso < replacementRatio) return asIso;
    }
  }

  return asUtf8;
}

// Helper functions
function normalizeRowKeys(row, configInfo) {
  const merged = {};
  Object.keys(row || {}).forEach((k) => {
    if (k == null || String(k).trim() === '') return;
    const ck = normalizeColumnName(String(k).trim());
    if (!ck) return;
    const v = row[k];
    if (!(ck in merged) || merged[ck] === '' || merged[ck] == null) {
      merged[ck] = v;
    }
  });
  row = merged;

  const normalizedRow = {};

  // Normalize supplement bacteria names (from config)
  configInfo.forEach(({ sp }) => {
    const matchKey = Object.keys(row).find(
      k => k.replace(/\s+/g, '').toLowerCase() === sp.replace(/\s+/g, '').toLowerCase()
    );
    if (matchKey) {
      normalizedRow[sp] = row[matchKey];
    }
  });

  // Normalize personal information fields
  // Fields listed here survive as-is. Anything else is only copied through when it
  // parses as a number (see below), so text fields like HN ("1234ds4") must be listed.
  const personalInfoFields = [
    'Full Name', 'Date of Birth', 'Gender', 'Report Date', 'Collection Date', 'HN',
    'Handed at', 'Test Panel', 'Order Number', 'Report Number', 'Sample Type', 'Client ID', 'Age', 'health',
    'Supplement', 'Panel', 'create_time', 'id', 'duration'
  ];

  personalInfoFields.forEach(expectedField => {
    const matchKey = Object.keys(row).find(
      k => k.replace(/\s+/g, '').toLowerCase() === expectedField.replace(/\s+/g, '').toLowerCase()
    );
    if (matchKey) {
      normalizedRow[expectedField] = row[matchKey];
    }
  });

  // Copy other fields (like FDA) if present
  if (row.FDA) normalizedRow.FDA = row.FDA;

  // Add ALL bacteria species and other numeric fields from the row
  Object.keys(row).forEach(key => {
    // Skip if already normalized or is a personal info field
    if (
      normalizedRow.hasOwnProperty(key) ||
      personalInfoFields.some(f =>
        f.replace(/\s+/g, '').toLowerCase() === key.replace(/\s+/g, '').toLowerCase()
      )
    ) {
      return;
    }
    
    // Include all bacteria species and other fields that might be numeric
    // This ensures we capture all bacteria data from the database
    if (
      typeof row[key] === 'number' ||
      (!isNaN(parseFloat(row[key])) && isFinite(row[key])) ||
      row[key] === null ||
      row[key] === undefined
    ) {
      normalizedRow[key] = row[key];
    }
  });

  // Handle spelling variations for bacteria species
  // Map common variations to standard names
  const bacteriaNameMappings = {
    'Bacteroidetes genus': 'Bacteroides genus', // Map to correct spelling
    'Bacteroides genus': 'Bacteroides genus'
  };

  Object.keys(bacteriaNameMappings).forEach(variant => {
    if (normalizedRow[variant] !== undefined && normalizedRow[bacteriaNameMappings[variant]] === undefined) {
      normalizedRow[bacteriaNameMappings[variant]] = normalizedRow[variant];
    }
  });

  return normalizedRow;
}

function normalizeColumnName(columnName) {
  const trimmed = String(columnName ?? '')
    .trim()
    .normalize('NFC');

  // Define the expected column names and their variations (English + Thai headers)
  const columnMappings = {
    'Order number': 'Order Number',
    'Report number': 'Report Number',
    'Sample type': 'Sample Type',
    'Client ID': 'Client ID',
    'Age': 'Age',
    'age': 'Age',
    'petname': 'Full Name',
    'order_number': 'Report Number',
    'Health': 'health',
    'health': 'health',
    'Full Name': 'Full Name',
    'Date of Birth': 'Date of Birth',
    'Gender': 'Gender',
    'Report Date': 'Report Date',
    'Collection Date': 'Collection Date',
    'Handed at': 'Handed at',
    'Test Panel': 'Test Panel',
    // Hospital Number (QBWCA / QBWCE / QBWCU)
    'HN': 'HN',
    'HN Number': 'HN',
    'HN No': 'HN',
    'hn_number': 'HN',
    'Hospital Number': 'HN',
    // Thai (common Excel / lab export labels)
    'ชื่อ': 'Full Name',
    'ชื่อเต็ม': 'Full Name',
    'ชื่อ นามสกุล': 'Full Name',
    'ชื่อ-นามสกุล': 'Full Name',
    'ชื่อสัตว์เลี้ยง': 'Full Name',
    'ชื่อสัตว์': 'Full Name',
    'เลขที่รายงาน': 'Report Number',
    'หมายเลขรายงาน': 'Report Number',
    'เลขรายงาน': 'Report Number',
    'อายุ': 'Age',
    'อายุ (ปี)': 'Age',
    'อายุ(ปี)': 'Age',
    'สุขภาพ': 'health',
    'สถานะสุขภาพ': 'health',
    'รหัสลูกค้า': 'Client ID',
    'เลขที่คำสั่งซื้อ': 'Order Number',
    'เพศ': 'Gender',
    'วันเกิด': 'Date of Birth',
    'วันที่รายงาน': 'Report Date',
    'วันที่เก็บตัวอย่าง': 'Collection Date',
    'วันที่รับตัวอย่าง': 'Collection Date',
    'เลขที่ผู้ป่วย': 'HN',
    'รหัสผู้ป่วย': 'HN',
    'ประเภทตัวอย่าง': 'Sample Type',
    'ชุดทดสอบ': 'Test Panel',
    'แผงทดสอบ': 'Test Panel'
  };

  const normalizedKey = Object.keys(columnMappings).find(
    (key) => key.toLowerCase() === trimmed.toLowerCase()
  );

  return normalizedKey ? columnMappings[normalizedKey] : trimmed;
}

/** "Last seen 5 min ago" for the team panel's offline rows; '' when never seen. */
function formatLastSeen(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return 'Last seen just now';
  if (mins < 60) return `Last seen ${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `Last seen ${hours} h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'Last seen yesterday' : `Last seen ${days} days ago`;
}

/**
 * Today in Asia/Bangkok as DD/MM/YYYY — the shape the lab CSVs and the manual
 * entry form already use, so a stamped date is indistinguishable from a typed
 * one and parses the same everywhere downstream.
 */
function todayReportDate() {
  return new Date().toLocaleDateString('en-GB', { timeZone: 'Asia/Bangkok' });
}

function formatToDateInput(str) {
  const raw = String(str ?? '').trim();
  if (!raw) return '';

  const pad2 = (v) => String(v).padStart(2, '0');

  // Keep day/month/year order from CSV text, avoid timezone shift.
  const dmy = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(raw);
  if (dmy) {
    const d = Number(dmy[1]);
    const m = Number(dmy[2]);
    const y = Number(dmy[3]);
    if (Number.isFinite(d) && Number.isFinite(m) && Number.isFinite(y) && d >= 1 && d <= 31 && m >= 1 && m <= 12) {
      return `${y}-${pad2(m)}-${pad2(d)}`;
    }
    return '';
  }

  // Keep ISO-style calendar date as-is (with optional time part).
  const ymd = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/.exec(raw);
  if (ymd) {
    const y = Number(ymd[1]);
    const m = Number(ymd[2]);
    const d = Number(ymd[3]);
    if (Number.isFinite(d) && Number.isFinite(m) && Number.isFinite(y) && d >= 1 && d <= 31 && m >= 1 && m <= 12) {
      return `${y}-${pad2(m)}-${pad2(d)}`;
    }
    return '';
  }

  // Last fallback for unusual formats: use local date components (not UTC/ISO).
  const date = new Date(raw);
  if (isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** User-facing labels for Raw Database table switcher (internal API keys unchanged). */
const RAW_DB_TABLE_UI_LABELS = {
  st_report: 'BioMed Database',
  biopet_report: 'BioPet Unhealthy Database',
  biopet_report_healthy: 'BioPet Healthy Database',
  partner_codes: 'Partner'
};

/** Where a stock movement came from — matches `stock_log.source` in routes/stock.js. */
const STOCK_SOURCE_LABELS = {
  partner_order: 'Partner order',
  report: 'Report saved',
  ssm_add: 'Added in SSM',
  ssm_reset: 'Reset in SSM',
  ssm_update: 'Edited in SSM'
};

/** Raw DB row: date fields for timeline filter (BioMed vs BioPet column names). */
function getRawDbRowDateCandidate(row) {
  if (!row) return '';
  return (
    row['Report Date'] ||
    row['create_time'] ||
    row['Collection Date'] ||
    row.report_date ||
    row.collection_date ||
    ''
  );
}

// eslint-disable-next-line no-unused-vars
const handleSaveToServer = async (reportData) => {
  try {
    const pages = document.querySelectorAll('.pdf-page-wrapper');
    if (!pages.length) {
      alert('No PDF content to export.');
      return;
    }

    const { jsPDF } = await import('jspdf');
    const html2canvas = (await import('html2canvas')).default;

    const pdf = new jsPDF('p', 'mm', 'a4');
    let isFirst = true;

    for (let i = 0; i < pages.length; i++) {
      const el = pages[i];
      el.scrollIntoView();

      await new Promise((r) => setTimeout(r, 100));

      const canvas = await html2canvas(el, {
        scale: 1.9,
        useCORS: true,
        backgroundColor: resolveCaptureBackgroundColor(),
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.90);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      if (!isFirst) pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, imgHeight);
      isFirst = false;
    }

    const blob = pdf.output('blob');
    const file = new File(
      [blob],
      `${reportData['Report Number'] || 'report'}.pdf`,
      { type: 'application/pdf' }
    );

    const formDataUpload = new FormData();
    formDataUpload.append('pdf', file);

    const apiBase = process.env.REACT_APP_API_BASE || '';
    // eslint-disable-next-line no-unused-vars
    const response = await axios.post(`${apiBase}/api/pdf/upload`, formDataUpload, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    alert('PDF saved successfully to server.');
    // console.log('Upload response:', response.data);
  } catch (err) {
    console.error(err);
    alert('Failed to save PDF to server.');
  }
};

/**
 * History people-pages: walk reports newest-first; each page = `pageSize` distinct people by *first
 * appearance* in that feed. Each group contains every loaded row for that person (older reports are not
 * left on a "page 12" flat slice).
 */
function buildPeoplePagesByFeed(reports, keyFn, pageSize) {
  if (!Array.isArray(reports) || reports.length === 0 || pageSize < 1) return [];

  const byName = new Map();
  for (const r of reports) {
    const k = keyFn(r);
    if (!byName.has(k)) byName.set(k, []);
    byName.get(k).push(r);
  }

  const sorted = [...reports].sort((a, b) => {
    const da = new Date(a['create_time'] || a['Report Date'] || 0).getTime();
    const db = new Date(b['create_time'] || b['Report Date'] || 0).getTime();
    return db - da;
  });

  const sortReportsDesc = (arr) =>
    [...arr].sort((a, b) => {
      const da = new Date(a['create_time'] || a['Report Date'] || 0).getTime();
      const db = new Date(b['create_time'] || b['Report Date'] || 0).getTime();
      return db - da;
    });

  const prior = new Set();
  const pages = [];
  let idx = 0;

  while (idx < sorted.length) {
    const seenOnPage = new Set();
    const namesThisPage = [];
    while (namesThisPage.length < pageSize && idx < sorted.length) {
      const name = keyFn(sorted[idx]);
      idx += 1;
      if (prior.has(name)) continue;
      if (seenOnPage.has(name)) continue;
      seenOnPage.add(name);
      namesThisPage.push(name);
    }
    if (namesThisPage.length === 0) break;
    for (const n of namesThisPage) prior.add(n);
    pages.push(
      namesThisPage.map((n) => ({
        fullName: n,
        reports: sortReportsDesc(byName.get(n) || [])
      }))
    );
  }

  return pages;
}

/** Map /api/reports/summary JSON to dashboard state (supports legacy body with only `total`). */
function normalizeReportSummaryResponse(d) {
  if (!d || typeof d !== 'object') {
    return {
      total: 0,
      biomed: 0,
      biopetHealthy: 0,
      biopetUnhealthy: 0,
      biopetTotal: 0,
      latestCreatedAt: null
    };
  }
  const hasBreakdown =
    d.biomed !== undefined ||
    d.biopetHealthy !== undefined ||
    d.biopetUnhealthy !== undefined ||
    d.biopetTotal !== undefined;
  if (!hasBreakdown) {
    const t = Number(d.total ?? 0);
    return {
      total: t,
      biomed: t,
      biopetHealthy: 0,
      biopetUnhealthy: 0,
      biopetTotal: 0,
      latestCreatedAt: d.latestCreatedAt ?? null
    };
  }
  const biomed = Number(d.biomed ?? 0);
  const biopetHealthy = Number(d.biopetHealthy ?? 0);
  const biopetUnhealthy = Number(d.biopetUnhealthy ?? 0);
  const biopetTotal = Number(d.biopetTotal ?? biopetHealthy + biopetUnhealthy);
  const total = Number(d.total ?? biomed + biopetTotal);
  return {
    total,
    biomed,
    biopetHealthy,
    biopetUnhealthy,
    biopetTotal,
    latestCreatedAt: d.latestCreatedAt ?? null
  };
}

// Product artwork for a New-formula SKU on the SSM stock page. The PNGs live at
// public/supplement/newformula/<SKU>.png (500x500, transparent padding), same
// convention as the 9P PDF card. If a file is missing the <img> onError swaps in
// a neutral accent-tinted tile showing the SKU prefix so the grid stays even.
const NewFormulaThumb = ({ sku, accent }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div
        className="newformula-stock-placeholder"
        style={{ backgroundColor: `${accent}14`, color: accent }}
      >
        {sku.split('-')[0]}
      </div>
    );
  }

  return (
    <img
      className="newformula-stock-thumb"
      src={`/supplement/newformula/${sku}.png`}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const DashboardPage = () => {
  // Add CSS for spinning animation with performance optimizations
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      @keyframes slideInUp {
        from { 
          opacity: 0;
          transform: translateY(20px) scale(0.95);
        }
        to { 
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      
      .performance-optimized {
        will-change: transform;
        transform: translateZ(0);
        backface-visibility: hidden;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [user, setUser] = useState(null);
  const [reportSummary, setReportSummary] = useState({
    total: 0,
    biomed: 0,
    biopetHealthy: 0,
    biopetUnhealthy: 0,
    biopetTotal: 0,
    latestCreatedAt: null
  });
  const [latestDate, setLatestDate] = useState('-'); // eslint-disable-line no-unused-vars
  const [members, setMembers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: '' });
  const [showMemberSettings, setShowMemberSettings] = useState(false);
  const [creatingReport, setCreatingReport] = useState(false);
  const [formData, setFormData] = useState({});
  const [calculatedData, setCalculatedData] = useState({}); // eslint-disable-line no-unused-vars
  const [scoreData, setScoreData] = useState({}); // eslint-disable-line no-unused-vars
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcStatusText, setCalcStatusText] = useState('');
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [reportType, setReportType] = useState("QPT"); // eslint-disable-line no-unused-vars
  const [viewingHistory, setViewingHistory] = useState(false);
  const [showHistoryPage, setShowHistoryPage] = useState(false); // eslint-disable-line no-unused-vars
  const [reportHistory, setReportHistory] = useState([]);
  const [groupedReportHistory, setGroupedReportHistory] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [showHistory, setShowHistory] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 30,
    hasNextPage: false,
    hasPrevPage: false
  });
  /** When true, history loaded all matching rows for grouping; UI shows only `limit` groups per page (client slice). */
  const [historyClientPaging, setHistoryClientPaging] = useState(false);
  /** Feed-order pages: each entry is `{ fullName, reports[] }[]` for one table page. */
  const [historyPeoplePages, setHistoryPeoplePages] = useState([]);
  /** History table: show people with only one loaded report vs. more than one. */
  const [historyReportCountTab, setHistoryReportCountTab] = useState('single');
  /** Increment when opening History from the sidebar so data refetches even if viewingHistory/showHistory stayed true (e.g. via Create Report). */
  const [historyEnterNonce, setHistoryEnterNonce] = useState(0);
  /** NEW badge counts per tab from API (payload is single- OR multiple-only; counts both without switching). */
  const [historyNewBadgeCounts, setHistoryNewBadgeCounts] = useState({
    single: 0,
    multiple: 0
  });
  /** Corporate client lab portal state */
  const [corpHistoryData, setCorpHistoryData] = useState([]);
  const [corpHistoryLoading, setCorpHistoryLoading] = useState(false);
  const [corpHistoryError, setCorpHistoryError] = useState(null);

  /** History source: standard `st_report` vs biopet tables. */
  const [historyPanelCategory, setHistoryPanelCategory] = useState('biomed');
  /**
   * Biopet history table: `biopet_report_healthy` vs `biopet_report`.
   * UI labels: Healthy / Unhealthy.
   */
  const [biopetHistoryHealthTab, setBiopetHistoryHealthTab] = useState('healthy');

  const historySourceParams = useMemo(
    () =>
      historyPanelCategory === 'biopet'
        ? { historyCategory: 'biopet', biopetHealth: biopetHistoryHealthTab }
        : { historyCategory: 'biomed' },
    [historyPanelCategory, biopetHistoryHealthTab]
  );

  const appendHistoryCategoryToSearchParams = (params) => {
    params.set('historyCategory', historyPanelCategory);
    if (historyPanelCategory === 'biopet') {
      params.set('biopetHealth', biopetHistoryHealthTab);
    } else {
      params.delete('biopetHealth');
    }
  };

  /* eslint-disable no-unused-vars */
  const [reportNumberFilter, setReportNumberFilter] = useState('');
  /* eslint-enable no-unused-vars */
  const [isFiltering, setIsFiltering] = useState(false);
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [selectedFilterColumn, setSelectedFilterColumn] = useState('Report Number');
  const [filterValue, setFilterValue] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  
  // Debug filter dropdown state changes
  useEffect(() => {
    console.log('🔍 Filter dropdown state changed:', showFilterDropdown);
    
    // Also log the history table state
    const historyTable = document.querySelector('.history-table');
    if (historyTable) {
      console.log('📊 History table classes:', historyTable.className);
      console.log('📊 History table disabled state:', historyTable.classList.contains('disabled'));
    }
  }, [showFilterDropdown]);
  const [showStaticPdf, setShowStaticPdf] = useState(false);
  /** True when static pet PDF was opened from History (Back returns to history, not CSV). */
  const [staticPdfFromHistory, setStaticPdfFromHistory] = useState(false);
  const [reportMode, setReportMode] = useState(null);

  const [showCsvPopup, setShowCsvPopup] = useState(false);
  /* eslint-disable no-unused-vars */
  const [csvPreviewData, setCsvPreviewData] = useState(null);
  /* eslint-enable no-unused-vars */
  const [csvFileName, setCsvFileName] = useState('');
  const [csvPreviewRows, setCsvPreviewRows] = useState([]); // instead of just one
  /** Pet Care Professional: Health header toggles raw CSV for all Unhealthy rows. */
  const [petHealthCsvColumnShowRaw, setPetHealthCsvColumnShowRaw] = useState(false);

  const [fromCsvPreview, setFromCsvPreview] = useState(false);
  const [isManualInput, setIsManualInput] = useState(false);

  const [isPdfReady, setIsPdfReady] = useState(false);

  const fileInputRef = React.useRef(null);
  /** Restore `reportMode` after closing Pet static preview opened from History. */
  const reportModeBeforePetHistoryRef = useRef(null);
  const [singleRowPreview, setSingleRowPreview] = useState(false);
  const [showCalcPopup, setShowCalcPopup] = useState(false);
  const [viewingStorage, setViewingStorage] = useState(false);
  const [viewingDatabase, setViewingDatabase] = useState(false);
  const [viewingAnalytics, setViewingAnalytics] = useState(false);
  const [viewingSetting, setViewingSetting] = useState(false);
  const [viewingFormScan, setViewingFormScan] = useState(false);
  const [settingSection, setSettingSection] = useState('biomed');
  const [animationKey, setAnimationKey] = useState(0);
  const [databaseData, setDatabaseData] = useState([]);
  const [databaseColumns, setDatabaseColumns] = useState([]);
  /** Raw Database source: BioMed `st_report` vs BioPet `biopet_report` / `biopet_report_healthy`. */
  const [rawDbTable, setRawDbTable] = useState('st_report');
  const [rawDbActiveButton, setRawDbActiveButton] = useState('st_report');
  const [databaseLoading, setDatabaseLoading] = useState(false);
  const [databaseError, setDatabaseError] = useState(null);
  const [databaseSearchTerm, setDatabaseSearchTerm] = useState('');
  const [databaseCurrentPage, setDatabaseCurrentPage] = useState(1);
  const [databaseRowsPerPage] = useState(50);
  const [selectedColumns, setSelectedColumns] = useState([]);
  // Raw Database table: the horizontal scrollbar lives ABOVE the table (a thin
  // sticky bar) and drives the table via translateX, so the wrapper is never a
  // scroll container and the sticky <th> can pin to .main_content's top edge.
  const databaseTableWrapperRef = useRef(null);
  const databaseTableRef = useRef(null);
  const databaseTopScrollRef = useRef(null);
  const databaseTopScrollSpacerRef = useRef(null);
  /* eslint-disable no-unused-vars */
  const [showColumnFilter, setShowColumnFilter] = useState(false);
  /* eslint-enable no-unused-vars */
  const [showNoColumns, setShowNoColumns] = useState(false);
  const [dbSelectedYear, setDbSelectedYear] = useState('');
  const [dbSelectedMonth, setDbSelectedMonth] = useState('');
  const [dbAvailableYears, setDbAvailableYears] = useState([]);
  const [dbAvailableMonths, setDbAvailableMonths] = useState([]);
  /* eslint-disable no-unused-vars */
  const [editingCell, setEditingCell] = useState(null);
  /* eslint-enable no-unused-vars */
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(false);
  const [showDatabaseCellModal, setShowDatabaseCellModal] = useState(false);
  const [editModalData, setEditModalData] = useState({ rowIndex: null, column: null, currentValue: '', rowData: null });
  const modalRenderRef = useRef(Date.now());

  /* eslint-disable no-unused-vars */
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [reportToEdit, setReportToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    report_name: '',
    report_type: '',
    report_date: '',
    report_time: '',
    report_status: ''
  });
  const [hoveredButton, setHoveredButton] = useState(null);
  const [showQptOptions, setShowQptOptions] = useState(true);
  const [biopetStockFormat, setBiopetStockFormat] = useState('essential');
  const [stockSection, setStockSection] = useState('biomed');
  /* eslint-enable no-unused-vars */
  const [hoveredMainButton, setHoveredMainButton] = useState(null);

  const [editStockKey, setEditStockKey] = useState(null);
  const [editStockValue, setEditStockValue] = useState('');
  const [showStockModal, setShowStockModal] = useState(false);

  const [confirmedRows, setConfirmedRows] = useState({});
  const [loadingConfirm, setLoadingConfirm] = useState({});
  const [allConfirmLoading, setAllConfirmLoading] = useState(false);
  const [allConfirmed, setAllConfirmed] = useState(false);

  const [hoveredConfirmIndex, setHoveredConfirmIndex] = useState(null);

  const [supplementResults, setSupplementResults] = useState({});
  const [coverageResults, setCoverageResults] = useState({});
  const [clientCounts, setClientCounts] = useState({});
  const [monthlyRowOrder, setMonthlyRowOrder] = useState(0);
  const [generatedClientIds, setGeneratedClientIds] = useState({});
  const [showClientIdPopup, setShowClientIdPopup] = useState(false);
  const [selectedClientIdIndex, setSelectedClientIdIndex] = useState(null);
  const [clientIdType, setClientIdType] = useState({}); // Store qPCR or 16S for each row

  // State for bulk download functionality
  const [selectedReports, setSelectedReports] = useState(new Set());
  const [selectAllReports, setSelectAllReports] = useState(false);
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  
  // State for ZIP download progress
  const [showZipProgress, setShowZipProgress] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);
  const [zipProgressText, setZipProgressText] = useState('');
  const [zipProgressDetails, setZipProgressDetails] = useState('');
  
  // Use ref for immediate cancellation (state updates are asynchronous)
  const isZipCancelledRef = useRef(false);
  
  // State for modify name modal
  const [showModifyNameModal, setShowModifyNameModal] = useState(false);
  const [modifyNameData, setModifyNameData] = useState({ id: null, currentName: '', newName: '' });
  const [modifyNameLoading, setModifyNameLoading] = useState(false);
  
  // State for inline name editing
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [editingNameLoading, setEditingNameLoading] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [detailedReportData, setDetailedReportData] = useState({});
  const [reportDataLoading, setReportDataLoading] = useState({});

  // State for password change modal
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');

  // State for modern notifications
  const [notifications, setNotifications] = useState([]);

  // Notification functions
  const addNotification = (message, type = 'success') => {
    const id = Date.now();
    const notification = {
      id,
      message,
      type,
      timestamp: new Date()
    };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      removeNotification(id);
    }, 5000);
  };

  /**
   * Which sidebar item is highlighted. Derived from the existing view flags so
   * this is presentation-only — no behaviour change. When the view flags are
   * collapsed into a single `activeView` state, this becomes that state.
   */
  const railView =
    creatingReport   ? 'create'
    : viewingHistory ? 'history'
    : viewingStorage ? 'storage'
    : viewingDatabase? 'database'
    : viewingAnalytics ? 'analytics'
    : viewingFormScan ? 'formscan'
    : viewingSetting ? 'setting'
    : 'home';

  // Convenience functions for different notification types
  const showSuccess = (message) => addNotification(message, 'success');
  const showError = (message) => addNotification(message, 'error');
  const showWarning = (message) => addNotification(message, 'warning');
  const showInfo = (message) => addNotification(message, 'info');

  const removeNotification = (id) => {
    // Add removal animation class
    const notificationElement = document.querySelector(`[data-notification-id="${id}"]`);
    if (notificationElement) {
      notificationElement.classList.add('removing');
      // Wait for animation to complete before removing
      setTimeout(() => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
      }, 300);
    } else {
      // Fallback: remove immediately if element not found
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }
  };

  // Load user data from JWT token on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      // No token found - ProtectedRoute should handle this, but as a safety check
      console.warn('⚠️ No token found in DashboardPage');
      // Don't redirect here - let ProtectedRoute handle it
      return;
    }
    
    try {
      // Decode JWT token to get user info
      const payload = JSON.parse(atob(token.split('.')[1]));
      console.log('🔍 JWT payload:', payload);
      
      // Handle different JWT payload structures
      const username = payload.username || payload.user || payload.sub || 'unknown';
      const role = payload.role || payload.userRole || payload.user_role || 'user';
      const account_type = payload.account_type || 'individual';
      const partner_name = payload.partner_name || null;

      setUser({
        username: username,
        role: role,
        account_type: account_type,
        partner_name: partner_name
      });
      console.log('🔍 User role set to:', role);
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      // Don't redirect immediately - token might still be valid on backend
      // Let API calls fail and interceptor handle it
    }
  }, []);

  // Ensure B2C column exists in database on component mount
  useEffect(() => {
    const ensureB2CColumnExists = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Use configured api instance which automatically adds Authorization header
          const response = await api.post('/api/reports/add-b2c-column', {});
          console.log('B2C column check result:', response.data);
        }
      } catch (error) {
        // Don't redirect on this setup call failure - it's not critical
        console.error('Error ensuring B2C column exists:', error);
        // Only log, don't throw - this shouldn't block dashboard access
      }
    };

    ensureB2CColumnExists();
  }, []);



  // Function to get the next monthly row order number
  const getNextMonthlyRowOrder = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/reports/monthly-row-order`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      return response.data.nextOrder;
    } catch (err) {
      console.error('Error getting monthly row order:', err);
      // Fallback: use current timestamp as order
      return Math.floor(Date.now() / 1000) % 1000;
    }
  };

  // Functions for bulk download functionality
  const handleReportSelection = (reportId) => {
    setSelectedReports(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };
  
  // Function to handle modify name
  const handleModifyName = (reportId, currentName) => {
    setModifyNameData({ id: reportId, currentName, newName: currentName });
    setShowModifyNameModal(true);
  };
  
  // Function to start inline editing
  const handleStartEdit = (reportId, currentName) => {
    setEditingNameId(reportId);
    setEditingNameValue(currentName);
  };

  const getReportFullNameRaw = (report) => {
    if (!report || typeof report !== 'object') return '';
    const tryKeys = [
      'Full Name',
      'full name',
      'Full name',
      'FULL NAME',
      'full_name',
      'FullName',
      'fullName'
    ];
    for (const k of tryKeys) {
      const v = report[k];
      if (v != null && String(v).trim() !== '') return v;
    }
    const dyn = Object.keys(report).find((k) => {
      const n = k.toLowerCase().replace(/[\s_-]/g, '');
      return n === 'fullname';
    });
    if (dyn != null && report[dyn] != null && String(report[dyn]).trim() !== '') {
      return report[dyn];
    }
    return '';
  };

  /**
   * Group key: prefer API `fullNameGroupKey` (same SQL as GROUP BY) so counts match st_report exactly;
   * fallback for older responses / non-grouped fetches.
   */
  const fullNameGroupKeyFromReport = (report) => {
    if (!report || typeof report !== 'object') return 'unknown';
    let srv = report.fullNameGroupKey ?? report.fullnamegroupkey;
    if (srv == null) {
      const alt = Object.keys(report).find(
        (x) => x.replace(/[\s_-]/g, '').toLowerCase() === 'fullnamegroupkey'
      );
      if (alt != null) srv = report[alt];
    }
    if (srv != null && String(srv).trim() !== '') {
      return String(srv).trim();
    }

    let raw = getReportFullNameRaw(report);
    if (typeof Buffer !== 'undefined' && Buffer.isBuffer && Buffer.isBuffer(raw)) {
      raw = raw.toString('utf8');
    }
    if (raw == null || raw === '') return 'unknown';
    let s = String(raw)
      .replace(/[\u00A0\uFEFF\u2000-\u200B]/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
    if (!s) return 'unknown';
    try {
      s = s.normalize('NFC');
    } catch {
      /* ignore */
    }
    return s.toLocaleLowerCase('en-US');
  };

  // Group by Full Name; keys come from server `fullNameGroupKey` when present (history API).
  const groupReportsByFullName = (reports) => {
    if (!Array.isArray(reports) || reports.length === 0) {
      setGroupedReportHistory([]);
      return;
    }

    const grouped = {};
    
    reports.forEach(report => {
      const fullName = fullNameGroupKeyFromReport(report);
      if (!grouped[fullName]) {
        grouped[fullName] = [];
      }
      grouped[fullName].push(report);
    });

    // Convert to array and sort by creation time (latest first), then by group key (case-folded full name).
    const groupedArray = Object.entries(grouped).map(([fullName, reports]) => ({
      fullName,
      reports: reports.sort((a, b) => {
        // Prioritize create_time (actual creation timestamp) over Report Date
        const dateA = new Date(a['create_time'] || a['Report Date'] || 0);
        const dateB = new Date(b['create_time'] || b['Report Date'] || 0);
        return dateB - dateA; // Latest first
      })
    })).sort((a, b) => {
      // Sort groups by the latest report creation time (newest first)
      const latestA = new Date(a.reports[0]['create_time'] || a.reports[0]['Report Date'] || 0);
      const latestB = new Date(b.reports[0]['create_time'] || b.reports[0]['Report Date'] || 0);
      return latestB - latestA; // Latest group first
    });

    setGroupedReportHistory(groupedArray);
  };

  // Group reports whenever reportHistory changes (used for non–client-paging mode and recalculate helpers).
  // With client paging, the table uses feed-order pages (buildPeoplePagesByFeed): first N *new* people in global
  // newest-first order, each with all loaded rows for that name.
  useEffect(() => {
    groupReportsByFullName(reportHistory);
  }, [reportHistory]);

  // Fetch corporate history when a corporate client enters the history section
  useEffect(() => {
    if (!(showHistory || viewingHistory)) return;
    if (user?.account_type !== 'corporate_client') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setCorpHistoryLoading(true);
    setCorpHistoryError(null);
    fetch('/api/reports/corporate-history', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(json => {
        if (json.data) setCorpHistoryData(json.data);
        else setCorpHistoryError(json.message || 'Failed to load data');
      })
      .catch(() => setCorpHistoryError('Network error'))
      .finally(() => setCorpHistoryLoading(false));
  }, [showHistory, viewingHistory, user?.account_type]);

  useEffect(() => {
    if (!(showHistory || viewingHistory)) return;
    if (!historyClientPaging) {
      setHistoryPeoplePages([]);
      if (groupedReportHistory.length > 0) {
        setPagination(prev => ({
          ...prev,
          hasNextPage: prev.currentPage < prev.totalPages,
          hasPrevPage: prev.currentPage > 1
        }));
      }
      return;
    }
    if (!Array.isArray(reportHistory) || reportHistory.length === 0) {
      setHistoryPeoplePages([]);
      setPagination(prev => ({
        ...prev,
        totalPages: 1,
        currentPage: 1,
        hasNextPage: false,
        hasPrevPage: false
      }));
      return;
    }
    const pages = buildPeoplePagesByFeed(
      reportHistory,
      fullNameGroupKeyFromReport,
      pagination.limit
    );
    setHistoryPeoplePages(pages);
    const tp = Math.max(1, pages.length);
    setPagination(prev => {
      const page = Math.min(Math.max(1, prev.currentPage), tp);
      return {
        ...prev,
        totalPages: tp,
        currentPage: page,
        hasNextPage: page < tp,
        hasPrevPage: page > 1
      };
    });
  }, [reportHistory, historyClientPaging, pagination.limit, showHistory, viewingHistory]);

  /** Incremented each history fetch; ignore stale responses when deps change quickly. */
  const historyFetchReqIdRef = useRef(0);
  const prevHistoryReportCountTabRef = useRef(historyReportCountTab);
  useEffect(() => {
    if (!(showHistory || viewingHistory)) {
      prevHistoryReportCountTabRef.current = historyReportCountTab;
      return;
    }
    if (prevHistoryReportCountTabRef.current === historyReportCountTab) return;
    prevHistoryReportCountTabRef.current = historyReportCountTab;
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    setExpandedGroups(new Set());
  }, [historyReportCountTab, showHistory, viewingHistory]);

  useEffect(() => {
    if (!(showHistory || viewingHistory) || !historyClientPaging) return;
    const tp = Math.max(1, historyPeoplePages.length);
    setPagination(prev => ({
      ...prev,
      totalPages: tp,
      hasNextPage: prev.currentPage < tp,
      hasPrevPage: prev.currentPage > 1
    }));
  }, [
    pagination.currentPage,
    historyPeoplePages,
    historyClientPaging,
    showHistory,
    viewingHistory
  ]);

  const groupedReportHistoryByTab = useMemo(() => {
    if (historyReportCountTab === 'single') {
      return groupedReportHistory.filter((g) => g.reports.length === 1);
    }
    return groupedReportHistory.filter((g) => g.reports.length > 1);
  }, [groupedReportHistory, historyReportCountTab]);

  const historyTableGroups =
    historyClientPaging && (showHistory || viewingHistory)
      ? historyPeoplePages[pagination.currentPage - 1] || []
      : groupedReportHistoryByTab;

  // Real-time update for row NEW badges (check every minute to handle 11:00 PM reset)
  useEffect(() => {
    const interval = setInterval(() => {
      setGroupedReportHistory(prev => [...prev]);
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Function to toggle group expansion
  const toggleGroup = (fullName) => {
    // Prevent expansion if filter dropdown is open
    if (showFilterDropdown) {
      console.log('🚫 Filter dropdown is open, preventing group expansion for:', fullName);
      return;
    }
    
    console.log('✅ Expanding/collapsing group:', fullName);
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fullName)) {
        newSet.delete(fullName);
      } else {
        newSet.add(fullName);
      }
      return newSet;
    });
  };
  
  // ---- History Result column performance -----------------------------------
  // calculateReportResultRowByRow is pure for a given report's detailed data, but it used
  // to run for EVERY visible row on EVERY re-render (every checkbox click, every state
  // change) — plus dozens of console.logs per run. Cache the result per report id.
  const rowResultCacheRef = useRef(new Map());
  const getRowByRowResult = (panel, detailedData, reportId) => {
    const cache = rowResultCacheRef.current;
    if (cache.has(reportId)) return cache.get(reportId);
    const result = calculateReportResultRowByRow(panel, detailedData, reportId);
    cache.set(reportId, result);
    return result;
  };

  // Ids already requested through the batch prefetch (or that failed once) — consulted via
  // ref so the prefetch effect only depends on which page/groups are visible.
  const batchRequestedIdsRef = useRef(new Set());
  const detailedReportDataRef = useRef(detailedReportData);
  detailedReportDataRef.current = detailedReportData;

  // Prefetch the visible page's detailed rows in ONE request (/api/reports/by-ids) with a
  // single state merge, instead of the per-row render-triggered /by-id fetches (each of
  // which committed its own update and re-rendered the entire dashboard).
  //
  // The ids are RESERVED synchronously (useMemo runs during the same render, before the
  // table JSX below evaluates) so the rows' own render-time fetch trigger sees them as
  // already claimed and shows "Calculating…" without firing a request storm on first paint.
  const pendingBatchIdsRef = useRef([]);
  useMemo(() => {
    if (!(showHistory || viewingHistory)) return;
    const wanted = [];
    for (const group of historyTableGroups) {
      for (const r of group.reports || []) {
        if (r._historyCategory === 'biopet') continue;
        if (detailedReportDataRef.current[r.id]) continue;
        if (batchRequestedIdsRef.current.has(r.id)) continue;
        batchRequestedIdsRef.current.add(r.id);
        wanted.push(r.id);
      }
    }
    if (wanted.length) pendingBatchIdsRef.current.push(...wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyTableGroups, showHistory, viewingHistory]);

  useEffect(() => {
    const wanted = pendingBatchIdsRef.current.splice(0);
    if (!wanted.length) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        // Chunk to the server's 100-id cap.
        const merged = {};
        for (let i = 0; i < wanted.length; i += 100) {
          const chunk = wanted.slice(i, i + 100);
          const res = await axios.get(`${process.env.REACT_APP_API_BASE}/api/reports/by-ids`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { ids: chunk.join(',') }
          });
          Object.assign(merged, res.data?.reports || {});
        }
        if (!alive || !Object.keys(merged).length) return;
        setDetailedReportData((prev) => ({ ...merged, ...prev }));
      } catch (err) {
        console.warn('Batch report prefetch failed (rows will fetch individually):', err.message);
        // Release the claim so the per-row fallback can try again for these ids.
        wanted.forEach((id) => batchRequestedIdsRef.current.delete(id));
      }
    })();
    return () => { alive = false; };
  }, [historyTableGroups, showHistory, viewingHistory]);

  // Function to fetch detailed report data for result calculations
  const fetchDetailedReportData = async (reportId) => {
    const meta = reportHistory.find((x) => String(x.id) === String(reportId));
    if (meta && meta._historyCategory === 'biopet') {
      return null;
    }

    // Check if we already have the data
    if (detailedReportData[reportId]) {
      return detailedReportData[reportId];
    }

    // Check if we're already loading this report
    if (reportDataLoading[reportId]) {
      return null;
    }

    setReportDataLoading(prev => ({ ...prev, [reportId]: true }));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/reports/by-id/${reportId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const reportData = response.data;
      
      // Store the detailed data
      setDetailedReportData(prev => ({ ...prev, [reportId]: reportData }));
      
      return reportData;
    } catch (error) {
      console.error('Error fetching detailed report data:', error);
      return null;
    } finally {
      setReportDataLoading(prev => ({ ...prev, [reportId]: false }));
    }
  };

  // Function to recalculate all results (clear cache and refetch)
  const recalculateAllResults = () => {
    console.log('🔄 Recalculating all results...');
    rowResultCacheRef.current.clear();
    batchRequestedIdsRef.current.clear();
    setDetailedReportData({});
    setReportDataLoading({});
    
    // groupedReportHistory covers all loaded rows (full history when clientGroupPagination loaded everything).
    groupedReportHistory.forEach(group => {
      group.reports.forEach(report => {
        if (
          report._historyCategory === 'biopet' ||
          !report.Panel ||
          !isPanelSupported(report.Panel)
        ) {
          return;
        }
        fetchDetailedReportData(report.id);
      });
    });
  };

  // Function to save inline edited name
  const handleSaveName = async (reportId) => {
    if (!editingNameValue.trim()) {
      alert('Please enter a valid name');
      return;
    }
    
    setEditingNameLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 Updating name for report ID:', reportId);
      console.log('🔍 New name:', editingNameValue.trim());
      console.log('🔍 API URL:', `${process.env.REACT_APP_API_BASE}/api/reports/update-name/${reportId}`);
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE}/api/reports/update-name/${reportId}`,
        { fullName: editingNameValue.trim() },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: historySourceParams
        }
      );
      
      console.log('🔍 API Response:', response);
      
      if (response.data.success) {
        // Update the local state
        setReportHistory(prev => 
          prev.map(report => 
            report.id === reportId 
              ? { ...report, 'Full Name': editingNameValue.trim() }
              : report
          )
        );
        
        // Exit edit mode
        setEditingNameId(null);
        setEditingNameValue('');
        
        // Show success notification
        setShowSuccessNotification(true);
        setTimeout(() => {
          // Add hiding class for smooth animation
          const notification = document.querySelector('.success-notification');
          if (notification) {
            notification.classList.add('hiding');
            // Remove after animation completes
            setTimeout(() => {
              setShowSuccessNotification(false);
            }, 300);
          } else {
            // Fallback if DOM element not found
            setTimeout(() => {
              setShowSuccessNotification(false);
            }, 1000);
          }
        }, 2000);
      } else {
        console.log('🔍 API returned success: false');
        console.log('🔍 Response data:', response.data);
        alert(`Failed to update name: ${response.data.message || 'Unknown error'}`);
      }
      
    } catch (error) {
      console.error('🔍 Error updating name:', error);
      console.error('🔍 Error response:', error.response);
      console.error('🔍 Error status:', error.response?.status);
      console.error('🔍 Error data:', error.response?.data);
      
      if (error.response?.status === 404) {
        alert('Report not found. Please refresh the page and try again.');
      } else if (error.response?.status === 400) {
        alert('Invalid name format. Please enter a valid name.');
      } else if (error.response?.status === 401) {
        alert('Authentication failed. Please log in again.');
      } else if (error.response?.status === 500) {
        alert('Server error. Please try again later.');
      } else {
        alert(`Error updating name: ${error.message}`);
      }
    } finally {
      setEditingNameLoading(false);
    }
  };
  
  // Function to cancel inline editing
  const handleCancelEdit = () => {
    setEditingNameId(null);
    setEditingNameValue('');
  };
  
  // Function to save modified name
  const handleSaveModifiedName = async () => {
    if (!modifyNameData.newName.trim()) {
      alert('Please enter a valid name');
      return;
    }
    
    setModifyNameLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE}/api/reports/update-name/${modifyNameData.id}`,
        { fullName: modifyNameData.newName.trim() },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: historySourceParams
        }
      );
      
      if (response.data.success) {
        // Update the local state
        setReportHistory(prev => 
          prev.map(report => 
            report.id === modifyNameData.id 
              ? { ...report, 'Full Name': modifyNameData.newName.trim() }
              : report
          )
        );
        
        setShowModifyNameModal(false);
        setModifyNameData({ id: null, currentName: '', newName: '' });
        alert('Name updated successfully!');
      } else {
        alert('Failed to update name. Please try again.');
      }
    } catch (error) {
      console.error('Error updating name:', error);
      alert('Error updating name. Please try again.');
    } finally {
      setModifyNameLoading(false);
    }
  };

  // Password change functions
  const handlePasswordChange = () => {
    setShowPasswordChangeModal(true);
    setPasswordChangeData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordChangeError('');
  };

  const handlePasswordChangeSubmit = async () => {
    // Validation
    if (!passwordChangeData.currentPassword || !passwordChangeData.newPassword || !passwordChangeData.confirmPassword) {
      setPasswordChangeError('All fields are required');
      showError('All fields are required');
      return;
    }

    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      setPasswordChangeError('New password and confirm password do not match');
      showError('New password and confirm password do not match');
      return;
    }

    if (passwordChangeData.newPassword.length < 6) {
      setPasswordChangeError('New password must be at least 6 characters long');
      showError('New password must be at least 6 characters long');
      return;
    }

    setPasswordChangeLoading(true);
    setPasswordChangeError('');

    try {
      const token = localStorage.getItem('token');
      const apiUrl = `${process.env.REACT_APP_API_BASE}/api/users/change-password`;
      
      console.log('🔐 Attempting to change password...');
      console.log('🔐 API URL:', apiUrl);
      console.log('🔐 Token exists:', !!token);
      
      const response = await axios.put(
        apiUrl,
        {
          currentPassword: passwordChangeData.currentPassword,
          newPassword: passwordChangeData.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('🔐 Password change response:', response.data);

      if (response.data.success) {
        showSuccess('Password changed successfully!');
        setShowPasswordChangeModal(false);
        setPasswordChangeData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setPasswordChangeError(response.data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('🔐 Error changing password:', error);
      console.error('🔐 Error response:', error.response);
      
      if (error.response?.status === 401) {
        setPasswordChangeError('Current password is incorrect');
        showError('Current password is incorrect');
      } else if (error.response?.status === 400) {
        setPasswordChangeError(error.response.data.message || 'Invalid password format');
        showError(error.response.data.message || 'Invalid password format');
      } else if (error.response?.status === 404) {
        setPasswordChangeError('API endpoint not found. Please contact administrator.');
        showError('API endpoint not found. Please contact administrator.');
      } else if (error.response?.status === 500) {
        setPasswordChangeError('Server error. Please try again later.');
        showError('Server error. Please try again later.');
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        setPasswordChangeError('Network error. Please check your connection.');
        showError('Network error. Please check your connection.');
      } else {
        setPasswordChangeError(`Failed to change password: ${error.message}`);
        showError(`Failed to change password: ${error.message}`);
      }
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const handlePasswordChangeCancel = () => {
    setShowPasswordChangeModal(false);
    setPasswordChangeData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordChangeError('');
  };

  // Helper functions to generate PDF content for different report types
  const generateQPTContent = (data) => {
    return `
      <div style="font-family: "Gilroy-Regular", sans-serif; padding: 20px;">
        <h1 style="color: #0b4780; text-align: center;">QPT Report</h1>
        <h2>Patient Information</h2>
        <p><strong>Full Name:</strong> ${data['Full Name'] || 'N/A'}</p>
        <p><strong>Report Number:</strong> ${data['Report number'] || 'N/A'}</p>
        <p><strong>Client ID:</strong> ${data['Client ID'] || 'N/A'}</p>
        <p><strong>Panel:</strong> ${data.Panel || 'N/A'}</p>
        <p><strong>Supplement:</strong> ${data.Supplement || 'N/A'}</p>
        <p><strong>Created:</strong> ${data.Created || 'N/A'}</p>
      </div>
    `;
  };

  const generate9PContent = (data) => {
    return `
      <div style="font-family: "Gilroy-Regular", sans-serif; padding: 20px;">
        <h1 style="color: #0b4780; text-align: center;">9P Report</h1>
        <h2>Patient Information</h2>
        <p><strong>Full Name:</strong> ${data['Full Name'] || 'N/A'}</p>
        <p><strong>Report Number:</strong> ${data['Report number'] || 'N/A'}</p>
        <p><strong>Client ID:</strong> ${data['Client ID'] || 'N/A'}</p>
        <p><strong>Panel:</strong> ${data.Panel || 'N/A'}</p>
        <p><strong>Supplement:</strong> ${data.Supplement || 'N/A'}</p>
        <p><strong>Created:</strong> ${data.Created || 'N/A'}</p>
      </div>
    `;
  };

  const generateAllergyContent = (data) => {
    return `
      <div style="font-family: "Gilroy-Regular", sans-serif; padding: 20px;">
        <h1 style="color: #0b4780; text-align: center;">Allergy and Skin Report</h1>
        <h2>Patient Information</h2>
        <p><strong>Full Name:</strong> ${data['Full Name'] || 'N/A'}</p>
        <p><strong>Report Number:</strong> ${data['Report number'] || 'N/A'}</p>
        <p><strong>Client ID:</strong> ${data['Client ID'] || 'N/A'}</p>
        <p><strong>Panel:</strong> ${data.Panel || 'N/A'}</p>
        <p><strong>Supplement:</strong> ${data.Supplement || 'N/A'}</p>
        <p><strong>Created:</strong> ${data.Created || 'N/A'}</p>
      </div>
    `;
  };

  const generateToxinContent = (data) => {
    return `
      <div style="font-family: "Gilroy-Regular", sans-serif; padding: 20px;">
        <h1 style="color: #0b4780; text-align: center;">Toxin Report</h1>
        <h2>Patient Information</h2>
        <p><strong>Full Name:</strong> ${data['Full Name'] || 'N/A'}</p>
        <p><strong>Report Number:</strong> ${data['Report number'] || 'N/A'}</p>
        <p><strong>Client ID:</strong> ${data['Client ID'] || 'N/A'}</p>
        <p><strong>Panel:</strong> ${data.Panel || 'N/A'}</p>
        <p><strong>Supplement:</strong> ${data.Supplement || 'N/A'}</p>
        <p><strong>Created:</strong> ${data.Created || 'N/A'}</p>
      </div>
    `;
  };

  const generateOmsContent = (data) => {
    return `
      <div style="font-family: "Gilroy-Regular", sans-serif; padding: 20px;">
        <h1 style="color: #0b4780; text-align: center;">OMS Report</h1>
        <h2>Patient Information</h2>
        <p><strong>Full Name:</strong> ${data['Full Name'] || 'N/A'}</p>
        <p><strong>Report Number:</strong> ${data['Report number'] || 'N/A'}</p>
        <p><strong>Client ID:</strong> ${data['Client ID'] || 'N/A'}</p>
        <p><strong>Panel:</strong> ${data.Panel || 'N/A'}</p>
        <p><strong>Supplement:</strong> ${data.Supplement || 'N/A'}</p>
        <p><strong>Created:</strong> ${data.Created || 'N/A'}</p>
      </div>
    `;
  };

  const generateBwcAllergyContent = (data) => {
    return `
      <div style="font-family: "Gilroy-Regular", sans-serif; padding: 20px;">
        <h1 style="color: #0b4780; text-align: center;">BWC Allergy Report</h1>
        <h2>Patient Information</h2>
        <p><strong>Full Name:</strong> ${data['Full Name'] || 'N/A'}</p>
        <p><strong>Report Number:</strong> ${data['Report number'] || 'N/A'}</p>
        <p><strong>Client ID:</strong> ${data['Client ID'] || 'N/A'}</p>
        <p><strong>Panel:</strong> ${data.Panel || 'N/A'}</p>
        <p><strong>Supplement:</strong> ${data.Supplement || 'N/A'}</p>
        <p><strong>Created:</strong> ${data.Created || 'N/A'}</p>
      </div>
    `;
  };

  const generateBwcCoreContent = (data) => {
    return `
      <div style="font-family: "Gilroy-Regular", sans-serif; padding: 20px;">
        <h1 style="color: #0b4780; text-align: center;">BWC Core Report</h1>
        <h2>Patient Information</h2>
        <p><strong>Full Name:</strong> ${data['Full Name'] || 'N/A'}</p>
        <p><strong>Report Number:</strong> ${data['Report number'] || 'N/A'}</p>
        <p><strong>Client ID:</strong> ${data['Client ID'] || 'N/A'}</p>
        <p><strong>Panel:</strong> ${data.Panel || 'N/A'}</p>
        <p><strong>Supplement:</strong> ${data.Supplement || 'N/A'}</p>
        <p><strong>Created:</strong> ${data.Created || 'N/A'}</p>
      </div>
    `;
  };

  const generateDefaultContent = (data) => {
    return `
      <div style="font-family: "Gilroy-Regular", sans-serif; padding: 20px;">
        <h1 style="color: #0b4780; text-align: center;">Report</h1>
        <h2>Patient Information</h2>
        <p><strong>Full Name:</strong> ${data['Full Name'] || 'N/A'}</p>
        <p><strong>Report Number:</strong> ${data['Report number'] || 'N/A'}</p>
        <p><strong>Client ID:</strong> ${data['Client ID'] || 'N/A'}</p>
        <p><strong>Panel:</strong> ${data.Panel || 'N/A'}</p>
        <p><strong>Supplement:</strong> ${data.Supplement || 'N/A'}</p>
        <p><strong>Created:</strong> ${data.Created || 'N/A'}</p>
      </div>
    `;
  };

  // Function to keep the process alive during long operations (aggressive anti-throttling)
  const keepAlive = async (ms) => {
    return new Promise(resolve => {
      const startTime = Date.now();
      
      // Create a more aggressive keep-alive mechanism
      const keepAliveInterval = setInterval(() => {
        // Force the browser to stay active
        requestAnimationFrame(() => {
          Math.random();
          
          // DOM operations to keep tab alive
          const temp = document.createElement('div');
          temp.style.position = 'absolute';
          temp.style.left = '-9999px';
          temp.textContent = `keepalive-${Date.now()}`;
          temp.style.opacity = '0.01';
          temp.style.pointerEvents = 'none';
          document.body.appendChild(temp);
          
          // Force a reflow
          void temp.offsetHeight;
          
          document.body.removeChild(temp);
        });
        
        // Check if we've reached the timeout
        if (Date.now() - startTime >= ms) {
          clearInterval(keepAliveInterval);
          resolve();
        }
      }, 16); // 60fps updates to prevent throttling
    });
  };

  // Function to force the process to continue even when tab is inactive (aggressive anti-throttling)
  const forceContinue = async (operation, maxTime = 60000) => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      let isCompleted = false;
      
      // Set up a watchdog timer with very aggressive anti-throttling
      const watchdog = setInterval(() => {
        if (isCompleted) {
          clearInterval(watchdog);
          return;
        }
        
        if (Date.now() - startTime > maxTime) {
          clearInterval(watchdog);
          reject(new Error('Operation timed out'));
          return;
        }
        
        // Very aggressive work to prevent throttling
        requestAnimationFrame(() => {
          Math.random();
          
          // DOM operations to keep tab alive
          const temp = document.createElement('div');
          temp.style.position = 'absolute';
          temp.style.left = '-9999px';
          temp.textContent = `watchdog-${Date.now()}`;
          temp.style.opacity = '0.01';
          temp.style.pointerEvents = 'none';
          document.body.appendChild(temp);
          
          // Force a reflow
          void temp.offsetHeight;
          
          document.body.removeChild(temp);
          
          // Additional CPU work
          let result = 0;
          for (let i = 0; i < 1000; i++) {
            result += Math.sin(i) * Math.cos(i);
          }
        });
      }, 16); // 60fps updates for maximum anti-throttling
      
      // Execute the operation
      operation().then(result => {
        isCompleted = true;
        clearInterval(watchdog);
        resolve(result);
      }).catch(error => {
        isCompleted = true;
        clearInterval(watchdog);
        reject(error);
      });
    });
  };

  // Function to start background processing using WEB WORKER trick (Chrome respects these!)
  const startBackgroundProcessing = () => {
    console.log('🚀 Starting WEB WORKER trick - Chrome respects Web Workers!');
    const intervals = [];
    
    // TRICK 1: Core Web Worker for Background Processing (Chrome doesn't throttle these!)
    let coreWorker = null;
    let workerInterval = null;
    
         // 🚀 THE "ALMOST ALWAYS WORKS" COMBO VARIABLES
     let websocket = null;
    let audioContext = null;
    let oscillator = null;
    let hiddenCanvas = null;
    let videoStream = null;
    let mediaRecorder = null;
    let ctx = null; // Canvas context for animation
    
    try {
      // Create Web Worker with core logic
      const workerCode = `
        // Web Worker: Core Background Processing (ULTRA AGGRESSIVE)
        let counter = 0;
        let isActive = true;
        
        // ULTRA AGGRESSIVE: Controlled loop with minimal delay to prevent stack overflow
        const ultraAggressiveLoop = () => {
          if (!isActive) return;
          
          counter++;
          
          // INTENSIVE background work to stay active
          let result = 0;
          for (let i = 0; i < 5000; i++) {
            result += Math.sin(i) * Math.cos(i);
          }
          
          // Additional intensive work
          let arrayWork = new Array(1000).fill(0);
          arrayWork.forEach((_, i) => {
            arrayWork[i] = i * Math.random() * Math.random();
          });
          
          // Send keep-alive message to main thread
          self.postMessage({ 
            type: 'worker-keepalive', 
            counter: counter, 
            result: result,
            arraySum: arrayWork.reduce((a, b) => a + b, 0),
            timestamp: Date.now()
          });
          
          // Use setTimeout with minimal delay to prevent stack overflow
          setTimeout(ultraAggressiveLoop, 1); // 1ms delay - Chrome can't throttle this
        };
        
        // Start the ULTRA AGGRESSIVE loop
        ultraAggressiveLoop();
        
        // Handle messages from main thread
        self.onmessage = (e) => {
          if (e.data === 'stop') {
            isActive = false;
            self.postMessage({ type: 'worker-stopped' });
          } else if (e.data === 'ping') {
            // Respond to ping with pong
            self.postMessage({ type: 'pong', timestamp: Date.now() });
          }
        };
        
        // Send initial ready message
        self.postMessage({ type: 'worker-ready' });
      `;
      
      // Create blob URL for worker
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const workerUrl = URL.createObjectURL(blob);
      
      // Create the worker
      coreWorker = new Worker(workerUrl);
      
      // Handle worker messages
      coreWorker.onmessage = (e) => {
        if (e.data.type === 'worker-ready') {
          console.log('✅ Core Web Worker ready - Chrome will NOT throttle this!');
        } else if (e.data.type === 'worker-keepalive') {
          // Force main thread to stay active
          Math.random();
          
          // Lightweight DOM access to keep tab alive
          if (document.body) {
            void document.body.offsetHeight;
          }
        } else if (e.data.type === 'pong') {
          // Worker is responding to pings
          Math.random();
        }
      };
      
      // Keep worker active with pings
      workerInterval = setInterval(() => {
        if (coreWorker) {
          coreWorker.postMessage('ping');
        }
      }, 100); // 10fps pings
      
      // Cleanup function
      intervals.push(() => {
        if (workerInterval) clearInterval(workerInterval);
        if (coreWorker) {
          coreWorker.postMessage('stop');
          coreWorker.terminate();
        }
        URL.revokeObjectURL(workerUrl);
      });
      
    } catch (e) {
      console.log('❌ Web Worker not available:', e);
    }
    
    // TRICK 2: Secondary Web Worker for Additional Reliability
    let secondaryWorker = null;
    let secondaryInterval = null;
    
    try {
      // Create secondary worker with different timing
      const secondaryWorkerCode = `
        // Secondary Web Worker: Backup Background Processing (ULTRA AGGRESSIVE)
        let counter = 0;
        let isActive = true;
        
        // ULTRA AGGRESSIVE: Controlled loop with minimal delay to prevent stack overflow
        const ultraAggressiveSecondaryLoop = () => {
          if (!isActive) return;
          
          counter++;
          
          // INTENSIVE different background work
          let result = 0;
          for (let i = 0; i < 3000; i++) {
            result += Math.random() * Math.random();
          }
          
          // Additional intensive array work
          let arrayWork = new Array(800).fill(0);
          arrayWork.forEach((_, i) => {
            arrayWork[i] = i * Math.sin(i) * Math.cos(i);
          });
          
          // Send secondary keep-alive
          self.postMessage({ 
            type: 'secondary-keepalive', 
            counter: counter, 
            result: result,
            arraySum: arrayWork.reduce((a, b) => a + b, 0),
            timestamp: Date.now()
          });
          
          // Use setTimeout with minimal delay to prevent stack overflow
          setTimeout(ultraAggressiveSecondaryLoop, 2); // 2ms delay - Chrome can't throttle this
        };
        
        // Start ULTRA AGGRESSIVE secondary loop
        ultraAggressiveSecondaryLoop();
        
        // Handle messages
        self.onmessage = (e) => {
          if (e.data === 'stop') {
            isActive = false;
            self.postMessage({ type: 'secondary-stopped' });
          }
        };
        
        // Send ready message
        self.postMessage({ type: 'secondary-ready' });
      `;
      
      // Create secondary worker
      const secondaryBlob = new Blob([secondaryWorkerCode], { type: 'application/javascript' });
      const secondaryWorkerUrl = URL.createObjectURL(secondaryBlob);
      
      secondaryWorker = new Worker(secondaryWorkerUrl);
      
      // Handle secondary worker messages
      secondaryWorker.onmessage = (e) => {
        if (e.data.type === 'secondary-ready') {
          console.log('✅ Secondary Web Worker ready - Double protection!');
        } else if (e.data.type === 'secondary-keepalive') {
          // Additional main thread activity
          Math.random();
          
          // More DOM access
          if (document.body) {
            void document.body.scrollHeight;
          }
        }
      };
      
      // Keep secondary worker active
      secondaryInterval = setInterval(() => {
        if (secondaryWorker) {
          // Send keep-alive to secondary worker
          Math.random();
        }
      }, 150); // 6.67fps - different timing
      
      // Cleanup for secondary worker
      intervals.push(() => {
        if (secondaryInterval) clearInterval(secondaryInterval);
        if (secondaryWorker) {
          secondaryWorker.postMessage('stop');
          secondaryWorker.terminate();
        }
        URL.revokeObjectURL(secondaryWorkerUrl);
      });
      
    } catch (e) {
      console.log('❌ Secondary Web Worker not available:', e);
    }
    
    // TRICK 3: PDF Generation Web Worker (Specialized for PDF tasks)
    let pdfWorker = null;
    let pdfWorkerInterval = null;
    
    try {
      // Create specialized PDF worker
      const pdfWorkerCode = `
        // PDF Generation Web Worker: Specialized for PDF tasks (ULTRA AGGRESSIVE)
        let counter = 0;
        let isActive = true;
        
        // ULTRA AGGRESSIVE: PDF-specific background work with minimal delay
        const ultraAggressivePdfLoop = () => {
          if (!isActive) return;
          
          counter++;
          
          // INTENSIVE PDF-related calculations
          let result = 0;
          for (let i = 0; i < 4000; i++) {
            result += Math.tan(i) * Math.atan(i);
          }
          
          // Additional intensive PDF work
          let pdfArray = new Array(1200).fill(0);
          pdfArray.forEach((_, i) => {
            pdfArray[i] = i * Math.log(i + 1) * Math.exp(i * 0.001);
          });
          
          // Send PDF keep-alive
          self.postMessage({ 
            type: 'pdf-keepalive', 
            counter: counter, 
            result: result,
            pdfArraySum: pdfArray.reduce((a, b) => a + b, 0),
            timestamp: Date.now(),
            message: 'PDF processing active'
          });
          
          // Use setTimeout with minimal delay to prevent stack overflow
          setTimeout(ultraAggressivePdfLoop, 3); // 3ms delay - Chrome can't throttle this
        };
        
        // Start ULTRA AGGRESSIVE PDF loop
        ultraAggressivePdfLoop();
        
        // Handle PDF-specific messages
        self.onmessage = (e) => {
          if (e.data === 'stop') {
            isActive = false;
            self.postMessage({ type: 'pdf-stopped' });
          } else if (e.data === 'pdf-ping') {
            // Respond to PDF-specific pings
            self.postMessage({ type: 'pdf-pong', timestamp: Date.now() });
          }
        };
        
        // Send ready message
        self.postMessage({ type: 'pdf-ready' });
      `;
      
      // Create PDF worker
      const pdfBlob = new Blob([pdfWorkerCode], { type: 'application/javascript' });
      const pdfWorkerUrl = URL.createObjectURL(pdfBlob);
      
      pdfWorker = new Worker(pdfWorkerUrl);
      
      // Handle PDF worker messages
      pdfWorker.onmessage = (e) => {
        if (e.data.type === 'pdf-ready') {
          console.log('✅ PDF Generation Web Worker ready - PDF tasks protected!');
        } else if (e.data.type === 'pdf-keepalive') {
          // PDF-specific main thread activity
          Math.random();
          
          // PDF-related DOM access
          if (document.body) {
            void document.body.offsetHeight;
            void document.body.scrollHeight;
          }
        } else if (e.data.type === 'pdf-pong') {
          // PDF worker responding
          Math.random();
        }
      };
      
      // Keep PDF worker active
      pdfWorkerInterval = setInterval(() => {
        if (pdfWorker) {
          pdfWorker.postMessage('pdf-ping');
        }
      }, 200); // 5fps - PDF-specific timing
      
      // Cleanup for PDF worker
      intervals.push(() => {
        if (pdfWorkerInterval) clearInterval(pdfWorkerInterval);
        if (pdfWorker) {
          pdfWorker.postMessage('stop');
          pdfWorker.terminate();
        }
        URL.revokeObjectURL(pdfWorkerUrl);
      });
      
    } catch (e) {
      console.log('❌ PDF Generation Web Worker not available:', e);
    }
    
    // TRICK 4: Hidden Tab Web Worker (ULTRA AGGRESSIVE when tab is hidden)
    let hiddenTabWorker = null;
    let hiddenTabInterval = null;
    
    try {
      // Create hidden tab worker
      const hiddenTabWorkerCode = `
        // Hidden Tab Web Worker: ULTRA AGGRESSIVE when tab is hidden
        let counter = 0;
        let isActive = true;
        
        // ULTRA AGGRESSIVE: Controlled loop for hidden tabs with minimal delay
        const hiddenTabLoop = () => {
          if (!isActive) return;
          
          counter++;
          
          // EXTREMELY INTENSIVE work when tab is hidden
          let result = 0;
          for (let i = 0; i < 10000; i++) {
            result += Math.sin(i) * Math.cos(i) * Math.tan(i);
          }
          
          // Massive array operations
          let massiveArray = new Array(2000).fill(0);
          massiveArray.forEach((_, i) => {
            massiveArray[i] = i * Math.random() * Math.random() * Math.random();
          });
          
          // Complex mathematical operations
          let complexResult = 0;
          for (let i = 0; i < 5000; i++) {
            complexResult += Math.pow(i, 2) * Math.sqrt(i + 1);
          }
          
          // Send hidden tab keep-alive
          self.postMessage({ 
            type: 'hidden-tab-keepalive', 
            counter: counter, 
            result: result,
            arraySum: massiveArray.reduce((a, b) => a + b, 0),
            complexResult: complexResult,
            timestamp: Date.now(),
            message: 'Hidden tab processing ULTRA ACTIVE'
          });
          
          // Use setTimeout with minimal delay to prevent stack overflow
          setTimeout(hiddenTabLoop, 1); // 1ms delay - Chrome can't throttle this
        };
        
        // Start hidden tab loop
        hiddenTabLoop();
        
        // Handle messages
        self.onmessage = (e) => {
          if (e.data === 'stop') {
            isActive = false;
            self.postMessage({ type: 'hidden-tab-stopped' });
          } else if (e.data === 'hidden-tab-ping') {
            // Respond to hidden tab pings
            self.postMessage({ type: 'hidden-tab-pong', timestamp: Date.now() });
          }
        };
        
        // Send ready message
        self.postMessage({ type: 'hidden-tab-ready' });
      `;
      
      // Create hidden tab worker
      const hiddenTabBlob = new Blob([hiddenTabWorkerCode], { type: 'application/javascript' });
      const hiddenTabWorkerUrl = URL.createObjectURL(hiddenTabBlob);
      
      hiddenTabWorker = new Worker(hiddenTabWorkerUrl);
      
      // Handle hidden tab worker messages
      hiddenTabWorker.onmessage = (e) => {
        if (e.data.type === 'hidden-tab-ready') {
          console.log('✅ Hidden Tab Web Worker ready - ULTRA AGGRESSIVE protection!');
        } else if (e.data.type === 'hidden-tab-keepalive') {
          // Force main thread to stay active
          Math.random();
          
          // Aggressive DOM access
          if (document.body) {
            void document.body.offsetHeight;
            void document.body.scrollHeight;
            void document.body.clientHeight;
          }
        } else if (e.data.type === 'hidden-tab-pong') {
          // Hidden tab worker responding
          Math.random();
        }
      };
      
      // Keep hidden tab worker active
      hiddenTabInterval = setInterval(() => {
        if (hiddenTabWorker) {
          hiddenTabWorker.postMessage('hidden-tab-ping');
        }
      }, 50); // 20fps - maximum aggression
      
      // Cleanup for hidden tab worker
      intervals.push(() => {
        if (hiddenTabInterval) clearInterval(hiddenTabInterval);
        if (hiddenTabWorker) {
          hiddenTabWorker.postMessage('stop');
          hiddenTabWorker.terminate();
        }
        URL.revokeObjectURL(hiddenTabWorkerUrl);
      });
      
    } catch (e) {
      console.log('❌ Hidden Tab Web Worker not available:', e);
    }
    
    // TRICK 5: Lightweight DOM Activity (Chrome respects this)
    let domActivity = null;
    try {
      domActivity = setInterval(() => {
        // Lightweight DOM operations - no element creation/deletion
        if (document.body) {
          // Just access DOM properties to keep it active
          void document.body.offsetHeight;
          void document.body.scrollHeight;
          
          // Minimal CPU work
          Math.random();
        }
      }, 200); // 5fps - lightweight
      
      console.log('✅ Lightweight DOM activity active');
      
      intervals.push(() => {
        if (domActivity) clearInterval(domActivity);
      });
    } catch (e) {
      console.log('❌ DOM activity not available:', e);
    }
    
    // TRICK 4: Lightweight Microtask Scheduling
    let microtaskScheduler = null;
    try {
      const scheduleMicrotask = () => {
        Promise.resolve().then(() => {
          // Minimal work
          Math.random();
          microtaskScheduler = setTimeout(scheduleMicrotask, 100); // 10fps instead of 0ms
        });
      };
      scheduleMicrotask();
      
      intervals.push(() => {
        if (microtaskScheduler) clearTimeout(microtaskScheduler);
      });
      
      console.log('✅ Lightweight microtask scheduling active');
    } catch (e) {
      console.log('❌ Microtask scheduling not available:', e);
    }
    
    // TRICK 5: Lightweight Intervals (Chrome respects these)
    let lightInterval1 = setInterval(() => {
      // Lightweight work every 200ms instead of 1ms
      Math.random();
      
      // Lightweight DOM access - no element creation/deletion
      if (document.body) {
        void document.body.offsetHeight;
      }
    }, 200); // 5fps - much lighter than 1000fps
    
    let lightInterval2 = setInterval(() => {
      // Minimal CPU work
      let result = 0;
      for (let i = 0; i < 100; i++) { // Reduced from 5000
        result += Math.sin(i) * Math.cos(i);
      }
    }, 200); // 5fps - much lighter than 1000fps
    
    // TRICK 6: Lightweight Event Loop Work
    let busyWork = null;
    try {
      const doWork = () => {
        // Lightweight CPU work
        let sum = 0;
        for (let i = 0; i < 100; i++) { // Reduced from 10000
          sum += Math.random() * Math.random();
        }
        
        // Schedule next work with delay instead of 0ms
        busyWork = setTimeout(doWork, 100); // 100ms instead of 0ms
      };
      doWork();
      
      intervals.push(() => {
        if (busyWork) clearTimeout(busyWork);
      });
      
      console.log('✅ Lightweight event loop work active');
    } catch (e) {
      console.log('❌ Event loop work not available:', e);
    }
    
    // TRICK 7: Page Visibility Handler (Intensifies Web Workers when hidden)
    let hiddenInterval = null;
    let visibleInterval = null;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('🚨 Tab hidden - Web Workers will intensify automatically!');
        
        // When hidden, the Web Workers are already running at maximum intensity
        // No need for additional main thread work - let the workers do the heavy lifting
        
        // Stop lightweight visible tricks
        if (visibleInterval) {
          clearInterval(visibleInterval);
          visibleInterval = null;
        }
      } else {
        if (hiddenInterval) {
          clearInterval(hiddenInterval);
          hiddenInterval = null;
        }
        
        console.log('✅ Tab visible - Web Workers continue running at full intensity');
        
        // When visible, use lightweight tricks to maintain speed
        visibleInterval = setInterval(() => {
          // Lightweight DOM access
          if (document.body) {
            void document.body.offsetHeight;
          }
          
          // Minimal CPU work
          Math.random();
        }, 50); // 20fps when visible - lightweight but effective
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // TRICK 8: Adaptive Keep-Alive (Intensifies when hidden)
    let keepAlive = null;
    let keepAliveMode = 'lightweight';
    
    const startKeepAlive = (mode = 'lightweight') => {
      if (keepAlive) clearInterval(keepAlive);
      
      if (mode === 'aggressive') {
        keepAlive = setInterval(() => {
          // AGGRESSIVE keep-alive every 16ms (60fps)
          Math.random();
          
          // AGGRESSIVE DOM operations
          const temp = document.createElement('div');
          temp.style.position = 'absolute';
          temp.style.left = '-9999px';
          temp.textContent = 'aggressive-' + Date.now();
          temp.style.opacity = '0.01';
          temp.style.pointerEvents = 'none';
          document.body.appendChild(temp);
          
          // Force a reflow
          void temp.offsetHeight;
          
          document.body.removeChild(temp);
          
          // AGGRESSIVE CPU work
          let result = 0;
          for (let i = 0; i < 500; i++) {
            result += Math.sin(i) * Math.cos(i);
          }
        }, 16); // 60fps - AGGRESSIVE
      } else {
        keepAlive = setInterval(() => {
          // Lightweight keep-alive every 200ms
          Math.random();
          
          // Lightweight DOM access
          if (document.body) {
            void document.body.offsetHeight;
          }
        }, 200); // 5fps - lightweight
      }
      
      keepAliveMode = mode;
      console.log(`🔄 Keep-alive mode: ${mode}`);
    };
    
    // Start with lightweight mode
    startKeepAlive('lightweight');
    
    // Switch to aggressive mode when tab is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && keepAliveMode !== 'aggressive') {
        startKeepAlive('aggressive');
      } else if (!document.hidden && keepAliveMode !== 'lightweight') {
        startKeepAlive('lightweight');
      }
    });
    
    // 🚀 THE "ALMOST ALWAYS WORKS" COMBO - Chrome Killer!
    console.log('🔥 Starting THE COMBO - WebSocket + AudioContext + Hidden Canvas!');
    
    // COMBO TRICK 1: WebSocket Connection (Chrome thinks it's real-time!)
    try {
      // Connect to real WebSocket server
      websocketService.connect();
      websocket = websocketService;
      
      // Keep WebSocket alive
      const websocketInterval = setInterval(() => {
        if (websocketService.getConnectionStatus().isConnected) {
          websocketService.emit('ping', 'keep-alive');
        }
      }, 30000); // Send ping every 30 seconds
      
      // Add to cleanup
      intervals.push(() => {
        if (websocketInterval) clearInterval(websocketInterval);
      });
      
      console.log('✅ WebSocket connected to real server - Chrome thinks this is a real-time app!');
    } catch (e) {
      console.log('❌ WebSocket not available:', e);
    }
    
    // COMBO TRICK 2: Muted AudioContext (Tiny oscillator - Chrome thinks it's media!)
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // Set gain to 0 (muted) - user won't hear anything
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      
      // Connect oscillator to gain to speakers
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Start oscillator with very low frequency (inaudible)
      oscillator.frequency.setValueAtTime(0.1, audioContext.currentTime);
      oscillator.start();
      
      console.log('✅ AudioContext trick active - Chrome thinks this is media playback!');
    } catch (e) {
      console.log('❌ AudioContext not available:', e);
    }
    
    // COMBO TRICK 3: Hidden Canvas + Video Stream (Chrome thinks it's video processing!)
    try {
      // Create hidden canvas
      hiddenCanvas = document.createElement('canvas');
      hiddenCanvas.width = 1;
      hiddenCanvas.height = 1;
      hiddenCanvas.style.position = 'absolute';
      hiddenCanvas.style.left = '-9999px';
      hiddenCanvas.style.opacity = '0.01';
      hiddenCanvas.style.pointerEvents = 'none';
      document.body.appendChild(hiddenCanvas);
      
      // Get canvas context and draw something
      ctx = hiddenCanvas.getContext('2d');
      ctx.fillStyle = 'rgba(0,0,0,0.01)';
      ctx.fillRect(0, 0, 1, 1);
      
      // Capture stream from canvas with error handling
      try {
        videoStream = hiddenCanvas.captureStream(30); // 30fps
        
        // Check if MediaRecorder is supported and find supported codec
        let supportedMimeType = null;
        const mimeTypes = [
          'video/webm;codecs=vp8',
          'video/webm;codecs=vp9',
          'video/webm',
          'video/mp4',
          'video/ogg;codecs=theora'
        ];
        
        for (const mimeType of mimeTypes) {
          if (MediaRecorder.isTypeSupported(mimeType)) {
            supportedMimeType = mimeType;
            break;
          }
        }
        
        if (supportedMimeType && videoStream) {
          // Create MediaRecorder with supported codec
          mediaRecorder = new MediaRecorder(videoStream, {
            mimeType: supportedMimeType
          });
          
          // Start recording (this keeps Chrome busy with video processing)
          try {
            mediaRecorder.start(1000); // 1 second chunks
            console.log('✅ MediaRecorder started with codec:', supportedMimeType);
          } catch (startError) {
            console.log('⚠️ MediaRecorder start failed, trying alternative approach:', startError);
            // Fallback: just keep the stream active without recording
            videoStream = null;
            mediaRecorder = null;
          }
          
          // Keep the stream active
          if (mediaRecorder) {
            mediaRecorder.ondataavailable = (event) => {
              // Discard the data - we just want Chrome to think we're processing video
              if (event.data && event.data.size > 0) {
                // Minimal processing to keep Chrome busy
                Math.random();
              }
            };
            
            // Restart recording when it stops
            mediaRecorder.onstop = () => {
              const restartTimeout = setTimeout(() => {
                if (mediaRecorder && mediaRecorder.state === 'inactive') {
                  try {
                    mediaRecorder.start(1000);
                  } catch (restartError) {
                    console.log('⚠️ MediaRecorder restart failed:', restartError);
                  }
                }
              }, 100);
              
              // Cleanup restart timeout if needed
              intervals.push(() => {
                if (restartTimeout) clearTimeout(restartTimeout);
              });
            };
          }
        } else {
          console.log('⚠️ MediaRecorder not supported, using canvas stream only');
          mediaRecorder = null;
        }
        
        console.log('✅ Hidden Canvas + Video Stream trick active - Chrome thinks this is video processing!');
      } catch (streamError) {
        console.log('⚠️ Canvas stream failed, using canvas only:', streamError);
        videoStream = null;
        mediaRecorder = null;
      }
      
    } catch (e) {
      console.log('❌ Hidden Canvas trick not available:', e);
      hiddenCanvas = null;
      ctx = null;
    }
    
    // COMBO TRICK 4: Continuous Animation Frame (Chrome thinks it's rendering!)
    let animationFrameId = null;
    const animate = () => {
      // Minimal animation work
      if (hiddenCanvas && ctx) {
        ctx.fillStyle = `rgba(${Math.random() * 255},${Math.random() * 255},${Math.random() * 255},0.01)`;
        ctx.fillRect(0, 0, 1, 1);
      }
      
      // Additional fallback: keep Chrome busy even without canvas
      if (!hiddenCanvas || !ctx) {
        // Use DOM manipulation as fallback
        if (document.body) {
          void document.body.offsetHeight;
          void document.body.scrollHeight;
        }
        // Minimal CPU work
        Math.random();
      }
      
      // Schedule next frame
      animationFrameId = requestAnimationFrame(animate);
    };
    animate();
    
    console.log('✅ Animation Frame trick active - Chrome thinks this is rendering!');
    
    intervals.push(() => {
      clearInterval(lightInterval1);
      clearInterval(lightInterval2);
      if (keepAlive) clearInterval(keepAlive);
      
      // Cleanup COMBO tricks
      if (websocket) {
        websocket.disconnect();
        websocket = null;
      }
      
      if (oscillator && oscillator.state !== 'stopped') {
        try {
          oscillator.stop();
        } catch (e) {
          console.log('Audio cleanup error:', e);
        }
      }
      
      if (audioContext && audioContext.state !== 'closed') {
        try {
          audioContext.close();
        } catch (e) {
          console.log('AudioContext cleanup error:', e);
        }
      }
      
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        try {
          mediaRecorder.stop();
        } catch (e) {
          console.log('MediaRecorder cleanup error:', e);
        }
      }
      
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
        videoStream = null;
      }
      
      if (hiddenCanvas && hiddenCanvas.parentNode) {
        hiddenCanvas.parentNode.removeChild(hiddenCanvas);
        hiddenCanvas = null;
      }
      
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    });
    
    return () => {
      console.log('🧹 Cleaning up Web Workers and COMBO tricks...');
      intervals.forEach(cleanup => cleanup());
      if (hiddenInterval) clearInterval(hiddenInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  };

  // Function to log status updates (for debugging)
  const logStatus = (status) => {
    console.log(`📊 Status: ${status}`);
  };

  const getHistoryByIdParams = (report) => {
    if (!report) return {};
    if (report._historyCategory === 'biopet') {
      return {
        historyCategory: 'biopet',
        biopetHealth: report._biopetHealth || biopetHistoryHealthTab
      };
    }
    return {};
  };

  // Function to generate PDF data for zip file (RAPID MODE - without downloading)
  const generatePDFDataForZip = async (reportData, progressCallback = null) => {
    try {
      console.log('🚀 RAPID MODE: Generating PDF data for zip:', reportData['Report number']);
      
      // Log status for debugging
      logStatus(`Generating: ${reportData['Report number']}`);
      
      // Use preview-aligned mode detection and rendering settings
      let mode = 'QPT';
      const panel = reportData.Panel;
      const isBiopetReport =
        reportData._historyCategory === 'biopet' ||
        panel === 'Pet Care Professional';

      if (isBiopetReport) {
        const previousReportMode = reportMode;
        if (progressCallback) progressCallback('Preparing Pet Care Professional PDF...');

        try {
          setReportMode('pet-care-pro');
          const petForm = { ...reportData };
          if (!petForm.petname && petForm['Full Name']) {
            petForm.petname = petForm['Full Name'];
          }

          let supplements = [];
          const supRaw = reportData.Supplement ?? reportData.probiotics_select;
          if (supRaw) {
            try {
              const parsed = typeof supRaw === 'string' ? JSON.parse(supRaw) : supRaw;
              supplements = Array.isArray(parsed) ? parsed : [];
            } catch {
              supplements = [];
            }
          }

          setCsvPreviewToggle(false);
          setFromCsvPreview(false);
          setPreviewRowIndex(0);
          setManualSupplements(supplements.length ? { 0: supplements } : {});
          setFormData(petForm);
          setShowPdfPreview(false);
          setIsPdfReady(true);
          setShowStaticPdf(true);
          loadQuartileGrid();

          await keepAlive(2200);

          // Ensure page-8 supplement layout is fully settled before capture.
          // This prevents preview/download position mismatch caused by early snapshot timing.
          const expectedSuppCount = Math.min(supplements.length, 3);
          await new Promise((resolve) => {
            let attempts = 0;
            const maxAttempts = 80; // ~8s max
            const tick = () => {
              attempts += 1;
              const page8 = document.getElementById('pet-professional-page-8');
              if (!page8) {
                if (attempts >= maxAttempts) return resolve();
                return setTimeout(tick, 100);
              }
              const suppImgs = page8.querySelectorAll(
                'img[alt="PetFlow"], img[alt="PetGard"], img[alt="PetReno"]'
              );
              const loaded = Array.from(suppImgs).every(
                (img) => img.complete && img.naturalWidth > 0 && img.naturalHeight > 0
              );
              const countOk = suppImgs.length === expectedSuppCount;
              if ((expectedSuppCount === 0 || (countOk && loaded)) || attempts >= maxAttempts) {
                return resolve();
              }
              return setTimeout(tick, 100);
            };
            tick();
          });

          const pages = document.querySelectorAll('.pdf-page-wrapper');
          if (!pages || pages.length === 0) {
            throw new Error('No Pet Care Professional PDF pages found in DOM');
          }

          if (progressCallback) progressCallback('Rendering Pet Care Professional PDF...');
          // Enforce max output size (<=25MB) and target near 24MB for best quality.
          const MAX_BYTES = 25 * 1024 * 1024;
          const TARGET_BYTES = 24 * 1024 * 1024;
          const scaleAttempts = [2.2, 2.0, 1.8, 1.6, 1.4, 1.2];
          let petBlob = null;
          let bestUnderMax = null;
          for (const scale of scaleAttempts) {
            // eslint-disable-next-line no-await-in-loop
            const candidate = await generatePDFBlob('eng', {
              scale,
              imageType: 'PNG',
              waitBackgroundImages: true
            });
            petBlob = candidate;
            if (candidate?.size && candidate.size <= MAX_BYTES) {
              if (!bestUnderMax || candidate.size > bestUnderMax.size) {
                bestUnderMax = candidate;
              }
              if (candidate.size >= TARGET_BYTES) {
                break;
              }
            }
          }
          // If PNG is still too large, enforce hard limit using high-quality JPEG fallback.
          if (!bestUnderMax && petBlob?.size > MAX_BYTES) {
            const jpegAttempts = [
              { scale: 2.0, quality: 0.95 },
              { scale: 1.8, quality: 0.92 },
              { scale: 1.6, quality: 0.9 },
              { scale: 1.4, quality: 0.88 },
              { scale: 1.2, quality: 0.85 },
              { scale: 1.0, quality: 0.82 }
            ];
            for (const opt of jpegAttempts) {
              // eslint-disable-next-line no-await-in-loop
              const candidate = await generatePDFBlob('eng', {
                scale: opt.scale,
                imageType: 'JPEG',
                imageQuality: opt.quality,
                waitBackgroundImages: true
              });
              petBlob = candidate;
              if (candidate?.size && candidate.size <= MAX_BYTES) {
                if (!bestUnderMax || candidate.size > bestUnderMax.size) {
                  bestUnderMax = candidate;
                }
                if (candidate.size >= TARGET_BYTES) break;
              }
            }
          }
          if (bestUnderMax) petBlob = bestUnderMax;
          setShowStaticPdf(false);
          return petBlob;
        } finally {
          setShowStaticPdf(false);
          setReportMode(previousReportMode);
        }
      }

      if (panel === 'Q9' || panel === '9P') mode = '9P';
      else if (panel === 'QA' || panel === 'Allergy and Skin') mode = 'Allergy and Skin';
      else if (panel === 'QI' || panel === 'QA(KIDs)' || panel === 'Allergy and Skin_kids') mode = 'Allergy and Skin_kids';
      else if (panel === 'QG' || panel === 'Toxin') mode = 'Toxin';
      else if (panel === 'QM' || panel === 'oms') mode = 'oms';
      else if (panel === 'QBWCA' || panel === 'bwcallergy') mode = 'bwcallergy';
      else if (reportData.Panel === 'QBWCE' || reportData.Panel === 'bwccore') mode = 'bwccore';
      else if (reportData.Panel === 'QBWCU' || reportData.Panel === 'bwcdiamond') mode = 'bwcdiamond';

      // Normalize row to formData (same as generateHistoryPDF)
      let configInfo;
      if (mode === '9P') configInfo = choosing_supplement_9P[0].info;
      else if (mode === 'oms') configInfo = choosing_supplement_oms[0].info;
      else if (mode === 'Allergy and Skin') configInfo = choosing_supplement_AMG[0].info;
      else if (mode === 'Allergy and Skin_kids') configInfo = qpt_choosing_supplement[0].info;
      else if (mode === 'Toxin') configInfo = choosing_supplement_toxin[0].info;
      else if ([
        'bwcallergy', 'bwccore', 'bwcdiamond', 'BWC-A', 'BWC-C', 'BWC-D'
      ].includes(mode)) configInfo = choosing_supplement_bwc[0].info;
      else configInfo = qpt_choosing_supplement[0].info;

      const normalized = normalizeRowKeys(reportData, configInfo);

      // Use stored supplement data if available, otherwise calculate
      let supplements = [];
      if (reportData.Supplement) {
        try {
          const storedSupplements = JSON.parse(reportData.Supplement);
          supplements = Array.isArray(storedSupplements) ? storedSupplements : [];
        } catch (e) {
          const supplementResult = getRecommendedSupplements(normalized, mode, stock, [], stockThreshold, {});
          supplements = supplementResult.supplements || [];
        }
      } else {
        const supplementResult = getRecommendedSupplements(normalized, mode, stock, [], stockThreshold, {});
        supplements = supplementResult.supplements || [];
      }

      // Set up the PDF preview data
      setPdfPreviewReportMode(mode);
      setPdfPreviewFormData(normalized);
      applyDetected9PVersion(mode, supplements);
      setPdfPreviewSupplements(supplements);
      setPdfPreviewThai(false); // Start with English version
      setPdfPreviewLoading(false);
      setShowPdfPreview(true);

      if (progressCallback) progressCallback('Setting up PDF preview...');
      
      // Check for cancellation
      if (isZipCancelledRef.current) {
        console.log('PDF generation cancelled during preview setup');
        setShowPdfPreview(false);
        return null;
      }
      
      // Wait for the PDF preview to render (align with preview flow for layout stability)
      await keepAlive(3000);
      // Check for cancellation after the wait
      if (isZipCancelledRef.current) {
        console.log('PDF generation cancelled during preview render wait');
        setShowPdfPreview(false);
        return null;
      }
      
      // Wait for PDF pages to be positioned
      if (progressCallback) progressCallback('Waiting for PDF elements to position...');
      await new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max wait
        
        const checkPositions = () => {
          // Check for cancellation during positioning
          if (isZipCancelledRef.current) {
            reject(new Error('PDF generation cancelled during positioning'));
            return;
          }
          
          attempts++;
          const pages = document.querySelectorAll('.pdf-page-wrapper');
          if (pages.length > 0) {
            const overlayElements = document.querySelectorAll('.pdf-overlay-text, .pdf-overlay-image');
            if (overlayElements.length > 0) {
              resolve();
            } else if (attempts >= maxAttempts) {
              reject(new Error('Timeout waiting for PDF elements to position'));
            } else {
              // Use keepAlive instead of setTimeout for better reliability
              keepAlive(50).then(() => checkPositions());
            }
          } else if (attempts >= maxAttempts) {
            reject(new Error('Timeout waiting for PDF pages to load'));
          } else {
            // Use keepAlive instead of setTimeout for better reliability
            keepAlive(50).then(() => checkPositions());
          }
        };
        checkPositions();
      });
      
      // Generate English PDF
      if (progressCallback) progressCallback('Generating English PDF...');
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;

      const pages = document.querySelectorAll('.pdf-page-wrapper');
      if (!pages || pages.length === 0) {
        throw new Error('No PDF pages found in DOM');
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      let isFirst = true;

      for (let i = 0; i < pages.length; i++) {
        // Check for cancellation during page processing
        if (isZipCancelledRef.current) {
          console.log('PDF generation cancelled during English page processing');
          setShowPdfPreview(false);
          return null;
        }
        
        const el = pages[i];
        if (!el) continue;
        
        if (progressCallback) progressCallback(`Processing English page ${i + 1} of ${pages.length}...`);
        
        el.scrollIntoView();
        // Minimal delay for page positioning
        await new Promise(r => setTimeout(r, 50));

        const canvas = await forceContinue(async () => { // ENGLISH VERSION
          // Start a very aggressive keep-alive mechanism specifically for this canvas generation
          const keepAliveInterval = setInterval(() => {
            // Force the browser to stay active during canvas generation
            requestAnimationFrame(() => {
              Math.random();
              
              // DOM operations to prevent throttling
              const temp = document.createElement('div');
              temp.style.position = 'absolute';
              temp.style.left = '-9999px';
              temp.textContent = `canvas-keepalive-eng-${Date.now()}`;
              temp.style.opacity = '0.01';
              temp.style.pointerEvents = 'none';
              document.body.appendChild(temp);
              
              // Force a reflow
              void temp.offsetHeight;
              
              document.body.removeChild(temp);
              
              // RAPID MODE: Intensive CPU work to prevent throttling
              let result = 0;
              for (let i = 0; i < 3000; i++) { // Increased from 2000 for more aggressive anti-throttling
              result += Math.sin(i) * Math.cos(i);
              }
            });
          }, 4); // RAPID MODE: 250fps updates for maximum speed
          
          try {
            // Keep html2canvas settings aligned with preview download for consistent layout
            const result = await html2canvas(el, {
              scale: 1.5,
              useCORS: true,
              backgroundColor: resolveCaptureBackgroundColor(),
              allowTaint: true,
              logging: false,
              removeContainer: false,
              foreignObjectRendering: false,
              imageTimeout: 30000
            });
            
            // Clear the keep-alive interval
            clearInterval(keepAliveInterval);
            return result;
          } catch (error) {
            // Clear the keep-alive interval on error
            clearInterval(keepAliveInterval);
            throw error;
          }
        }, 60000); // 60 second timeout for canvas generation

        const imgData = canvas.toDataURL('image/jpeg', 0.90);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (!isFirst) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, imgHeight);
        isFirst = false;
      }

      const engBlob = pdf.output('blob');

      // Generate Thai version
      logStatus('Generating Thai PDF...');
      if (progressCallback) progressCallback('Generating Thai PDF...');
      
      // Check for cancellation before Thai generation
      if (isZipCancelledRef.current) {
        console.log('PDF generation cancelled before Thai generation');
        setShowPdfPreview(false);
        return null;
      }
      
      setPdfPreviewThai(true);
      // Keep wait aligned with preview flow so Thai overlays settle before capture
      await keepAlive(3000);
      // Check for cancellation after the wait
      if (isZipCancelledRef.current) {
        console.log('PDF generation cancelled during Thai preview render wait');
        setShowPdfPreview(false);
        return null;
      }
      
      // Wait for Thai version positioning calculations
      await new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max wait
        
        const checkPositions = () => {
          // Check for cancellation during Thai positioning
          if (isZipCancelledRef.current) {
            reject(new Error('PDF generation cancelled during Thai positioning'));
            return;
          }
          
          attempts++;
          const pages = document.querySelectorAll('.pdf-page-wrapper');
          if (pages.length > 0) {
            const overlayElements = document.querySelectorAll('.pdf-overlay-text, .pdf-overlay-image');
            if (overlayElements.length > 0) {
              resolve();
            } else if (attempts >= maxAttempts) {
              reject(new Error('Timeout waiting for Thai PDF elements to position'));
            } else {
              // Use keepAlive instead of setTimeout for better reliability
              keepAlive(50).then(() => checkPositions());
            }
          } else if (attempts >= maxAttempts) {
            reject(new Error('Timeout waiting for Thai PDF pages to load'));
          } else {
            // Use keepAlive instead of setTimeout for better reliability
            keepAlive(50).then(() => checkPositions());
          }
        };
        checkPositions();
      });
      
      // Generate Thai PDF
      const thaiPdf = new jsPDF('p', 'mm', 'a4');
      const thaiPages = document.querySelectorAll('.pdf-page-wrapper');
      isFirst = true;

      for (let i = 0; i < thaiPages.length; i++) {
        // Check for cancellation during Thai page processing
        if (isZipCancelledRef.current) {
          console.log('PDF generation cancelled during Thai page processing');
          setShowPdfPreview(false);
          return null;
        }
        
        const el = thaiPages[i];
        if (!el) continue;
        
        if (progressCallback) progressCallback(`Processing Thai page ${i + 1} of ${thaiPages.length}...`);
        
        el.scrollIntoView();
        // Minimal delay for page positioning
        await new Promise(r => setTimeout(r, 50));

        const canvas = await forceContinue(async () => { // THAI VERSION
          // Start a very aggressive keep-alive mechanism specifically for this canvas generation
          const keepAliveInterval = setInterval(() => {
            // Force the browser to stay active during canvas generation
            requestAnimationFrame(() => {
              Math.random();
              
              // DOM operations to prevent throttling
              const temp = document.createElement('div');
              temp.style.position = 'absolute';
              temp.style.left = '-9999px';
              temp.textContent = `canvas-keepalive-thai-${Date.now()}`;
              temp.style.opacity = '0.01';
              temp.style.pointerEvents = 'none';
              document.body.appendChild(temp);
              
              // Force a reflow
              void temp.offsetHeight;
              
              document.body.removeChild(temp);
              
              // Additional CPU work to prevent throttling
              let result = 0;
              for (let i = 0; i < 2000; i++) {
                result += Math.sin(i) * Math.cos(i);
              }
            });
          }, 8); // 120fps updates during critical operations
          
          try {
            const result = await html2canvas(el, {
              scale: 1.9,
              useCORS: true,
              backgroundColor: resolveCaptureBackgroundColor(),
              allowTaint: true,
              logging: false,
              // Add these options to improve reliability when tab is inactive
              removeContainer: false,
              foreignObjectRendering: false,
              imageTimeout: 30000, // 30 second timeout
              onclone: (clonedDoc) => {
                // Ensure the cloned document has proper dimensions
                const clonedElement = clonedDoc.querySelector('.pdf-page-wrapper');
                if (clonedElement) {
                  clonedElement.style.position = 'relative';
                  clonedElement.style.overflow = 'visible';
                }
              }
            });
            
            // Clear the keep-alive interval
            clearInterval(keepAliveInterval);
            return result;
          } catch (error) {
            // Clear the keep-alive interval on error
            clearInterval(keepAliveInterval);
            throw error;
          }
        }, 60000); // 60 second timeout for canvas generation

        const imgData = canvas.toDataURL('image/jpeg', 0.90);
        const pdfWidth = thaiPdf.internal.pageSize.getWidth();
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (!isFirst) thaiPdf.addPage();
        thaiPdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, imgHeight);
        isFirst = false;
      }

      const thaiBlob = thaiPdf.output('blob');

      // Merge PDFs
      if (progressCallback) progressCallback('Merging English and Thai PDFs...');
      
      // Check for cancellation before merging
      if (isZipCancelledRef.current) {
        console.log('PDF generation cancelled before merging');
        setShowPdfPreview(false);
        return null;
      }
      
      const { PDFDocument } = await import('pdf-lib');
      const [engPdfDoc, thaiPdfDocForMerge] = await Promise.all([
        PDFDocument.load(await engBlob.arrayBuffer()),
        PDFDocument.load(await thaiBlob.arrayBuffer())
      ]);

      const mergedPdf = await PDFDocument.create();

      const engPages = await mergedPdf.copyPages(engPdfDoc, engPdfDoc.getPageIndices());
      engPages.forEach(p => mergedPdf.addPage(p));

      const thaiPdfPages = await mergedPdf.copyPages(thaiPdfDocForMerge, thaiPdfDocForMerge.getPageIndices());
      thaiPdfPages.forEach(p => mergedPdf.addPage(p));

      const mergedBlob = new Blob([await mergedPdf.save()], { type: 'application/pdf' });

      // Clean up
      setShowPdfPreview(false);
      
      // Log status for debugging
      logStatus('PDF Generation Complete!');
      
      // Return the merged PDF blob
      return mergedBlob;
      
    } catch (err) {
      console.error('Error generating PDF data for zip:', err);
      
      // Clean up on error
      setShowPdfPreview(false);
      
      // Log status for debugging
      logStatus('PDF Generation Failed');
      
      return null;
    }
  };

  // Biopet reports now render server-side like every BioMed panel (BiopetReportPages is in
  // the renderer bundle). This still identifies them, because they live in biopet_report*
  // rather than st_report and the download request has to say so.
  const isBiopetHistoryReport = (report) =>
    report?._historyCategory === 'biopet' ||
    report?.Panel === 'Pet Care Professional' ||
    report?.Panel === 'Pet Care Essential';

  // Fast History download: the server renders the whole report (ENG + THAI) in ONE
  // Puppeteer pass over the same PdfStaticModal (GET /api/reports/dashboard-pdf/:id),
  // instead of html2canvas screenshotting ~96 pages in the browser. b2b / formula are
  // sent so the server renders exactly what the History preview shows (same values as
  // the preview handler and applyDetected9PVersion).
  // onProgress(frac01, message): called continuously so the popup never sits still.
  // The server renders in one Puppeteer pass (~5–15 s) with no incremental signal, so
  // 0 → 0.8 is smooth simulated progress with stage messages, and 0.8 → 1 is REAL
  // byte progress from the PDF response download.
  const fetchServerReportPdfBlob = async (report, onProgress = null) => {
    const token = localStorage.getItem('token');
    const b2b = (report.B2C === 0 || report.B2C === '0') ? '1' : '0';
    let storedSupplements = [];
    try {
      const parsed = JSON.parse(report.Supplement || '[]');
      if (Array.isArray(parsed)) storedSupplements = parsed;
    } catch (e) { /* fall through: unparseable = old formula */ }
    const formula = storedSupplements.some((s) => supplement_9P_skus.includes(s)) ? 'new' : 'old';

    // Biopet rows live in biopet_report* rather than st_report, so the server needs to be
    // told which table to read. The "no lacking" fallback setting lives only in this
    // browser's localStorage — send it along so the PDF matches the on-screen preview.
    const biopetParams = {};
    if (isBiopetHistoryReport(report)) {
      biopetParams.historyCategory = 'biopet';
      biopetParams.biopetHealth = report._biopetHealth || biopetHistoryHealthTab;
      try {
        biopetParams.noLackingMode = localStorage.getItem('biopet_no_lacking_mode') || 'petflow';
        const snap = localStorage.getItem('biopet_stock_snapshot');
        if (snap) biopetParams.stock = snap;
      } catch (_) { /* storage blocked: server falls back to PetFlow */ }
    }

    // Simulated progress creeps ASYMPTOTICALLY toward 95% — fast at first, slower later,
    // but it NEVER stops moving, however long the server render takes (a hard 80% ceiling
    // here previously left the bar parked when a render ran long). Reported values are
    // monotonic, and after ~8 s the detail line also shows elapsed seconds.
    const SIM_ASYMPTOTE = 0.95;
    const stages = [
      [0.0, 'Rendering report pages on the server…'],
      [0.3, 'Generating English version…'],
      [0.5, 'Generating Thai version…'],
      [0.7, 'Finalizing PDF…'],
    ];
    const t0 = Date.now();
    let simFrac = 0;
    let reportedFrac = 0;
    let downloading = false;
    const report_ = (frac, msg) => {
      reportedFrac = Math.max(reportedFrac, Math.min(0.99, frac));
      onProgress(reportedFrac, msg);
    };
    const tick = onProgress
      ? setInterval(() => {
          if (downloading) return; // real byte progress has taken over
          simFrac = Math.min(SIM_ASYMPTOTE, simFrac + (SIM_ASYMPTOTE - simFrac) * 0.05 + 0.003);
          const stage = [...stages].reverse().find(([at]) => simFrac >= at) || stages[0];
          const secs = Math.round((Date.now() - t0) / 1000);
          report_(simFrac, secs >= 8 ? `${stage[1]} · ${secs}s` : stage[1]);
        }, 350)
      : null;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/reports/dashboard-pdf/${report.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { b2b, formula, ...biopetParams },
          responseType: 'blob',
          timeout: 300000,
          onDownloadProgress: (e) => {
            if (!onProgress) return;
            downloading = true;
            const mb = (e.loaded / 1048576).toFixed(1);
            // Real bytes fill whatever range the simulation hasn't consumed yet.
            const frac = e.total
              ? simFrac + (1 - simFrac) * (e.loaded / e.total)
              : simFrac + (1 - simFrac) * 0.5;
            report_(frac, e.total ? `Downloading PDF… ${mb} / ${(e.total / 1048576).toFixed(1)} MB` : `Downloading PDF… ${mb} MB`);
          },
        }
      );
      if (!res.data || res.data.size === 0) throw new Error('Server returned empty PDF');
      if (onProgress) onProgress(1, 'PDF ready');
      return res.data;
    } finally {
      if (tick) clearInterval(tick);
    }
  };

  const handleSelectAllReports = () => {
    if (selectAllReports) {
      setSelectedReports(new Set());
      setSelectAllReports(false);
    } else {
      const allReportIds = reportHistory.map(report => report.id);
      setSelectedReports(new Set(allReportIds));
      setSelectAllReports(true);
    }
  };

  const handleApprove = async (approve) => {
    if (selectedReports.size === 0) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    setApproving(true);
    try {
      const endpoint = approve ? '/api/reports/approve' : '/api/reports/unapprove';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...selectedReports] })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error('Approve failed:', res.status, data.message);
        alert(data.message || `Approve failed (${res.status})`);
        return;
      }
      const approvedValue = approve ? 1 : 0;
      const idSet = new Set([...selectedReports]);
      setReportHistory(prev => prev.map(r => idSet.has(r.id) ? { ...r, approved: approvedValue } : r));
      setSelectedReports(new Set());
      setSelectAllReports(false);
    } catch (err) {
      console.error('Approve error:', err);
      alert('Network error — could not approve.');
    } finally {
      setApproving(false);
    }
  };

  const handleBulkDownload = async () => {
    if (selectedReports.size === 0) {
      alert('Please select at least one report to download.');
      return;
    }

    setBulkDownloadLoading(true);
    
    // Declare stopBackgroundProcessing at function scope
    let stopBackgroundProcessing = null;
    
    try {
      const token = localStorage.getItem('token');
      const selectedReportIds = Array.from(selectedReports);
      
      // Handle single report download (PDF directly)
      if (selectedReportIds.length === 1) {
        console.log(`Downloading single report as PDF: ${selectedReportIds[0]}`);
        
        // Show progress modal
        setShowZipProgress(true);
        setZipProgress(0);
        setZipProgressText('Preparing single report download...');
        setZipProgressDetails('Processing 1 report');
        isZipCancelledRef.current = false;

        try {
          const reportId = selectedReportIds[0];

          // Find report in current page (for the by-id routing params); the full row
          // comes from ONE by-id fetch either way.
          let report = reportHistory.find(r => r.id === reportId);

          setZipProgress(20);
          setZipProgressText('Generating PDF...');
          setZipProgressDetails(report ? `Format: ${report.Panel || 'QPT'} - ${report['Report number']}` : `Fetching report ${reportId} from database...`);

          // Fetch full report data (single fetch — the old code called by-id twice)
          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE}/api/reports/by-id/${reportId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: getHistoryByIdParams(report)
            }
          );
          if (!report) report = res.data;

          if (report) {
            const row = { ...res.data, ...getHistoryByIdParams(report), _historyCategory: report._historyCategory, _biopetHealth: report._biopetHealth };

            setZipProgressText('Generating PDF...');
            setZipProgressDetails(`Format: ${report.Panel || 'QPT'} - ${report['Report number']}`);

            // Fast path: one server-side Puppeteer render, for every panel including Biopet.
            // Any server failure falls back to the client html2canvas path so downloads
            // never hard-break.
            let pdfData = null;
            try {
              // 20% → 95% follows the render/download continuously (see fetchServerReportPdfBlob)
              pdfData = await fetchServerReportPdfBlob(row, (frac, msg) => {
                if (isZipCancelledRef.current) return;
                setZipProgress(Math.min(95, Math.round(20 + frac * 75)));
                setZipProgressDetails(msg);
              });
            } catch (err) {
              console.warn('Server PDF failed, falling back to client rendering:', err.message);
            }
            if (isZipCancelledRef.current) return;
            if (!pdfData) {
              // Client fallback needs the anti-throttle machinery the fast path doesn't.
              stopBackgroundProcessing = startBackgroundProcessing();
              setZipProgressDetails('Creating English and Thai versions...');
              pdfData = await generatePDFDataForZip(row, (progressText) => {
                if (isZipCancelledRef.current) {
                  throw new Error('Download cancelled by user');
                }
                setZipProgressDetails(progressText);
              });
            }

            if (pdfData) {
              // Reach 100% FIRST, give the bar a beat to paint, THEN hand the file to the
              // browser — otherwise the download shelf appears while the bar still reads ~95%.
              setZipProgress(100);
              setZipProgressText('Download completed!');
              setZipProgressDetails('Saving file…');
              await new Promise(r => setTimeout(r, 450));

              // Create download link for PDF directly
              const blob = new Blob([pdfData], { type: 'application/pdf' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = pdfFileNameFromReportNumber(report);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              setZipProgressDetails('Report downloaded successfully');
              
              // Hide progress after a delay
              setTimeout(() => {
                setShowZipProgress(false);
                setZipProgress(0);
                setZipProgressText('');
                setZipProgressDetails('');
              }, 2000);
              
              alert('Report downloaded successfully.');
            } else {
              throw new Error('Failed to generate PDF');
            }
          } else {
            throw new Error('Report not found');
          }
        } catch (err) {
          console.error('Error downloading single report:', err);
          setShowZipProgress(false);
          setZipProgress(0);
          setZipProgressText('');
          setZipProgressDetails('');
          alert('Download failed. Please try again later.');
        } finally {
          if (typeof stopBackgroundProcessing === 'function') {
            stopBackgroundProcessing();
          }
        }
      } 
      // Handle multiple reports download (ZIP file)
      else if (selectedReportIds.length > 1) {
        // Create ZIP file for multiple reports
        console.log(`Creating ZIP file for ${selectedReportIds.length} report(s)`);
        
        // Show ZIP progress modal
        setShowZipProgress(true);
        setZipProgress(0);
        setZipProgressText('Preparing ZIP download...');
        setZipProgressDetails(`Processing ${selectedReportIds.length} reports`);
        isZipCancelledRef.current = false; // Reset cancellation flag for new download

        // Background anti-throttle processing is started lazily, only if a report has to
        // fall back to the client html2canvas path — the server path doesn't need it.

        // Log status for debugging
        logStatus(`Starting bulk download: ${selectedReportIds.length} reports`);
        
        // Create zip file
        const zip = new JSZip();
        let successfulReports = 0;
        
        // Process each report and add to zip
        console.log(`Starting to process ${selectedReportIds.length} selected report(s):`, selectedReportIds);
        
        for (let i = 0; i < selectedReportIds.length; i++) {
          // Check if user cancelled the download
          if (isZipCancelledRef.current) {
            console.log('ZIP download cancelled during processing - exiting function');
            return; // Exit the function immediately
          }
          
          const reportId = selectedReportIds[i];
          console.log(`Processing report ${i + 1}/${selectedReportIds.length}: ID ${reportId}`);
          
          // First, try to find the report in current page history
          let report = reportHistory.find(r => r.id === reportId);
          console.log(`Report found in current page:`, !!report, `Report ID: ${reportId}`);
          
          // If not found in current page, we need to fetch basic info for progress display
          if (!report) {
            console.log(`Report ${reportId} not found in current page, fetching from API...`);
            setZipProgressDetails(`Fetching report ${reportId} from database...`);
            
            try {
              // Fetch basic report info for progress display
              const basicRes = await axios.get(`${process.env.REACT_APP_API_BASE}/api/reports/by-id/${reportId}`, {
                headers: { Authorization: `Bearer ${token}` },
                // Keep Biopet table routing deterministic even when row metadata is not in current page.
                params: historyPanelCategory === 'biopet'
                  ? { historyCategory: 'biopet', biopetHealth: biopetHistoryHealthTab }
                  : getHistoryByIdParams(reportHistory.find(r => r.id === reportId))
              });
              report = basicRes.data;
              console.log(`Successfully fetched report ${reportId} from API:`, report['Report number']);
              setZipProgressDetails(`Report ${report['Report number']} loaded successfully`);
              
              // Minimal delay to show the progress update
              await new Promise(resolve => setTimeout(resolve, 100));
            } catch (err) {
              console.error(`Failed to fetch basic info for report ${reportId}:`, err);
              setZipProgressDetails(`Error fetching report ${reportId} - continuing...`);
              continue; // Skip this report and continue with next
            }
          }
          
          if (report) {
            const currentReport = i + 1;
            const totalReports = selectedReportIds.length;
            
            // Update progress for current report
            const baseProgress = (i / totalReports) * 70; // 70% for report processing (PDF generation can take time)
            setZipProgress(Math.round(baseProgress));
            setZipProgressText(`Processing report ${currentReport} of ${totalReports}`);
            setZipProgressDetails(`${report['Report number']} - ${report['Full Name'] || 'Unknown'}`);
            
            try {
              // Check for cancellation before starting PDF generation
              if (isZipCancelledRef.current) {
                console.log('ZIP download cancelled before PDF generation - exiting function');
                return;
              }
              
              // Update progress to show format detection
              setZipProgressText(`Generating PDF for report ${currentReport} of ${totalReports}`);
              setZipProgressDetails(`Format: ${report.Panel || 'QPT'} - ${report['Report number']}`);
              
              // Fetch the full report data (always fetch fresh data to ensure completeness)
              const res = await axios.get(`${process.env.REACT_APP_API_BASE}/api/reports/by-id/${reportId}`, {
                headers: { Authorization: `Bearer ${token}` },
                params: getHistoryByIdParams(report)
              });
              const row = { ...res.data, _historyCategory: report._historyCategory, _biopetHealth: report._biopetHealth };
              
              // Check for cancellation before PDF generation
              if (isZipCancelledRef.current) {
                console.log('ZIP download cancelled before PDF generation - exiting function');
                return;
              }

              // Update progress to show PDF generation start
              setZipProgressText(`Generating PDF for report ${currentReport} of ${totalReports}`);
              setZipProgressDetails(`Creating English and Thai versions...`);

              // Fast path: one server-side Puppeteer render per report, Biopet included.
              // Server failures fall back to the client html2canvas path.
              let pdfData = null;
              try {
                // Each report owns a slice of the 0–70% range; the render/download
                // progress fills that slice continuously so the bar never sits still.
                const sliceBase = (i / totalReports) * 70;
                const sliceSpan = 70 / totalReports;
                pdfData = await fetchServerReportPdfBlob(row, (frac, msg) => {
                  if (isZipCancelledRef.current) return;
                  setZipProgress(Math.min(74, Math.round(sliceBase + frac * sliceSpan)));
                  setZipProgressDetails(`${report['Report number']} — ${msg}`);
                });
              } catch (err) {
                console.warn(`Server PDF failed for ${report['Report number']}, falling back to client rendering:`, err.message);
              }
              if (isZipCancelledRef.current) {
                console.log('ZIP download cancelled after server PDF - exiting function');
                return;
              }
              if (!pdfData) {
                try {
                  // Client fallback needs the anti-throttle machinery the fast path doesn't.
                  if (typeof stopBackgroundProcessing !== 'function') {
                    stopBackgroundProcessing = startBackgroundProcessing();
                  }
                  pdfData = await generatePDFDataForZip(row, (progressText) => {
                    // Check for cancellation during progress updates
                    if (isZipCancelledRef.current) {
                      console.log('ZIP download cancelled during progress update');
                      throw new Error('ZIP download cancelled by user');
                    }
                    setZipProgressDetails(progressText);
                  });
                } catch (err) {
                  if (err.message === 'ZIP download cancelled by user') {
                    console.log('PDF generation cancelled - exiting function');
                    return; // Exit the entire function
                  }
                  throw err; // Re-throw other errors
                }
              }
              
              if (pdfData) {
                // Create a safe filename
                const safeFileName = pdfFileNameFromReportNumber(report);
                zip.file(safeFileName, pdfData);
                
                successfulReports++;
                
                // Update progress to show report completion
                setZipProgressText(`Report ${currentReport} of ${totalReports} completed`);
                setZipProgressDetails(`Added to ZIP: ${safeFileName}`);
              } else {
                setZipProgressDetails(`Failed to generate PDF for ${report['Report number']} - continuing...`);
              }
            } catch (err) {
              console.error(`Failed to generate PDF for report ${report['Report number']}:`, err);
              setZipProgressDetails(`Error processing ${report['Report number']} - continuing...`);
              // Continue with other reports
            }
          }
        }
        
        // Final cancellation check before ZIP creation
        if (isZipCancelledRef.current) {
          console.log('ZIP download cancelled before ZIP creation - exiting function');
          return;
        }
        
        // Final ZIP creation progress
        setZipProgress(75);
        setZipProgressText('Creating ZIP file...');
        setZipProgressDetails(`Compressing ${successfulReports} PDF files`);

        // Final cancellation check before ZIP download
        if (isZipCancelledRef.current) {
          console.log('ZIP download cancelled before ZIP download - exiting function');
          return;
        }
        
        // Check if we have any files in the ZIP
        const zipFiles = Object.keys(zip.files);
        console.log(`ZIP creation completed. Total files: ${zipFiles.length}, Successful reports: ${successfulReports}`);
        
        if (zipFiles.length === 0) {
          throw new Error('No PDFs were generated successfully. Please try again.');
        }
        
        console.log(`ZIP contains ${zipFiles.length} files:`, zipFiles);
        
        // Generate the zip, reach 100% FIRST, give the bar a beat to paint, THEN hand the
        // file to the browser — otherwise the download shelf appears before the bar finishes.
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        setZipProgress(100);
        setZipProgressText('Download completed!');
        setZipProgressDetails('Saving ZIP file…');
        await new Promise(r => setTimeout(r, 450));

        const zipUrl = URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = zipUrl;
        link.download = `Reports_${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(zipUrl);

        setZipProgressDetails(`Successfully downloaded ${zipFiles.length} reports`);
        
        // Hide progress after a delay
        setTimeout(() => {
          setShowZipProgress(false);
          setZipProgress(0);
          setZipProgressText('');
          setZipProgressDetails('');
        }, 2000);
        
        alert(`ZIP file with ${zipFiles.length} reports downloaded successfully.`);

        // Stop background processing (only started if a client fallback ran)
        if (typeof stopBackgroundProcessing === 'function') {
          stopBackgroundProcessing();
          stopBackgroundProcessing = null;
        }
      }
      
      setSelectedReports(new Set());
      setSelectAllReports(false);
      
    } catch (err) {
      console.error('Error in bulk download process:', err);
      
      // Hide progress and show error
      setShowZipProgress(false);
      setZipProgress(0);
      setZipProgressText('');
      setZipProgressDetails('');
      
      alert('Download failed. Please try again later.');
    } finally {
      setBulkDownloadLoading(false);
      // Stop background processing in all cases
      if (typeof stopBackgroundProcessing === 'function') {
        stopBackgroundProcessing();
      }
    }
  };

  // Generate Client IDs when CSV data and monthly row order are available
  useEffect(() => {
    if (csvPreviewRows.length > 0 && monthlyRowOrder > 0 && Object.keys(clientCounts).length > 0) {
      const newClientIds = {};
      
      csvPreviewRows.forEach((row, index) => {
        // Use Thai timezone (Asia/Bangkok) for date generation
        const currentDate = new Date();
        const thaiDate = new Date(currentDate.toLocaleString('en-US', { timeZone: 'Asia/Bangkok' }));
        const year = thaiDate.getFullYear().toString().slice(-2);
        const month = (thaiDate.getMonth() + 1).toString().padStart(2, '0');
        const datePart = `${year}${month}`;
        
        // Row order number (padded to 3 digits) - use monthly counter
        // This will continue from the highest number, even if there are gaps
        const rowOrder = (monthlyRowOrder + index).toString().padStart(3, '0');
        
        // Check if client already exists in database by Full Name
        const fullName = row['Full Name'] || '';
        let clientUsageCount = '01';
        
        if (fullName) {
          // Get the existing count from state (fetched during CSV processing)
          const existingCount = clientCounts[fullName] || 0;
          
          // Check if this full name appears earlier in the current CSV
          const earlierOccurrences = csvPreviewRows
            .slice(0, index)
            .filter(earlierRow => 
              (earlierRow['Full Name'] || '').toLowerCase() === fullName.toLowerCase()
            ).length;
          
          // Total count = existing database count + earlier occurrences in current CSV + 1 (for current row)
          const totalCount = existingCount + earlierOccurrences + 1;
          clientUsageCount = totalCount.toString().padStart(2, '0');
        }
        
        // Use selected type (qpcr or 16s) or default to "qpcr"
        const prefix = clientIdType[index] || 'qpcr';
        const clientId = `${prefix}-${datePart}-${rowOrder}-${clientUsageCount}`;
        newClientIds[index] = clientId;
      });
      
      setGeneratedClientIds(newClientIds);
    }
  }, [csvPreviewRows, monthlyRowOrder, clientCounts, clientIdType]);

  // 1. Add state for Modify modal and manual supplement selection
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [modifyRowIndex, setModifyRowIndex] = useState(null);
  const [manualSupplements, setManualSupplements] = useState({});

  // 1. Add state to track the previewed row index
  const [previewRowIndex, setPreviewRowIndex] = useState(null);

  const [showConfirmAllDialog, setShowConfirmAllDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [deleteReportId, setDeleteReportId] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const successShownRef = useRef(false);
  const successTimeoutRef = useRef(null);
  const operationIdRef = useRef(null);
  const globalSuccessFlag = useRef(false);

  const [currentRowIndex, setCurrentRowIndex] = useState(null);

  // FDA Modify button state
  const [fdaModifyClicked, setFdaModifyClicked] = useState(false);
  
  // State to track selected buttons in FDA Modify mode
  const [selectedButtons, setSelectedButtons] = useState(new Set());

  // Loading state for FDA Modify button
  const [fdaModifyLoading, setFdaModifyLoading] = useState(false);

  // State for FDA checkboxes in CSV popup
  const [fdaCheckboxes, setFdaCheckboxes] = useState({});
  const [fdaRecalculatedRows, setFdaRecalculatedRows] = useState({});

                  // State for supplement popup modal
                const [showSupplementPopup, setShowSupplementPopup] = useState(false);
                const [supplementPopupRowIndex, setSupplementPopupRowIndex] = useState(null);
                
                // State for duration selection modal
                  const [showDurationModal, setShowDurationModal] = useState(false);
  const [durationModalRowIndex, setDurationModalRowIndex] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });
                
                // State for manual supplement selection
                const [selectedSupplements, setSelectedSupplements] = useState({});
  const [fdaCompliantOnly, setFdaCompliantOnly] = useState(false);
  
  // Debug useEffect to monitor FDA checkbox changes
  useEffect(() => {
    console.log('FDA checkbox state changed:', fdaCompliantOnly);
  }, [fdaCompliantOnly]);

  // (Old first-5 pre-fetch removed: the by-ids batch prefetch above loads the whole
  // visible page in one request, so these per-row calls only added extra renders.)
                const [showManualSelection, setShowManualSelection] = useState(false);
  const [manualSelectionKey, setManualSelectionKey] = useState(0); // Force re-render key
  
  // Reset selected supplements when manual selection modal opens
  useEffect(() => {
    if (showManualSelection && supplementPopupRowIndex !== null) {
      setSelectedSupplements({});
      setManualSelectionKey(prev => prev + 1); // Force re-render
    }
  }, [showManualSelection, supplementPopupRowIndex]);

  // State for Stock Condition Decision
  const [stockThreshold, setStockThreshold] = useState(1000); // Default threshold
  const [showStockDecisionModal, setShowStockDecisionModal] = useState(false);
  const [tempStockThreshold, setTempStockThreshold] = useState(1000); // Temporary value for modal input
  
  // State for color thresholds
  const [redThreshold, setRedThreshold] = useState(500); // Low stock (red)
  const [yellowThreshold, setYellowThreshold] = useState(1000); // Medium stock (yellow)
  const [tempRedThreshold, setTempRedThreshold] = useState(500);
  const [tempYellowThreshold, setTempYellowThreshold] = useState(1000);
  const [showBiopetFallbackModal, setShowBiopetFallbackModal] = useState(false);
  const [showBiopetStockDecisionModal, setShowBiopetStockDecisionModal] = useState(false);
  const [biopetFallbackMode, setBiopetFallbackMode] = useState(() => {
    try {
      return localStorage.getItem('biopet_no_lacking_mode') || 'petflow';
    } catch {
      return 'petflow';
    }
  });
  const [tempBiopetFallbackMode, setTempBiopetFallbackMode] = useState('petflow');
  const [biopetStockThreshold, setBiopetStockThreshold] = useState(() => {
    try {
      const raw = localStorage.getItem('biopet_stock_condition');
      if (!raw) return 1000;
      const parsed = JSON.parse(raw);
      return Number.isFinite(parsed?.st_check) ? parsed.st_check : 1000;
    } catch {
      return 1000;
    }
  });
  const [biopetYellowThreshold, setBiopetYellowThreshold] = useState(() => {
    try {
      const raw = localStorage.getItem('biopet_stock_condition');
      if (!raw) return 1000;
      const parsed = JSON.parse(raw);
      return Number.isFinite(parsed?.st_yellow) ? parsed.st_yellow : 1000;
    } catch {
      return 1000;
    }
  });
  const [biopetRedThreshold, setBiopetRedThreshold] = useState(() => {
    try {
      const raw = localStorage.getItem('biopet_stock_condition');
      if (!raw) return 500;
      const parsed = JSON.parse(raw);
      return Number.isFinite(parsed?.st_red) ? parsed.st_red : 500;
    } catch {
      return 500;
    }
  });

  const [showBlueConfirm, setShowBlueConfirm] = useState(false);

  // Progress tracking for CSV processing
  const [isProcessingCsv, setIsProcessingCsv] = useState(false);

  // Inventory popup state
  const [showInventoryPopup, setShowInventoryPopup] = useState(false);
  const [inventoryRowIndex, setInventoryRowIndex] = useState(null);
  const [supplementDurations, setSupplementDurations] = useState({});
  const [appliedDurations, setAppliedDurations] = useState({});
  const [csvProgress, setCsvProgress] = useState({ current: 0, total: 0 });
  const [csvProgressText, setCsvProgressText] = useState('');
  
  // State to store panel supplement stock information
  const [panelSupplementStock, setPanelSupplementStock] = useState({});
  
  // State to store real-time updated stock after selections
  const [updatedSupplementStock, setUpdatedSupplementStock] = useState({});

  // Pet CSV: per-species quartile 3×3 grids in `quartile` (one DB row per species)
  const [showQuartileModal, setShowQuartileModal] = useState(false);
  const [quartileGridBySpecies, setQuartileGridBySpecies] = useState(() => createEmptyQuartileGridBySpecies());
  /** Modify Quartile modal: scroll/highlight Young | Adult | Senior column block */
  const [quartileLifeStageFocus, setQuartileLifeStageFocus] = useState('Young');
  const [quartileModalLoading, setQuartileModalLoading] = useState(false);
  const [quartileSaving, setQuartileSaving] = useState(false);
  const [quartileCalibrating, setQuartileCalibrating] = useState(false);

  const biopetQuartileBySpecies = useMemo(
    () => buildQuartileBySpeciesFromGrids(quartileGridBySpecies),
    [quartileGridBySpecies]
  );

  const loadQuartileGrid = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE}/api/reports/quartile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuartileGridBySpecies(quartileApiResponseToGridBySpecies(res.data));
    } catch (e) {
      console.warn('Quartile load failed:', e);
    }
  }, []);

  useEffect(() => {
    if (showStaticPdf && reportMode === 'pet-care-pro') {
      loadQuartileGrid();
    }
  }, [showStaticPdf, reportMode, loadQuartileGrid]);

  /**
   * All Biopet page data in one shot, via the same pure function the server-side PDF render
   * calls (computeBiopetReportData in BiopetReportPages). Preview and download therefore
   * grade identically — they run the same code over the same row.
   */
  const biopetReportData = useMemo(() => {
    if (reportMode !== 'pet-care-pro' && reportMode !== 'pet-care-essential') return null;
    const rowIndex = Number.isInteger(previewRowIndex) ? previewRowIndex : 0;
    return computeBiopetReportData({
      formData,
      quartileBySpecies: biopetQuartileBySpecies,
      manualSupplements: manualSupplements[rowIndex] || null
    });
  }, [reportMode, formData, biopetQuartileBySpecies, previewRowIndex, manualSupplements]);

  // 1. Add popupStock state
  const [popupStock, setPopupStock] = useState({});
  
  // State to store Plan A and Plan B results
  const [planAResults, setPlanAResults] = useState({});
  const [planBResults, setPlanBResults] = useState({});
  
  // State to track hovered select button for tooltip
  const [hoveredSelectButton, setHoveredSelectButton] = useState(null);
  
  // State for custom tooltip
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // State to track selected plan for each row
  const [selectedPlans, setSelectedPlans] = useState({});
  
  // State to track whether to ignore stock level conditions in inventory popup
  const [ignoreStockLevels, setIgnoreStockLevels] = useState(false);
  
  // State for CSV Import Preview toggle switch
  const [csvPreviewToggle, setCsvPreviewToggle] = useState(false); // false = B2C, true = B2B (default to B2C)

  // 9P calculation version: 'new' (single-strain SKU cards, default) or
  // 'old' (original BioAll/BioMeta/BioZen blend). Chosen from the CSV Import
  // Preview toggle; kept in sync with the calculation module below.
  const [supplement9PVersion, setSupplement9PVersion] = useState('new');
  useEffect(() => {
    set9PSupplementVersion(supplement9PVersion);
  }, [supplement9PVersion]);

  // Every toggle-capable panel defaults to the New formula; the user can switch
  // to Original. History preview overrides this via applyDetected9PVersion.
  useEffect(() => {
    setSupplement9PVersion('new');
  }, [reportMode]);

  // When previewing a SAVED report (history) for a panel that supports the
  // toggle, pick the formula version from the stored supplement names so it
  // renders the way it was recorded: any new-formula SKU present -> 'new'
  // (single-strain cards), otherwise -> 'old' (that panel's original products).
  // Keeps history preview consistent with the CSV-import preview.
  const applyDetected9PVersion = (mode, supplements) => {
    if (!panelSupportsNewFormula(mode)) return;
    const isNew = (supplements || []).some((s) => supplement_9P_skus.includes(s));
    const version = isNew ? 'new' : 'old';
    setSupplement9PVersion(version);
    set9PSupplementVersion(version); // sync now; the effect runs after this handler
  };

  // Monitor csvPreviewToggle state changes for debugging
  useEffect(() => {
    console.log('🔍 csvPreviewToggle state changed:', csvPreviewToggle);
  }, [csvPreviewToggle]);
  
  // Reset toggle based on panel type
  // For QPT and QI (Infant Allergy) panel, default to B2B mode
  // For Q9/9P format, default to B2C mode
  useEffect(() => {
    if (reportMode === 'QPT' || reportMode === 'Allergy and Skin_kids') {
      setCsvPreviewToggle(true); // B2B for QPT and QI panel
    } else if (reportMode === '9P') {
      setCsvPreviewToggle(false); // B2C for 9P/Q9 format
    } else {
      setCsvPreviewToggle(false); // B2C for other formats
    }
  }, [reportMode]);

  // Function to get display name for supplements based on panel and toggle state
  const getSupplementDisplayName = (supplementName) => {
    // Check if we're in 9P or QPT panel context (either main reportMode or pdfPreviewReportMode)
    const is9PPanel = reportMode === '9P' || pdfPreviewReportMode === '9P';
    const isQPTPanel = reportMode === 'QPT' || pdfPreviewReportMode === 'QPT';
    
    if (is9PPanel && csvPreviewToggle) {
      const nameMappings = {
        'BioAll': 'B2B - 8x3SA',
        'BioZen': 'B2B - 5x3C',
        'BioMeta': 'B2B - 6x3V'
      };
      return nameMappings[supplementName] || supplementName;
    }
    
    if (isQPTPanel && csvPreviewToggle) {
      // For QU format, add B2B prefix to supplement names
      return `B2B - ${supplementName}`;
    }
    
    return supplementName;
  };

  // Function to validate personal information fields
  const isPersonalInfoComplete = () => {
    if (!reportMode) return false;
    
    const formSections = formSectionsByFormat[reportMode] || [];
    const personalInfoSection = formSections.find(section => 
      section.title.toLowerCase().includes('personal information')
    );
    
    if (!personalInfoSection) return true; // If no personal info section, allow calculation
    
    // For all formats, exclude Collection Date from required fields
    const requiredFields = personalInfoSection.inputs
      .map(input => input.label)
      .filter(field => field !== 'Collection Date'); // Exclude Collection Date for all formats
    
    // Check if all required fields (except Collection Date) are filled
    for (const field of requiredFields) {
      const value = formData[field];
      if (!value || value.trim() === '' || value === 'Select') {
        return false;
      }
    }
    
    return true;
  };

  // Function to get missing personal information fields
  const getMissingPersonalInfoFields = () => {
    if (!reportMode) return [];
    
    const formSections = formSectionsByFormat[reportMode] || [];
    const personalInfoSection = formSections.find(section => 
      section.title.toLowerCase().includes('personal information')
    );
    
    if (!personalInfoSection) return [];
    
    // For all formats, exclude Collection Date from required fields
    const requiredFields = personalInfoSection.inputs
      .map(input => input.label)
      .filter(field => field !== 'Collection Date'); // Exclude Collection Date for all formats
    
    const missingFields = [];
    for (const field of requiredFields) {
      const value = formData[field];
      if (!value || value.trim() === '' || value === 'Select') {
        missingFields.push(field);
      }
    }
    
    return missingFields;
  };

  // Function to download PDF from server
  // Client-side PDF generation function (same as Download All)
  // Standalone function for generating PDF blob - can be used by multiple functions
  const generatePDFBlob = async (lang, options = {}) => {
    try {
      const {
        scale = 1.9,
        imageType = 'JPEG',
        imageQuality = 0.90,
        waitBackgroundImages = false,
        useForeignObjectRendering = false
      } = options;

      const preloadBackgroundImages = async (rootEl) => {
        if (!waitBackgroundImages || !rootEl) return;
        // Preload all background-image URLs inside this page wrapper.
        const nodes = rootEl.querySelectorAll('*');
        const urls = new Set();
        for (const el of nodes) {
          const style = window.getComputedStyle(el);
          const bg = style?.backgroundImage;
          if (!bg || bg === 'none') continue;
          // Extract all url(...) occurrences.
          const re = /url\((['"]?)(.*?)\1\)/g;
          let match = null;
          while ((match = re.exec(bg)) !== null) {
            const u = match?.[2];
            if (u) urls.add(u);
          }
        }
        if (urls.size === 0) return;
        // Load images so html2canvas can render them sharply.
        await Promise.all(
          Array.from(urls).map(
            (u) =>
              new Promise((resolve) => {
                const img = new Image();
                img.onload = () => resolve();
                img.onerror = () => resolve(); // best-effort: don't block on broken URLs
                img.src = u;
              })
          )
        );
      };
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;

      const pages = document.querySelectorAll('.pdf-page-wrapper');
      if (!pages || pages.length === 0) {
        throw new Error('No PDF pages found in DOM');
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      let isFirst = true;

      for (let i = 0; i < pages.length; i++) {
        const el = pages[i];
        if (!el) continue;
        
        el.scrollIntoView();
        await new Promise(r => setTimeout(r, 100)); // Increased wait time

        // Biopet: background-image assets (petflow/petguard/etc) must be decoded
        // before html2canvas captures page 8.
        await preloadBackgroundImages(el);

        const canvas = await html2canvas(el, {
          scale,
          useCORS: true,
          backgroundColor: resolveCaptureBackgroundColor(),
          allowTaint: true,
          logging: false,
          foreignObjectRendering: useForeignObjectRendering
        });

        const isPng = String(imageType).toUpperCase() === 'PNG';
        const imgData = isPng
          ? canvas.toDataURL('image/png')
          : canvas.toDataURL('image/jpeg', imageQuality);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (!isFirst) pdf.addPage();
        pdf.addImage(imgData, isPng ? 'PNG' : 'JPEG', 0, 0, pdfWidth, imgHeight);
        isFirst = false;
      }

      return pdf.output('blob');
    } catch (error) {
      console.error('Error generating PDF blob:', error);
      throw new Error(`Failed to generate PDF: ${error.message}`);
    }
  };

  const generateIndividualPDF = async (rowData, rowIndex) => {
    try {
      console.log('Starting individual PDF generation for row:', rowIndex);
      
      // Set loading state for this specific row
      setIndividualDownloadLoading(prev => ({ ...prev, [rowIndex]: true }));
      
      // Get supplements based on selected plan for this row
      const selectedPlan = selectedPlans[rowIndex];
      let supplements = [];
      
      if (selectedPlan === 'Plan A') {
        supplements = planAResults[rowIndex]?.supplements || [];
      } else if (selectedPlan === 'Plan B') {
        supplements = planBResults[rowIndex]?.supplements || [];
      } else if (selectedPlan === 'Plan C') {
        supplements = Object.keys(selectedSupplements).filter(supplement => selectedSupplements[supplement]);
      } else {
        // Fallback to default supplements if no plan is selected
        supplements = supplementResults[rowIndex] || [];
      }

      // Normalize the CSV row data for consistent format
      let configInfo;
      if (reportMode === '9P') configInfo = choosing_supplement_9P[0].info;
      else if (reportMode === 'oms') configInfo = choosing_supplement_oms[0].info;
      else if (reportMode === 'Allergy and Skin') configInfo = choosing_supplement_AMG[0].info;
      else if (reportMode === 'Allergy and Skin_kids') configInfo = qpt_choosing_supplement[0].info;
      else if (reportMode === 'Toxin') configInfo = choosing_supplement_toxin[0].info;
      else if (['bwcallergy', 'bwccore', 'bwcdiamond'].includes(reportMode)) configInfo = choosing_supplement_bwc[0].info;
      else configInfo = qpt_choosing_supplement[0].info;
      
      const normalizedRowData = normalizeRowKeys(rowData, configInfo);

      // Add all bacteria from bacteriaMetaConfig with value 0 as default
      const allBacteriaDefaults = {};
      Object.keys(bacteriaMetaConfig).forEach(bacteriaName => {
        allBacteriaDefaults[bacteriaName] = 0;
      });
      
      // Merge defaults with normalized row data
      const formDataWithDefaults = {
        ...allBacteriaDefaults,
        ...normalizedRowData
      };

      // Set up the data for PDF generation
      setFormData(formDataWithDefaults);
      setSupplementResults(prev => ({ ...prev, [rowIndex]: supplements }));
      setManualSupplements(prev => ({ ...prev, [rowIndex]: supplements }));
      setPreviewRowIndex(rowIndex);
      setVersionOptions({ eng: true, thai: false });
      setIsPdfReady(true);
      setShowStaticPdf(true);

      // Wait for positioning calculations to complete
      await new Promise(r => setTimeout(r, 2000));
      
      // Additional wait to ensure all DOM elements are properly positioned
      await new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max wait
        
        const checkPositions = () => {
          attempts++;
          const pages = document.querySelectorAll('.pdf-page-wrapper');
          if (pages.length > 0) {
            // Check if overlay elements are positioned
            const overlayElements = document.querySelectorAll('.pdf-overlay-text, .pdf-overlay-image');
            if (overlayElements.length > 0) {
              resolve();
            } else if (attempts >= maxAttempts) {
              reject(new Error('Timeout waiting for PDF elements to position'));
            } else {
              setTimeout(checkPositions, 100);
            }
          } else if (attempts >= maxAttempts) {
            reject(new Error('Timeout waiting for PDF pages to load'));
          } else {
            setTimeout(checkPositions, 100);
          }
        };
        checkPositions();
      });
      
      // Generate English PDF
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;

      const pages = document.querySelectorAll('.pdf-page-wrapper');
      if (!pages || pages.length === 0) {
        throw new Error('No PDF pages found in DOM');
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      let isFirst = true;

      for (let i = 0; i < pages.length; i++) {
        const el = pages[i];
        if (!el) continue;
        
        el.scrollIntoView();
        // Minimal delay for page positioning
        await new Promise(r => setTimeout(r, 50));

        const canvas = await forceContinue(async () => {
          // Start a keep-alive mechanism specifically for this canvas generation
          const keepAliveInterval = setInterval(() => {
            // Force the browser to stay active during canvas generation
            requestAnimationFrame(() => {
              Math.random();
              // Small DOM operation to prevent throttling
              const temp = document.createElement('div');
              temp.style.position = 'absolute';
              temp.style.left = '-9999px';
              temp.textContent = `canvas-keepalive-static-${Date.now()}`;
              document.body.appendChild(temp);
              document.body.removeChild(temp);
            });
          }, 8); // 120fps updates during critical operations
          
          try {
            const result = await html2canvas(el, {
              scale: 1.9,
              useCORS: true,
              backgroundColor: resolveCaptureBackgroundColor(),
              allowTaint: true,
              logging: false,
              // Add these options to improve reliability when tab is inactive
              removeContainer: false,
              foreignObjectRendering: false,
              imageTimeout: 30000, // 30 second timeout
              onclone: (clonedDoc) => {
                // Ensure the cloned document has proper dimensions
                const clonedElement = clonedDoc.querySelector('.pdf-page-wrapper');
                if (clonedElement) {
                  clonedElement.style.position = 'relative';
                  clonedElement.style.overflow = 'visible';
                }
              }
            });
            
            // Clear the keep-alive interval
            clearInterval(keepAliveInterval);
            return result;
          } catch (error) {
            // Clear the keep-alive interval on error
            clearInterval(keepAliveInterval);
            throw error;
          }
        }, 60000); // 60 second timeout for canvas generation

        const imgData = canvas.toDataURL('image/jpeg', 0.90);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (!isFirst) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, imgHeight);
        isFirst = false;
      }

      const engBlob = pdf.output('blob');

      // Generate Thai version - Use the same approach as Download All
      // Close and reopen for Thai version
      setShowStaticPdf(false);
      setIsPdfReady(false);
      await new Promise(r => setTimeout(r, 500));

      setVersionOptions({ eng: false, thai: true });
      setIsPdfReady(true);
      setShowStaticPdf(true);
      await new Promise(r => setTimeout(r, 2000));
      
      // Wait for Thai version positioning calculations
      await new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max wait
        
        const checkPositions = () => {
          attempts++;
          const pages = document.querySelectorAll('.pdf-page-wrapper');
          if (pages.length > 0) {
            const overlayElements = document.querySelectorAll('.pdf-overlay-text, .pdf-overlay-image');
            if (overlayElements.length > 0) {
              resolve();
            } else if (attempts >= maxAttempts) {
              reject(new Error('Timeout waiting for Thai PDF elements to position'));
            } else {
              setTimeout(checkPositions, 100);
            }
          } else if (attempts >= maxAttempts) {
            reject(new Error('Timeout waiting for Thai PDF pages to load'));
          } else {
            setTimeout(checkPositions, 100);
          }
        };
        checkPositions();
      });
      
      // Generate Thai PDF using the same approach as Download All
      const thaiBlob = await generatePDFBlob('thai');

      // Merge PDFs
      const { PDFDocument } = await import('pdf-lib');
      const [engPdfDoc, thaiPdfDocForMerge] = await Promise.all([
        PDFDocument.load(await engBlob.arrayBuffer()),
        PDFDocument.load(await thaiBlob.arrayBuffer())
      ]);

      const mergedPdf = await PDFDocument.create();

      const engPages = await mergedPdf.copyPages(engPdfDoc, engPdfDoc.getPageIndices());
      engPages.forEach(p => mergedPdf.addPage(p));

      const thaiPdfPages = await mergedPdf.copyPages(thaiPdfDocForMerge, thaiPdfDocForMerge.getPageIndices());
      thaiPdfPages.forEach(p => mergedPdf.addPage(p));

      const mergedBlob = new Blob([await mergedPdf.save()], { type: 'application/pdf' });

      // Download the merged PDF
      const fileName = `${rowData['Report Number'] || rowData['Report number'] || `report_${rowIndex + 1}`}.pdf`;
      const url = window.URL.createObjectURL(mergedBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Clean up
      setShowStaticPdf(false);
      setIsPdfReady(false);
      
      console.log('Individual PDF generated and downloaded successfully:', fileName);
      
    } catch (error) {
      console.error('Error generating individual PDF:', error);
      
      // Clean up on error
      setShowStaticPdf(false);
      setIsPdfReady(false);
      
      alert(`Error generating PDF: ${error.message}. Please try again.`);
    } finally {
      // Clear loading state
      setIndividualDownloadLoading(prev => ({ ...prev, [rowIndex]: false }));
    }
  };

  // History PDF generation using EXACT same logic as preview
  const generateHistoryPDF = async (reportData) => {
    try {
      console.log('Starting history PDF generation using preview logic for report:', reportData['Report number']);
      
      // Show progress bar and start at 0%
      setShowDownloadProgress(true);
      setDownloadProgress(0);
      
      // Use EXACT same logic as the preview button
      // Determine reportMode from Panel
      let mode = 'QPT';
      if (reportData.Panel === 'Q9' || reportData.Panel === '9P') mode = '9P';
      else if (reportData.Panel === 'QA' || reportData.Panel === 'Allergy and Skin') mode = 'Allergy and Skin';
      else if (reportData.Panel === 'QI' || reportData.Panel === 'QA(KIDs)' || reportData.Panel === 'Allergy and Skin_kids') mode = 'Allergy and Skin_kids';
      else if (reportData.Panel === 'QG' || reportData.Panel === 'Toxin') mode = 'Toxin';
      else if (reportData.Panel === 'QM' || reportData.Panel === 'oms') mode = 'oms';
      else if (reportData.Panel === 'QBWCA' || reportData.Panel === 'bwcallergy') mode = 'bwcallergy';
      else if (reportData.Panel === 'QBWCE' || reportData.Panel === 'bwccore') mode = 'bwccore';
      else if (reportData.Panel === 'QBWCU' || reportData.Panel === 'bwcdiamond') mode = 'bwcdiamond';

      console.log('Detected report mode:', mode);
      setDownloadProgress(5); // 5% - Mode detected

      // Normalize row to formData (EXACT same as preview)
      let configInfo;
      if (mode === '9P') configInfo = choosing_supplement_9P[0].info;
      else if (mode === 'oms') configInfo = choosing_supplement_oms[0].info;
      else if (mode === 'Allergy and Skin') configInfo = choosing_supplement_AMG[0].info;
      else if (mode === 'Allergy and Skin_kids') configInfo = qpt_choosing_supplement[0].info;
      else if (mode === 'Toxin') configInfo = choosing_supplement_toxin[0].info;
      else if ([
        'bwcallergy', 'bwccore', 'bwcdiamond', 'BWC-A', 'BWC-C', 'BWC-D'
      ].includes(mode)) configInfo = choosing_supplement_bwc[0].info;
      else configInfo = qpt_choosing_supplement[0].info;

      const normalized = normalizeRowKeys(reportData, configInfo);
      console.log('Data normalized using preview logic');
      
      // Debug: Check duration field
      console.log('🔍 GenerateHistoryPDF - Original reportData duration:', reportData['Probiotics (months)']);
      console.log('🔍 GenerateHistoryPDF - Normalized duration:', normalized['Probiotics (months)']);
      console.log('🔍 GenerateHistoryPDF - All normalized keys:', Object.keys(normalized));
      
      setDownloadProgress(10); // 10% - Data normalized

      // Use stored supplement data if available, otherwise calculate (EXACT same as preview)
      let supplements = [];
      if (reportData.Supplement) {
        try {
          const storedSupplements = JSON.parse(reportData.Supplement);
          supplements = Array.isArray(storedSupplements) ? storedSupplements : [];
          console.log('Using stored supplements from preview logic:', supplements);
        } catch (e) {
          console.warn('Failed to parse stored supplements, calculating instead:', e);
          const supplementResult = getRecommendedSupplements(normalized, mode, stock, [], stockThreshold, {});
          supplements = supplementResult.supplements || [];
        }
      } else {
        // Calculate supplements if not stored (EXACT same as preview)
        const supplementResult = getRecommendedSupplements(normalized, mode, stock, [], stockThreshold, {});
        supplements = supplementResult.supplements || [];
      }
      setDownloadProgress(15); // 15% - Supplements processed

      // Set up the data using PREVIEW state variables (EXACT same as preview)
      setPdfPreviewReportMode(mode);
      setPdfPreviewFormData(normalized);
      applyDetected9PVersion(mode, supplements);
      setPdfPreviewSupplements(supplements);
      setPdfPreviewThai(false); // Start with English version
      setPdfPreviewLoading(false); // Ensure loading is false so modal can render
      setShowPdfPreview(true);

      console.log('Preview data setup completed, waiting for DOM...');
      setDownloadProgress(20); // 20% - Preview setup completed

      // Wait for DOM to be ready and modal to render
      await new Promise(r => setTimeout(r, 3000));
      setDownloadProgress(25); // 25% - Initial wait completed
      
      // Check if the modal is actually rendered
      const modalElement = document.querySelector('.modal-content');
      console.log('Modal element found:', !!modalElement);
      
      if (!modalElement) {
        console.log('Modal not rendered yet, waiting more...');
        await new Promise(r => setTimeout(r, 2000));
        setDownloadProgress(30); // 30% - Additional wait completed
      }
      
      // Check if PDF preview pages exist
      const pages = document.querySelectorAll('.pdf-page-wrapper');
      console.log('Found PDF pages:', pages.length);
      console.log('All elements with pdf-page-wrapper class:', pages);
      
      if (!pages || pages.length === 0) {
        // Let's also check what's actually in the DOM
        const allDivs = document.querySelectorAll('div');
        console.log('All divs in DOM:', allDivs.length);
        const modalDivs = document.querySelectorAll('.modal-content div');
        console.log('Modal content divs:', modalDivs.length);
        
        // Check if modal is rendered but no PDF pages
        const modalContent = document.querySelector('.modal-content');
        if (modalContent) {
          console.log('Modal content HTML:', modalContent.innerHTML);
        }
        
        throw new Error('No PDF preview pages found in DOM');
      }

      console.log(`Found ${pages.length} PDF preview pages, starting generation...`);
      setDownloadProgress(35); // 35% - PDF pages found

      // Generate English PDF
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;
      setDownloadProgress(40); // 40% - Libraries loaded

      const pdf = new jsPDF('p', 'mm', 'a4');
      let isFirst = true;

      for (let i = 0; i < pages.length; i++) {
        const el = pages[i];
        if (!el) continue;
        
        console.log(`Processing preview page ${i + 1}/${pages.length}`);
        
        el.scrollIntoView();
        await new Promise(r => setTimeout(r, 500));

        const canvas = await html2canvas(el, {
          scale: 1.5,
          useCORS: true,
          backgroundColor: resolveCaptureBackgroundColor(),
          allowTaint: true,
          logging: false
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.90);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (!isFirst) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, imgHeight);
        isFirst = false;
        
        // Update progress for each page (40% to 60%)
        const pageProgress = 40 + ((i + 1) / pages.length) * 20;
        setDownloadProgress(Math.round(pageProgress));
      }

      const engBlob = pdf.output('blob');
      console.log('English PDF generated successfully from preview');
      setDownloadProgress(65); // 65% - English PDF completed

      // Generate Thai version
      console.log('Starting Thai PDF generation from preview...');
      setPdfPreviewThai(true);
      setDownloadProgress(70); // 70% - Starting Thai generation

      // Wait for Thai version to load
      await new Promise(r => setTimeout(r, 3000));
      setDownloadProgress(75); // 75% - Thai version loaded
      
      const thaiPageElements = document.querySelectorAll('.pdf-page-wrapper');
      if (!thaiPageElements || thaiPageElements.length === 0) {
        throw new Error('No Thai PDF preview pages found in DOM');
      }

      const thaiPdfDoc = new jsPDF('p', 'mm', 'a4');
      isFirst = true;

      for (let i = 0; i < thaiPageElements.length; i++) {
        const el = thaiPageElements[i];
        if (!el) continue;
        
        console.log(`Processing Thai preview page ${i + 1}/${thaiPageElements.length}`);
        
        el.scrollIntoView();
        await new Promise(r => setTimeout(r, 500));

        const canvas = await html2canvas(el, {
          scale: 1.5,
          useCORS: true,
          backgroundColor: resolveCaptureBackgroundColor(),
          allowTaint: true,
          logging: false
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.90);
        const pdfWidth = thaiPdfDoc.internal.pageSize.getWidth();
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (!isFirst) thaiPdfDoc.addPage();
        thaiPdfDoc.addImage(imgData, 'JPEG', 0, 0, pdfWidth, imgHeight);
        isFirst = false;
        
        // Update progress for each Thai page (75% to 85%)
        const thaiPageProgress = 75 + ((i + 1) / thaiPageElements.length) * 10;
        setDownloadProgress(Math.round(thaiPageProgress));
      }

      const thaiBlob = thaiPdfDoc.output('blob');
      console.log('Thai PDF generated successfully from preview');
      setDownloadProgress(90); // 90% - Thai PDF completed

      // Merge PDFs
      console.log('Starting PDF merge...');
      const { PDFDocument } = await import('pdf-lib');
      const [engPdfDoc, thaiPdfDocForMerge] = await Promise.all([
        PDFDocument.load(await engBlob.arrayBuffer()),
        PDFDocument.load(await thaiBlob.arrayBuffer())
      ]);

      const mergedPdf = await PDFDocument.create();

      const engPages = await mergedPdf.copyPages(engPdfDoc, engPdfDoc.getPageIndices());
      engPages.forEach(p => mergedPdf.addPage(p));

      const thaiPdfPages = await mergedPdf.copyPages(thaiPdfDocForMerge, thaiPdfDocForMerge.getPageIndices());
      thaiPdfPages.forEach(p => mergedPdf.addPage(p));

      const mergedBlob = new Blob([await mergedPdf.save()], { type: 'application/pdf' });
      console.log('PDFs merged successfully');
      setDownloadProgress(95); // 95% - PDFs merged

      // Download the merged PDF
      const fileName = pdfFileNameFromReportNumber(reportData);
      
      const url = window.URL.createObjectURL(mergedBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.style.display = 'none';
      
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 1000);

      // Clean up state
      setShowPdfPreview(false);
      setDownloadProgress(100); // 100% - Download completed
      
      console.log('History PDF generated and downloaded successfully using preview logic:', fileName);
      
      // Hide progress bar after a short delay
      setTimeout(() => {
        setShowDownloadProgress(false);
        setDownloadProgress(0);
      }, 2000);
      
      alert(`PDF downloaded successfully: ${fileName}`);
      
    } catch (error) {
      console.error('Error generating history PDF using preview logic:', error);
      
      // Clean up on error
      setShowPdfPreview(false);
      setShowDownloadProgress(false);
      setDownloadProgress(0);
      
      alert(`Error generating PDF: ${error.message}. Please try again.`);
    }
  };

  // Keep the server-side function for backward compatibility
  const downloadServerPdf = async (rowData, htmlString) => {
    try {
      console.log('Starting server PDF download for:', rowData['Report Number'] || rowData['Report number'] || 'report');
      console.log('Report mode:', reportMode);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/pdf/generate`,
        {
          html: htmlString,
          reportNumber: rowData['Report Number'] || rowData['Report number'] || 'report',
          reportMode: reportMode
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob',
          timeout: 60000 // 60 second timeout
        }
      );

      // Check if response is valid
      if (!response.data || response.data.size === 0) {
        throw new Error('Server returned empty PDF data');
      }

      console.log('PDF generated successfully, size:', response.data.size);

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfFileNameFromReportNumber(rowData);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log('Download initiated successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      
      let errorMessage = 'Error generating PDF. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error occurred. Please try again later.';
        } else if (error.response.status === 413) {
          errorMessage = 'PDF data too large. Please try with fewer supplements.';
        } else {
          errorMessage = `Server error (${error.response.status}): ${error.response.statusText}`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.message) {
        // Other error
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  // PDF Progress tracking states
  const [showPdfProgress, setShowPdfProgress] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ current: 0, total: 0 });
  const [pdfProgressText, setPdfProgressText] = useState('');
  const [pdfProgressDetails, setPdfProgressDetails] = useState('');
  const [pdfCancelRequested, setPdfCancelRequested] = useState(false);
  const [individualDownloadLoading, setIndividualDownloadLoading] = useState({});

  const [downloadProgress, setDownloadProgress] = useState(0);
  const [showDownloadProgress, setShowDownloadProgress] = useState(false);

  // Pagination button delay states
  const [paginationButtonsDisabled, setPaginationButtonsDisabled] = useState(false);

  // Function to handle pagination with delay
  const handlePagination = (direction) => {
    if (paginationButtonsDisabled) return;
    
    setPaginationButtonsDisabled(true);
    
    if (direction === 'prev' && pagination.hasPrevPage) {
      setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
      // Keep selected reports when changing pages - no reset needed
    } else if (direction === 'next' && pagination.hasNextPage) {
      setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
      // Keep selected reports when changing pages - no reset needed
    }
    
    // Re-enable buttons after 0.5 seconds
    setTimeout(() => {
      setPaginationButtonsDisabled(false);
    }, 500);
  };

  // Update stock calculations when appliedDurations or panelSupplementStock changes
  useEffect(() => {
    if (Object.keys(panelSupplementStock).length > 0) {
      const updated = calculateUpdatedStock();
      setUpdatedSupplementStock(updated);
    }
  }, [appliedDurations, panelSupplementStock]);

  // Load stock threshold from database on component mount
  useEffect(() => {
    const loadStockThreshold = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_BASE}/api/stock-condition`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStockThreshold(response.data.st_check);
        setTempStockThreshold(response.data.st_check);
        setRedThreshold(response.data.st_red || 500);
        setTempRedThreshold(response.data.st_red || 500);
        setYellowThreshold(response.data.st_yellow || 1000);
        setTempYellowThreshold(response.data.st_yellow || 1000);
      } catch (error) {
        console.error('Failed to load stock threshold:', error);
        // Keep default values if loading fails
      }
    };
    
    loadStockThreshold();
  }, []);

  // Function to handle FDA checkbox changes
  const handleFdaCheckboxChange = (index, checked) => {
    setFdaCheckboxes(prev => ({ ...prev, [index]: checked }));
    setFdaRecalculatedRows(prev => ({ ...prev, [index]: false }));
  };

  // Function to handle inventory icon click
  const handleInventoryClick = (index) => {
    // Always recalculate popupStock as original stock minus all other rows' usages
    let recalculatedStock = { ...panelSupplementStock };
    Object.entries(supplementDurations).forEach(([rowIdx, durations]) => {
      if (parseInt(rowIdx) === index) return;
      
      // Get supplements for this row to calculate stock division
      const selectedPlan = selectedPlans[rowIdx];
      let supplements = [];
      
      if (selectedPlan === 'Plan A') {
        supplements = planAResults[rowIdx]?.supplements || [];
      } else if (selectedPlan === 'Plan B') {
        supplements = planBResults[rowIdx]?.supplements || [];
      } else if (selectedPlan === 'Plan C') {
        supplements = Object.keys(selectedSupplements).filter(supplement => selectedSupplements[supplement]);
      } else {
        supplements = supplementResults[rowIdx] || [];
      }
      
      const supplementCount = supplements.length;
      
      Object.entries(durations).forEach(([supplement, duration]) => {
        if (duration !== 'None' && duration !== 'No Duration' && recalculatedStock[supplement] !== undefined) {
          // 60 sticks per month; new-formula SKUs consume the full amount each
          // (taken concurrently), legacy blends split the total across products.
          const months = parseInt(String(duration).match(/\d+/)?.[0] || '0', 10);
          const totalRequired = months * 60;
          const isNewFormulaSku = supplement_9P_skus.includes(supplement);
          const required = isNewFormulaSku
            ? totalRequired
            : (supplementCount > 0 ? totalRequired / supplementCount : 0);
          recalculatedStock[supplement] = Math.max(0, recalculatedStock[supplement] - Math.ceil(required));
        }
      });
    });
    setPopupStock(recalculatedStock);

    // Get supplements based on selected plan
    const selectedPlan = selectedPlans[index];
    let supplements = [];
    
    if (selectedPlan === 'Plan A') {
      supplements = planAResults[index]?.supplements || [];
    } else if (selectedPlan === 'Plan B') {
      supplements = planBResults[index]?.supplements || [];
    } else if (selectedPlan === 'Plan C') {
      supplements = Object.keys(selectedSupplements).filter(supplement => selectedSupplements[supplement]);
    } else {
      // Fallback to default supplements if no plan is selected
      supplements = supplementResults[index] || [];
    }

    // Ensure all supplements in this row have a default of 'None' if not set
    setSupplementDurations(prev => {
      const current = prev[index] || {};
      const newDurations = { ...current };
      supplements.forEach(supplement => {
        if (!newDurations[supplement]) {
          newDurations[supplement] = 'None';
        }
      });
      return { ...prev, [index]: newDurations };
    });

    setInventoryRowIndex(index);
    setShowInventoryPopup(true);
  };

  // Function to handle duration change
  const handleDurationChange = (supplement, duration) => {
    setSupplementDurations(prev => ({
      ...prev,
      [inventoryRowIndex]: {
        ...prev[inventoryRowIndex],
        [supplement]: duration
      }
    }));
  };

  // Function to automatically confirm a row when durations are auto-selected
  const handleAutoConfirm = (rowIndex, selectedPlan) => {
    console.log(`🔄 Auto-confirming row ${rowIndex} with plan ${selectedPlan}`);
    
    // Check if we have the necessary data
    if (!planAResults[rowIndex] && !planBResults[rowIndex]) {
      console.warn(`⚠️ No plan results available for row ${rowIndex}, cannot auto-confirm`);
      return;
    }
    
    // Set the selected plan
    setSelectedPlans(prev => ({ ...prev, [rowIndex]: selectedPlan }));
    
    // Get the supplements based on the selected plan
    let supplements = [];
    if (selectedPlan === 'Plan A') {
      supplements = planAResults[rowIndex]?.supplements || [];
    } else if (selectedPlan === 'Plan B') {
      supplements = planBResults[rowIndex]?.supplements || [];
    }
    
    // Update supplement results
    setSupplementResults(prev => ({
      ...prev,
      [rowIndex]: supplements
    }));
    
    // Ensure durations are applied
    const autoSelectedDuration = appliedDurations[rowIndex];
    if (autoSelectedDuration && Object.keys(autoSelectedDuration).length > 0) {
      setSupplementDurations(prev => ({
        ...prev,
        [rowIndex]: autoSelectedDuration
      }));
    }
    
    // Mark the row as confirmed
    setConfirmedRows(prev => ({ ...prev, [rowIndex]: true }));
    
    // Log the auto-confirmation (for debugging only)
    console.log(`✅ Row ${rowIndex} auto-confirmed with plan ${selectedPlan}`);
  };

  // Function to show notification
  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'info' });
    }, 2000);
  };

  // Function to handle clicking on Amt of Supp. column for editing
  const handleAmtOfSuppClick = (rowIndex) => {
    // Check if a plan is selected for this row
    if (!selectedPlans[rowIndex]) {
      showNotification('Please select a plan first before editing supplement durations.', 'warning');
      return;
    }
    
    console.log(`🔄 Opening duration selection modal for row ${rowIndex}`);
    
    // Set the current row index for editing
    setInventoryRowIndex(rowIndex);
    
    // Open the duration selection modal
    setShowDurationModal(true);
    setDurationModalRowIndex(rowIndex);
    
    console.log(`📋 Opening duration selection for row ${rowIndex}`);
  };

  // Function to handle bulk duration selection for all supplements
  const handleBulkDurationChange = (duration) => {
    // Get supplements based on selected plan
    const selectedPlan = selectedPlans[inventoryRowIndex];
    let supplements = [];
    
    if (selectedPlan === 'Plan A') {
      supplements = planAResults[inventoryRowIndex]?.supplements || [];
    } else if (selectedPlan === 'Plan B') {
      supplements = planBResults[inventoryRowIndex]?.supplements || [];
    } else if (selectedPlan === 'Plan C') {
      supplements = Object.keys(selectedSupplements).filter(supplement => selectedSupplements[supplement]);
    } else {
      // Fallback to default supplements if no plan is selected
      supplements = supplementResults[inventoryRowIndex] || [];
    }
    
    const newDurations = {};
    
    supplements.forEach(supplement => {
      newDurations[supplement] = duration;
    });
    
    setSupplementDurations(prev => ({
      ...prev,
      [inventoryRowIndex]: newDurations
    }));
    
    // Also update appliedDurations for the current row
    setAppliedDurations(prev => ({
      ...prev,
      [inventoryRowIndex]: newDurations
    }));
    
    // Close the duration modal
    setShowDurationModal(false);
    setDurationModalRowIndex(null);
    
    console.log(`Applied ${duration} to all supplements in row ${inventoryRowIndex}`);
  };

  // Function to check if supplement has sufficient stock for selected duration
  const hasSufficientStock = (supplement, duration, stockSource = popupStock) => {
    // If ignore stock levels is checked, always return true
    if (ignoreStockLevels) {
      return true;
    }

    // New-formula SKUs are not inventory-gated (no stock columns yet), so they
    // never block duration selection — consistent with the recommendation which
    // also skips stock filtering for the new formula.
    if (supplement_9P_skus.includes(supplement)) {
      return true;
    }

    const currentStock = (stockSource && stockSource[supplement]) || 0;

    // Get supplements for current row to calculate stock division
    const selectedPlan = selectedPlans[inventoryRowIndex];
    let supplements = [];

    if (selectedPlan === 'Plan A') {
      supplements = planAResults[inventoryRowIndex]?.supplements || [];
    } else if (selectedPlan === 'Plan B') {
      supplements = planBResults[inventoryRowIndex]?.supplements || [];
    } else if (selectedPlan === 'Plan C') {
      supplements = Object.keys(selectedSupplements).filter(supplement => selectedSupplements[supplement]);
    } else {
      supplements = supplementResults[inventoryRowIndex] || [];
    }

    const supplementCount = supplements.length;

    // 60 sticks per month, divided across the (alternating) legacy products.
    const months = parseInt(String(duration).match(/\d+/)?.[0] || '0', 10);
    const roundedStockPerSupplement = Math.ceil(supplementCount > 0 ? (months * 60) / supplementCount : 0);

    return currentStock >= roundedStockPerSupplement;
  };

  // Function to calculate updated stock based on current selections
  const calculateUpdatedStock = () => {
    const updated = { ...panelSupplementStock };
    
    // Go through all rows and their applied durations
    Object.entries(appliedDurations).forEach(([rowIndex, durations]) => {
      // Get supplements for this row to calculate stock division
      const selectedPlan = selectedPlans[rowIndex];
      let supplements = [];
      
      if (selectedPlan === 'Plan A') {
        supplements = planAResults[rowIndex]?.supplements || [];
      } else if (selectedPlan === 'Plan B') {
        supplements = planBResults[rowIndex]?.supplements || [];
      } else if (selectedPlan === 'Plan C') {
        supplements = Object.keys(selectedSupplements).filter(supplement => selectedSupplements[supplement]);
      } else {
        supplements = supplementResults[rowIndex] || [];
      }
      
      const supplementCount = supplements.length;
      
      Object.entries(durations).forEach(([supplement, duration]) => {
        if (duration !== 'None' && duration !== 'No Duration' && updated[supplement] !== undefined) {
          // 60 sticks per month of duration (2/day). Parse the month count from
          // any format: "1 months", "3 months", "1 month", "3", 3 ...
          const months = parseInt(String(duration).match(/\d+/)?.[0] || '0', 10);
          const totalRequired = months * 60;

          // New-formula single-strain SKUs are taken concurrently, so EACH SKU
          // consumes the full amount (e.g. 180 for 3 months). Legacy blends
          // alternate months, so the fixed total is divided among the products.
          const isNewFormulaSku = supplement_9P_skus.includes(supplement);
          const required = isNewFormulaSku
            ? totalRequired
            : (supplementCount > 0 ? totalRequired / supplementCount : 0);

          updated[supplement] = Math.max(0, updated[supplement] - Math.ceil(required));
        }
      });
    });
    
    return updated;
  };

  // Function to get supplement image name based on toggle state and supplement name
  const getSupplementImageName = (supplementName) => {
    // Check if we're in 9P or QPT panel context (either main reportMode or pdfPreviewReportMode)
    const is9PPanel = reportMode === '9P' || pdfPreviewReportMode === '9P';
    const isQPTPanel = reportMode === 'QPT' || pdfPreviewReportMode === 'QPT';
    
    if (is9PPanel && csvPreviewToggle) {
      // B2B mode - use 9P-specific image names
      const imageMappings = {
        'BioAll': 'bioall9P',
        'BioZen': 'biozen9P', 
        'BioMeta': 'biometa9P'
      };
      return imageMappings[supplementName] || supplementName;
    }
    
    if (isQPTPanel && csvPreviewToggle) {
      // B2B mode for QU format - use B2B-specific image names
      const imageMappings = {
        'BioAll': 'bioall_b2b',
        'BioZen': 'biozen_b2b', 
        'BioMeta': 'biometa_b2b'
      };
      return imageMappings[supplementName] || supplementName;
    }
    
    // B2C mode or other panels - use regular image names
    return supplementName;
  };

  // Function to get supplement image based on stock availability
  const getSupplementImage = (supplement, duration) => {
    if (duration === 'None' || duration === 'No Duration') return '/non_pill.png';
    return hasSufficientStock(supplement, duration) ? '/pill.png' : '/pill.png';
  };

  // Function to check if all inventory icons have been applied
  const areAllInventoryApplied = () => {
    if (reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro') {
      return true;
    }
    if (!csvPreviewRows || csvPreviewRows.length === 0) return false;
    
    return csvPreviewRows.every((_, index) => {
      // Check if this row has applied durations (inventory icon shows done.png)
      if (!appliedDurations[index] || Object.keys(appliedDurations[index]).length === 0) {
        return false;
      }
      
      // If appliedDurations[index] exists and has keys, it means the user has confirmed
      // their selections (including "None" and "No Duration" which are valid choices)
      return true;
    });
  };

  // Function to check if all rows have plans selected
  const areAllPlansSelected = () => {
    if (!csvPreviewRows || csvPreviewRows.length === 0) return false;
    
    return csvPreviewRows.every((_, index) => {
      // Check if this row has a plan selected (including "No supp. Need" for healthy clients)
      return selectedPlans[index] && selectedPlans[index] !== '';
    });
  };

  // Function to check if manual input inventory is applied
  const isManualInventoryApplied = () => {
    return appliedDurations[0] && Object.keys(appliedDurations[0]).length > 0;
  };

  // Function to show custom tooltip
  const showCustomTooltip = (event, text) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setTooltipText(text);
    setShowTooltip(true);
  };

  // Function to hide custom tooltip
  const hideCustomTooltip = () => {
    setShowTooltip(false);
  };

  // Function to clean data by replacing missing values with 0
  const cleanReportData = (report) => {
    const cleanedReport = { ...report };
    
    // Replace null, undefined, empty string, or NaN values with 0
    Object.keys(cleanedReport).forEach(key => {
      const value = cleanedReport[key];
      if (value === null || value === undefined || value === '' || 
          (typeof value === 'string' && value.trim() === '') ||
          (typeof value === 'number' && isNaN(value))) {
        cleanedReport[key] = 0;
      }
    });
    
    return cleanedReport;
  };

  // Function to handle Record button click - save data directly to database
  // Push the recalculated stock to the DB and refresh it locally.
  //
  // This runs AFTER the reports have already been committed to st_report, so a
  // failure here must never bubble up: it would report a successful save as
  // failed and — in the retry flow — re-POST the reports and duplicate rows.
  // A common failure is a panel whose supplements have no matching columns in
  // the `stock` table yet, which the API answers with 400 "No valid fields to
  // update." Stock simply goes un-decremented in that case.
  const syncSupplementStock = async () => {
    if (Object.keys(updatedSupplementStock).length === 0) return;
    try {
      console.log('Updating supplement stock:', updatedSupplementStock);
      await axios.post(`${process.env.REACT_APP_API_BASE}/api/stock/update`, { stock: updatedSupplementStock });
      const stockRes = await axios.get(`${process.env.REACT_APP_API_BASE}/api/stock`);
      setStock(stockRes.data);
    } catch (err) {
      console.warn(
        'Supplement stock was not updated (reports were still saved):',
        err.response?.data?.error || err.response?.data?.message || err.message
      );
    }
  };

  const savePartnerCodes = async (rows, token) => {
    console.log('🔵 savePartnerCodes called, rows:', rows.length);
    console.log('🔵 First row keys:', rows[0] ? Object.keys(rows[0]) : 'no rows');
    console.log('🔵 First row Partner Name:', rows[0]?.['Partner Name']);
    console.log('🔵 First row Collection Kit Number:', rows[0]?.['Collection Kit Number']);

    const partnerEntries = rows
      .filter(r => r['Partner Name'] && String(r['Partner Name']).trim() !== '')
      .map(r => ({
        code: String(r['Collection Kit Number'] || '').trim(),
        partner_name: String(r['Partner Name']).trim()
      }))
      .filter(e => e.code !== '');

    console.log('🔵 partnerEntries to save:', partnerEntries);

    if (partnerEntries.length === 0) {
      console.warn('⚠️ savePartnerCodes: no valid entries found (Partner Name or Collection Kit Number missing)');
      return;
    }
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/reports/save-partner-codes`,
        { entries: partnerEntries },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('✅ Partner codes saved:', res.data);
    } catch (err) {
      console.error('❌ Failed to save partner codes:', err.response?.data || err.message);
    }
  };

  const handleRecordData = async () => {
    // Prevent multiple calls
    if (allConfirmLoading) return;
    
    setAllConfirmLoading(true);
    
    try {
      console.log('Recording data to database...');

      const isPetCsvSave =
        reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro';
      
      // Clean and prepare all reports for database storage
      const reportsToSave = await Promise.all(csvPreviewRows.map(async (report, reportIndex) => {
        // Pet biopet rows: keep string fields (e.g. health "normal"); other panels use numeric clean-up
        const cleanedReport = isPetCsvSave ? { ...report } : cleanReportData(report);
        
        // Get supplement data
        const manual = manualSupplements[reportIndex];
        const auto = supplementResults[reportIndex];
        const supplements = manual && manual.length > 0 ? manual : auto;
        
        // Use the Client ID that was generated in the preview
        const clientId = generatedClientIds[reportIndex] || '01-000-000-01';
        
        // Get duration data for this report
        const reportDurations = appliedDurations[reportIndex] || {};
        const firstSupplement = supplements && supplements.length > 0 ? supplements[0] : null;
        
        // Debug: Log all duration-related data
        console.log(`🔍 Report ${reportIndex + 1} - All appliedDurations:`, appliedDurations);
        console.log(`🔍 Report ${reportIndex + 1} - Report durations:`, reportDurations);
        console.log(`🔍 Report ${reportIndex + 1} - Supplements:`, supplements);
        console.log(`🔍 Report ${reportIndex + 1} - First supplement:`, firstSupplement);
        
        // Try to get duration value - check all supplements if first one doesn't have duration
        let durationValue = null;
        if (firstSupplement && reportDurations[firstSupplement]) {
          durationValue = reportDurations[firstSupplement];
        } else if (supplements && supplements.length > 0) {
          // Check all supplements to find one with duration
          for (const supplement of supplements) {
            if (reportDurations[supplement]) {
              durationValue = reportDurations[supplement];
              break;
            }
          }
        }
        
        // If still no duration found, check if there's a single duration value in the row
        if (!durationValue && Object.keys(reportDurations).length > 0) {
          const durationValues = Object.values(reportDurations);
          if (durationValues.length > 0) {
            durationValue = durationValues[0]; // Use the first duration value found
          }
        }
        
        // Clean up duration value - remove "months" suffix if present and convert to integer
        if (durationValue && typeof durationValue === 'string') {
          // Remove "months" suffix and convert to integer
          const cleanValue = durationValue.replace(' months', '').replace(' months', '');
          durationValue = cleanValue === 'None' ? null : parseInt(cleanValue);
        }
        
        const finalReport = {
          ...cleanedReport,
          'Client ID': clientId,
          'Supplement': supplements && supplements.length > 0 ? JSON.stringify(supplements) : null,
          'duration': durationValue, // Add duration data
          'B2C': csvPreviewToggle ? 0 : 1 // 1 = B2C, 0 = B2B
        };
        
        // Debug log to see what data is being sent
        console.log(`🔍 Report ${reportIndex + 1} - Final Duration Value:`, durationValue);
        console.log(`🔍 Report ${reportIndex + 1} - Final Report with Duration:`, finalReport);
        
        return finalReport;
      }));

      const token = localStorage.getItem('token');

      if (isPetCsvSave) {
        const saveResponse = await axios.post(
          `${process.env.REACT_APP_API_BASE}/api/reports/save-biopet-batch`,
          { reports: reportsToSave },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Biopet reports saved:', saveResponse.data);

        const nHealthy = saveResponse.data?.savedHealthy?.length ?? 0;
        const nRegular = saveResponse.data?.savedRegular?.length ?? 0;

        const confirmedState = {};
        csvPreviewRows.forEach((_, index) => {
          confirmedState[index] = true;
        });
        setConfirmedRows(confirmedState);
        setAllConfirmed(true);

        setSuccessMessage(
          `Recorded ${csvPreviewRows.length} pet row(s): ${nHealthy} → biopet_report_healthy, ${nRegular} → biopet_report (health = "normal" vs other).`
        );
        setShowSuccessDialog(true);
        setTimeout(() => {
          setShowSuccessDialog(false);
          setSuccessMessage('');
        }, 3000);
        await savePartnerCodes(csvPreviewRows, token);
        fetchSummary();
        return;
      }

      // Determine panel name based on report mode
      let panelName = 'QPT'; // default
      if (reportMode === '9P') {
        panelName = 'Q9';
      } else if (reportMode === 'Allergy and Skin') {
        panelName = 'QA';
      } else if (reportMode === 'Allergy and Skin_kids') {
        panelName = 'QI';
      } else if (reportMode === 'Toxin') {
        panelName = 'QG';
      } else if (reportMode === 'oms') {
        panelName = 'QM';
      } else if (reportMode === 'bwcallergy') {
        panelName = 'QBWCA';
      } else if (reportMode === 'bwccore') {
        panelName = 'QBWCE';
      } else if (reportMode === 'bwcdiamond') {
        panelName = 'QBWCU';
      }

      const saveResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/reports/save-to-st-report`,
        {
          reports: reportsToSave,
          panel: panelName
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('Reports saved to st_report table:', saveResponse.data);
      console.log('🔍 All reports data sent to backend:', JSON.stringify(reportsToSave, null, 2));

      // Update supplement stock (never fails the save - see syncSupplementStock)
      await syncSupplementStock();

      // Mark all rows as confirmed
      const confirmedState = {};
      csvPreviewRows.forEach((_, index) => {
        confirmedState[index] = true;
      });
      setConfirmedRows(confirmedState);
      setAllConfirmed(true);

      // Show success message
      setSuccessMessage(`Successfully recorded ${csvPreviewRows.length} reports to database with panel: ${panelName}`);
      setShowSuccessDialog(true);
      
      // Auto-close success popup after 3 seconds
      setTimeout(() => {
        setShowSuccessDialog(false);
        setSuccessMessage('');
      }, 3000);

      // Refresh the total report count
      await savePartnerCodes(csvPreviewRows, token);
      fetchSummary();

    } catch (err) {
      console.error('Error recording data:', err);
      // Some endpoints reply {error}, others {message} - show whichever exists.
      const serverMsg =
        err.response?.data?.message || err.response?.data?.error || err.message;
      alert(`Failed to record data to database!\nError: ${serverMsg}`);
    } finally {
      setAllConfirmLoading(false);
    }
  };

  // Function to calculate coverage for manually selected supplements
  const calculateCoverageForManualSupplements = (formData, reportMode, manualSupplements) => {
    let config, supplementMap;

    if (reportMode === '9P') {
      config = choosing_supplement_9P[0];
      supplementMap = supplement_bacteria_map_9P;
    } else if (reportMode === 'oms') {
      config = choosing_supplement_oms[0];
      supplementMap = supplement_bacteria_map_oms;
    } else if (reportMode === 'Allergy and Skin') {
      config = choosing_supplement_AMG[0];
      supplementMap = supplement_bacteria_map_AMG;
    } else if (reportMode === 'Allergy and Skin_kids') {
      config = qpt_choosing_supplement[0];
      supplementMap = qpt_supplement_bacteria_map;
    } else if (reportMode === 'Toxin') {
      config = choosing_supplement_toxin[0];
      supplementMap = supplement_bacteria_map_toxin;
    } else if ([
      'bwcallergy', 'bwccore', 'bwcdiamond', 'BWC-A', 'BWC-C', 'BWC-D'
    ].includes(reportMode)) {
      config = choosing_supplement_bwc[0];
      supplementMap = supplement_bacteria_map_bwc;
    } else {
      config = qpt_choosing_supplement[0];
      supplementMap = qpt_supplement_bacteria_map;
    }

    // Get deficient species
    const missingSpecies = config.info
      .filter(({ sp, less }) => {
        const value = parseFloat(formData[sp]) || 0;
        const calculatedLabel = calculateSpeciesLabel(sp, value, formData);
        const isDeficient = Array.isArray(less) ? less.includes(calculatedLabel) : false;
        return isDeficient;
      })
      .map(({ sp }) => sp);

    // If no deficient species, return full coverage
    if (missingSpecies.length === 0) {
      return { covered: 0, total: 0, percentage: 100, uncoveredSpecies: [] };
    }

    // Calculate coverage based on manually selected supplements
    const coveredSpecies = new Set();
    
    manualSupplements.forEach(supplement => {
      if (supplementMap[supplement]) {
        Object.keys(supplementMap[supplement]).forEach(species => {
          if (missingSpecies.includes(species)) {
            coveredSpecies.add(species);
          }
        });
      }
    });

    const uncoveredSpecies = missingSpecies.filter(species => !coveredSpecies.has(species));
    const coveragePercentage = Math.round((coveredSpecies.size / missingSpecies.length) * 100);

    console.log('=== MANUAL COVERAGE CALCULATION ===');
    console.log('Missing species:', missingSpecies);
    console.log('Manual supplements:', manualSupplements);
    console.log('Covered species:', Array.from(coveredSpecies));
    console.log('Uncovered species:', uncoveredSpecies);
    console.log('Coverage:', `${coveredSpecies.size}/${missingSpecies.length} species (${coveragePercentage}%)`);

    return {
      covered: coveredSpecies.size,
      total: missingSpecies.length,
      percentage: coveragePercentage,
      uncoveredSpecies: uncoveredSpecies
    };
  };

  // Function to save selected buttons to database
  const saveSelectedButtonsToDB = async () => {
    try {
      setFdaModifyLoading(true);
      
      const token = localStorage.getItem('token');
      
      // Convert selected buttons to array format for database
      const buttonsToSave = Array.from(selectedButtons).map(buttonName => {
        // Determine button group based on button name
        let buttonGroup = '';
        if (qptOptions.includes(buttonName)) {
          buttonGroup = 'qptOptions';
        } else if (otherButtonGroups[0].includes(buttonName)) {
          buttonGroup = 'bioGroup';
        } else if (otherButtonGroups[1].includes(buttonName)) {
          buttonGroup = '8x3Group';
        } else if (otherButtonGroups[2].includes(buttonName)) {
          buttonGroup = 'proGroup';
        }
        
        return {
          button_name: buttonName,
          button_group: buttonGroup,
          is_selected: true
        };
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/fda-selected-buttons`,
        { buttons: buttonsToSave },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        // console.log('Selected buttons saved successfully:', buttonsToSave);
        
        // Add 1.5 second delay for loading animation
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    } catch (error) {
      console.error('Error saving selected buttons:', error);
    } finally {
      setFdaModifyLoading(false);
    }
  };

  // Function to load saved FDA selections from database
  const loadSavedFDAButtons = async () => {
    try {
      // Use configured api instance which automatically adds Authorization header
      const response = await api.get('/api/fda-selected-buttons');

      if (response.status === 200 && response.data.selectedButtons) {
        const savedButtonNames = response.data.selectedButtons.map(btn => btn.button_name);
        setSelectedButtons(new Set(savedButtonNames));
        // console.log('Loaded saved FDA selections:', savedButtonNames);
      }
    } catch (error) {
      console.error('Error loading saved FDA buttons:', error);
      // Don't show alert for loading errors as it might be expected if no data exists
    }
  };

  // Function to get FDA-selected supplements for a specific row
  const getFdaExcludedSupplements = (rowIndex) => {
    if (!fdaCheckboxes[rowIndex]) {
      return []; // If FDA checkbox is not checked, don't exclude any supplements
    }
    
    // Convert selected buttons to supplement names
    const excludedSupplements = [];
    selectedButtons.forEach(buttonName => {
      // Map button names to supplement names
      if (buttonName === 'BIO-A01') excludedSupplements.push('BIO-A01');
      else if (buttonName === 'BIO-G02') excludedSupplements.push('BIO-G02');
      else if (buttonName === 'BIO-GA03') excludedSupplements.push('BIO-GA03');
      else if (buttonName === 'BIO-I04') excludedSupplements.push('BIO-I04');
      else if (buttonName === 'BIO-M05') excludedSupplements.push('BIO-M05');
      else if (buttonName === 'BIO-MS06') excludedSupplements.push('BIO-MS06');
      else if (buttonName === 'BIO-P07') excludedSupplements.push('BIO-P07');
      else if (buttonName === 'BioAll') excludedSupplements.push('BioAll');
      else if (buttonName === 'BioMeta') excludedSupplements.push('BioMeta');
      else if (buttonName === 'BioZen') excludedSupplements.push('BioZen');
      else if (buttonName === '8x3 SA') excludedSupplements.push('8x3 SA');
      else if (buttonName === '6x3 V') excludedSupplements.push('6x3 V');
      else if (buttonName === '5x3 C') excludedSupplements.push('5x3 C');
      else if (buttonName === 'Pro 4') excludedSupplements.push('Pro 4');
      else if (buttonName === 'Pro 6') excludedSupplements.push('Pro 6');
    });
    
    return excludedSupplements;
  };

  // Function to get FDA excluded supplements for manual selection (independent of row FDA checkbox)
  const getFdaExcludedSupplementsForManual = () => {
    // Convert selected buttons to supplement names (always return FDA excluded supplements)
    const excludedSupplements = [];
    selectedButtons.forEach(buttonName => {
      // Map button names to supplement names
      if (buttonName === 'BIO-A01') excludedSupplements.push('BIO-A01');
      else if (buttonName === 'BIO-G02') excludedSupplements.push('BIO-G02');
      else if (buttonName === 'BIO-GA03') excludedSupplements.push('BIO-GA03');
      else if (buttonName === 'BIO-I04') excludedSupplements.push('BIO-I04');
      else if (buttonName === 'BIO-M05') excludedSupplements.push('BIO-M05');
      else if (buttonName === 'BIO-MS06') excludedSupplements.push('BIO-MS06');
      else if (buttonName === 'BIO-P07') excludedSupplements.push('BIO-P07');
      else if (buttonName === 'BioAll') excludedSupplements.push('BioAll');
      else if (buttonName === 'BioMeta') excludedSupplements.push('BioMeta');
      else if (buttonName === 'BioZen') excludedSupplements.push('BioZen');
      else if (buttonName === '8x3 SA') excludedSupplements.push('8x3 SA');
      else if (buttonName === '6x3 V') excludedSupplements.push('6x3 V');
      else if (buttonName === '5x3 C') excludedSupplements.push('5x3 C');
      else if (buttonName === 'Pro 4') excludedSupplements.push('Pro 4');
      else if (buttonName === 'Pro 6') excludedSupplements.push('Pro 6');
    });
    
    console.log('=== FDA EXCLUDED SUPPLEMENTS FOR MANUAL SELECTION ===');
    console.log('selectedButtons:', Array.from(selectedButtons));
    console.log('excludedSupplements:', excludedSupplements);
    console.log('==================================================');
    
    return excludedSupplements;
  };

  const qptOptions = [
    'BIO-A01',
    'BIO-G02',
    'BIO-GA03',
    'BIO-I04',
    'BIO-M05',
    'BIO-MS06',
    'BIO-P07'
  ];
  const otherButtonGroups = [
    ['BioAll', 'BioMeta', 'BioZen'],
    ['8x3 SA', '6x3 V', '5x3 C'],
    ['Pro 4', 'Pro 6']
  ];
  const biopetStockOptions = ['PetGard', 'PetReno', 'PetFlow'];

  // Selectable supplement durations (months). "None" (0 months) is handled
  // separately in the UI. 60 sticks are consumed per month. `color` is used by
  // the coloured bulk-duration modal (the compact bulk row ignores it).
  const DURATION_COLORS = {
    1: { border: '#4CAF50', bg: '#f1f8e9', text: '#2E7D32', hoverBorder: '#45a049', hoverBg: '#e8f5e8' },
    3: { border: '#2196F3', bg: '#e3f2fd', text: '#1565c0', hoverBorder: '#1976d2', hoverBg: '#bbdefb' },
    4: { border: '#009688', bg: '#e0f2f1', text: '#00695c', hoverBorder: '#00796b', hoverBg: '#b2dfdb' },
    6: { border: '#FF9800', bg: '#fff3e0', text: '#e65100', hoverBorder: '#f57c00', hoverBg: '#ffe0b2' },
    7: { border: '#3F51B5', bg: '#e8eaf6', text: '#283593', hoverBorder: '#303f9f', hoverBg: '#c5cae9' },
    9: { border: '#E91E63', bg: '#fce4ec', text: '#ad1457', hoverBorder: '#c2185b', hoverBg: '#f8bbd0' },
    12: { border: '#9C27B0', bg: '#f3e5f5', text: '#6a1b9a', hoverBorder: '#7b1fa2', hoverBg: '#e1bee7' }
  };
  const DURATION_OPTIONS = [1, 3, 4, 6, 7, 9, 12].map((months) => ({
    key: `${months} months`,
    months,
    label: `${months} months (${months * 60})`,
    color: DURATION_COLORS[months]
  }));

  const [stock, setStock] = useState({});
  const [stockLevels, setStockLevels] = useState([]);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE}/api/stock`)
      .then(res => setStock(res.data))
      .catch(err => console.error(err));
  }, []);

  // Stock movement history for the SSM page.
  const [stockHistory, setStockHistory] = useState([]);
  const [stockHistoryLoading, setStockHistoryLoading] = useState(false);
  const [stockHistoryError, setStockHistoryError] = useState('');
  const [stockHistorySku, setStockHistorySku] = useState('');

  const loadStockHistory = useCallback(async () => {
    setStockHistoryLoading(true);
    setStockHistoryError('');
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE}/api/stock/history?limit=200`);
      setStockHistory(Array.isArray(res.data?.history) ? res.data.history : []);
    } catch (err) {
      setStockHistoryError(
        err.response?.data?.error || err.message || 'Could not load stock history'
      );
    } finally {
      setStockHistoryLoading(false);
    }
  }, []);

  // Reload on entering SSM so a movement made moments ago is already listed.
  useEffect(() => {
    if (viewingStorage) loadStockHistory();
  }, [viewingStorage, loadStockHistory]);

  useEffect(() => {
    // console.log('Stock data:', stock);
  }, [stock]);

  useEffect(() => {
    try {
      localStorage.setItem('biopet_no_lacking_mode', biopetFallbackMode);
    } catch {
      // ignore storage errors
    }
  }, [biopetFallbackMode]);

  useEffect(() => {
    try {
      localStorage.setItem(
        'biopet_stock_snapshot',
        JSON.stringify({
          PetGard: Number(stock?.PetGard || 0),
          PetReno: Number(stock?.PetReno || 0),
          PetFlow: Number(stock?.PetFlow || 0)
        })
      );
    } catch {
      // ignore storage errors
    }
  }, [stock]);

  // Load saved FDA selections when component mounts
  useEffect(() => {
    loadSavedFDAButtons();
  }, []);

  // Reset FDA Modify state when leaving SSM page
  useEffect(() => {
    if (!viewingStorage) {
      setFdaModifyClicked(false);
    }
  }, [viewingStorage]);

  // Check if user has permission to access storage page
  useEffect(() => {
    if (viewingStorage && user && !['admin', 'superadmin'].includes(user.role)) {
      setViewingStorage(false);
      alert('You do not have permission to access the Storage Supplement Management (SSM) page. Only Admin and Super Admin users can access this feature.');
    }
  }, [viewingStorage, user]);

  // Check if user has permission to access database page
  useEffect(() => {
    if (viewingDatabase && user && !['admin', 'superadmin'].includes(user.role)) {
      setViewingDatabase(false);
      alert('You do not have permission to access the Database page. Only Admin and Super Admin users can access this feature.');
    }
  }, [viewingDatabase, user]);

  const handleCsvImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    let csvText;
    try {
      csvText = await readCsvFileAsUtf8OrThaiWindows(file);
    } catch (e) {
      console.error('CSV read failed:', e);
      alert('Could not read CSV file.');
      event.target.value = null;
      return;
    }

    Papa.parse(csvText, {
      skipEmptyLines: true,
      complete: function(results) {
        const allRows = Array.isArray(results.data) ? results.data : [];
        if (allRows.length === 0) {
          alert('CSV file is empty.');
          return;
        }

        const isLikelyHeaderRow = (row) => {
          if (!Array.isArray(row)) return false;
          const normalized = row
            .map((cell) => normalizeColumnName((cell || '').toString().trim()))
            .filter(Boolean);
          return normalized.some((key) =>
            [
              'Full Name',
              'Report Number',
              'petname',
              'order_number',
              'species_animal',
              'collection_date',
              'report_date',
              'Age',
              'health'
            ].includes(key)
          );
        };

        // Support both formats:
        // - legacy files with extra top row (header at index 1)
        // - normal CSV files (header at index 0)
        const headerIndex = isLikelyHeaderRow(allRows[0]) ? 0 : 1;
        const headerRow = allRows[headerIndex] || [];
        const dataRows = allRows.slice(headerIndex + 1);

        /* Pet panels keep their own `report_date` column and the save route
           already defaults it server-side, so only the BioMed panels are
           stamped here — adding a 'Report Date' key to a pet row would also
           add a stray column to the preview table. */
        const isPetPanel = reportMode === 'pet-care-pro' || reportMode === 'pet-care-essential';
        const importReportDate = todayReportDate();

        const cleanedRows = dataRows
          .filter(row => Array.isArray(row) && row.some(cell => cell && cell.trim() !== '')) // ✅ skip empty lines
          .map(row => {
            const obj = {};
            headerRow.forEach((key, i) => {
              if (key && key.trim()) {
                // Normalize column names during parsing
                const normalizedKey = normalizeColumnName(key.trim());
                obj[normalizedKey] = row[i]?.toString().trim();
              }
            });

            // Pet CSV compatibility aliases so preview columns always show values.
            if (!obj['Full Name'] && obj.petname) obj['Full Name'] = obj.petname;
            if (!obj['Report Number'] && obj.order_number) obj['Report Number'] = obj.order_number;
            // normalizeColumnName() rewrites the CSV header `order_number` to
            // `Report Number`, so the raw key is gone by this point. The PDF
            // overlay reads formData.order_number (same key the manual form
            // uses), so restore it or the value renders blank in preview.
            if (!obj.order_number && obj['Report Number']) obj.order_number = obj['Report Number'];

            /* A blank or missing Report Date is stamped with today's date, so
               the PDF preview and the saved row carry a real value instead of
               the '-' the renderer falls back to. Every row in one import gets
               the same stamp. A date the CSV did supply is never overwritten;
               a stamped one can be corrected afterwards in the Raw Database
               cell editor. */
            if (!isPetPanel && !String(obj['Report Date'] ?? '').trim()) {
              obj['Report Date'] = importReportDate;
            }

            if (reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro') {
              const ageVal = obj.Age ?? obj.age;
              if (ageVal !== undefined && ageVal !== null && String(ageVal).trim() !== '') {
                obj.age = String(ageVal).trim();
              }
              const healthVal = obj.health ?? obj.Health;
              if (healthVal !== undefined && healthVal !== null && String(healthVal).trim() !== '') {
                obj.health = String(healthVal).trim();
              }
              delete obj['Client ID'];
            }

            return obj;
          });

        if (cleanedRows.length === 0) {
          alert('No data rows found in CSV. Please check your header and data format.');
          return;
        }
    
        // Reset all state to default when importing new CSV
        setCsvPreviewRows(cleanedRows);
        setPetHealthCsvColumnShowRaw(false);
        setCsvFileName(file.name);
        setFdaCheckboxes({});
        setSupplementDurations({});
        setAppliedDurations({});
        setInventoryRowIndex(null);
        setShowInventoryPopup(false);
        setGeneratedClientIds({});
        setClientIdType({});
        setIsManualInput(false); // Reset manual input state when importing CSV
        // Set default toggle based on current report mode
        if (reportMode === 'QPT' || reportMode === 'Allergy and Skin_kids') {
          setCsvPreviewToggle(true); // Default to B2B for QPT and QI (Infant Allergy) panel
        } else {
          setCsvPreviewToggle(false); // Default to B2C for other formats
        }

        // Every CSV import defaults to the New formula; the user can switch to
        // Original via the toggle. Sync the calc module now so the initial compute
        // below uses New (the state effect runs after this handler).
        setSupplement9PVersion('new');
        set9PSupplementVersion('new');

        // Reset plan selections and supplement states
        setSelectedPlans({});
        setPlanAResults({});
        setPlanBResults({});
        setSelectedSupplements({});
        setSupplementResults({});
        setCoverageResults({});
        setManualSupplements({});
        
        // Reset popup states
        setShowSupplementPopup(false);
        setShowManualSelection(false);
        setSupplementPopupRowIndex(null);
        setManualSelectionKey(0);
        
        // Reset FDA states
        setFdaRecalculatedRows({});
        setFdaCompliantOnly(false);
        
        // Reset loading states
        setLoadingConfirm({});
        setAllConfirmLoading(false);
        setAllConfirmed(false);
        setConfirmedRows({});
        
        // Reset hover states
        setHoveredSelectButton(null);
        setHoveredConfirmIndex(null);
        
        // Reset stock states
        setPopupStock({});
        setUpdatedSupplementStock({});
        setIgnoreStockLevels(false);
        
        event.target.value = null;

        // Process rows with progress tracking first, then show popup
        processCsvRowsWithProgress(cleanedRows);
      }
    });      
  };

  const processCsvRowsWithProgress = async (cleanedRows) => {
    setIsProcessingCsv(true);
    setCsvProgress({ current: 0, total: cleanedRows.length });
    setCsvProgressText(`Processing CSV data... (0/${cleanedRows.length})`);

    // Get all available supplements for this panel and log them
    const allPanelSupplements = getAllPanelSupplements(reportMode);
    console.log('=== CSV IMPORT - PANEL SUPPLEMENTS ===');
    console.log('Panel Type:', reportMode);
    console.log('All Available Supplements for this Panel:', allPanelSupplements);
    console.log('Total Supplements Available:', allPanelSupplements.length);
    console.log('=====================================');

    let petQuartileGridBySpeciesForCsv = quartileGridBySpecies;
    if (reportMode === 'pet-care-pro') {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${process.env.REACT_APP_API_BASE}/api/reports/quartile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        petQuartileGridBySpeciesForCsv = quartileApiResponseToGridBySpecies(res.data);
        setQuartileGridBySpecies(petQuartileGridBySpeciesForCsv);
      } catch (e) {
        console.warn('Quartile load failed during CSV process:', e);
      }
    }

    // Track all supplements used across all rows
    const allUsedSupplements = new Set();
    const supplementStockCounts = {};

    const configInfo =
      reportMode === '9P' ? choosing_supplement_9P[0].info :
      reportMode === 'oms' ? choosing_supplement_oms[0].info :
      reportMode === 'Allergy and Skin' ? choosing_supplement_AMG[0].info : reportMode === 'Allergy and Skin_kids' ? qpt_choosing_supplement[0].info :
      reportMode === 'Toxin' ? choosing_supplement_toxin[0].info :
      ['bwcallergy', 'bwccore', 'bwcdiamond', 'BWC-A', 'BWC-C', 'BWC-D'].includes(reportMode) ? choosing_supplement_bwc[0].info :
      qpt_choosing_supplement[0].info;

    const supplementResultsObj = {};
    const coverageResultsObj = {};
    const selectedPlansObj = {};
    const appliedDurationsObj = {};

    for (let idx = 0; idx < cleanedRows.length; idx++) {
      const row = cleanedRows[idx];
      
      // Update progress
      setCsvProgress({ current: idx + 1, total: cleanedRows.length });
      setCsvProgressText(`Processing row ${idx + 1}/${cleanedRows.length}...`);
      
      // Process the row
      const normalizedRow = normalizeRowKeys(row, configInfo);
      const excludedSupplements = getFdaExcludedSupplements(idx);
      const durationExclusions = appliedDurations[idx] || {};
      const result = getRecommendedSupplements(
        normalizedRow,
        reportMode,
        stock,
        excludedSupplements,
        stockThreshold,
        durationExclusions,
        true,
        reportMode === 'pet-care-pro' ? petQuartileGridBySpeciesForCsv : null
      );
      supplementResultsObj[idx] = result.supplements;
      coverageResultsObj[idx] = result.coverage;

      if (reportMode === 'pet-care-pro') {
        if (result.supplements.length === 0) {
          selectedPlansObj[idx] = 'No supp. Need';
          appliedDurationsObj[idx] = { 'No Duration': true };
        } else {
          selectedPlansObj[idx] = 'Plan A';
        }
      }

      // Handle healthy clients (no supplements needed)
      if (result.supplements.length === 0 && reportMode !== 'pet-care-pro') {
        console.log(`Row ${idx + 1}: Checking if client is truly healthy...`);
        console.log(`Row ${idx + 1}: Normalized data:`, normalizedRow);
        console.log(`Row ${idx + 1}: Coverage result:`, result.coverage);
        
        // Double-check by manually calculating deficient species
        const deficientSpecies = configInfo
          .filter(({ sp, less }) => {
            const value = parseFloat(normalizedRow[sp]) || 0;
            const calculatedLabel = calculateSpeciesLabel(sp, value, normalizedRow);
            const isDeficient = Array.isArray(less) ? less.includes(calculatedLabel) : false;
            console.log(`Row ${idx + 1}: Species ${sp} - Value: ${value}, Label: ${calculatedLabel}, Deficient: ${isDeficient}`);
            return isDeficient;
          })
          .map(({ sp }) => sp);
        
        console.log(`Row ${idx + 1}: Manually calculated deficient species:`, deficientSpecies);
        
        if (deficientSpecies.length === 0) {
          console.log(`Row ${idx + 1}: CONFIRMED - Healthy client detected - no supplements needed`);
          // Auto-select a plan for healthy clients
          selectedPlansObj[idx] = 'No supp. Need';
          // Set duration to "None" for healthy clients
          appliedDurationsObj[idx] = { 'No Duration': true };
        } else {
          console.log(`Row ${idx + 1}: ERROR - Client has deficient species but no supplements recommended!`);
          console.log(`Row ${idx + 1}: This might be a bug in supplement calculation logic`);
        }
      }
      
      // Track supplements used in this row
      result.supplements.forEach(supplement => {
        allUsedSupplements.add(supplement);
        if (!supplementStockCounts[supplement]) {
          supplementStockCounts[supplement] = stock[supplement] || 0;
        }
      });
      
      // Wait 0.5 seconds before processing next row (except for the last row)
      if (idx < cleanedRows.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // Set final results
    setSupplementResults(supplementResultsObj);
    setCoverageResults(coverageResultsObj);
    
    // Set plans and durations for healthy clients
    if (Object.keys(selectedPlansObj).length > 0) {
      setSelectedPlans(prev => ({ ...prev, ...selectedPlansObj }));
    }
    if (Object.keys(appliedDurationsObj).length > 0) {
      setAppliedDurations(prev => ({ ...prev, ...appliedDurationsObj }));
    }
    
    // Store all possible supplements for this panel with their stock counts
    const panelStockData = {};
    allPanelSupplements.forEach(supplement => {
      const stockCount = stock[supplement] || 0;
      panelStockData[supplement] = stockCount;
    });
    
    // Save to state for later use
    setPanelSupplementStock(panelStockData);
    
    // Log all possible supplements for this panel with their stock counts
    console.log('=== ALL POSSIBLE SUPPLEMENTS FOR THIS PANEL ===');
    allPanelSupplements.forEach(supplement => {
      const stockCount = stock[supplement] || 0;
      console.log(`${supplement}: ${stockCount}`);
    });
    console.log('=====================================');
    
    // Fetch client counts for all unique full names
    const uniqueFullNames = [...new Set(cleanedRows.map(row => row['Full Name']).filter(name => name))];
    const clientCountsObj = {};
    
    try {
      const token = localStorage.getItem('token');
      for (const fullName of uniqueFullNames) {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE}/api/reports/client-count/${encodeURIComponent(fullName)}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        clientCountsObj[fullName] = response.data.count || 0;
      }
      setClientCounts(clientCountsObj);
    } catch (err) {
      console.error('Error fetching client counts:', err);
      // Fallback: set all counts to 0
      uniqueFullNames.forEach(fullName => {
        clientCountsObj[fullName] = 0;
      });
      setClientCounts(clientCountsObj);
    }
    
    // Fetch the current monthly row order
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/reports/monthly-row-order`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setMonthlyRowOrder(response.data.nextOrder);
    } catch (err) {
      console.error('Error fetching monthly row order:', err);
      setMonthlyRowOrder(0);
    }
    
    // Clear progress
    setIsProcessingCsv(false);
    setCsvProgress({ current: 0, total: 0 });
    setCsvProgressText('');
    
    // Show CSV popup only after processing is complete
    setIsManualInput(false);
    setShowCsvPopup(true);
  };

  const formSections = formSectionsByFormat[reportMode] || [];
  const [versionOptions, setVersionOptions] = useState({
    eng: true,
    thai: false
  });  

  const fetchSummary = () => {
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_API_BASE}/api/reports/summary`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const next = normalizeReportSummaryResponse(res.data);
      setReportSummary(next);
      setLatestDate(next.latestCreatedAt ?? '-');
    }).catch(err => {
      console.error("Failed to fetch summary:", err);
    });
  };

  const generatePdfFromRow = async (rowData) => {
    return new Promise(async (resolve) => {
      setFormData(rowData);
      setIsPdfReady(true);
      setVersionOptions({ eng: true, thai: false });
      setShowStaticPdf(true);
  
      // Wait for positioning calculations to complete
      setTimeout(async () => {
        // Additional wait to ensure all DOM elements are properly positioned
        await new Promise(resolve => {
          const checkPositions = () => {
            const pages = document.querySelectorAll('.pdf-page-wrapper');
            if (pages.length > 0) {
              const overlayElements = document.querySelectorAll('.pdf-overlay-text, .pdf-overlay-image');
              if (overlayElements.length > 0) {
                resolve();
              } else {
                setTimeout(checkPositions, 100);
              }
            } else {
              setTimeout(checkPositions, 100);
            }
          };
          checkPositions();
        });
        
        const pages = document.querySelectorAll('.pdf-page-wrapper');
        if (!pages.length) {
          alert('PDF pages not found.');
          return;
        }
  
        const pdf = new jsPDF('p', 'mm', 'a4');
        let isFirstPage = true;
  
        for (let i = 0; i < pages.length; i++) {
          const pageEl = pages[i];
  
          // Scroll into view to force full render (if needed)
          pageEl.scrollIntoView({ behavior: 'instant' });
  
          // Wait for layout/graphics to render
          await new Promise(res => setTimeout(res, 100));
  
          const canvas = await html2canvas(pageEl, {
            scale: 1.9,
            useCORS: true,
            backgroundColor: resolveCaptureBackgroundColor()
          });
  
          const imgData = canvas.toDataURL('image/jpeg', 0.90);
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const imgHeight = (canvas.height * pdfWidth) / canvas.width;
  
          if (!isFirstPage) pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, imgHeight);
  
          isFirstPage = false;
        }
  
        const filename = rowData['Report Number'] || 'Report';
        pdf.save(`${filename}.pdf`);
        setShowStaticPdf(false);
        setIsPdfReady(false);
        resolve();
      }, 2000); // increased time for positioning calculations
    });
  };

const downloadAllAsZip = async () => {
  try {
    console.log('Starting downloadAllAsZip function');
    console.log('CSV preview rows count:', csvPreviewRows.length);
    
    // Validate required dependencies
    if (typeof JSZip === 'undefined') {
      throw new Error('JSZip library is not loaded');
    }
    if (typeof saveAs === 'undefined') {
      console.warn('saveAs function is not available, will use fallback');
    }
    
    // Check browser compatibility
    if (!window.URL || !window.URL.createObjectURL) {
      throw new Error('Your browser does not support file downloads. Please use a modern browser like Google Chrome.');
    }
    
    setPdfCancelRequested(false);
    setShowPdfProgress(true);
    setPdfProgress({ current: 0, total: csvPreviewRows.length * 2 }); // 2 versions per row
    setPdfProgressText('Preparing to generate PDFs...');
    setPdfProgressDetails('Initializing download process...');

    const zip = new JSZip();
    const folder = zip.folder('BiomedReports');



    const mergePDFs = async (engBlob, thaiBlob) => {
      try {
        const [engPdf, thaiPdf] = await Promise.all([
          PDFDocument.load(await engBlob.arrayBuffer()),
          PDFDocument.load(await thaiBlob.arrayBuffer())
        ]);

        const mergedPdf = await PDFDocument.create();

        const engPages = await mergedPdf.copyPages(engPdf, engPdf.getPageIndices());
        engPages.forEach(p => mergedPdf.addPage(p));

        const thaiPages = await mergedPdf.copyPages(thaiPdf, thaiPdf.getPageIndices());
        thaiPages.forEach(p => mergedPdf.addPage(p));

        return await mergedPdf.save();
      } catch (error) {
        console.error('Error merging PDFs:', error);
        throw new Error(`Failed to merge PDFs: ${error.message}`);
      }
    };

    for (let i = 0; i < csvPreviewRows.length; i++) {
      try {
        // Check for cancel request
        if (pdfCancelRequested) {
          setShowPdfProgress(false);
          setPdfProgress({ current: 0, total: 0 });
          setPdfProgressText('');
          setPdfProgressDetails('');
          return;
        }

        const row = csvPreviewRows[i];
        const reportNumber = row['Report Number'] || `report_${i + 1}`;

        // Update progress for English version
        setPdfProgress(prev => ({ ...prev, current: i * 2 }));
        setPdfProgressText(`Processing ${reportNumber} (${i + 1}/${csvPreviewRows.length})`);
        setPdfProgressDetails('Generating English version...');

        // Close any existing PDF modal first
        setShowStaticPdf(false);
        setIsPdfReady(false);
        await new Promise(r => setTimeout(r, 500)); // Wait for modal to close

        // Get supplements based on selected plan for this row
        const selectedPlan = selectedPlans[i];
        let supplements = [];
        
        if (selectedPlan === 'Plan A') {
          supplements = planAResults[i]?.supplements || [];
        } else if (selectedPlan === 'Plan B') {
          supplements = planBResults[i]?.supplements || [];
        } else if (selectedPlan === 'Plan C') {
          supplements = Object.keys(selectedSupplements).filter(supplement => selectedSupplements[supplement]);
        } else {
          // Fallback to default supplements if no plan is selected
          supplements = supplementResults[i] || [];
        }

        // Normalize the CSV row data for consistent format
        let configInfo;
        if (reportMode === '9P') configInfo = choosing_supplement_9P[0].info;
        else if (reportMode === 'oms') configInfo = choosing_supplement_oms[0].info;
        else if (reportMode === 'Allergy and Skin') configInfo = choosing_supplement_AMG[0].info;
        else if (reportMode === 'Allergy and Skin_kids') configInfo = qpt_choosing_supplement[0].info;
        else if (reportMode === 'Toxin') configInfo = choosing_supplement_toxin[0].info;
        else if (['bwcallergy', 'bwccore', 'bwcdiamond'].includes(reportMode)) configInfo = choosing_supplement_bwc[0].info;
        else configInfo = qpt_choosing_supplement[0].info;
        
        const normalizedRow = normalizeRowKeys(row, configInfo);

        // Add all bacteria from bacteriaMetaConfig with value 0 as default
        const allBacteriaDefaults = {};
        Object.keys(bacteriaMetaConfig).forEach(bacteriaName => {
          allBacteriaDefaults[bacteriaName] = 0;
        });
        
        // Merge defaults with normalized row data
        const formDataWithDefaults = {
          ...allBacteriaDefaults,
          ...normalizedRow
        };

        // Set up new row data for English version
        setFormData(formDataWithDefaults);
        setSupplementResults(prev => ({ ...prev, [i]: supplements }));
        setManualSupplements(prev => ({ ...prev, [i]: supplements }));
        setPreviewRowIndex(i);
        setVersionOptions({ eng: true, thai: false });
        setIsPdfReady(true);
        setShowStaticPdf(true);

        // Wait for positioning calculations to complete
        await new Promise(r => setTimeout(r, 2000));
        
        // Additional wait to ensure all DOM elements are properly positioned
        await new Promise((resolve, reject) => {
          let attempts = 0;
          const maxAttempts = 50; // 5 seconds max wait
          
          const checkPositions = () => {
            attempts++;
            const pages = document.querySelectorAll('.pdf-page-wrapper');
            if (pages.length > 0) {
              // Check if overlay elements are positioned
              const overlayElements = document.querySelectorAll('.pdf-overlay-text, .pdf-overlay-image');
              if (overlayElements.length > 0) {
                resolve();
              } else if (attempts >= maxAttempts) {
                reject(new Error('Timeout waiting for PDF elements to position'));
              } else {
                setTimeout(checkPositions, 100);
              }
            } else if (attempts >= maxAttempts) {
              reject(new Error('Timeout waiting for PDF pages to load'));
            } else {
              setTimeout(checkPositions, 100);
            }
          };
          checkPositions();
        });
        
        const engBlob = await generatePDFBlob('eng');

        // Update progress for Thai version
        setPdfProgress(prev => ({ ...prev, current: i * 2 + 1 }));
        setPdfProgressDetails('Generating Thai version...');

        // Close and reopen for Thai version
        setShowStaticPdf(false);
        setIsPdfReady(false);
        await new Promise(r => setTimeout(r, 500));

        setVersionOptions({ eng: false, thai: true });
        setIsPdfReady(true);
        setShowStaticPdf(true);
        await new Promise(r => setTimeout(r, 2000));
        
        // Wait for Thai version positioning calculations
        await new Promise((resolve, reject) => {
          let attempts = 0;
          const maxAttempts = 50; // 5 seconds max wait
          
          const checkPositions = () => {
            attempts++;
            const pages = document.querySelectorAll('.pdf-page-wrapper');
            if (pages.length > 0) {
              const overlayElements = document.querySelectorAll('.pdf-overlay-text, .pdf-overlay-image');
              if (overlayElements.length > 0) {
                resolve();
              } else if (attempts >= maxAttempts) {
                reject(new Error('Timeout waiting for Thai PDF elements to position'));
              } else {
                setTimeout(checkPositions, 100);
              }
            } else if (attempts >= maxAttempts) {
              reject(new Error('Timeout waiting for Thai PDF pages to load'));
            } else {
              setTimeout(checkPositions, 100);
            }
          };
          checkPositions();
        });
        
        const thaiBlob = await generatePDFBlob('thai');

        const mergedBytes = await mergePDFs(engBlob, thaiBlob);
        const fileName = `${row['Report Number'] || `report_${i + 1}`}.pdf`;

        folder.file(fileName, mergedBytes);
        
        // Add a small delay between processing rows to prevent memory issues
        if (i < csvPreviewRows.length - 1) {
          await new Promise(r => setTimeout(r, 200));
        }
        
      } catch (error) {
        console.error(`Error processing row ${i + 1}:`, error);
        // Continue with next row instead of failing completely
        setPdfProgressDetails(`Error processing row ${i + 1}: ${error.message}. Continuing...`);
        await new Promise(r => setTimeout(r, 1000));
      }
    }

    // Check for cancel request before finalizing
    if (pdfCancelRequested) {
      setShowPdfProgress(false);
      setPdfProgress({ current: 0, total: 0 });
      setPdfProgressText('');
      setPdfProgressDetails('');
      return;
    }

    // Update progress for final step
    setPdfProgress(prev => ({ ...prev, current: prev.total }));
    setPdfProgressText('Finalizing download...');
    setPdfProgressDetails('Creating ZIP file and preparing download...');

    // Close the PDF modal after processing all rows
    setShowStaticPdf(false);
    setIsPdfReady(false);

    // Generate ZIP with error handling
    try {
      console.log('Generating ZIP file...');
      const content = await zip.generateAsync({ type: 'blob' });
      console.log('ZIP file generated successfully, size:', content.size);
      
      // Test if saveAs is available and working
      if (typeof saveAs === 'function') {
        try {
          console.log('Using saveAs function to download ZIP');
          saveAs(content, 'AllReports.zip');
          console.log('Download initiated successfully with saveAs');
        } catch (saveError) {
          console.warn('saveAs failed, using fallback:', saveError);
          // Fallback for browsers that don't support saveAs properly
          const url = URL.createObjectURL(content);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'AllReports.zip';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 1000);
          console.log('Download initiated successfully with fallback method');
        }
      } else {
        // Fallback for browsers that don't support saveAs
        console.log('Using fallback method to download ZIP');
        const url = URL.createObjectURL(content);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'AllReports.zip';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        console.log('Download initiated successfully with fallback method');
      }
      
      // Cleanup
      try {
        await axios.post(`${process.env.REACT_APP_API_BASE}/api/pdf/cleanup`);
      } catch (cleanupError) {
        console.warn('Cleanup failed:', cleanupError);
      }
      
      setShowPdfProgress(false);
      setPdfProgress({ current: 0, total: 0 });
      setPdfProgressText('');
      setPdfProgressDetails('');
      setShowCsvPopup(true);
      
      // Show success message
      alert('Download completed successfully! The ZIP file should start downloading automatically.');
      
    } catch (error) {
      console.error('Error generating ZIP file:', error);
      throw new Error(`Failed to create ZIP file: ${error.message}`);
    }
    
  } catch (error) {
    console.error('Download All As ZIP Error:', error);
    
    // Cleanup on error
    setShowPdfProgress(false);
    setPdfProgress({ current: 0, total: 0 });
    setPdfProgressText('');
    setPdfProgressDetails('');
    setShowStaticPdf(false);
    setIsPdfReady(false);
    setShowCsvPopup(true);
    
    // Show user-friendly error message
    alert(`Error generating reports: ${error.message}. Please try again or contact support if the issue persists.\n\nNote: Make sure your browser allows downloads and that you're using Google Chrome for best compatibility.`);
  }
};

  useEffect(() => {
    const main = document.querySelector('.main_content');
    if (main) {
      main.scrollTop = 0;
      main.classList.remove('fade-in');
      if (main) {
        void main.offsetWidth;
      }
      main.classList.add('fade-in');
    }
  }, [creatingReport]);

  // Cleanup effect for success popup timeout
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      // Reset global flag on unmount
      globalSuccessFlag.current = false;
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      // No token - ProtectedRoute should handle this
      console.warn('⚠️ No token found in useEffect');
      return;
    }

    // Use configured api instance which automatically adds Authorization header
    // Don't redirect on /api/me failure - it's just for user info
    api.get('/api/me')
      .then(res => {
        if (res.data && res.data.user) {
          setUser(res.data.user);
        }
      })
      .catch(err => {
        console.error('Error fetching user info:', err);
        // Don't redirect - just log the error
        // Token might be invalid, but let interceptor handle redirect
      });

    api.get('/api/reports/summary')
      .then(res => {
        if (res.data) {
          const next = normalizeReportSummaryResponse(res.data);
          setReportSummary(next);
          setLatestDate(next.latestCreatedAt ?? '-');
        }
      })
      .catch(err => {
        console.error('Error fetching report summary:', err);
        // Don't redirect on summary failure
      });

    api.get('/api/users')
      .then(res => {
        if (res.data && res.data.users) {
          setMembers(res.data.users);
        }
      })
      .catch(err => console.error('Error fetching members:', err));
  }, []);

  // Keep the team panel's online/offline dots live: re-poll /api/users every minute while
  // the tab is visible. One small query; the server decides "online" from session activity.
  useEffect(() => {
    const refresh = () => {
      if (document.hidden) return;
      api.get('/api/users')
        .then(res => { if (res.data && res.data.users) setMembers(res.data.users); })
        .catch(() => {});
    };
    const id = setInterval(refresh, 60000);
    document.addEventListener('visibilitychange', refresh);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', refresh); };
  }, []);

  useEffect(() => {
    if (formData['Full Name'] && !formData['Report Number']) {
      const token = localStorage.getItem('token');
      axios.post(`${process.env.REACT_APP_API_BASE}/api/reports/generate-report-number`, {
        full_name: formData['Full Name'],
        report_type: reportMode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        setFormData(prev => ({
          ...prev,
          'Report Number': res.data.reportNumber
        }));
      })
      .catch(err => {
        console.error('Failed to generate report number:', err);
      });
    }
  }, [formData['Full Name'], reportType]);

  useEffect(() => {
    if (!showHistory && !viewingHistory) return;
    const token = localStorage.getItem('token');
    if (!token) {
      setIsFiltering(false);
      return;
    }

    const reqId = ++historyFetchReqIdRef.current;
    const ac = new AbortController();

    setIsFiltering(true);
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/reports/history`, {
        signal: ac.signal,
        headers: { Authorization: `Bearer ${token}` },
        params: {
          ...historySourceParams,
          page: 1,
          limit: pagination.limit,
          filterColumn: selectedFilterColumn,
          filterValue: debouncedFilter,
          groupByFullName: true,
          clientGroupPagination: true,
          // Explicit strings so the API always applies SQL single vs multi detection
          reportCountFilter:
            historyReportCountTab === 'multiple' ? 'multiple' : 'single',
          // Bust any intermediary/browser cache so each load gets fresh rows for grouping
          _hb: Date.now()
        }
      })
      .then(res => {
        if (reqId !== historyFetchReqIdRef.current) return;
        const p = res.data.pagination;
        const raw = res.data.reports;
        const reports = Array.isArray(raw) ? raw.slice() : [];
        const nb = res.data.newBadgeCounts;
        setHistoryNewBadgeCounts(
          nb && typeof nb === 'object'
            ? {
                single: Math.max(0, Number(nb.single) || 0),
                multiple: Math.max(0, Number(nb.multiple) || 0)
              }
            : { single: 0, multiple: 0 }
        );
        setHistoryClientPaging(!!p?.clientGroupPagination);
        setReportHistory(reports);
        setPagination(prev => {
          if (!p) return prev;
          if (p.clientGroupPagination) {
            const tp = Math.max(1, Number(p.totalPages) || 1);
            return {
              ...p,
              currentPage: 1,
              totalPages: tp,
              hasNextPage: tp > 1,
              hasPrevPage: false,
              limit: p.limit ?? prev.limit
            };
          }
          const tp = Math.max(1, Number(p.totalPages) || 1);
          const page = Math.min(Math.max(1, Number(p.currentPage) || 1), tp);
          return {
            ...p,
            currentPage: page,
            hasNextPage: page < tp,
            hasPrevPage: page > 1,
            limit: p.limit ?? prev.limit
          };
        });
        setIsFiltering(false);
      })
      .catch(err => {
        if (reqId !== historyFetchReqIdRef.current) return;
        const aborted =
          err.code === 'ERR_CANCELED' ||
          err.name === 'CanceledError' ||
          err.name === 'AbortError' ||
          (typeof axios.isCancel === 'function' && axios.isCancel(err));
        if (aborted) return;
        const d = err.response?.data;
        console.error(
          'Failed to fetch report history:',
          d?.message || d?.code || err.message,
          d || err.response?.status
        );
        setIsFiltering(false);
      });

    return () => {
      ac.abort();
    };
  }, [
    showHistory,
    viewingHistory,
    pagination.limit,
    debouncedFilter,
    selectedFilterColumn,
    historyReportCountTab,
    historyEnterNonce,
    historySourceParams
  ]);
  
  // Load FDA-selected buttons when component mounts
  useEffect(() => {
    loadSavedFDAButtons();
  }, []);
  
  const handleHistoryFilterInputChange = (value) => {
    setFilterValue(value);
  };

  /** Apply filter text + column to the history API (no request until this runs or column changes). */
  const applyHistoryFilterSearch = () => {
    setDebouncedFilter(filterValue.trim());
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    setSelectedReports(new Set());
    setSelectAllReports(false);
    setExpandedGroups(new Set());
  };

  const clearHistoryFilter = () => {
    setFilterValue('');
    setDebouncedFilter('');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    setSelectedReports(new Set());
    setSelectAllReports(false);
    setExpandedGroups(new Set());
  };

  const handleFilterColumnChange = (column) => {
    setSelectedFilterColumn(column);
    setFilterValue(''); // Clear filter value when changing column
    setDebouncedFilter('');
    setShowFilterDropdown(false);
    // Reset to first page when changing filter column
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    // Reset selected reports when filter column changes
    setSelectedReports(new Set());
    setSelectAllReports(false);
    setExpandedGroups(new Set());
  };

  // State for panel selection modal
  const [showPanelSelectionModal, setShowPanelSelectionModal] = useState(false);
  const [availablePanels, setAvailablePanels] = useState([]);
  
  // State for period selection modal
  const [showPeriodSelectionModal, setShowPeriodSelectionModal] = useState(false);
  const [selectedPanelForExport, setSelectedPanelForExport] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [availablePeriods, setAvailablePeriods] = useState([]);
  
  // State for export success popup
  const [showExportSuccessPopup, setShowExportSuccessPopup] = useState(false);
  const [exportSuccessMessage, setExportSuccessMessage] = useState('');
  
  // Set current year and month as defaults
  useEffect(() => {
    const now = new Date();
    setSelectedYear(now.getFullYear().toString());
    setSelectedMonths([(now.getMonth() + 1).toString().padStart(2, '0')]);
  }, []);

  // Function to show panel selection modal
  const showPanelSelectionForExport = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get all reports to extract available panels
      const params = new URLSearchParams();
      if (debouncedFilter) {
        params.append('filterColumn', selectedFilterColumn);
        params.append('filterValue', debouncedFilter);
      }
      params.append('limit', 10000);
      params.append('page', 1);
      appendHistoryCategoryToSearchParams(params);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/reports/history?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const reports = response.data.reports || [];
      
      if (reports.length === 0) {
        alert('No reports found to export.');
        return;
      }
      
      // Extract unique panels from reports
      const panels = [...new Set(reports.map(report => report['Panel']).filter(Boolean))];
      setAvailablePanels(panels);
      setShowPanelSelectionModal(true);
      
    } catch (error) {
      console.error('Error fetching reports for panel selection:', error);
      alert('Failed to load reports for panel selection. Please try again.');
    }
  };

  // Function to handle panel selection and show period selection
  const handlePanelSelection = async (selectedPanel) => {
    try {
      const token = localStorage.getItem('token');
      
      // Get all reports for this panel to extract available periods
      const params = new URLSearchParams();
      if (debouncedFilter) {
        params.append('filterColumn', selectedFilterColumn);
        params.append('filterValue', debouncedFilter);
      }
      params.append('filterColumn', 'Panel');
      params.append('filterValue', selectedPanel);
      params.append('limit', 10000);
      params.append('page', 1);
      appendHistoryCategoryToSearchParams(params);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/reports/history?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const reports = response.data.reports || [];
      
      if (reports.length === 0) {
        alert(`No reports found for panel "${selectedPanel}".`);
        return;
      }
      
      // Extract unique periods from Report Date column
      const periods = new Set();
      reports.forEach(report => {
        const reportDate = report['Report Date'] || report['create_time'] || '';
        
        if (reportDate) {
          try {
            // Parse the date (handle different formats)
            let date;
            if (typeof reportDate === 'string') {
              // Try different date formats
              if (reportDate.includes('/')) {
                // Format: DD/MM/YYYY or MM/DD/YYYY
                const parts = reportDate.split('/');
                if (parts.length === 3) {
                  // Assume DD/MM/YYYY format
                  date = new Date(parts[2], parts[1] - 1, parts[0]);
                }
              } else if (reportDate.includes('-')) {
                // Format: YYYY-MM-DD
                date = new Date(reportDate);
              } else {
                // Try direct parsing
                date = new Date(reportDate);
              }
            } else {
              // If it's already a Date object
              date = new Date(reportDate);
            }
            
            // Check if date is valid
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = (date.getMonth() + 1).toString().padStart(2, '0');
              periods.add(`${year}-${month}`);
            }
          } catch (error) {
            console.warn('Could not parse date:', reportDate, error);
          }
        }
      });
      
      const sortedPeriods = Array.from(periods).sort().reverse(); // Most recent first
      setAvailablePeriods(sortedPeriods);
      setSelectedPanelForExport(selectedPanel);
      setShowPanelSelectionModal(false);
      setShowPeriodSelectionModal(true);
      
    } catch (error) {
      console.error('Error fetching periods:', error);
      alert('Failed to load periods for panel selection. Please try again.');
    }
  };

  // Function to export filtered reports to CSV for specific panel and period
  const exportFilteredReportsToCSV = async (selectedPanel, selectedPeriod = null) => {
    try {
      const token = localStorage.getItem('token');
      
      // Build query parameters for filtered export with panel filter
      const params = new URLSearchParams();
      if (debouncedFilter) {
        params.append('filterColumn', selectedFilterColumn);
        params.append('filterValue', debouncedFilter);
      }
      // Add panel filter
      params.append('filterColumn', 'Panel');
      params.append('filterValue', selectedPanel);
      
      // Add period filter if specified
      if (selectedPeriod) {
        // Filter by Report Date for the selected period
        // We'll need to handle this in the frontend since we need to parse dates
        // For now, we'll get all data and filter in frontend
      }
      
      params.append('limit', 10000); // Get all records
      params.append('page', 1);
      appendHistoryCategoryToSearchParams(params);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/reports/history?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      let reports = response.data.reports || [];
      
      // Filter by period if specified
      if (selectedPeriod) {
        reports = reports.filter(report => {
          const reportDate = report['Report Date'] || report['create_time'] || '';
          
          if (reportDate) {
            try {
              let date;
              if (typeof reportDate === 'string') {
                if (reportDate.includes('/')) {
                  const parts = reportDate.split('/');
                  if (parts.length === 3) {
                    date = new Date(parts[2], parts[1] - 1, parts[0]);
                  }
                } else if (reportDate.includes('-')) {
                  date = new Date(reportDate);
                } else {
                  date = new Date(reportDate);
                }
              } else {
                date = new Date(reportDate);
              }
              
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const reportPeriod = `${year}-${month}`;
                return reportPeriod === selectedPeriod;
              }
            } catch (error) {
              console.warn('Could not parse date for filtering:', reportDate, error);
            }
          }
          return false;
        });
      }
      
      if (reports.length === 0) {
        const periodText = selectedPeriod ? ` for period "${selectedPeriod}"` : '';
        alert(`No reports found for panel "${selectedPanel}"${periodText}.`);
        return;
      }
      
      // Convert database data to CSV format (using SQL-like approach for complete data)
      const convertToCSV = (reports) => {
        // Get all column names from all reports
        const allColumns = new Set();
        reports.forEach(report => {
          Object.keys(report).forEach(key => {
            allColumns.add(key);
          });
        });
        
        // Define the exact column order you want
        const desiredColumnOrder = [
          // Personal Information
          'Full Name',
          'Gender', 
          'Date of Birth',
          'Collection Date',
          'Report Date',
          'Collection Kit Number',
          'B2C',
          'Client ID',
          // Probiotics
          'Bifidobacterium bifidum',
          'Bifidobacterium breve',
          'Bifidobacterium lactis',
          'Bifidobacterium longum',
          'Lactobacillus acidophilus',
          'Lactobacillus gasseri',
          'Lactobacillus paracasei',
          'Lactobacillus reuteri',
          'Lactobacillus rhamnosus',
          // Universal
          'Universal bacteria'
        ];
        
        // Sort columns according to desired order, with any missing columns at the end
        const csvHeaders = desiredColumnOrder.filter(col => allColumns.has(col));
        const remainingColumns = Array.from(allColumns).filter(col => !desiredColumnOrder.includes(col)).sort();
        csvHeaders.push(...remainingColumns);
        
        // Debug: Log the first report to see what data we're getting
        console.log('🔍 First report data:', reports[0]);
        console.log('🔍 All columns found:', csvHeaders);
        
        const csvData = reports.map(report => {
          // Create a row with all columns, filling missing values with empty strings
          return csvHeaders.map(header => {
            let value = report[header];
            
            // Debug: Log specific bacteria values
            if (header.includes('Bacillus') || header.includes('Candida') || header.includes('Clostridium')) {
              console.log(`🔍 ${header}:`, value, typeof value);
            }
            
            // Handle special cases
            if (header === 'create_time' && value) {
              // Format the date consistently
              value = new Date(value).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                timeZone: 'Asia/Bangkok'
              });
            }
            
            // Convert null/undefined to empty string, but keep 0 values
            if (value === null || value === undefined) {
              value = '';
            }
            
            // Convert to string and escape quotes
            return String(value).replace(/"/g, '""');
          });
        });
        
        // Create simplified category header row
        const categoryHeaders = [
          'Personal Information', '', '', '', '', '', '', '',
          'Probiotics', '', '', '', '', '', '', '', '',
          'Universal'
        ];
        
        // Add empty strings for any remaining columns
        const remainingColumnsCount = csvHeaders.length - categoryHeaders.length;
        for (let i = 0; i < remainingColumnsCount; i++) {
          categoryHeaders.push('');
        }
        
        // Create CSV content with category header
        return [
          categoryHeaders.join(','),
          csvHeaders.join(','),
          ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
      };
      
      // Generate CSV content from complete database data
      const csvContent = convertToCSV(reports);
      
      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with panel, period, and filter info
      let filename = `reports_export_${selectedPanel.replace(/\s+/g, '_')}`;
      if (selectedPeriod) {
        filename += `_${selectedPeriod.replace(/[^a-zA-Z0-9]/g, '_')}`;
      }
      if (debouncedFilter) {
        filename += `_${selectedFilterColumn.replace(/\s+/g, '_')}_${debouncedFilter.replace(/[^a-zA-Z0-9]/g, '_')}`;
      }
      filename += `_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      const periodText = selectedPeriod ? ` for period "${selectedPeriod}"` : '';
      setExportSuccessMessage(`Successfully exported ${reports.length} reports for panel "${selectedPanel}"${periodText} to CSV!`);
      setShowExportSuccessPopup(true);
      setShowPeriodSelectionModal(false);
      
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Failed to export reports to CSV. Please try again.');
    }
  };

  // Function to export multiple months with feedback for missing data
  const exportMultipleMonthsToCSV = async (selectedPanel, selectedYear, selectedMonths) => {
    try {
      const token = localStorage.getItem('token');
      
      // Build query parameters for filtered export with panel filter
      const params = new URLSearchParams();
      if (debouncedFilter) {
        params.append('filterColumn', selectedFilterColumn);
        params.append('filterValue', debouncedFilter);
      }
      // Add panel filter
      params.append('filterColumn', 'Panel');
      params.append('filterValue', selectedPanel);
      
      params.append('limit', 10000); // Get all records
      params.append('page', 1);
      appendHistoryCategoryToSearchParams(params);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/reports/history?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      let allReports = response.data.reports || [];
      const monthNames = {
        '01': 'January', '02': 'February', '03': 'March', '04': 'April',
        '05': 'May', '06': 'June', '07': 'July', '08': 'August',
        '09': 'September', '10': 'October', '11': 'November', '12': 'December'
      };
      
      const results = [];
      const monthsWithData = [];
      const monthsWithoutData = [];
      
      // Process each selected month
      for (const month of selectedMonths) {
        const period = `${selectedYear}-${month}`;
        const monthReports = allReports.filter(report => {
          const reportDate = report['Report Date'] || report['create_time'] || '';
          
          if (reportDate) {
            try {
              let date;
              if (typeof reportDate === 'string') {
                if (reportDate.includes('/')) {
                  const parts = reportDate.split('/');
                  if (parts.length === 3) {
                    date = new Date(parts[2], parts[1] - 1, parts[0]);
                  }
                } else if (reportDate.includes('-')) {
                  date = new Date(reportDate);
                } else {
                  date = new Date(reportDate);
                }
              } else {
                date = new Date(reportDate);
              }
              
              if (!isNaN(date.getTime())) {
                const year = date.getFullYear();
                const monthNum = (date.getMonth() + 1).toString().padStart(2, '0');
                const reportPeriod = `${year}-${monthNum}`;
                return reportPeriod === period;
              }
            } catch (error) {
              console.warn('Could not parse date for filtering:', reportDate, error);
            }
          }
          return false;
        });
        
        if (monthReports.length > 0) {
          monthsWithData.push(monthNames[month]);
          results.push(...monthReports);
        } else {
          monthsWithoutData.push(monthNames[month]);
        }
      }
      
      if (results.length === 0) {
        alert(`No data found for any of the selected months (${selectedMonths.map(m => monthNames[m]).join(', ')}) in ${selectedYear}.`);
        return;
      }
      
      // Convert to CSV and download
      const convertToCSV = (reports) => {
        if (reports.length === 0) return '';
        
        // Get all unique column names from all reports
        const allColumns = new Set();
        reports.forEach(report => {
          Object.keys(report).forEach(key => allColumns.add(key));
        });
        
        // Define desired column order (personal info first, then probiotics, then universal)
        const personalInfoColumns = ['Full Name', 'Report Number', 'Report Date', 'Panel', 'Age', 'Gender', 'Weight', 'Height'];
        const probioticsColumns = ['Bifidobacterium', 'Lactobacillus', 'Akkermansia', 'Faecalibacterium', 'Roseburia', 'Eubacterium'];
        const universalColumns = ['create_time', 'update_time', 'id'];
        
        // Create ordered columns array
        const orderedColumns = [
          ...personalInfoColumns.filter(col => allColumns.has(col)),
          ...probioticsColumns.filter(col => allColumns.has(col)),
          ...universalColumns.filter(col => allColumns.has(col))
        ];
        
        // Add remaining columns alphabetically
        const remainingColumns = Array.from(allColumns)
          .filter(col => !orderedColumns.includes(col))
          .sort();
        
        const finalColumns = [...orderedColumns, ...remainingColumns];
        
        // Create category header row
        const categoryHeader = 'Personal Information,,,,,,,,,Probiotics,,,,,,,,,Universal,,,,,,,,,,,,,,,,,,,,,,,,,';
        
        // Create CSV header with category grouping
        const headerRow = finalColumns.join(',');
        
        // Create data rows
        const dataRows = reports.map(report => {
          return finalColumns.map(column => {
            const value = report[column];
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            // Escape commas and quotes in CSV
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(',');
        });
        
        return [categoryHeader, headerRow, ...dataRows].join('\n');
      };
      
      const csvContent = convertToCSV(results);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${selectedPanel}_${selectedYear}_${selectedMonths.join('_')}_reports.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show success message with details about which months had data
      let message = `Successfully exported ${results.length} reports for panel "${selectedPanel}" in ${selectedYear}!`;
      
      if (monthsWithData.length > 0) {
        message += `\n\nMonths with data: ${monthsWithData.join(', ')}`;
      }
      
      if (monthsWithoutData.length > 0) {
        message += `\n\nMonths without data: ${monthsWithoutData.join(', ')}`;
      }
      
      setExportSuccessMessage(message);
      setShowExportSuccessPopup(true);
      setShowPeriodSelectionModal(false);
      
    } catch (error) {
      console.error('Error exporting multiple months to CSV:', error);
      alert('Failed to export reports to CSV. Please try again.');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown && !event.target.closest('.filter-dropdown-container')) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${process.env.REACT_APP_API_BASE}/api/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
  };

  const handleRemoveMember = (username) => {
    // Only Super Admin can remove users
    if (user?.role !== 'superadmin') {
      alert('Access Denied: Only Super Admin users can remove users.');
      return;
    }
    
    axios.delete(`${process.env.REACT_APP_API_BASE}/api/users/${username}`)
      .then(() => {
        setMembers(prev => prev.filter(m => m.username !== username));
      })
      .catch(err => alert("Failed to remove user"));
  };

  const handleRoleChange = (username, newRole) => {
    // Only Super Admin can change user roles
    if (user?.role !== 'superadmin') {
      alert('Access Denied: Only Super Admin users can change user roles.');
      return;
    }
    
    axios.put(`${process.env.REACT_APP_API_BASE}/api/users/${username}`, { role: newRole })
      .then(() => {
        setMembers(prev => prev.map(m => m.username === username ? { ...m, role: newRole } : m));
      })
      .catch(err => alert("Failed to update role"));
  };

  const handleAddUser = () => {
    // Only Super Admin can add users
    if (user?.role !== 'superadmin') {
      alert('Access Denied: Only Super Admin users can add new users.');
      return;
    }
    
    if (!newUser.username || !newUser.password || !newUser.role) {
      alert('Please fill in all fields: username, password, and role.');
      return;
    }

    axios.post(`${process.env.REACT_APP_API_BASE}/api/users`, newUser)
      .then(() => {
        setShowAddModal(false);
        setNewUser({ username: '', password: '', role: '' });
        const token = localStorage.getItem('token');
        axios.get(`${process.env.REACT_APP_API_BASE}/api/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => setMembers(res.data.users));
      })
      .catch(err => {
        console.error('Error adding user:', err);
      });
  };

  const handleDeleteReport = (id) => {
    setDeleteReportId(id);
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteReport = () => {
    if (!deleteReportId) return;
    
    // Additional security check - only Super Admin can delete reports
    if (user?.role !== 'superadmin') {
      alert('Access Denied: Only Super Admin users can delete reports.');
      setShowDeleteConfirmDialog(false);
      setDeleteReportId(null);
      return;
    }
    
    const token = localStorage.getItem('token');
    axios.delete(`${process.env.REACT_APP_API_BASE}/api/reports/delete/by-id/${deleteReportId}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: historySourceParams
    })
      .then(() => {
        // Refresh the current page after deletion
        const token = localStorage.getItem('token');
        axios.get(`${process.env.REACT_APP_API_BASE}/api/reports/history`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            ...historySourceParams,
            page: 1,
            limit: pagination.limit,
            filterColumn: selectedFilterColumn,
            filterValue: debouncedFilter,
            groupByFullName: true,
            clientGroupPagination: true,
            reportCountFilter:
              historyReportCountTab === 'multiple' ? 'multiple' : 'single'
          }
        })
        .then(res => {
          const p = res.data.pagination;
          const nb = res.data.newBadgeCounts;
          setHistoryNewBadgeCounts(
            nb && typeof nb === 'object'
              ? {
                  single: Math.max(0, Number(nb.single) || 0),
                  multiple: Math.max(0, Number(nb.multiple) || 0)
                }
              : { single: 0, multiple: 0 }
          );
          setHistoryClientPaging(!!p?.clientGroupPagination);
          setReportHistory(Array.isArray(res.data.reports) ? res.data.reports.slice() : []);
          setPagination(prev => {
            if (!p) return prev;
            if (p.clientGroupPagination) {
              const tp = Math.max(1, Number(p.totalPages) || 1);
              return {
                ...p,
                currentPage: 1,
                totalPages: tp,
                hasNextPage: tp > 1,
                hasPrevPage: false,
                limit: p.limit ?? prev.limit
              };
            }
            const tp = Math.max(1, Number(p.totalPages) || 1);
            const page = Math.min(Math.max(1, Number(p.currentPage) || 1), tp);
            return { ...p, currentPage: page, limit: p.limit ?? prev.limit };
          });
        })
        .catch(err => console.error("Failed to refresh reports:", err));
        
        fetchSummary();
        setShowDeleteConfirmDialog(false);
        setDeleteReportId(null);
      })
      .catch(err => {
        console.error(err);
        alert('Failed to delete report.');
        setShowDeleteConfirmDialog(false);
        setDeleteReportId(null);
      });
  };

  const calculateSupplements = useCallback((formData, rowIndex) => {
    const excludedSupplements = getFdaExcludedSupplements(rowIndex);
    // console.log('=== SUPPLEMENT CALCULATION START ===');
    // console.log('Row Index:', rowIndex);
    // console.log('FDA Excluded Supplements:', excludedSupplements);
    // console.log('Form Data:', formData);

    // Get the initial supplement selection (without FDA exclusions)
    const initialResults = getRecommendedSupplements(formData, 'dashboard', stock, [], stockThreshold);
    
    // Check for low stock supplements
    const lowStockSupplements = initialResults.selectedSupplements.filter(supplement => {
      const stockInfo = stockLevels.find(item => item.supplement === supplement);
      return stockInfo && stockInfo.stock < 10;
    });

    // console.log('=== LOW STOCK ANALYSIS ===');
    // console.log('Low stock supplements:', lowStockSupplements);
    // console.log('All recommended supplements:', initialResults.selectedSupplements);
    // console.log('Stock levels:', stockLevels);

    if (lowStockSupplements.length === 0) {
      // No low stock issues, use initial results
      // console.log('=== FINAL RESULTS (No Low Stock Issues) ===');
      // console.log('Selected Supplements:', initialResults.selectedSupplements);
      // console.log('Coverage:', initialResults.coverage);
      // console.log('Lacking Species:', initialResults.lackingSpecies);
      
      return {
        selectedSupplements: initialResults.selectedSupplements,
        coverage: initialResults.coverage,
        lackingSpecies: initialResults.lackingSpecies
      };
    }

    // Simple approach: exclude low stock supplements and run selection again
    const allExcludedSupplements = [...excludedSupplements, ...lowStockSupplements];
    const alternativeResults = getRecommendedSupplements(formData, 'dashboard', stock, allExcludedSupplements, stockThreshold);

    // console.log('=== FINAL RESULTS (With Low Stock Handling) ===');
    // console.log('Excluded supplements (FDA + Low Stock):', allExcludedSupplements);
    // console.log('Selected Supplements:', alternativeResults.selectedSupplements);
    // console.log('Coverage:', alternativeResults.coverage);
    // console.log('Lacking Species:', alternativeResults.lackingSpecies);

    return {
      selectedSupplements: alternativeResults.selectedSupplements,
      coverage: alternativeResults.coverage,
      lackingSpecies: alternativeResults.lackingSpecies
    };
  }, [stockLevels, stock]);

  const fetchDatabaseData = useCallback(async () => {
    if (!viewingDatabase) return;
    
    setDatabaseLoading(true);
    setDatabaseError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/database/${rawDbTable}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setDatabaseData(response.data.data);
        if (response.data.data.length > 0) {
          setDatabaseColumns(Object.keys(response.data.data[0]));
        } else {
          setDatabaseColumns([]);
        }
      } else {
        setDatabaseError(response.data.message || 'Failed to fetch database data');
      }
    } catch (error) {
      console.error('Error fetching database data:', error);
      setDatabaseError('Failed to fetch database data. Please try again.');
    } finally {
      setDatabaseLoading(false);
    }
  }, [viewingDatabase, rawDbTable]);

  // Filter and paginate database data
  const filteredDatabaseData = useMemo(() => {
    const startTime = performance.now();
    console.log('🔄 Starting data filtering at:', startTime);
    
    // Check if this computation is blocking the main thread
    const blockingCheck = performance.now();
    if (blockingCheck - startTime > 16) { // 16ms = 60fps threshold
      console.log('⚠️ WARNING: Data filtering is taking too long:', blockingCheck - startTime, 'ms');
    }
    
    let filtered = databaseData;
    
    // Apply search filter
    if (databaseSearchTerm) {
      filtered = filtered.filter(row => 
        Object.values(row).some(value => 
          String(value).toLowerCase().includes(databaseSearchTerm.toLowerCase())
        )
      );
    }
    
    // Apply year filter
    if (dbSelectedYear) {
      filtered = filtered.filter(row => {
        const reportDate = getRawDbRowDateCandidate(row);
        if (reportDate) {
          const date = new Date(reportDate);
          return date.getFullYear().toString() === dbSelectedYear;
        }
        return false;
      });
    }
    
    // Apply month filter
    if (dbSelectedMonth) {
      filtered = filtered.filter(row => {
        const reportDate = getRawDbRowDateCandidate(row);
        if (reportDate) {
          const date = new Date(reportDate);
          return (date.getMonth() + 1).toString().padStart(2, '0') === dbSelectedMonth;
        }
        return false;
      });
    }
    
    const endTime = performance.now();
    console.log('⚡ Data filtering completed in:', endTime - startTime, 'ms');
    
    return filtered;
  }, [databaseData, databaseSearchTerm, dbSelectedYear, dbSelectedMonth]);

  // Get visible columns based on selection
  const visibleColumns = useMemo(() => {
    if (showNoColumns) return []; // Show no columns when "Clear All" is clicked
    if (selectedColumns.length === 0) return databaseColumns; // Show all columns by default
    return selectedColumns; // Show only selected columns
  }, [selectedColumns, databaseColumns, showNoColumns]);

  // Extract available years and months from database data
  useEffect(() => {
    if (databaseData.length > 0) {
      const years = new Set();
      const months = new Set();
      
      databaseData.forEach(row => {
        const reportDate = getRawDbRowDateCandidate(row);
        if (reportDate) {
          try {
            const date = new Date(reportDate);
            if (!isNaN(date.getTime())) {
              years.add(date.getFullYear().toString());
              months.add((date.getMonth() + 1).toString().padStart(2, '0'));
            }
          } catch (error) {
            // Skip invalid dates
          }
        }
      });
      
      setDbAvailableYears(Array.from(years).sort((a, b) => parseInt(b) - parseInt(a))); // Sort years descending
      setDbAvailableMonths(Array.from(months).sort((a, b) => parseInt(a) - parseInt(b))); // Sort months ascending
    }
  }, [databaseData]);

  // Reset all database page state when viewing database
  useEffect(() => {
    if (viewingDatabase) {
      // Reset all filters and selections
      setDatabaseSearchTerm('');
      setDbSelectedYear('');
      setDbSelectedMonth('');
      setSelectedColumns([]);
      setShowNoColumns(false);
      setDatabaseCurrentPage(1);
      setEditingCell(null);
      setEditingValue('');
      setSavingCell(false);
      setShowDatabaseCellModal(false);
      setEditModalData({ rowIndex: null, column: null, currentValue: '', rowData: null });
    }
  }, [viewingDatabase]);

  // Switching BioMed / BioPet table: clear filters so column sets stay consistent
  useEffect(() => {
    if (!viewingDatabase) return;
    setDatabaseSearchTerm('');
    setDbSelectedYear('');
    setDbSelectedMonth('');
    setSelectedColumns([]);
    setShowNoColumns(false);
    setDatabaseCurrentPage(1);
    setEditingCell(null);
    setEditingValue('');
    setShowDatabaseCellModal(false);
    setEditModalData({ rowIndex: null, column: null, currentValue: '', rowData: null });
  }, [rawDbTable, viewingDatabase]);

  // Monitor modal state changes for debugging
  useEffect(() => {
    console.log('🔍 Modal state changed - showDatabaseCellModal:', showDatabaseCellModal);
    if (showDatabaseCellModal) {
      console.log('🔍 Modal visibility set to true at:', performance.now());
      
      // Check if there are any blocking operations
      const checkTime = performance.now();
      console.log('🔍 Checking for blocking operations at:', checkTime);
      
      // Force a micro-task to check if event loop is blocked
      Promise.resolve().then(() => {
        const afterMicroTask = performance.now();
        console.log('🔍 Micro-task completed in:', afterMicroTask - checkTime, 'ms');
      });
    }
  }, [showDatabaseCellModal]);

  // Force immediate modal rendering with useLayoutEffect
  useLayoutEffect(() => {
    if (showDatabaseCellModal) {
      console.log('🔧 useLayoutEffect: Forcing immediate render at:', performance.now());
      
      // Force a re-render by updating a ref
      if (modalRenderRef.current) {
        modalRenderRef.current = Date.now();
      }
    }
  }, [showDatabaseCellModal]);

  useEffect(() => {
    if (editModalData.rowIndex !== null) {
      console.log('🔍 Modal data set at:', performance.now(), 'Data:', editModalData);
    }
  }, [editModalData]);

  // Handle column selection
  const handleColumnToggle = (column) => {
    setSelectedColumns(prev => {
      // If showing no columns, start with just this column
      if (showNoColumns) {
        setShowNoColumns(false);
        return [column];
      }
      
      // If no columns are explicitly selected (showing all), start with all columns
      if (prev.length === 0) {
        // Remove the clicked column from all columns
        return databaseColumns.filter(col => col !== column);
      } else {
        // Normal toggle behavior when some columns are already selected
        if (prev.includes(column)) {
          return prev.filter(col => col !== column);
        } else {
          return [...prev, column];
        }
      }
    });
  };

  // Select all columns
  const selectAllColumns = () => {
    setSelectedColumns([]);
    setShowNoColumns(false);
  };

  // Deselect all columns
  const deselectAllColumns = () => {
    setSelectedColumns([]);
    setShowNoColumns(true);
  };

  // Export filtered data to CSV
  const exportFilteredDataToCSV = () => {
    try {
      // Get the data to export (filtered data with visible columns)
      const dataToExport = filteredDatabaseData.map(row => {
        const exportRow = {};
        visibleColumns.forEach(column => {
          exportRow[column] = row[column] || '';
        });
        return exportRow;
      });

      if (dataToExport.length === 0) {
        alert('No data to export. Please check your filters.');
        return;
      }

      // Create CSV content
      const headers = visibleColumns.join(',');
      const csvContent = dataToExport.map(row => 
        visibleColumns.map(column => {
          const value = row[column];
          // Handle values that contain commas, quotes, or newlines
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      ).join('\n');

      const fullCsv = `${headers}\n${csvContent}`;

      // Create and download the file
      const blob = new Blob(['﻿' + fullCsv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with current date and filter info
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      
      let filename = `${rawDbTable}_export_${dateStr}_${timeStr}`;
      if (dbSelectedYear) filename += `_year_${dbSelectedYear}`;
      if (dbSelectedMonth) filename += `_month_${dbSelectedMonth}`;
      if (databaseSearchTerm) filename += `_search_${databaseSearchTerm.substring(0, 20)}`;
      filename += '.csv';

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Show success message
      alert(`Successfully exported ${dataToExport.length} rows with ${visibleColumns.length} columns to ${filename}`);
      
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  // Modal-based cell editing functions - Ultra-optimized for instant opening
  const openEditModal = useCallback((rowIndex, column, value, rowData) => {
    const startTime = performance.now();
    console.log('🔄 Modal opening started at:', startTime);
    
    // STEP 1: Show loading state INSTANTLY (no delay)
    console.log('🔄 Step 1: Setting modal visibility INSTANTLY...');
    const step1Start = performance.now();
    setShowDatabaseCellModal(true);
    const step1End = performance.now();
    console.log('⏱️ Step 1 (setShowDatabaseCellModal) took:', step1End - step1Start, 'ms');
    
    // STEP 2: Prepare data (fast)
    console.log('🔄 Step 2: Preparing modal data...');
    const step2Start = performance.now();
    const modalData = {
      rowIndex,
      column,
      currentValue: String(value || ''),
      rowData
    };
    const step2End = performance.now();
    console.log('⏱️ Step 3 (data preparation) took:', step2End - step2Start, 'ms');
    
    // STEP 3: Set data with minimal delay (100ms for loading animation)
    console.log('🔄 Step 3: Setting modal data with minimal delay...');
    const step3Start = performance.now();
    
    // Use setTimeout with minimal delay to show loading animation
    setTimeout(() => {
      console.log('🔄 Setting modal data after loading animation...');
      setEditModalData(modalData);
      setEditingValue(String(value || ''));
      
      const step3End = performance.now();
      console.log('⏱️ Step 3 (setEditModalData + setEditingValue) took:', step3End - step3Start, 'ms');
      console.log('✅ Modal data set, full modal should appear');
    }, 100); // Only 100ms delay for loading animation
    
    const endTime = performance.now();
    console.log('⚡ Total modal opening time:', endTime - startTime, 'ms');
    console.log('✅ Modal opening completed - loading state shown instantly');
  }, []);

  const closeEditModal = useCallback(() => {
    // Immediate state updates for instant modal closing
    setShowDatabaseCellModal(false);
    setEditModalData({ rowIndex: null, column: null, currentValue: '', rowData: null });
    setEditingValue('');
  }, []);

  const saveCellEdit = useCallback(async () => {
    console.log('saveCellEdit called with:', { editModalData, editingValue });
    
    if (editModalData.rowIndex === null || editModalData.rowIndex === undefined || !editModalData.column) {
      console.log('Missing required data:', { rowIndex: editModalData.rowIndex, column: editModalData.column });
      return;
    }
    
    // Get the current value from the input field directly
    let newValue = '';
    
    console.log('Column being edited:', editModalData.column);
    console.log('Is date column?', editModalData.column?.toLowerCase().includes('date'));
    
    // Date columns have a single manual-entry field; read it directly so an
    // emptied field clears the cell instead of falling back to the old value.
    if (editModalData.column?.toLowerCase().includes('date')) {
      const manualInput = document.querySelector('input[data-input-type="date-manual"]');
      console.log('Manual input found:', !!manualInput);

      if (manualInput) {
        newValue = manualInput.value.trim();
        console.log('Using manual input value:', newValue);
      } else {
        newValue = editingValue.trim();
        console.log('Input element not found, using editingValue:', newValue);
      }
    } else {
      // For non-date columns, use the regular text input with data attribute
      const inputElement = document.querySelector('input[data-input-type="main"]');
      console.log('Main input found:', !!inputElement);
      
      if (inputElement) {
        newValue = inputElement.value.trim();
        console.log('Found main text input element, value:', newValue);
      } else {
        newValue = editingValue.trim();
        console.log('Input element not found, using editingValue:', newValue);
      }
    }
    
    // Allow empty values to be saved (for clearing cells)
    // Only show warning if user tries to save without any input at all
    if (newValue === undefined || newValue === null) {
      showNotification('Please enter a value before saving.', 'warning');
      return;
    }
    
    try {
      setSavingCell(true);
      
      // Get the row data
      const rowData = editModalData.rowData;
      // partner_codes uses 'code' as its natural key; other tables use 'id'
      const rowId = rawDbTable === 'partner_codes'
        ? rowData.code
        : (rowData.id || rowData.ID || rowData['Report Number']);

      if (!rowId) {
        showNotification('Cannot edit: Row ID not found. Please refresh the data.', 'error');
        return;
      }

      // Make API call to update the database
      const token = localStorage.getItem('token');
      const apiUrl = `${process.env.REACT_APP_API_BASE}/api/database/${rawDbTable}/${rowId}`;
      const requestData = {
        column: editModalData.column,
        value: newValue
      };
      
      console.log('Making API call to:', apiUrl);
      console.log('Request data:', requestData);
      console.log('Token:', token ? 'Present' : 'Missing');
      console.log('Value type:', typeof newValue);
      console.log('Value length:', newValue ? newValue.length : 0);
      
      const response = await axios.put(apiUrl, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('API response:', response.data);
      
      if (response.data.success) {
        // Update the local state for immediate feedback
        const updatedData = [...databaseData];
        const dataIndex = databaseData.findIndex(row => {
          const key = rawDbTable === 'partner_codes'
            ? row.code
            : (row.id || row.ID || row['Report Number']);
          return key === rowId;
        });
        
        if (dataIndex !== -1) {
          updatedData[dataIndex] = { ...updatedData[dataIndex], [editModalData.column]: newValue };
          setDatabaseData(updatedData);
        }

        // Close modal and show modern success notification
        closeEditModal();
        const successMessage = newValue === '' 
          ? `Successfully cleared ${editModalData.column}`
          : `Successfully updated ${editModalData.column} to "${newValue}"`;
        showNotification(successMessage, 'success');
      } else {
        throw new Error(response.data.message || 'Failed to update database');
      }
      
    } catch (error) {
      console.error('Error saving cell edit:', error);
      
      // Enhanced error reporting
      let errorMessage = 'Failed to save changes. Please try again.';
      
      if (error.response) {
        console.error('Server error response:', error.response.data);
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else {
          errorMessage = `Server error: ${error.response.statusText}`;
        }
        
        // Log additional error details if available
        if (error.response.data.details) {
          console.error('Error details:', error.response.data.details);
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = error.message || 'Unknown error occurred.';
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setSavingCell(false);
    }
  }, [editModalData, editingValue, databaseData, closeEditModal, rawDbTable]);

  const paginatedDatabaseData = useMemo(() => {
    const startIndex = (databaseCurrentPage - 1) * databaseRowsPerPage;
    const endIndex = startIndex + databaseRowsPerPage;
    return filteredDatabaseData.slice(startIndex, endIndex);
  }, [filteredDatabaseData, databaseCurrentPage, databaseRowsPerPage]);

  const totalPages = Math.ceil(filteredDatabaseData.length / databaseRowsPerPage);

  // Keep the top scrollbar's track as wide as the table, shift the table when
  // the bar scrolls, and forward horizontal wheel/trackpad gestures over the
  // table to the bar.
  useEffect(() => {
    const wrapper = databaseTableWrapperRef.current;
    const table = databaseTableRef.current;
    const bar = databaseTopScrollRef.current;
    const spacer = databaseTopScrollSpacerRef.current;
    if (!wrapper || !table || !bar || !spacer) return undefined;

    const applyShift = () => {
      // Round to whole pixels: a fractional translate blurs the glyphs.
      const x = Math.round(bar.scrollLeft);
      table.style.transform = x ? `translateX(-${x}px)` : '';
    };
    const syncWidth = () => {
      const overflow = Math.max(0, table.scrollWidth - wrapper.clientWidth);
      spacer.style.width = `${table.scrollWidth}px`;
      bar.classList.toggle('is-hidden', overflow === 0);
      if (bar.scrollLeft > overflow) bar.scrollLeft = overflow;
      applyShift();
    };
    // Mouse wheel over the table scrolls it horizontally (vertical wheel ticks
    // included). Once the table is at either end, the event falls through so
    // the page keeps scrolling normally.
    const onWheel = (e) => {
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!delta) return;
      const before = bar.scrollLeft;
      bar.scrollLeft += delta;
      if (bar.scrollLeft !== before) {
        e.preventDefault();
        applyShift();
      }
    };

    bar.addEventListener('scroll', applyShift);
    wrapper.addEventListener('wheel', onWheel, { passive: false });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncWidth) : null;
    if (ro) { ro.observe(wrapper); ro.observe(table); }
    syncWidth();

    return () => {
      bar.removeEventListener('scroll', applyShift);
      wrapper.removeEventListener('wheel', onWheel);
      if (ro) ro.disconnect();
      table.style.transform = '';
    };
  }, [paginatedDatabaseData, visibleColumns, databaseLoading, databaseError, rawDbTable]);

  useEffect(() => {
    fetchDatabaseData();
  }, [fetchDatabaseData]);

  const handleCalculate = async () => {
    console.log('Calculate clicked - isManualInput:', isManualInput, 'fromCsvPreview:', fromCsvPreview);

    // Pet Care Professional uses static ENG template only (no batch calculation yet).
    if (reportMode === 'pet-care-pro') {
      // Non-manual (CSV-preview generate) path: go straight to the static PDF.
      if (!isManualInput) {
        setVersionOptions({ eng: true, thai: false });
        setIsPdfReady(true);
        setShowStaticPdf(true);
        setShowCsvPopup(false);
        return;
      }

      // Manual input: build a single-row preview mirroring the CSV import flow so
      // the "Manually Input Preview" table shows the row the user typed.
      const manualRow = { ...formData };

      // Pet compatibility aliases so preview columns always show values.
      if (!manualRow['Full Name'] && manualRow.petname) manualRow['Full Name'] = manualRow.petname;
      if (!manualRow['Report Number'] && manualRow.order_number) manualRow['Report Number'] = manualRow.order_number;

      // Reset relevant states to ensure fresh calculations.
      setSupplementResults({});
      setCoverageResults({});
      setSelectedPlans({});
      setPlanAResults({});
      setPlanBResults({});
      setSelectedSupplements({});
      setManualSupplements({});
      setSupplementDurations({});
      setAppliedDurations({});
      setFdaCheckboxes({});
      setFdaRecalculatedRows({});
      setLoadingConfirm({});
      setConfirmedRows({});
      setAllConfirmLoading(false);
      setAllConfirmed(false);

      // Load the quartile grid (same source the CSV path uses) so the
      // supplement/plan calculation is accurate.
      let petQuartileGridBySpecies = quartileGridBySpecies;
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${process.env.REACT_APP_API_BASE}/api/reports/quartile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        petQuartileGridBySpecies = quartileApiResponseToGridBySpecies(res.data);
        setQuartileGridBySpecies(petQuartileGridBySpecies);
      } catch (e) {
        console.warn('Quartile load failed during manual input:', e);
      }

      // Compute supplements and auto-select a plan, matching processCsvRowsWithProgress.
      const configInfo = qpt_choosing_supplement[0].info;
      const normalizedRow = normalizeRowKeys(manualRow, configInfo);
      const result = getRecommendedSupplements(
        normalizedRow,
        reportMode,
        stock,
        getFdaExcludedSupplements(0),
        stockThreshold,
        appliedDurations[0] || {},
        true,
        petQuartileGridBySpecies
      );
      setSupplementResults({ 0: result.supplements });
      setCoverageResults({ 0: result.coverage });
      if (result.supplements.length === 0) {
        setSelectedPlans({ 0: 'No supp. Need' });
        setAppliedDurations({ 0: { 'No Duration': true } });
      } else {
        setSelectedPlans({ 0: 'Plan A' });
      }

      // Populate the preview with the single manual row and open the preview modal.
      setCsvPreviewData([manualRow]);
      setCsvPreviewRows([manualRow]);
      setCsvFileName('Manual_Input.csv');
      setIsManualInput(true);
      setFromCsvPreview(true);
      setShowCsvPopup(true);
      return;
    }

    // If data is from manual input (not from CSV preview), show CSV Import Preview
    if (isManualInput) {
      console.log('Manual input detected - showing preview modal');
      // Convert manual form data to CSV preview format
      const manualRow = { ...formData };
      
      // Format date fields properly for PDF display
      if (manualRow['Date of Birth']) {
        const dobDate = new Date(manualRow['Date of Birth']);
        if (!isNaN(dobDate)) {
          manualRow['Date of Birth'] = dobDate.toLocaleDateString('en-GB'); // DD/MM/YYYY format
        }
      }
      
      if (manualRow['Collection Date']) {
        const collectionDate = new Date(manualRow['Collection Date']);
        if (!isNaN(collectionDate)) {
          manualRow['Collection Date'] = collectionDate.toLocaleDateString('en-GB'); // DD/MM/YYYY format
        }
      }
      
      if (manualRow['Report Date']) {
        const reportDate = new Date(manualRow['Report Date']);
        if (!isNaN(reportDate)) {
          manualRow['Report Date'] = reportDate.toLocaleDateString('en-GB'); // DD/MM/YYYY format
        }
      }
      
      // Generate Client ID for manual input using the same logic as CSV import
      const generateClientIdForManual = async () => {
        try {
          // Get monthly row order
          const rowOrder = await getNextMonthlyRowOrder();
          
          // Get current date parts using Thai timezone (Asia/Bangkok)
          const currentDate = new Date();
          const thaiDate = new Date(currentDate.toLocaleString('en-US', { timeZone: 'Asia/Bangkok' }));
          const year = thaiDate.getFullYear().toString().slice(-2);
          const month = (thaiDate.getMonth() + 1).toString().padStart(2, '0');
          const datePart = `${year}${month}`;
          
          // Row order number (padded to 3 digits)
          const rowOrderStr = rowOrder.toString().padStart(3, '0');
          
          // Check if client already exists in database by Full Name
          const fullName = manualRow['Full Name'] || '';
          let clientUsageCount = '01';
          
          if (fullName) {
            try {
              const token = localStorage.getItem('token');
              const response = await axios.get(
                `${process.env.REACT_APP_API_BASE}/api/reports/client-count/${encodeURIComponent(fullName)}`,
                {
                  headers: { Authorization: `Bearer ${token}` }
                }
              );
              const existingCount = response.data.count || 0;
              const totalCount = existingCount + 1; // +1 for current manual input
              clientUsageCount = totalCount.toString().padStart(2, '0');
            } catch (err) {
              console.error('Error fetching client count:', err);
              // Fallback to '01' if error
            }
          }
          
          // Use default type "qpcr" for manual input
          const prefix = 'qpcr';
          const clientId = `${prefix}-${datePart}-${rowOrderStr}-${clientUsageCount}`;
          
          // Set the generated Client ID
          setGeneratedClientIds({ 0: clientId });
          setClientIdType({ 0: 'qpcr' });
          
          // Update the manual row with the generated Client ID
          manualRow['Client ID'] = clientId;
        } catch (err) {
          console.error('Error generating Client ID:', err);
          // Fallback Client ID
          manualRow['Client ID'] = 'qpcr-000-000-01';
          setGeneratedClientIds({ 0: 'qpcr-000-000-01' });
          setClientIdType({ 0: 'qpcr' });
        }
      };
      
      // Generate Client ID and then show preview
      await generateClientIdForManual();
      
      // Process manual input data similar to CSV import
      const processManualInput = async () => {
        // Reset all relevant states to ensure fresh calculations
        setSupplementResults({});
        setCoverageResults({});
        setSelectedPlans({});
        setPlanAResults({});
        setPlanBResults({});
        setSelectedSupplements({});
        setManualSupplements({});
        setSupplementDurations({});
        setAppliedDurations({});
        setFdaCheckboxes({});
        setFdaRecalculatedRows({});
        setLoadingConfirm({});
        setConfirmedRows({});
        setAllConfirmLoading(false);
        setAllConfirmed(false);
        
        // Get all available supplements for this panel
        const allPanelSupplements = getAllPanelSupplements(reportMode);
        
        // Store all possible supplements for this panel with their stock counts
        const panelStockData = {};
        allPanelSupplements.forEach(supplement => {
          const stockCount = stock[supplement] || 0;
          panelStockData[supplement] = stockCount;
        });
        
        // Save to state for later use
        setPanelSupplementStock(panelStockData);
        
        // Process the manual row for supplements
        const configInfo =
          reportMode === '9P' ? choosing_supplement_9P[0].info :
          reportMode === 'oms' ? choosing_supplement_oms[0].info :
          reportMode === 'Allergy and Skin' ? choosing_supplement_AMG[0].info : reportMode === 'Allergy and Skin_kids' ? qpt_choosing_supplement[0].info :
          reportMode === 'Toxin' ? choosing_supplement_toxin[0].info :
          ['bwcallergy', 'bwccore', 'bwcdiamond', 'BWC-A', 'BWC-C', 'BWC-D'].includes(reportMode) ? choosing_supplement_bwc[0].info :
          qpt_choosing_supplement[0].info;
        
        const normalizedRow = normalizeRowKeys(manualRow, configInfo);
        const excludedSupplements = getFdaExcludedSupplements(0);
        const durationExclusions = appliedDurations[0] || {};
        const result = getRecommendedSupplements(
          normalizedRow,
          reportMode,
          stock,
          excludedSupplements,
          stockThreshold,
          durationExclusions,
          true,
          reportMode === 'pet-care-pro' ? quartileGridBySpecies : null
        );

        // Set supplement results for the manual input
        setSupplementResults({ 0: result.supplements });
        setCoverageResults({ 0: result.coverage });
        
        // Handle healthy clients (no supplements needed) for manual input (non-pet panels only)
        if (result.supplements.length === 0 && reportMode !== 'pet-care-pro') {
          console.log('Manual input: Checking if client is truly healthy...');
          console.log('Manual input: Normalized data:', normalizedRow);
          console.log('Manual input: Coverage result:', result.coverage);
          
          // Double-check by manually calculating deficient species
          const deficientSpecies = configInfo
            .filter(({ sp, less }) => {
              const value = parseFloat(normalizedRow[sp]) || 0;
              const calculatedLabel = calculateSpeciesLabel(sp, value, normalizedRow);
              const isDeficient = Array.isArray(less) ? less.includes(calculatedLabel) : false;
              console.log(`Manual input: Species ${sp} - Value: ${value}, Label: ${calculatedLabel}, Deficient: ${isDeficient}`);
              return isDeficient;
            })
            .map(({ sp }) => sp);
          
          console.log('Manual input: Manually calculated deficient species:', deficientSpecies);
          
          if (deficientSpecies.length === 0) {
            console.log('Manual input: CONFIRMED - Healthy client detected - no supplements needed');
            // Auto-select a plan for healthy clients
            setSelectedPlans({ 0: 'No supp. Need' });
            // Set duration to "None" for healthy clients
            setAppliedDurations({ 0: { 'No Duration': true } });
          } else {
            console.log('Manual input: ERROR - Client has deficient species but no supplements recommended!');
            console.log('Manual input: This might be a bug in supplement calculation logic');
          }
        }
        
        // Set client counts for the manual input
        const fullName = manualRow['Full Name'] || '';
        if (fullName) {
          try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
              `${process.env.REACT_APP_API_BASE}/api/reports/client-count/${encodeURIComponent(fullName)}`,
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );
            setClientCounts({ [fullName]: response.data.count || 0 });
          } catch (err) {
            console.error('Error fetching client count:', err);
            setClientCounts({ [fullName]: 0 });
          }
        }
      };
      
      // Process the manual input data
      await processManualInput();
      
      // Set CSV preview data with the manual input
      setCsvPreviewData([manualRow]);
      setCsvPreviewRows([manualRow]);
      setCsvFileName('Manual_Input.csv');
      setIsManualInput(true);
      setFromCsvPreview(true);
      setShowCsvPopup(true);
      return;
    }

    // If already in CSV preview mode, proceed with PDF generation
    console.log('Proceeding to PDF generation');
    try {
      setCalcStatusText('Generating PDF (English & Thai versions)...');
      setIsCalculating(true);
      setShowStaticPdf(false); // Reset states at start
      setIsPdfReady(false);

      const generatePDFBlob = async () => {
        const { jsPDF } = await import('jspdf');
        const html2canvas = (await import('html2canvas')).default;
        const pages = document.querySelectorAll('.pdf-page-wrapper');
        if (!pages.length) {
          throw new Error('No PDF pages found');
        }
        const pdf = new jsPDF('p', 'mm', 'a4');
        let first = true;
        for (let el of pages) {
          el.scrollIntoView();
          await new Promise(r => setTimeout(r, 100));
          const canvas = await html2canvas(el, { 
            scale: 1.9, 
            useCORS: true,
            logging: false,
            allowTaint: true
          });
          const imgData = canvas.toDataURL('image/jpeg', 0.90);
          const width = pdf.internal.pageSize.getWidth();
          const height = (canvas.height * width) / canvas.width;
          if (!first) pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, 0, width, height);
          first = false;
        }
        return pdf.output('blob');
      };

      // Generate English PDF
      setCalcStatusText('Generating English version...');
      setVersionOptions({ eng: true, thai: false });
      setIsPdfReady(true);
      setShowStaticPdf(true);
      
      // Wait for positioning calculations to complete
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Additional wait to ensure all DOM elements are properly positioned
      await new Promise(resolve => {
        const checkPositions = () => {
          const pages = document.querySelectorAll('.pdf-page-wrapper');
          if (pages.length > 0) {
            const overlayElements = document.querySelectorAll('.pdf-overlay-text, .pdf-overlay-image');
            if (overlayElements.length > 0) {
              resolve();
            } else {
              setTimeout(checkPositions, 100);
            }
          } else {
            setTimeout(checkPositions, 100);
          }
        };
        checkPositions();
      });
      
      const engBlob = await generatePDFBlob();

      // Generate Thai PDF
      setCalcStatusText('Generating Thai version...');
      setVersionOptions({ eng: false, thai: true });
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Wait for Thai version positioning calculations
      await new Promise(resolve => {
        const checkPositions = () => {
          const pages = document.querySelectorAll('.pdf-page-wrapper');
          if (pages.length > 0) {
            const overlayElements = document.querySelectorAll('.pdf-overlay-text, .pdf-overlay-image');
            if (overlayElements.length > 0) {
              resolve();
            } else {
              setTimeout(checkPositions, 100);
            }
          } else {
            setTimeout(checkPositions, 100);
          }
        };
        checkPositions();
      });
      
      const thaiBlob = await generatePDFBlob();

      // Merge PDFs
      const [engPdf, thaiPdf] = await Promise.all([
        PDFDocument.load(await engBlob.arrayBuffer()),
        PDFDocument.load(await thaiPdf.arrayBuffer())
      ]);

      const mergedPdf = await PDFDocument.create();
      const engPages = await mergedPdf.copyPages(engPdf, engPdf.getPageIndices());
      const thaiPages = await mergedPdf.copyPages(thaiPdf, thaiPdf.getPageIndices());
      engPages.forEach(p => mergedPdf.addPage(p));
      thaiPages.forEach(p => mergedPdf.addPage(p));

      const finalBlob = new Blob([await mergedPdf.save()], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(finalBlob);
      a.download = pdfFileNameFromReportNumber(formData);
      a.click();

      // Cleanup
      URL.revokeObjectURL(a.href);
      setShowStaticPdf(false);
      setIsPdfReady(false);
      setIsCalculating(false);
      setCalcStatusText('');
      setShowCsvPopup(true); // Force back to CSV Import Preview

    } catch (err) {
      console.error('PDF Generation Error:', err);
      // Cleanup on error
      setShowStaticPdf(false);
      setIsPdfReady(false);
      setIsCalculating(false);
      setCalcStatusText('');
      setShowCsvPopup(true); // Force back to CSV Import Preview even on error
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleSupplementClick = (supplementType, rowIndex) => {
    // Get the current form data
    const currentFormData = csvPreviewRows[rowIndex];
    
    // Set the current row index
    setCurrentRowIndex(rowIndex);
    
    // Set loading state
    setLoadingConfirm(prev => ({ ...prev, [rowIndex]: true }));

    // If supplementType is null (Select button clicked), reset the inventory icon
    if (supplementType === null) {
      // Reset applied durations for this row (this will change inventory icon back to unapplied state)
      setAppliedDurations(prev => {
        const newAppliedDurations = { ...prev };
        delete newAppliedDurations[rowIndex];
        return newAppliedDurations;
      });
      
      // Also reset supplement durations for this row
      setSupplementDurations(prev => {
        const newSupplementDurations = { ...prev };
        delete newSupplementDurations[rowIndex];
        return newSupplementDurations;
      });
      
      console.log(`🔄 Select clicked for row ${rowIndex} - Inventory icon reset to unapplied state`);
    }

    // Get FDA excluded supplements only (no stock condition exclusions)
    const excludedSupplements = getFdaExcludedSupplements(rowIndex);
    
    // Console log for debugging
    console.log('=== HANDLE SUPPLEMENT CLICK DEBUG (NO STOCK CONDITIONS) ===');
    console.log('Row Index:', rowIndex);
    console.log('FDA Excluded Supplements:', excludedSupplements);
    console.log('Form Data:', currentFormData);
    console.log('=== END DEBUG ===');
    
    // Call getRecommendedSupplements for both Plan A (no stock conditions) and Plan B (with stock conditions)
    const durationExclusions = appliedDurations[rowIndex] || {};
    
    // Plan A: Calculate without stock level considerations
    const planAResult = getRecommendedSupplements(
      currentFormData,
      reportMode,
      stock,
      excludedSupplements,
      stockThreshold,
      durationExclusions,
      false,
      reportMode === 'pet-care-pro' ? quartileGridBySpecies : null
    );

    // Plan B: Calculate with stock level considerations using updated local stock
    const updatedStock = calculateUpdatedStock();
    const planBResult = getRecommendedSupplements(
      currentFormData,
      reportMode,
      updatedStock,
      excludedSupplements,
      stockThreshold,
      durationExclusions,
      true,
      reportMode === 'pet-care-pro' ? quartileGridBySpecies : null
    );
    
    // Use Plan B results as the default (current behavior)
    const result = planBResult;
    const recommendedSupplements = result.supplements;
    
    console.log('=== FINAL RESULTS ===');
    console.log('Plan A Supplements:', planAResult.supplements);
    console.log('Plan A Coverage:', planAResult.coverage);
    console.log('Plan B Supplements:', planBResult.supplements);
    console.log('Plan B Coverage:', planBResult.coverage);
    console.log('Selected Supplements (Plan B):', recommendedSupplements);
    console.log('Coverage:', result.coverage);
    console.log('Lacking Species:', result.coverage.uncoveredSpecies);
    
    // Store both Plan A and Plan B results
    setPlanAResults(prev => ({
      ...prev,
      [rowIndex]: planAResult
    }));
    
    setPlanBResults(prev => ({
      ...prev,
      [rowIndex]: planBResult
    }));
    
    // Update supplement results (using Plan B as default)
    setSupplementResults(prev => ({
      ...prev,
      [rowIndex]: recommendedSupplements
    }));
    
    // Update coverage results (using Plan B as default)
    setCoverageResults(prev => ({
      ...prev,
      [rowIndex]: result.coverage
    }));
    
    // Clear loading state
    setLoadingConfirm(prev => ({ ...prev, [rowIndex]: false }));
    setFdaRecalculatedRows(prev => ({ ...prev, [rowIndex]: true }));

    // Show supplement popup modal
    setSupplementPopupRowIndex(rowIndex);
    setShowSupplementPopup(true);
  };

  // Helper: for each group, return the exceptions for the currently hovered main button
  const getExceptions = (group) => {
    if (hoveredMainButton === 'QPT') {
      if (group === 'qptOptions') return ['BIO-A01', 'BIO-G02', 'BIO-GA03', 'BIO-I04', 'BIO-M05', 'BIO-MS06', 'BIO-P07'];
      return [];
    }
    if (hoveredMainButton === '9P') {
      if (group === 'bioGroup') return ['BioAll', 'BioMeta', 'BioZen'];
      return [];
    }
    if (['AS', 'GTT', 'OMs'].includes(hoveredMainButton)) {
      if (group === '8x3Group') return ['8x3 SA', '6x3 V', '5x3 C'];
      // All other groups: no exceptions
      return [];
    }
    if (['BWC-A', 'BWC-C', 'BWC-D'].includes(hoveredMainButton)) {
      if (group === 'proGroup') return ['Pro 4', 'Pro 6'];
      // All other groups: no exceptions
      return [];
    }
    return [];
  };

  // Helper: should this group be greyed out for the current hovered main button?
  const shouldGreyGroup = (group) => {
    if (!hoveredMainButton) return false;
    if (hoveredMainButton === 'QPT') return ['bioGroup', '8x3Group', 'proGroup'].includes(group) || group === 'qptOptions';
    if (hoveredMainButton === '9P') return ['qptOptions', 'bioGroup', '8x3Group', 'proGroup'].includes(group);
    if (['AS', 'GTT', 'OMs'].includes(hoveredMainButton)) return ['qptOptions', 'bioGroup', '8x3Group', 'proGroup'].includes(group);
    // BWC-A, BWC-C, BWC-D: grey all groups below (qptOptions, bioGroup, 8x3Group, proGroup)
    if (['BWC-A', 'BWC-C', 'BWC-D'].includes(hoveredMainButton)) return ['qptOptions', 'bioGroup', '8x3Group', 'proGroup'].includes(group);
    return false;
  };

  // Reusable SSM stock button (same look/behaviour as the inline groups). The
  // New-formula group now uses renderNewFormulaStockCard instead, so this is
  // currently uncalled — kept as the refactor target for the legacy groups
  // (qptOptions / bioGroup / 8x3Group / proGroup), which still inline this markup.
  // eslint-disable-next-line no-unused-vars
  const renderStockOptionButton = (option, groupKey) => {
    const isGrey = shouldGreyGroup(groupKey) && !getExceptions(groupKey).includes(option);
    const value = Number(stock[option]);
    const isSelected = selectedButtons.has(option);
    const border = isGrey
      ? undefined
      : value <= redThreshold
        ? '1px solid #7b0909'
        : value < yellowThreshold
          ? '1px solid #b5b510'
          : undefined;
    let backgroundColor = isGrey
      ? '#e0e0e0'
      : value <= redThreshold
        ? '#ffd2d2'
        : value < yellowThreshold
          ? '#ffffc8'
          : undefined;
    if (fdaModifyClicked && isSelected) backgroundColor = '#d3e9ff';
    const boxShadow = fdaModifyClicked
      ? ' rgba(14, 30, 37, 0.12) 0px 2px 4px 0px, rgba(14, 30, 37, 0.32) 0px 2px 16px 0px'
      : undefined;
    return (
      <button
        key={option}
        className="mode_btn_customeST syndrome-btn"
        style={{ minWidth: '120px', marginBottom: '5px', color: 'var(--text-primary)', border, backgroundColor, boxShadow, position: 'relative' }}
        onClick={() => {
          if (fdaModifyClicked) {
            const newSelected = new Set(selectedButtons);
            if (isSelected) newSelected.delete(option); else newSelected.add(option);
            setSelectedButtons(newSelected);
          } else {
            setEditStockKey(option);
            setEditStockValue('');
            setShowStockModal(true);
          }
        }}
      >
        {isSelected && (
          <img
            src={`${process.env.PUBLIC_URL}/star.png`}
            alt="Selected"
            style={{ position: 'absolute', top: '-5px', right: '-5px', width: '20px', height: '20px', zIndex: 10 }}
          />
        )}
        <span style={{ color: 'var(--text-muted)' }}>{option}</span>
        <span style={{ marginLeft: 8, fontWeight: 700 }}>
          (Stock: {stock[option] !== undefined ? stock[option] : ''})
        </span>
      </button>
    );
  };

  // Class list for an SSM stock tile. Same threshold logic the inline groups
  // used for their inline colours (grey > selected > critical > low), moved to
  // CSS classes in ssm-lab.css so hover/focus styles can layer on top.
  const ssmTileClass = ({ isGrey, value, red, yellow, isSelected, modify }) => {
    const cls = ['ssm-tile'];
    if (isGrey) cls.push('is-grey');
    else if (value <= red) cls.push('is-critical');
    else if (value < yellow) cls.push('is-low');
    if (modify) cls.push('is-modify');
    if (modify && isSelected) cls.push('is-selected');
    return cls.join(' ');
  };

  // Header readouts for the SSM view: how many SKUs in the visible section are
  // tracked, and how many sit under the yellow / red thresholds.
  const ssmSummary = useMemo(() => {
    const isBiopet = stockSection === 'biopet';
    const keys = isBiopet
      ? biopetStockOptions
      : [...qptOptions, ...otherButtonGroups.flat(), ...supplement_9P_skus];
    const red = isBiopet ? biopetRedThreshold : redThreshold;
    const yellow = isBiopet ? biopetYellowThreshold : yellowThreshold;
    let tracked = 0; let low = 0; let critical = 0;
    keys.forEach((k) => {
      const v = Number(stock[k]);
      if (!Number.isFinite(v)) return;
      tracked += 1;
      if (v <= red) critical += 1;
      else if (v < yellow) low += 1;
    });
    return { total: keys.length, tracked, low, critical };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock, stockSection, redThreshold, yellowThreshold, biopetRedThreshold, biopetYellowThreshold]);

  // Card version of the SSM stock button, used only by the New-formula group so
  // the single-strain SKUs can be told apart by their artwork. Same state, same
  // thresholds and same click behaviour as renderStockOptionButton above.
  const renderNewFormulaStockCard = (sku) => {
    const isGrey = shouldGreyGroup('newFormulaGroup') && !getExceptions('newFormulaGroup').includes(sku);
    const raw = stock[sku];
    const value = Number(raw);
    const isSelected = selectedButtons.has(sku);
    const accent = newformula_facts_9P[sku]?.color || '#0b4780';
    const strain = newformula_facts_9P[sku]?.activeStrain || '';

    // Same thresholds as the SSM tiles (ssmTileClass). A SKU with no stock
    // column yet stays neutral rather than reading as healthy. Colours live in
    // ssm-lab.css under these classes so hover/focus can layer on top.
    const status = !Number.isFinite(value)
      ? { key: 'is-na', label: 'n/a' }
      : value <= redThreshold
        ? { key: 'is-critical', label: 'Critical' }
        : value < yellowThreshold
          ? { key: 'is-low', label: 'Low' }
          : { key: 'is-ok', label: 'OK' };

    const cardClass = [
      'newformula-stock-card',
      'syndrome-btn',
      isGrey ? 'is-grey' : status.key,
      fdaModifyClicked ? 'is-modify' : '',
      fdaModifyClicked && isSelected ? 'is-selected' : ''
    ].filter(Boolean).join(' ');

    return (
      <button
        key={sku}
        type="button"
        className={cardClass}
        aria-pressed={fdaModifyClicked ? isSelected : undefined}
        aria-label={`${sku}${strain ? ` — ${strain}` : ''}, stock ${raw !== undefined ? raw : 'unknown'}${fdaModifyClicked ? '' : ' — click to edit stock'}`}
        onClick={() => {
          if (fdaModifyClicked) {
            const newSelected = new Set(selectedButtons);
            if (isSelected) newSelected.delete(sku); else newSelected.add(sku);
            setSelectedButtons(newSelected);
          } else {
            setEditStockKey(sku);
            setEditStockValue('');
            setShowStockModal(true);
          }
        }}
      >
        {isSelected && (
          <img
            src={`${process.env.PUBLIC_URL}/star.png`}
            alt="Selected"
            className="ssm-tile-star"
          />
        )}
        <span className="newformula-stock-accent" style={{ backgroundColor: accent }} />
        <span className="newformula-stock-figure">
          <NewFormulaThumb sku={sku} accent={accent} />
          {/* Hover/focus affordance: says what a click will actually do. */}
          <span className="newformula-stock-hint" style={{ backgroundColor: accent }}>
            {fdaModifyClicked
              ? (isSelected ? 'Deselect' : 'Select')
              : 'Edit stock'}
          </span>
        </span>
        <span className="newformula-stock-code" style={{ color: accent }}>{sku}</span>
        <span className="newformula-stock-strain">{strain}</span>
        <span className="newformula-stock-row">
          <span className="newformula-stock-value">
            <small>stock</small>{raw !== undefined ? raw : '—'}
          </span>
          <span className="newformula-stock-pill">{status.label}</span>
        </span>
      </button>
    );
  };

  // Legacy SKU groups (BIO series / Bio blends / Combos / Pro) rendered as the
  // same horizontal card as the New-formula SKUs. There is no artwork for these
  // yet, so the figure shows a capsule icon in the group's accent colour.
  const LEGACY_GROUP_ACCENT = {
    qptOptions: '#2E6B72',
    bioGroup: '#4b3f8f',
    '8x3Group': '#8a5a00',
    proGroup: '#b23a48'
  };

  const renderLegacyStockCard = (sku, groupKey, groupLabel) => {
    const isGrey = shouldGreyGroup(groupKey) && !getExceptions(groupKey).includes(sku);
    const raw = stock[sku];
    const value = Number(raw);
    const isSelected = selectedButtons.has(sku);
    const accent = LEGACY_GROUP_ACCENT[groupKey] || '#0b4780';

    const status = !Number.isFinite(value)
      ? { key: 'is-na', label: 'n/a' }
      : value <= redThreshold
        ? { key: 'is-critical', label: 'Critical' }
        : value < yellowThreshold
          ? { key: 'is-low', label: 'Low' }
          : { key: 'is-ok', label: 'OK' };

    const cardClass = [
      'newformula-stock-card',
      'syndrome-btn',
      isGrey ? 'is-grey' : status.key,
      fdaModifyClicked ? 'is-modify' : '',
      fdaModifyClicked && isSelected ? 'is-selected' : ''
    ].filter(Boolean).join(' ');

    return (
      <button
        key={sku}
        type="button"
        className={cardClass}
        aria-pressed={fdaModifyClicked ? isSelected : undefined}
        aria-label={`${sku} (${groupLabel}), stock ${raw !== undefined ? raw : 'unknown'}${fdaModifyClicked ? '' : ' — click to edit stock'}`}
        onClick={() => {
          if (fdaModifyClicked) {
            const newSelected = new Set(selectedButtons);
            if (isSelected) newSelected.delete(sku); else newSelected.add(sku);
            setSelectedButtons(newSelected);
          } else {
            setEditStockKey(sku);
            setEditStockValue('');
            setShowStockModal(true);
          }
        }}
      >
        {isSelected && (
          <img
            src={`${process.env.PUBLIC_URL}/star.png`}
            alt="Selected"
            className="ssm-tile-star"
          />
        )}
        <span className="newformula-stock-accent" style={{ backgroundColor: accent }} />
        <span className="newformula-stock-figure">
          {/* Dummy icon until per-SKU artwork exists: a capsule in the group colour. */}
          <span
            className="newformula-stock-placeholder ssm-stock-icon"
            style={{ backgroundColor: `${accent}14`, color: accent }}
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2.5" y="8" width="19" height="8" rx="4" transform="rotate(-35 12 12)" />
              <line x1="9.2" y1="8.2" x2="14.8" y2="15.8" />
              <path d="M6.5 9.8l2 2.8" opacity="0.5" />
            </svg>
          </span>
          <span className="newformula-stock-hint" style={{ backgroundColor: accent }}>
            {fdaModifyClicked
              ? (isSelected ? 'Deselect' : 'Select')
              : 'Edit stock'}
          </span>
        </span>
        <span className="newformula-stock-code" style={{ color: accent }}>{sku}</span>
        <span className="newformula-stock-strain">{groupLabel}</span>
        <span className="newformula-stock-row">
          <span className="newformula-stock-value">
            <small>stock</small>{raw !== undefined ? raw : '—'}
          </span>
          <span className="newformula-stock-pill">{status.label}</span>
        </span>
      </button>
    );
  };

  const handleConfirm = async (clientId, index) => {
    setLoadingConfirm(prev => ({ ...prev, [index]: true }));
    
    // Retry logic with 5 attempts
    const maxRetries = 5;
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Attempt ${attempt}/${maxRetries} to save single report (row ${index})...`);
        
        // Get the report data for this specific row
        const reportData = csvPreviewRows[index];
        
        // Get supplement data for this row
        const manual = manualSupplements[index];
        const auto = supplementResults[index];
        const supplements = manual && manual.length > 0 ? manual : auto;
        
        // Add supplement data to the report
        const reportDataWithSupplements = {
          ...reportData,
          'Supplement': supplements && supplements.length > 0 ? JSON.stringify(supplements) : null,
          'B2C': csvPreviewToggle ? 0 : 1 // 1 = B2C, 0 = B2B
        };
        
        // Determine panel name based on report mode
        let panelName = 'QPT'; // default
        if (reportMode === '9P') {
          panelName = 'Q9';
        } else if (reportMode === 'Allergy and Skin') {
          panelName = 'QA';
        } else if (reportMode === 'Allergy and Skin_kids') {
          panelName = 'QI';
        } else if (reportMode === 'Toxin') {
          panelName = 'QG';
        } else if (reportMode === 'oms') {
          panelName = 'QM';
        } else if (reportMode === 'bwcallergy') {
          panelName = 'QBWCA';
        } else if (reportMode === 'bwccore') {
          panelName = 'QBWCE';
        } else if (reportMode === 'bwcdiamond') {
          panelName = 'QBWCU';
        }

        // Save single report to st_report table
        const token = localStorage.getItem('token');
        const saveResponse = await axios.post(
          `${process.env.REACT_APP_API_BASE}/api/reports/save-to-st-report`,
          {
            reports: [reportDataWithSupplements],
            panel: panelName
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        console.log(`Attempt ${attempt} successful - Single report saved to st_report table:`, saveResponse.data);

        // Success! Break out of retry loop
        console.log(`Successfully saved single report after ${attempt} attempt(s)`);
        break;

      } catch (err) {
        lastError = err;
        console.error(`Attempt ${attempt}/${maxRetries} failed for single report (row ${index}):`, err);
        
        if (attempt < maxRetries) {
          // Wait before retrying (exponential backoff: 1s, 2s, 4s, 8s)
          const waitTime = Math.pow(2, attempt - 1) * 1000;
          console.log(`Waiting ${waitTime}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        } else {
          // All attempts failed
          console.error(`All ${maxRetries} attempts failed for single report (row ${index}). Last error:`, err);
          alert(`Failed to save report after ${maxRetries} attempts!\nLast error: ${err.response?.data?.message || err.message}`);
          setLoadingConfirm(prev => ({ ...prev, [index]: false }));
          return;
        }
      }
    }

    // If we reach here, the save was successful
    // Simulate loading for 1 second
    setTimeout(() => {
      setConfirmedRows(prev => ({ ...prev, [index]: true }));
      setLoadingConfirm(prev => ({ ...prev, [index]: false }));
      // Refresh the total report count
      fetchSummary();
    }, 1000);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    let csvText;
    try {
      csvText = await readCsvFileAsUtf8OrThaiWindows(file);
    } catch (e) {
      console.error('CSV read failed:', e);
      alert('Could not read CSV file.');
      event.target.value = null;
      return;
    }

    setCsvFileName(file.name);
    setConfirmedRows({}); // Reset confirmed status when new file is imported
    setLoadingConfirm({}); // Reset loading status
    setAllConfirmLoading(false); // Reset all confirm loading state
    setAllConfirmed(false); // Reset all confirmed state
    setSupplementDurations({}); // Reset duration selections
    setAppliedDurations({}); // Reset applied durations
    setInventoryRowIndex(null); // Reset inventory popup
    setShowInventoryPopup(false); // Close inventory popup

            Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (h) => normalizeColumnName(String(h ?? '').trim()) || String(h ?? '').trim(),
          complete: (results) => {
            // console.log('🔍 CSV Import Results - First row:', results.data[0]);
            // console.log('🔍 CSV headers:', results.meta.fields);
            // console.log('🔍 Bacteroides-related columns in CSV:', results.meta.fields.filter(field => field.toLowerCase().includes('bacteroid')));
            setCsvPreviewRows(results.data);
            setShowCsvPopup(true);
            setShowBlueConfirm(true);

        // Calculate supplements for each row
        const supplementResultsObj = {};
        const coverageResultsObj = {};
        
        results.data.forEach((row, idx) => {
          // console.log(`\nProcessing Row ${idx}:`, row);
          
          // Get the appropriate config based on report mode
          const configInfo = reportMode === '9P' ? choosing_supplement_9P[0].info :
            reportMode === 'oms' ? choosing_supplement_oms[0].info :
            reportMode === 'Allergy and Skin' ? choosing_supplement_AMG[0].info : reportMode === 'Allergy and Skin_kids' ? qpt_choosing_supplement[0].info :
            reportMode === 'Toxin' ? choosing_supplement_toxin[0].info :
            ['bwcallergy', 'bwccore', 'bwcdiamond', 'BWC-A', 'BWC-C', 'BWC-D'].includes(reportMode) ? choosing_supplement_bwc[0].info :
            qpt_choosing_supplement[0].info;
          
          // console.log('Using config for report mode:', reportMode);
          const normalizedRow = normalizeRowKeys(row, configInfo);
          // console.log('Normalized Row:', normalizedRow);
          const durationExclusions = appliedDurations[idx] || {};
          const result = getRecommendedSupplements(
            normalizedRow,
            reportMode,
            stock,
            getFdaExcludedSupplements(idx),
            stockThreshold,
            durationExclusions,
            true,
            reportMode === 'pet-care-pro' ? quartileGridBySpecies : null
          );
          supplementResultsObj[idx] = result.supplements;
          coverageResultsObj[idx] = result.coverage;
          
          // Note: Durations will be auto-filled when user selects a plan, not during CSV import
        });
        
        // console.log('Final Supplement Results:', supplementResultsObj);
        setSupplementResults(supplementResultsObj);
        setCoverageResults(coverageResultsObj);
        
        // Note: Durations will be auto-filled when user selects a plan, not during CSV import
      }
    });
  };

  // Modified handleConfirmAll to save data to st_report table and decrement stock
  const handleConfirmAll = async () => {
    // Prevent multiple calls with global flag
    if (allConfirmLoading || globalSuccessFlag.current) return;
    
    // Set global flag to prevent any other operations
    globalSuccessFlag.current = true;
    
    // Generate unique operation ID
    const currentOperationId = Date.now() + Math.random();
    operationIdRef.current = currentOperationId;
    
    // Reset success shown flag and clear any existing timeout
    successShownRef.current = false;
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    
    setAllConfirmLoading(true);
    // Set loading state only for unconfirmed rows
    const loadingState = {};
    const confirmedState = { ...confirmedRows }; // Keep existing confirmed states

    csvPreviewRows.forEach((_, index) => {
      if (!confirmedRows[index]) { // Only set loading for unconfirmed rows
        loadingState[index] = true;
      }
    });
    setLoadingConfirm(loadingState);

    // Retry logic with 5 attempts
    const maxRetries = 5;
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Attempt ${attempt}/${maxRetries} to save reports...`);
        
        // --- Save reports to st_report table ---
        const confirmedReports = csvPreviewRows.filter((_, index) => !confirmedRows[index]);
        
        // Add supplement data and Client ID to each confirmed report
        const confirmedReportsWithSupplements = await Promise.all(confirmedReports.map(async (report, reportIndex) => {
          const originalIndex = csvPreviewRows.findIndex(r => r === report);
          const manual = manualSupplements[originalIndex];
          const auto = supplementResults[originalIndex];
          const supplements = manual && manual.length > 0 ? manual : auto;
          
          // Use the Client ID that was generated in the preview
          const clientId = generatedClientIds[originalIndex] || '01-000-000-01';
          
          return {
            ...report,
            'Client ID': clientId,
            'Supplement': supplements && supplements.length > 0 ? JSON.stringify(supplements) : null,
            'B2C': csvPreviewToggle ? 0 : 1 // 1 = B2C, 0 = B2B
          };
        }));
        
        const token = localStorage.getItem('token');

        if (reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro') {
          const saveResponse = await axios.post(
            `${process.env.REACT_APP_API_BASE}/api/reports/save-biopet-batch`,
            { reports: confirmedReportsWithSupplements },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log(`Attempt ${attempt} successful - Biopet reports saved:`, saveResponse.data);
        } else {
          // Determine panel name based on report mode
          let panelName = 'QPT'; // default
          if (reportMode === '9P') {
            panelName = 'Q9';
          } else if (reportMode === 'Allergy and Skin') {
            panelName = 'QA';
          } else if (reportMode === 'Allergy and Skin_kids') {
            panelName = 'QI';
          } else if (reportMode === 'Toxin') {
            panelName = 'QG';
          } else if (reportMode === 'oms') {
            panelName = 'QM';
          } else if (reportMode === 'bwcallergy') {
            panelName = 'QBWCA';
          } else if (reportMode === 'bwccore') {
            panelName = 'QBWCE';
          } else if (reportMode === 'bwcdiamond') {
            panelName = 'QBWCU';
          }

          const saveResponse = await axios.post(
            `${process.env.REACT_APP_API_BASE}/api/reports/save-to-st-report`,
            {
              reports: confirmedReportsWithSupplements,
              panel: panelName
            },
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          console.log(`Attempt ${attempt} successful - Reports saved to st_report table:`, saveResponse.data);

          // --- Decrement supplement stock in the database ---
          // Isolated: a stock failure here must not trigger the retry loop,
          // which would re-save the reports and create duplicate rows.
          await syncSupplementStock();
        }

        // Success! Break out of retry loop
        console.log(`Successfully saved after ${attempt} attempt(s)`);
        break;

      } catch (err) {
        lastError = err;
        console.error(`Attempt ${attempt}/${maxRetries} failed:`, err);
        
        if (attempt < maxRetries) {
          // Wait before retrying (exponential backoff: 1s, 2s, 4s, 8s)
          const waitTime = Math.pow(2, attempt - 1) * 1000;
          console.log(`Waiting ${waitTime}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        } else {
          // All attempts failed
          console.error(`All ${maxRetries} attempts failed. Last error:`, err);
          alert(`Failed to save reports after ${maxRetries} attempts!\nLast error: ${err.response?.data?.message || err.message}`);
          // Reset global flag on error
          globalSuccessFlag.current = false;
          setAllConfirmLoading(false);
          setLoadingConfirm({});
          return;
        }
      }
    }

    // If we reach here, the save was successful
    // Simulate loading for 1 second
    setTimeout(() => {
      // Set confirmed state only for previously unconfirmed rows
      csvPreviewRows.forEach((_, index) => {
        if (!confirmedRows[index]) {
          confirmedState[index] = true;
        }
      });
      setConfirmedRows(confirmedState);
      setLoadingConfirm({});
      setAllConfirmLoading(false);
      setAllConfirmed(true);

      // Show success message using custom popup - only once with aggressive checks
      if (!successShownRef.current && operationIdRef.current === currentOperationId && globalSuccessFlag.current) {
        // console.log('Showing success popup for', confirmedReports.length, 'reports, operation ID:', currentOperationId);
        
        // Immediately set all flags to prevent any other popups
        successShownRef.current = true;
        globalSuccessFlag.current = true;
        
        const confirmedReports = csvPreviewRows.filter((_, index) => !confirmedRows[index]);
        const panelLabel =
          reportMode === '9P'
            ? 'Q9'
            : reportMode === 'Allergy and Skin'
              ? 'QA'
              : reportMode === 'Allergy and Skin_kids'
                ? 'QI'
                : reportMode === 'Toxin'
                  ? 'QG'
                  : reportMode === 'oms'
                    ? 'QM'
                    : reportMode === 'bwcallergy'
                      ? 'QBWCA'
                      : reportMode === 'bwccore'
                        ? 'QBWCE'
                        : reportMode === 'bwcdiamond'
                          ? 'QBWCU'
                          : 'QPT';
        setSuccessMessage(
          reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro'
            ? `Successfully saved ${confirmedReports.length} pet report(s) (biopet_report / biopet_report_healthy by health column).`
            : `Successfully saved ${confirmedReports.length} reports to database with panel: ${panelLabel}`
        );
        setShowSuccessDialog(true);
        
        // Clear any existing timeout
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
        }
        
        // Auto-close success popup after 2 seconds
        successTimeoutRef.current = setTimeout(() => {
          setShowSuccessDialog(false);
          setSuccessMessage('');
          successShownRef.current = false;
          successTimeoutRef.current = null;
          operationIdRef.current = null;
          globalSuccessFlag.current = false;
        }, 2000);
      }

      // Refresh the total report count
      fetchSummary();
    }, 1000);
  };

  // Add this ConfirmPopup component near the top of the file (after imports)
  const ConfirmPopup = ({ anchorRef, show, children }) => {
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    useEffect(() => {
      if (show && anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        setCoords({
          top: rect.top + window.scrollY - 10, // 10px above the button
          left: rect.left + window.scrollX + rect.width / 2,
        });
      }
    }, [show, anchorRef]);

    if (!show) return null;

    return ReactDOM.createPortal(
      <div style={{
        position: 'absolute',
        top: coords.top,
        left: coords.left,
        transform: 'translate(-50%, -100%)',
        background: 'rgba(34,34,34,0.92)', // semi-transparent black
        border: '1px solid #111',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        padding: '10px 18px',
        zIndex: 99999,
        fontSize: '14px',
        color: '#fff', // white font
        whiteSpace: 'nowrap'
      }}>
        {children}
      </div>,
      document.body
    );
  };

  const confirmBtnRefs = useRef([]);

    // Client ID Type Selection Popup
  const ClientIdTypePopup = () => (
    <div className="csv-popup-overlay" style={{ zIndex: 10000 }}>
      <div
        style={{
          maxWidth: '450px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '20px',
          margin: '60px auto',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3), 0 15px 20px rgba(0,0,0,0.2)',
          padding: '0',
          color: '#fff',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Header with gradient */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '30px 40px 20px',
          textAlign: 'center',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: '15px',
            right: '20px',
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            color: '#fff',
            fontSize: '18px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
          onClick={() => {
            setShowClientIdPopup(false);
            setSelectedClientIdIndex(null);
          }}
          >
            ×
          </div>
          
          <div style={{ 
            fontWeight: 700, 
            fontSize: '1.4rem', 
            marginBottom: 8,
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}>
            Select Client ID Type
          </div>
          <div style={{ 
            fontSize: '0.95rem', 
            opacity: 0.9,
            marginBottom: 5
          }}>
            Choose the type for your Client ID
          </div>
        </div>

        {/* Content */}
        <div style={{
          background: 'var(--bg-surface)',
          padding: '30px 40px',
          color: 'var(--text-primary)'
        }}>
          {/* Current Client ID Display */}
          <div style={{
            background: 'var(--bg-surface-2)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '25px',
            border: '2px solid #e9ecef',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '8px' }}>
              Current Client ID
            </div>
            <div style={{ 
              fontSize: '1.1rem', 
              fontWeight: 600, 
              color: 'var(--text-primary)',
              fontFamily: 'monospace',
              background: 'var(--bg-surface)',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #ddd'
            }}>
              {generatedClientIds[selectedClientIdIndex] || 'qpcr-000-000-01'}
            </div>
          </div>

          {/* Option Buttons */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '15px',
            marginBottom: '25px'
          }}>
            <button
              onClick={() => {
                setClientIdType(prev => ({
                  ...prev,
                  [selectedClientIdIndex]: 'qpcr'
                }));
                setShowClientIdPopup(false);
                setSelectedClientIdIndex(null);
              }}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '20px 15px',
                fontWeight: 600,
                fontSize: '1.1rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '1.2rem' }}>qpcr</div>
              <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '4px' }}>Quantitative PCR</div>
            </button>
            
            <button
              onClick={() => {
                setClientIdType(prev => ({
                  ...prev,
                  [selectedClientIdIndex]: '16s'
                }));
                setShowClientIdPopup(false);
                setSelectedClientIdIndex(null);
              }}
              style={{
                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '20px 15px',
                fontWeight: 600,
                fontSize: '1.1rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(40, 167, 69, 0.4)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(40, 167, 69, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(40, 167, 69, 0.4)';
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '1.2rem' }}>16s</div>
              <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '4px' }}>16S rRNA Gene</div>
            </button>
          </div>

          {/* Cancel Button */}
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => {
                setShowClientIdPopup(false);
                setSelectedClientIdIndex(null);
              }}
              style={{
                background: 'transparent',
                color: 'var(--text-muted)',
                border: '2px solid #e9ecef',
                borderRadius: '10px',
                padding: '12px 24px',
                fontWeight: 500,
                fontSize: '0.95rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                minWidth: '120px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f8f9fa';
                e.target.style.borderColor = '#adb5bd';
                e.target.style.color = '#495057';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = '#e9ecef';
                e.target.style.color = '#666';
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Confirmation dialog for 'Confirm all reports' - styled like delete popup
  const ConfirmAllDialog = () => (
    <div className="csv-popup-overlay" style={{ zIndex: 10000 }}>
      <div
        style={{
          maxWidth: '500px',
          background: 'var(--warning-soft)',
          border: '1.5px solid #e6c200',
          borderRadius: '8px',
          margin: '60px auto',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          padding: '24px 32px',
          color: 'var(--warning-text)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 4 }}>
              Warning alert!
            </div>
            <div style={{ fontSize: '1rem', color: 'var(--warning-text)', marginBottom: 18 }}>
              The data will be saved to the database, the supplement will be removed from stock, and you will not be able to modify the supplement anymore. Are you sure you want to continue?
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: 8 }}>
              <button
                onClick={() => {
                  setShowConfirmAllDialog(false);
                  handleConfirmAll();
                }}
                style={{
                  background: '#b48a00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 18px',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                Continue
              </button>
              <button
                onClick={() => setShowConfirmAllDialog(false)}
                style={{
                  background: 'none',
                  color: 'var(--warning-text)',
                  border: '1.5px solid #b48a00',
                  borderRadius: '4px',
                  padding: '8px 18px',
                  fontWeight: 500,
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Custom delete confirmation dialog styled as a warning alert
  const DeleteConfirmDialog = () => (
    <div className="csv-popup-overlay" style={{ zIndex: 10000 }}>
      <div
        style={{
          maxWidth: '500px',
          background: 'var(--warning-soft)',
          border: '1.5px solid #e6c200',
          borderRadius: '8px',
          margin: '60px auto',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          padding: '24px 32px',
          color: 'var(--warning-text)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 4 }}>
              Warning alert!
            </div>
            <div style={{ fontSize: '1rem', color: 'var(--warning-text)', marginBottom: 18 }}>
              Are you sure you want to delete this report? This action cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: 8 }}>
              <button
                onClick={confirmDeleteReport}
                style={{
                  background: '#b48a00',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 18px',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirmDialog(false);
                  setDeleteReportId(null);
                }}
                style={{
                  background: 'none',
                  color: 'var(--warning-text)',
                  border: '1.5px solid #b48a00',
                  borderRadius: '4px',
                  padding: '8px 18px',
                  fontWeight: 500,
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Success dialog styled with green tones - auto-closes after 2 seconds
  const SuccessDialog = () => {
    // Auto-close after 2 seconds
    useEffect(() => {
      if (showSuccessDialog) {
        const timer = setTimeout(() => {
          setShowSuccessDialog(false);
          setSuccessMessage('');
        }, 2000);
        
        return () => clearTimeout(timer);
      }
    }, [showSuccessDialog]);

    return (
      <div className="csv-popup-overlay" style={{ zIndex: 10000 }}>
        <div
          style={{
            maxWidth: '500px',
            background: 'var(--success-soft)',
            border: '1.5px solid #68d391',
            borderRadius: '8px',
            margin: '60px auto',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            padding: '24px 32px',
            color: 'var(--success-text)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 4 }}>
                Success!
              </div>
              <div style={{ fontSize: '1rem', color: 'var(--success-text)', marginBottom: 18 }}>
                {successMessage}
              </div>
            </div>
                  </div>
      </div>
      

    </div>
  );
};

  // Stock Condition Decision Modal
  const StockDecisionModal = () => (
    <div className="csv-popup-overlay" style={{ zIndex: 10000 }}>
      <div
        style={{
          maxWidth: '500px',
          background: 'var(--accent-soft)',
          border: '2px solidrgb(5, 47, 88)',
          borderRadius: '10px',
          margin: '60px auto',
          boxShadow: '0 2px 16px rgba(25, 118, 210, 0.15)',
          padding: '24px 32px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, color: 'var(--text-accent)', fontWeight: 'bold' }}>Stock Condition Decision (Advanced Settings)</h3>
          <button 
            onClick={() => setShowStockDecisionModal(false)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: 'var(--text-accent)'
            }}
          >
            ×
          </button>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <p style={{ marginBottom: '15px', color: 'var(--text-accent)' }}>
            Set the stock level thresholds for supplement selection and color:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: 'var(--text-accent)' }}>
                Stock Threshold (below this value, supplements will be excluded):
              </label>
              <input
                type="number"
                defaultValue={tempStockThreshold}
                ref={(input) => {
                  if (input) {
                    input.value = tempStockThreshold;
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #1976d2',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--bg-surface)'
                }}
                min="0"
                placeholder="Enter stock threshold"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: 'var(--text-accent)' }}>
                Medium stock (yellow color):
              </label>
              <input
                type="number"
                defaultValue={tempYellowThreshold}
                ref={(input) => {
                  if (input) {
                    input.value = tempYellowThreshold;
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #1976d2',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--bg-surface)'
                }}
                min="0"
                placeholder="Enter medium stock threshold"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: 'var(--text-accent)' }}>
                Low stock (red color):
              </label>
              <input
                type="number"
                defaultValue={tempRedThreshold}
                ref={(input) => {
                  if (input) {
                    input.value = tempRedThreshold;
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #1976d2',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  backgroundColor: 'var(--bg-surface)'
                }}
                min="0"
                placeholder="Enter low stock threshold"
              />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button 
            onClick={() => {
              setTempStockThreshold(stockThreshold); // Reset to original value
              setTempYellowThreshold(yellowThreshold);
              setTempRedThreshold(redThreshold);
              setShowStockDecisionModal(false);
            }}
            style={{
              padding: '10px 20px',
              border: '1.5px solid #1976d2',
              borderRadius: '5px',
              background: 'var(--bg-surface)',
              cursor: 'pointer',
              color: 'var(--text-accent)',
              fontWeight: '600'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={async () => {
              try {
                // Get all input values from the form
                const inputElements = document.querySelectorAll('input[type="number"]');
                const newStockThreshold = inputElements[0] ? parseInt(inputElements[0].value) || 0 : tempStockThreshold;
                const newYellowThreshold = inputElements[1] ? parseInt(inputElements[1].value) || 0 : tempYellowThreshold;
                const newRedThreshold = inputElements[2] ? parseInt(inputElements[2].value) || 0 : tempRedThreshold;
                
                // Save to database
                const token = localStorage.getItem('token');
                await axios.post(`${process.env.REACT_APP_API_BASE}/api/stock-condition`, 
                  { 
                    st_check: newStockThreshold,
                    st_yellow: newYellowThreshold,
                    st_red: newRedThreshold
                  },
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                
                // Update local state immediately
                setStockThreshold(newStockThreshold);
                setTempStockThreshold(newStockThreshold);
                setYellowThreshold(newYellowThreshold);
                setTempYellowThreshold(newYellowThreshold);
                setRedThreshold(newRedThreshold);
                setTempRedThreshold(newRedThreshold);
                
                // Close the modal
                setShowStockDecisionModal(false);
                
                // Show success message
                setSuccessMessage(`Stock thresholds updated: Exclude=${newStockThreshold}, Medium=${newYellowThreshold}, Low=${newRedThreshold}`);
                setShowSuccessDialog(true);
                
                // console.log('Stock thresholds saved successfully:', { newStockThreshold, newYellowThreshold, newRedThreshold });
              } catch (error) {
                console.error('Failed to save stock thresholds:', error);
                alert('Failed to save stock thresholds. Please try again.');
              }
            }}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '5px',
              background: '#1976d2',
              cursor: 'pointer',
              color: 'white',
              fontWeight: '600'
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  const BiopetFallbackModal = () => (
    <div className="csv-popup-overlay" style={{ zIndex: 10000 }}>
      <div
        style={{
          maxWidth: '560px',
          background: 'var(--accent-soft)',
          border: '2px solid rgb(5, 47, 88)',
          borderRadius: '10px',
          margin: '60px auto',
          boxShadow: '0 2px 16px rgba(25, 118, 210, 0.15)',
          padding: '24px 32px'
        }}
      >
        <h3 style={{ marginTop: 0, color: 'var(--text-accent)' }}>Biopet No-Lacking Supplement Setting</h3>
        <p style={{ color: 'var(--text-accent)', marginBottom: '16px' }}>
          Choose supplement behavior when report has no lacking probiotics.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[
            { id: 'petgard', label: 'PetGard' },
            { id: 'petreno', label: 'PetReno' },
            { id: 'petflow', label: 'PetFlow' },
            { id: 'auto-lowest', label: 'Autofill lowest probiotics stock (PetGard/PetReno/PetFlow)' }
          ].map((item) => (
            <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-accent)' }}>
              <input
                type="radio"
                name="biopet-fallback-mode"
                checked={tempBiopetFallbackMode === item.id}
                onChange={() => setTempBiopetFallbackMode(item.id)}
              />
              {item.label}
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
          <button
            onClick={() => setShowBiopetFallbackModal(false)}
            className="popup-btn secondary"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              setBiopetFallbackMode(tempBiopetFallbackMode);
              setShowBiopetFallbackModal(false);
            }}
            className="popup-btn primary"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  const BiopetStockDecisionModal = () => {
    const [localStockThreshold, setLocalStockThreshold] = useState(biopetStockThreshold);
    const [localYellowThreshold, setLocalYellowThreshold] = useState(biopetYellowThreshold);
    const [localRedThreshold, setLocalRedThreshold] = useState(biopetRedThreshold);

    return (
      <div className="csv-popup-overlay" style={{ zIndex: 10000 }}>
        <div
          style={{
            maxWidth: '500px',
            background: 'var(--accent-soft)',
            border: '2px solid rgb(5, 47, 88)',
            borderRadius: '10px',
            margin: '60px auto',
            boxShadow: '0 2px 16px rgba(25, 118, 210, 0.15)',
            padding: '24px 32px'
          }}
        >
          <h3 style={{ marginTop: 0, color: 'var(--text-accent)' }}>Biopet Stock Condition Decision</h3>
          <p style={{ color: 'var(--text-accent)' }}>This setting affects only Biopet stock section.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input type="number" value={localStockThreshold} onChange={(e) => setLocalStockThreshold(parseInt(e.target.value, 10) || 0)} placeholder="Exclude below (st_check)" />
            <input type="number" value={localYellowThreshold} onChange={(e) => setLocalYellowThreshold(parseInt(e.target.value, 10) || 0)} placeholder="Medium stock (yellow)" />
            <input type="number" value={localRedThreshold} onChange={(e) => setLocalRedThreshold(parseInt(e.target.value, 10) || 0)} placeholder="Low stock (red)" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <button onClick={() => setShowBiopetStockDecisionModal(false)} className="popup-btn secondary">Cancel</button>
            <button
              onClick={() => {
                setBiopetStockThreshold(localStockThreshold);
                setBiopetYellowThreshold(localYellowThreshold);
                setBiopetRedThreshold(localRedThreshold);
                try {
                  localStorage.setItem('biopet_stock_condition', JSON.stringify({
                    st_check: localStockThreshold,
                    st_yellow: localYellowThreshold,
                    st_red: localRedThreshold
                  }));
                } catch {
                  // ignore storage errors
                }
                setShowBiopetStockDecisionModal(false);
              }}
              className="popup-btn primary"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add at the top of the component
  const [previewingReport, setPreviewingReport] = useState(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);

  // Add at the top of the component
  const [pdfPreviewFormData, setPdfPreviewFormData] = useState(null);
  const [pdfPreviewReportMode, setPdfPreviewReportMode] = useState(null);
  const [pdfPreviewSupplements, setPdfPreviewSupplements] = useState([]);
  const [pdfPreviewThai, setPdfPreviewThai] = useState(false);

  return (
    <div className="dashboard-page">
      {/* Download Progress Bar (single-report legacy path) — themed dlp-* popup */}
      {showDownloadProgress && (
        <div className="dlp-overlay" role="dialog" aria-modal="true" aria-label="Generating PDF">
          <div className={`dlp-card${downloadProgress === 100 ? ' is-done' : ''}`}>
            <div className="dlp-icon">
              <Icon name="download" size={20} />
            </div>
            <h3 className="dlp-title">Generating PDF</h3>
            <p className="dlp-status">
              {downloadProgress < 20 && 'Preparing data…'}
              {downloadProgress >= 20 && downloadProgress < 40 && 'Loading libraries…'}
              {downloadProgress >= 40 && downloadProgress < 65 && 'Generating English PDF…'}
              {downloadProgress >= 65 && downloadProgress < 90 && 'Generating Thai PDF…'}
              {downloadProgress >= 90 && downloadProgress < 100 && 'Merging PDFs…'}
              {downloadProgress === 100 && 'Download completed'}
            </p>
            <div className="dlp-bar">
              <div className="dlp-bar-fill" style={{ width: `${downloadProgress}%` }} />
            </div>
            <div className="dlp-meta">
              <span className="dlp-detail" />
              <span className="dlp-pct">{downloadProgress}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Download / ZIP progress popup — themed dlp-* card (was the purple gradient modal) */}
      {showZipProgress && (
        <div className="dlp-overlay" role="dialog" aria-modal="true" aria-label="Download progress">
          <div className={`dlp-card${zipProgress === 100 ? ' is-done' : ''}`}>
            <div className="dlp-icon">
              <Icon name="download" size={20} />
            </div>
            <h3 className="dlp-title">
              {selectedReports.size > 1 ? 'Preparing ZIP Download' : 'Preparing Download'}
            </h3>
            <p className="dlp-status">{zipProgressText || 'Working…'}</p>
            <div className="dlp-bar">
              <div className="dlp-bar-fill" style={{ width: `${zipProgress}%` }} />
            </div>
            <div className="dlp-meta">
              <span className="dlp-detail" title={zipProgressDetails}>{zipProgressDetails}</span>
              <span className="dlp-pct">{zipProgress}%</span>
            </div>
            <button
              type="button"
              className="dlp-cancel"
              onClick={() => {
                // Set cancellation flag to stop all ongoing operations (immediate)
                isZipCancelledRef.current = true;
                
                // Hide progress modal immediately
                setShowZipProgress(false);
                setZipProgress(0);
                setZipProgressText('');
                setZipProgressDetails('');
                setBulkDownloadLoading(false);
                
                // Reset selected reports to clear the selection
                setSelectedReports(new Set());
                setSelectAllReports(false);
                
                // Ensure we're back to history view
                setViewingHistory(true);
                setShowHistory(true);
                
                console.log('ZIP download cancelled by user - cancellation flag set to:', isZipCancelledRef.current);
                
                // Force stop any ongoing operations
                setTimeout(() => {
                  if (isZipCancelledRef.current) {
                    console.log('Cancellation confirmed - all operations should be stopped');
                  }
                }, 100);
              }}
            >
              Cancel download
            </button>
          </div>
        </div>
      )}
      
      <a className="skip-link" href="#main">Skip to content</a>

      <div className="dashboard-header">
        <div className="dashboard-left">
          <img src={`${process.env.PUBLIC_URL}/bandge.png`} alt="Logo" className="dashboard-logo" />
          <div className="header-caption" aria-hidden="true">
            <span className="header-caption-k">Lab console</span>
            <span className="header-caption-v">Gut Health Reporting</span>
          </div>
        </div>
        <div className="dashboard-user-info">
          <span className="user-avatar"><Icon name="user" size={20} /></span>
          <div className="user-text">
            <div 
              className="user-name" 
              onClick={handlePasswordChange}
              style={{ cursor: 'pointer' }}
              title="Click to change password"
            >
              {user?.username || 'Loading...'}
            </div>
            <div className="user-role">
              {user?.role || '-'}{' '}
              <span className="logout-link" onClick={handleLogout}>(logout)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-layout">
        <nav className="rail" aria-label="Main">
          <ul className="rail__list">
          <li><button
            type="button"
            aria-label="Dashboard"
            aria-current={railView === 'home' ? 'page' : undefined}
            className={`rail__item ${railView === 'home' ? 'is-active' : ''}`}
            onClick={() => {
              setCreatingReport(false);
              setViewingHistory(false);
              setShowHistory(false);
              setHistoryClientPaging(false);
              setIsFiltering(false);
              setHistoryNewBadgeCounts({ single: 0, multiple: 0 });
              setViewingStorage(false);
              setViewingDatabase(false);
              setViewingAnalytics(false); setViewingFormScan(false);
              setViewingSetting(false);
              // Reset selected reports when closing history
              setSelectedReports(new Set());
              setSelectAllReports(false);
            }}
          >
            <Icon name="home" size={20} />
            <span className="rail__label">Dashboard</span>
          </button></li>
          {user && user.role !== 'user' ? (
            <li><button
              type="button"
              aria-label="Create Report"
              aria-current={railView === 'create' ? 'page' : undefined}
              className={`rail__item ${railView === 'create' ? 'is-active' : ''}`}
              onClick={() => {
                setFormData({});
                setCalculatedData({});
                setIsManualInput(false); // Reset manual input state when creating new report
                setCreatingReport(true);
                setViewingHistory(false);
                setShowHistory(false);
                setHistoryClientPaging(false);
                setIsFiltering(false);
                setViewingStorage(false);
                setViewingDatabase(false);
                setViewingAnalytics(false); setViewingFormScan(false);
                setViewingSetting(false);
                // Reset selected reports when creating new report
                setSelectedReports(new Set());
                setSelectAllReports(false);
                // Trigger animation
                setAnimationKey(prev => prev + 1);
              }}
            >
              <Icon name="file-plus" size={20} />
              <span className="rail__label">Create Report</span>
            </button></li>
          ) : null}
          <li><button
            type="button"
            aria-label="History"
            aria-current={railView === 'history' ? 'page' : undefined}
            className={`rail__item ${railView === 'history' ? 'is-active' : ''}`}
            onClick={() => {
              historyFetchReqIdRef.current += 1;
              setIsFiltering(true);
              setReportHistory([]);
              setGroupedReportHistory([]);
              setHistoryPeoplePages([]);
              setExpandedGroups(new Set());
              setPagination(prev => ({ ...prev, currentPage: 1, limit: 30 }));
              setReportNumberFilter('');
              setFilterValue('');
              setDebouncedFilter('');
              setSelectedReports(new Set());
              setSelectAllReports(false);
              setCreatingReport(false);
              setViewingHistory(true);
              setShowHistory(true);
              setHistoryEnterNonce(n => n + 1);
              setViewingStorage(false);
              setViewingDatabase(false);
              setViewingAnalytics(false); setViewingFormScan(false);
              setViewingSetting(false);
              setAnimationKey(prev => prev + 1);
            }}
          >
            <Icon name="clock" size={20} />
            <span className="rail__label">History</span>
          </button></li>
          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <li><button
              type="button"
              aria-label="Storage"
              aria-current={railView === 'storage' ? 'page' : undefined}
              className={`rail__item ${railView === 'storage' ? 'is-active' : ''}`}
              onClick={() => {
                setCreatingReport(false);
                setViewingHistory(false);
                setShowHistory(false);
                setHistoryClientPaging(false);
                setIsFiltering(false);
                setViewingStorage(true);
                setViewingDatabase(false);
                setViewingAnalytics(false); setViewingFormScan(false);
                setViewingSetting(false);
                // Reset selected reports when closing history
                setSelectedReports(new Set());
                setSelectAllReports(false);
              // Trigger animation
              setAnimationKey(prev => prev + 1);
              }}
            >
              <Icon name="package" size={20} />
              <span className="rail__label">Storage</span>
            </button></li>
          )}
          {user && (user.role === 'admin' || user.role === 'superadmin') ? (
            <li><button
              type="button"
              aria-label="Database"
              aria-current={railView === 'database' ? 'page' : undefined}
              className={`rail__item ${railView === 'database' ? 'is-active' : ''}`}
              onClick={() => {
                setCreatingReport(false);
                setViewingHistory(false);
                setShowHistory(false);
                setHistoryClientPaging(false);
                setIsFiltering(false);
                setViewingStorage(false);
                setViewingDatabase(true);
                setRawDbTable('st_report');
                setRawDbActiveButton('st_report');
                setViewingAnalytics(false); setViewingFormScan(false);
                setViewingSetting(false);
                // Reset selected reports when closing other views
                setSelectedReports(new Set());
                setSelectAllReports(false);
                // Trigger animation
                setAnimationKey(prev => prev + 1);
              }}
            >
              <Icon name="database" size={20} />
              <span className="rail__label">Database</span>
            </button></li>
          ) : null}
          <li><button
            type="button"
            aria-label="Data Analytics"
            aria-current={railView === 'analytics' ? 'page' : undefined}
            className={`rail__item ${railView === 'analytics' ? 'is-active' : ''}`}
            onClick={() => {
              setCreatingReport(false);
              setViewingHistory(false);
              setShowHistory(false);
              setHistoryClientPaging(false);
              setIsFiltering(false);
              setViewingStorage(false);
              setViewingDatabase(false);
              setViewingAnalytics(true); setViewingFormScan(false);
              setViewingSetting(false);
              // Reset selected reports when closing other views
              setSelectedReports(new Set());
              setSelectAllReports(false);
              // Trigger animation
              setAnimationKey(prev => prev + 1);
            }}
          >
            <Icon name="bar-chart" size={20} />
            <span className="rail__label">Analytics</span>
          </button></li>
          <li><button
            type="button"
            aria-label="Form Scan"
            aria-current={railView === 'formscan' ? 'page' : undefined}
            className={`rail__item ${railView === 'formscan' ? 'is-active' : ''}`}
            onClick={() => {
              setCreatingReport(false);
              setViewingHistory(false);
              setShowHistory(false);
              setHistoryClientPaging(false);
              setIsFiltering(false);
              setViewingStorage(false);
              setViewingDatabase(false);
              setViewingAnalytics(false);
              setViewingFormScan(true);
              setViewingSetting(false);
              setSelectedReports(new Set());
              setSelectAllReports(false);
              setAnimationKey(prev => prev + 1);
            }}
          >
            <Icon name="scan" size={20} />
            <span className="rail__label">Form Scan</span>
          </button></li>
          {(user?.role === 'admin' || user?.role === 'superadmin') && (
            <li><button
              type="button"
              aria-label="Setting"
              aria-current={railView === 'setting' ? 'page' : undefined}
              className={`rail__item ${railView === 'setting' ? 'is-active' : ''}`}
              onClick={() => {
                setCreatingReport(false);
                setViewingHistory(false);
                setShowHistory(false);
                setHistoryClientPaging(false);
                setIsFiltering(false);
                setViewingStorage(false);
                setViewingDatabase(false);
                setViewingAnalytics(false); setViewingFormScan(false);
                setViewingSetting(true);
                setSelectedReports(new Set());
                setSelectAllReports(false);
                setAnimationKey(prev => prev + 1);
              }}
            >
              <Icon name="settings" size={20} />
              <span className="rail__label">Settings</span>
            </button></li>
          )}
          </ul>
        </nav>

        <div className="dashboard-main" id="main">
          {/* Overview banner. In normal flow inside the content column, so it
              cannot overlap the rail at any rail width. */}
          <div className='title_image'>
            <div className='block_titletext'>
              <div className='text_title'>
                <Icon name="activity" size={14} className="title-icon" />
                Monitoring · reports on file
              </div>
              <div className="banner-primary">
                {Number(reportSummary.total || 0).toLocaleString()} reports
              </div>
            </div>
            <div className='text_report' aria-label="Report totals">
              <div className="rdb-readout rdb-readout--total">
                <span className="rdb-readout-k">Total</span>
                <span className="rdb-readout-v">{Number(reportSummary.total || 0).toLocaleString()}</span>
              </div>
              <div className="rdb-readout">
                <span className="rdb-readout-k">BioMed</span>
                <span className="rdb-readout-v">{Number(reportSummary.biomed || 0).toLocaleString()}</span>
              </div>
              <div className="rdb-readout">
                <span className="rdb-readout-k">BioPet</span>
                <span className="rdb-readout-v">{Number(reportSummary.biopetTotal || 0).toLocaleString()}</span>
              </div>
              <div className="rdb-readout">
                <span className="rdb-readout-k">Healthy</span>
                <span className="rdb-readout-v" style={{ color: 'var(--success-text)' }}>{Number(reportSummary.biopetHealthy || 0).toLocaleString()}</span>
              </div>
              <div className="rdb-readout">
                <span className="rdb-readout-k">Unhealthy</span>
                <span className="rdb-readout-v" style={{ color: 'var(--danger-text)' }}>{Number(reportSummary.biopetUnhealthy || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {creatingReport ? (
            <div key={animationKey} className="main_content_cr cr-lab fade-in">
              {/* Lab-style header shared with the other views (rdb-* primitives). */}
              {(() => {
                const panelCodes = {
                  QPT: 'QU', '9P': 'Q9', 'Allergy and Skin': 'QA', 'Allergy and Skin_kids': 'QI',
                  Toxin: 'QG', oms: 'QM', bwcallergy: 'QBWCA', bwccore: 'QBWCE', bwcdiamond: 'QBWCU',
                  'pet-care-pro': 'PET PRO', 'pet-care-essential': 'PET ESS'
                };
                const missing = reportMode ? getMissingPersonalInfoFields().length : 0;
                return (
                  <div className="rdb-header">
                    <div className="rdb-header-main">
                      <span className="rdb-microlabel">
                        <span className={`rdb-led${isCalculating ? ' rdb-led--busy' : reportMode ? '' : ' rdb-led--error'}`} aria-hidden="true" />
                        New report · {reportMode ? (panelCodes[reportMode] || reportMode) : 'no panel selected'}{isCalculating ? ' · calculating' : ''}
                      </span>
                      <h2 className="rdb-title font_web">Create New Report</h2>
                      <p className="rdb-title-sub">choose a panel, then import a CSV or fill the form by hand</p>
                    </div>
                    <div className="rdb-readouts" aria-label="Report form summary">
                      <div className="rdb-readout">
                        <span className="rdb-readout-k">Panel</span>
                        <span className="rdb-readout-v">{reportMode ? (panelCodes[reportMode] || reportMode) : '—'}</span>
                      </div>
                      <div className="rdb-readout">
                        <span className="rdb-readout-k">Sections</span>
                        <span className="rdb-readout-v">{formSections.length}</span>
                      </div>
                      <div className="rdb-readout">
                        <span className="rdb-readout-k">Missing</span>
                        <span className="rdb-readout-v" style={{ color: missing ? 'var(--danger-text)' : reportMode ? 'var(--success-text)' : undefined }}>
                          {reportMode ? missing : '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="cr-section-label">
                <span className="cr-step">01</span>
                <span className="rdb-microlabel">Report type</span>
              </div>
              <div className="report-type-layout">
                <div
                  className={`report-type-group-card${reportMode && !String(reportMode).startsWith('pet-care') ? ' is-active' : ''}`}
                  style={{ '--biomed-logo-url': `url(${process.env.PUBLIC_URL}/logo/biomed_logo.png)` }}
                >
                  <div className="report-type-group-header">
                    <span className="report-type-group-name">BioMed Panel</span>
                  </div>
                  <div className="report-type-button-row">
                    <button
                      className={`mode_btn syndrome-btn ${reportMode === 'QPT' ? 'active' : ''}`}
                      onClick={() => setReportMode('QPT')}
                    >
                      QU
                      <span className="syndrome-tooltip">QU - Ultimate Biome</span>
                    </button>

                    <button
                      className={`mode_btn syndrome-btn ${reportMode === '9P' ? 'active' : ''}`}
                      onClick={() => {
                        setReportMode('9P');
                        setFormData({});
                        setIsManualInput(false); // Reset manual input state when changing report mode
                      }}
                    >
                    Q9
                    <span className="syndrome-tooltip">Q9 Tailor-made Probiotics</span>  
                    </button>

                  <button
                    className={`mode_btn syndrome-btn ${reportMode === 'Allergy and Skin' ? 'active' : ''}`}
                    onClick={() => {
                      setReportMode('Allergy and Skin');
                      setFormData({});
                    setIsManualInput(false); // Reset manual input state when changing report mode
                    }}
                  >
                    QA
                    <span className="syndrome-tooltip">Allergy and Skin</span>                    
                  </button>

                  <button
                    className={`mode_btn syndrome-btn ${reportMode === 'Allergy and Skin_kids' ? 'active' : ''}`}
                    onClick={() => {
                      setReportMode('Allergy and Skin_kids');
                      setFormData({});
                      setIsManualInput(false);
                    }}
                  >
                    QI
                    <span className="syndrome-tooltip">Infant Allergy Biome Panel</span>
                  </button>

                  <button
                    className={`mode_btn syndrome-btn ${reportMode === 'Toxin' ? 'active' : ''}`}
                    onClick={() => {
                      setReportMode('Toxin');
                      setFormData({});
                    setIsManualInput(false); // Reset manual input state when changing report mode
                    }}
                  >
                    QG
                    <span className="syndrome-tooltip">Gastrointestinal Tract Toxin</span>                  
                  </button>

                  <button
                    className={`mode_btn syndrome-btn ${reportMode === 'oms' ? 'active' : ''}`}
                    onClick={() => {
                      setReportMode('oms');
                      setFormData({});
                    setIsManualInput(false); // Reset manual input state when changing report mode
                    }}
                  >
                    QM
                    <span className="syndrome-tooltip">Obesity and Metabolic syndrome</span>
                  </button>

                  <button
                    className={`mode_btn syndrome-btn ${reportMode === 'bwcallergy' ? 'active' : ''}`}
                    onClick={() => {
                      setReportMode('bwcallergy');
                      setFormData({});
                      setIsManualInput(false); // Reset manual input state when changing report mode
                    }}
                  >
                    QBWCA
                    <span className="syndrome-tooltip">BWC Allergy</span>
                  </button>

                  <button
                    className={`mode_btn syndrome-btn ${reportMode === 'bwccore' ? 'active' : ''}`}
                    onClick={() => {
                      setReportMode('bwccore');
                      setFormData({});
                      setIsManualInput(false); // Reset manual input state when changing report mode
                    }}
                  >
                    QBWCE
                    <span className="syndrome-tooltip">BWC Essentials</span>
                  </button>

                  <button
                    className={`mode_btn syndrome-btn ${reportMode === 'bwcdiamond' ? 'active' : ''}`}
                    onClick={() => {
                      setReportMode('bwcdiamond');
                      setFormData({});
                      setIsManualInput(false); // Reset manual input state when changing report mode
                    }}
                  >
                    QBWCU
                    <span className="syndrome-tooltip">BWC Comprehensive</span>
                  </button>
                  </div>
                </div>

                <div
                  className={`report-type-group-card report-type-group-card-pet${String(reportMode || '').startsWith('pet-care') ? ' is-active' : ''}`}
                  style={{ '--pet-logo-url': `url(${process.env.PUBLIC_URL}/logo/biopet_logo.png)` }}
                >
                  <div className="report-type-group-header">
                    <span className="report-type-group-name">BioPet Panel</span>
                  </div>
                  <div className="report-type-button-row">
                    <button
                      className={`mode_btn syndrome-btn ${reportMode === 'pet-care-pro' ? 'active' : ''}`}
                      onClick={() => {
                        setReportMode('pet-care-pro');
                        setFormData({});
                        setIsManualInput(false);
                      }}
                    >
                      Pet Care Professional Panel
                      <span className="syndrome-tooltip">Pet Care Professional Panel</span>
                    </button>
                    <button
                      className={`mode_btn syndrome-btn ${reportMode === 'pet-care-essential' ? 'active' : ''}`}
                      onClick={() => {
                        setReportMode('pet-care-essential');
                        setFormData({});
                        setIsManualInput(false);
                      }}
                    >
                      Pet Care Essential Panel
                      <span className="syndrome-tooltip">Pet Care Essential Panel</span>
                    </button>
                  </div>
                </div>
              </div>

              {!reportMode && (
                <div className="cr-empty">select a panel above to start a report</div>
              )}

                                {reportMode && (
                  <div className="cr-import">
                    <div className="cr-section-label">
                      <span className="cr-step">02</span>
                      <span className="rdb-microlabel">Import CSV</span>
                      <span className="rdb-microlabel" style={{ marginLeft: 'auto', letterSpacing: '0.04em', textTransform: 'none' }}>or fill the form below by hand</span>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".csv"
                      style={{ display: 'none' }}
                      onChange={(e) => handleCsvImport(e)}
                    />

                    {/* Drag and Drop Zone */}
                    <div 
                      className="drag-drop-zone"
                      onClick={() => {
                        if (!reportMode) {
                          alert('Please select type of format report before importing CSV file.');
                          return;
                        }
                        fileInputRef.current.click();
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.add('drag-over');
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('drag-over');
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('drag-over');
                        
                        if (!reportMode) {
                          alert('Please select type of format report before importing CSV file.');
                          return;
                        }

                        const files = Array.from(e.dataTransfer.files);
                        const csvFile = files.find(file => file.name.toLowerCase().endsWith('.csv'));
                        
                        if (csvFile) {
                          const event = { target: { files: [csvFile] } };
                          handleCsvImport(event);
                        } else {
                          alert('Please drop a CSV file.');
                        }
                      }}
                    >
                      <div className="drag-drop-content">
                        <div className="drag-drop-icon">
                          <img src={`${process.env.PUBLIC_URL}/draganddrop.png`} alt="Drag and Drop" />
                        </div>
                        <div className="drag-drop-text">
                          <strong>Drag & Drop CSV File Here</strong>
                          <span>or click here to browse files</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {(reportMode === 'QPT' || reportMode === '9P' || reportMode === 'Allergy and Skin' || reportMode === 'Allergy and Skin_kids' || reportMode === 'Toxin' || reportMode === 'oms'|| reportMode === 'bwcallergy' || reportMode === 'bwccore' || reportMode === 'bwcdiamond' || reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro') && (
                <>
                <div className="cr-section-label">
                  <span className="cr-step">03</span>
                  <span className="rdb-microlabel">Sample data</span>
                </div>
                <div className="create-report-form-wrapper">
                  {formSections.map((section, i) => (
                    <div
                      key={i}
                      className="create-report-form-section"
                    >
                      <h3 className="form-section-title title_color">{section.title}</h3>
                      {section.inputs.map((input, idx) => (
                        <React.Fragment key={input.label || idx}>
                          <div className="create-report-input-wrapper">
                            <label
                              className={[
                                'Bifidobacterium infantis',
                                'Enterococcus faecium',
                                'Lactobacillus acidophilus',
                                'Lactobacillus paracasei',
                                'Lactobacillus plantarum',
                                'Lactobacillus rhamnosus',
                                'Streptococcus thermophilus',
                                'Faecalibacterium',
                                'Peptacetobacter hiranomis',
                                'Escherichia coli',
                                'Streptococcus'
                              ].includes(input.label) ? 'bacteria-italic-label' : ''}
                            >
                              {input.label}
                              {section.title.toLowerCase().includes('personal information') && 
                               input.label !== 'Collection Date' && (
                                <span className="required-field">*</span>
                              )}
                            </label>
                            {input.type === 'select' ? (
                              <select
                                value={input.type === 'date'
                                  ? formatToDateInput(formData[input.label])
                                  : formData[input.label] || ''}
                                onChange={(e) => {
                                  const selectedValue = e.target.value;
                                  setFormData((prev) => {
                                    const next = {
                                      ...prev,
                                      [input.label]: selectedValue
                                    };

                                    // Keep pet mode flags mutually exclusive:
                                    // Yes on one side auto-sets the other side to No.
                                    if (input.label === 'calculate' && selectedValue === 'Yes') {
                                      next.uncalculate = 'No';
                                    } else if (input.label === 'uncalculate' && selectedValue === 'Yes') {
                                      next.calculate = 'No';
                                    }

                                    return next;
                                  });
                                  setIsManualInput(true); // Mark as manual input when user types
                                  console.log('Select input changed - setting isManualInput to true');
                                }}
                              >
                                {input.options.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={input.type}
                                onWheel={(e) => e.target.blur()}
                                placeholder={input.placeholder || ''}
                                value={input.type === 'date'
                                  ? formatToDateInput(formData[input.label])
                                  : formData[input.label] || ''}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    [input.label]: e.target.value
                                  }));
                                  setIsManualInput(true); // Mark as manual input when user types
                                  console.log('Input field changed - setting isManualInput to true');
                                }}
                                step={input.step}
                              />
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  ))}
                </div>
                </>
              )}

              {reportMode && (
                <div className="cr-footer">
                  <div>
                    <div className="rdb-microlabel">
                      <span className="required-field" style={{ margin: 0 }}>*</span> required in Personal Information before calculation
                    </div>
                    {!isPersonalInfoComplete() && getMissingPersonalInfoFields().length > 0 ? (
                      <div className="cr-missing">
                        missing · <b>{getMissingPersonalInfoFields().join(', ')}</b>
                      </div>
                    ) : (
                      <div className="cr-ready">personal information complete · ready to calculate</div>
                    )}
                  </div>
                <div className="form-actions">
                  {isCalculating && (
                    <div className="calc-overlay">
                      <div className="calc-center">
                        <div className="loading-overlay">
                          <div className="loading-spinner"style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid rgb(32, 184, 40)',
                            // backgroundColor: 'var(--bg-surface-2)',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></div>
                        </div>
                        <p className="calc-status-text">{calcStatusText}</p>
                      </div>
                    </div>
                  )}
                  <button 
                    className={`submit-button ${!isPersonalInfoComplete() ? 'disabled' : ''}`}
                    onClick={handleCalculate}
                    disabled={!isPersonalInfoComplete()}
                    title={!isPersonalInfoComplete() ? 'Please fill in all personal information fields first' : ''}
                  >
                    Calculate
                  </button>
                  <button
                    className="cancel-button font_web"
                    onClick={() => {
                      setCreatingReport(false);
                      setReportMode(null);
                      setViewingStorage(false);
                      setTimeout(() => {
                        const mainContent = document.querySelector('.main_content');
                        if (mainContent) {
                          mainContent.scrollTop = 0;
                          mainContent.classList.remove('fade-in');
                          if (mainContent) {
                            void mainContent.offsetWidth;
                          }
                          mainContent.classList.add('fade-in');
                        }
                      }, 0);
                    }}
                  >
                    Back
                  </button>
                </div>
                </div>
              )}

              {showCsvPopup && (
                <div className="csv-popup-overlay">
                  <div className="csv-popup-content modern" style={{ maxWidth: '80%', maxHeight: '75%' }}>
                    <div className="csv-popup-header" style={{ position: 'relative' }}>
                      <>
                        <div className="csv-head-row">
                          <h3>{isManualInput ? 'Manually Input Preview' : 'CSV Import Preview'}</h3>

                          {/* Toggle Switch */}
                          <div className="csv-control" style={{ marginRight: '36px' }}>
                            <span className="csv-control-k">Business</span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={csvPreviewToggle}
                              aria-label="Toggle B2B / B2C"
                              className={`csv-switch${csvPreviewToggle ? ' is-on' : ''}${(reportMode === '9P' || reportMode === 'QPT') ? '' : ' is-locked'}`}
                              onClick={() => {
                                if (reportMode === '9P' || reportMode === 'QPT') {
                                  setCsvPreviewToggle(!csvPreviewToggle);
                                }
                              }}
                            />
                            <span className="csv-switch-label">{csvPreviewToggle ? 'B2B' : 'B2C'}</span>
                          </div>
                        </div>

                        <div className="csv-controls">
                        {/* Supplement formula version — segmented toggle */}
                        {panelSupportsNewFormula(reportMode) && (
                          <div className="csv-control">
                            <span className="csv-control-k">Calculation</span>
                            <div className="rdb-rack-tabs">
                              {[
                                { key: 'new', label: 'New formula' },
                                { key: 'old', label: 'Original' }
                              ].map((opt) => {
                                const active = supplement9PVersion === opt.key;
                                return (
                                  <button
                                    key={opt.key}
                                    type="button"
                                    className={`rdb-tab${active ? ' is-active' : ''}`}
                                    onClick={() => {
                                      if (supplement9PVersion === opt.key) return;
                                      setSupplement9PVersion(opt.key);
                                      // Sync the calc module now (the effect runs
                                      // after this handler) then recompute the
                                      // preview: the two versions use different
                                      // products, so results/durations must reset.
                                      set9PSupplementVersion(opt.key);

                                      // Switching formula changes the products
                                      // entirely, so reset plan / duration /
                                      // supplement / confirmation selections back
                                      // to "before choosing", then recompute.
                                      setSelectedPlans({});
                                      setPlanAResults({});
                                      setPlanBResults({});
                                      setSelectedSupplements({});
                                      setSupplementResults({});
                                      setCoverageResults({});
                                      setManualSupplements({});
                                      setSupplementDurations({});
                                      setAppliedDurations({});
                                      setConfirmedRows({});
                                      setAllConfirmed(false);

                                      if (csvPreviewRows && csvPreviewRows.length > 0) {
                                        processCsvRowsWithProgress(csvPreviewRows);
                                      }
                                    }}
                                  >
                                    {opt.label}
                                  </button>
                                );
                              })}
                            </div>
                            <span className="csv-control-hint">
                              {supplement9PVersion === 'old'
                                ? (reportMode === '9P' ? 'BioAll / BioMeta / BioZen' : 'Original products')
                                : 'Single-strain SKUs'}
                            </span>
                          </div>
                        )}

                        <div className="csv-control">
                          <span className="csv-control-k">Supplements available</span>
                          <span className="csv-control-hint">
                          on {(() => {
                            if (reportMode === 'pet-care-pro') return 'Pet Care Professional Panel';
                            if (reportMode === 'pet-care-essential') return 'Pet Care Essential Panel';
                            const panelNames = {
                              'QPT': 'QU',
                              '9P': 'Q9',
                              'oms': 'QM',
                              'Allergy and Skin': 'QA',
                              'Allergy and Skin_kids': 'QI',
                              'Toxin': 'QG',
                              'bwcallergy': 'QBWCA',
                              'bwccore': 'QBWCC',
                              'bwcdiamond': 'QBWCD',
                              'BWC-A': 'QBWCA',
                              'BWC-C': 'QBWCC',
                              'BWC-D': 'QBWCD'
                            };
                            const code = panelNames[reportMode] || reportMode;
                            return `${code} panel`;
                          })()} · <b style={{ color: 'var(--rdb-brand-deep)' }}>{Object.keys(panelSupplementStock).length}</b>
                          </span>
                        </div>
                        </div>

                        {/* Auto-selection Summary */}
                        {(() => {
                          const autoSelectedRows = csvPreviewRows.filter((_, index) => {
                            const autoSelectedDuration = appliedDurations[index];
                            return autoSelectedDuration && Object.keys(autoSelectedDuration).length > 0;
                          }).length;
                          
                          const autoConfirmedRows = csvPreviewRows.filter((_, index) => {
                            const autoSelectedDuration = appliedDurations[index];
                            const isConfirmed = confirmedRows[index];
                            return autoSelectedDuration && Object.keys(autoSelectedDuration).length > 0 && isConfirmed;
                          }).length;
                          
                          if (autoSelectedRows > 0) {
                            return (
                              <div className="rdb-note" style={{ margin: 'var(--sp-3) 0 0', borderLeftColor: 'var(--success)' }}>
                                <span className="rdb-note-tag" style={{ color: 'var(--success-text)' }}>Auto</span>
                                <span>
                                  <strong>{autoSelectedRows}</strong> row{autoSelectedRows > 1 ? 's' : ''} with auto-filled supplement durations (set when plan was chosen)
                                  {autoConfirmedRows > 0 && (
                                    <div style={{ marginTop: '2px', color: 'var(--success-text)' }}>
                                      <strong>{autoConfirmedRows}</strong> row{autoConfirmedRows > 1 ? 's' : ''} auto-confirmed and ready for processing
                                    </div>
                                  )}
                                </span>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {(reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro') &&
                          Object.keys(panelSupplementStock).length > 0 && (
                            <div
                              style={{
                                marginTop: '10px',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center'
                              }}
                            >
                              <button
                                type="button"
                                className="popup-btn secondary font_web"
                                onClick={async () => {
                                  setShowQuartileModal(true);
                                  setQuartileModalLoading(true);
                                  try {
                                    await loadQuartileGrid();
                                  } finally {
                                    setQuartileModalLoading(false);
                                  }
                                }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  fontWeight: 500
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden
                                >
                                  <circle cx="12" cy="12" r="3" />
                                  <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                                </svg>
                                Modify Quartile Value
                              </button>
                            </div>
                          )}
                        
                        {/* Combined Stock Count Display with Centered Downward Arrows */}
                        {Object.keys(panelSupplementStock).length > 0 && (
                          <div className="csv-stock-strip">
                            <div className="csv-stock-strip-k">Stock · before → after this batch</div>
                            <div className="csv-stock-row">
                              {Object.keys(panelSupplementStock).map((supplement) => {
                                const beforeStock = panelSupplementStock[supplement];
                                const afterStock = updatedSupplementStock[supplement] ?? beforeStock;
                                const reduction = beforeStock - afterStock;
                                const hasReduction = reduction > 0;
                                // Same thresholds the old inline colours used.
                                const chipStatus = (v) => (v <= redThreshold ? ' is-critical' : v <= yellowThreshold ? ' is-low' : '');
                                return (
                                  <div key={supplement} className="csv-stock-col">
                                    <div className={`csv-stock-chip${chipStatus(beforeStock)}`}>
                                      <span>{getSupplementDisplayName(supplement)}</span>
                                      <b>{beforeStock}</b>
                                    </div>
                                    <div className={`csv-stock-arrow${hasReduction ? ' is-down' : ''}`}>
                                      {hasReduction ? `−${reduction}` : '±0'}
                                    </div>
                                    <div className={`csv-stock-chip${chipStatus(afterStock)}`}>
                                      <span>{getSupplementDisplayName(supplement)}</span>
                                      <b>{afterStock}</b>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                      <button
                        onClick={() => {
                          setShowCsvPopup(false);
                          setCsvPreviewRows([]);
                          setCsvFileName('');
                          setConfirmedRows({});
                          setLoadingConfirm({});
                          setAllConfirmLoading(false);
                          setAllConfirmed(false);
                          setManualSupplements({});
                          setSupplementResults({});
                          setFdaCheckboxes({});
                          setFromCsvPreview(false); // Reset this so manual input logic works correctly
                          
                          // Check if there's manual form data and set isManualInput accordingly
                          const hasManualData = Object.keys(formData).length > 0 && 
                            Object.values(formData).some(value => value && value.toString().trim() !== '');
                          setIsManualInput(hasManualData);
                        }}
                        type="button"
                        className="csv-popup-close"
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>

                    <div className="csv-popup-body" style={{
                      maxHeight: '60vh',
                      overflowY: 'auto'
                    }}>
                      <table
                        className={[
                          'csv-popup-table',
                          reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro'
                            ? 'csv-popup-table--pet-csv'
                            : '',
                          reportMode === 'pet-care-pro' ? 'csv-popup-table--pet-essential-csv' : ''
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <thead>
                          <tr>
                            <th>No.</th>
                            {reportMode !== 'pet-care-pro' && <th>File Name</th>}
                            <th>Full Name</th>
                            <th>Report Number</th>
                            <th>Action</th>
                            <th>
                              {reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro'
                                ? 'Age'
                                : 'Client ID'}
                            </th>
                            {(reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro') && (
                              <th
                                style={{
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  textDecoration: petHealthCsvColumnShowRaw ? 'underline' : undefined
                                }}
                                title="Click to show raw CSV health for every Unhealthy row; click again to show Unhealthy"
                                onClick={() => setPetHealthCsvColumnShowRaw((v) => !v)}
                              >
                                Health
                                <div
                                  style={{
                                    fontSize: '10px',
                                    color: 'var(--text-muted)',
                                    fontWeight: 'normal',
                                    marginTop: '2px',
                                    lineHeight: 1.2
                                  }}
                                >
                                  Click header to toggle raw CSV (Unhealthy rows)
                                </div>
                              </th>
                            )}
                            <th style={{ width: '150px' }}>Supplement</th>
                            <th style={{ width: '140px' }}>
                              Amt of Supp.
                              <div style={{
                                fontSize: '10px',
                                color: 'var(--text-muted)',
                                fontWeight: 'normal',
                                marginTop: '2px'
                              }}>
                                Shows auto-filled durations
                              </div>
                            </th>
                            <th style={{ width: '120px' }}>Partner</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvPreviewRows.map((row, index) => {
                            // Check if this row has auto-selected durations (set when plan was chosen)
                            const autoSelectedDuration = appliedDurations[index];
                            const hasAutoDuration = autoSelectedDuration && Object.keys(autoSelectedDuration).length > 0;
                            
                            return (
                            <tr key={index} style={{
                              backgroundColor: 'transparent',
                              borderLeft: 'none'
                            }}>
                              <td>{index + 1}</td>
                              {reportMode !== 'pet-care-pro' && <td>{csvFileName}</td>}
                              <td>{row['Full Name'] || row['petname'] || '-'}</td>
                              <td>{row['Report Number'] || row['order_number'] || '-'}</td>
                              <td>
                                <button
                                  className={`preview-button font_web mouse ${!selectedPlans[index] ? 'disabled' : ''}`}
                                  onClick={() => {
                                    // Check if a plan has been selected for this row
                                    if (!selectedPlans[index]) {
                                      alert('Please select a plan first before previewing this report.');
                                      return;
                                    }

                                    // Pet Care Professional uses static PNG preview only for now.
                                    if (reportMode === 'pet-care-pro') {
                                      setPreviewRowIndex(index);
                                      setFormData({ ...row });
                                      setFromCsvPreview(true);
                                      setVersionOptions({ eng: true, thai: false });
                                      setIsPdfReady(true);
                                      setShowCsvPopup(false);
                                      setShowStaticPdf(true);
                                      return;
                                    }
                                    
                                    setPreviewRowIndex(index);
                                    // Normalize row keys for the correct format
                                    const configInfo =
                                      reportMode === '9P' ? choosing_supplement_9P[0].info :
                                      reportMode === 'oms' ? choosing_supplement_oms[0].info :
                                      reportMode === 'Allergy and Skin' ? choosing_supplement_AMG[0].info : reportMode === 'Allergy and Skin_kids' ? qpt_choosing_supplement[0].info :
                                      reportMode === 'Toxin' ? choosing_supplement_toxin[0].info :
                                      ['bwcallergy', 'bwccore', 'bwcdiamond', 'BWC-A', 'BWC-C', 'BWC-D'].includes(reportMode) ? choosing_supplement_bwc[0].info :
                                      qpt_choosing_supplement[0].info;
                                    const normalizedRow = normalizeRowKeys(row, configInfo);
                                    let supplements;
                                    // Use the latest supplement results from supplementResults
                                    if (supplementResults[index]) {
                                      supplements = supplementResults[index];
                                    } else {
                                                                          const excludedSupplements = getFdaExcludedSupplements(index);
                                    const durationExclusions = appliedDurations[index] || {};
                                    const result = getRecommendedSupplements(
                                      normalizedRow,
                                      reportMode,
                                      stock,
                                      excludedSupplements,
                                      stockThreshold,
                                      durationExclusions,
                                      true,
                                      reportMode === 'pet-care-pro' ? quartileGridBySpecies : null
                                    );
                                      supplements = result.supplements;
                                      setSupplementResults(prev => ({ ...prev, [index]: result.supplements }));
                                      setCoverageResults(prev => ({ ...prev, [index]: result.coverage }));
                                    }
                                    setFromCsvPreview(true);
                                    setCalcStatusText('Preparing PDF...');
                                    setIsCalculating(true);
                                    setIsPdfReady(false);
                                    setShowStaticPdf(false);

                                    setTimeout(() => {
                                      // Get the current duration for this row
                                      const rowDurations = supplementDurations[index] || {};
                                      const firstSupplement = supplements[0];
                                      const currentDuration = firstSupplement ? rowDurations[firstSupplement] : null;
                                      
                                      // Normalize the CSV row data similar to how history preview does it
                                      // This ensures consistent data format between CSV and history preview
                                      let configInfo;
                                      if (reportMode === '9P') configInfo = choosing_supplement_9P[0].info;
                                      else if (reportMode === 'oms') configInfo = choosing_supplement_oms[0].info;
                                      else if (reportMode === 'Allergy and Skin') configInfo = choosing_supplement_AMG[0].info;
                                      else if (reportMode === 'Allergy and Skin_kids') configInfo = qpt_choosing_supplement[0].info;
                                      else if (reportMode === 'Toxin') configInfo = choosing_supplement_toxin[0].info;
                                      else if (['bwcallergy', 'bwccore', 'bwcdiamond'].includes(reportMode)) configInfo = choosing_supplement_bwc[0].info;
                                      else configInfo = qpt_choosing_supplement[0].info;
                                      
                                      // Normalize the row data
                                      const normalizedRow = normalizeRowKeys(row, configInfo);
                                      
                                      // Add all bacteria from bacteriaMetaConfig with value 0 as default
                                      // This ensures CSV data has the same structure as database data
                                      const allBacteriaDefaults = {};
                                      Object.keys(bacteriaMetaConfig).forEach(bacteriaName => {
                                        allBacteriaDefaults[bacteriaName] = 0;
                                      });
                                      
                                      // Create formData with bacteria defaults, then overlay normalized CSV data
                                      const formDataWithDuration = {
                                        ...allBacteriaDefaults,  // All bacteria default to 0
                                        ...normalizedRow,        // Override with actual CSV values
                                        duration: currentDuration
                                      };
                                      
                                      console.log('🔍 CSV Preview - Setting normalized formData:', formDataWithDuration);
                                      console.log('🔍 CSV Preview - Bacteria values check:');
                                      console.log('  Universal bacteria:', formDataWithDuration['Universal bacteria'], typeof formDataWithDuration['Universal bacteria']);
                                      console.log('  Bifidobacterium bifidum:', formDataWithDuration['Bifidobacterium bifidum'], typeof formDataWithDuration['Bifidobacterium bifidum']);
                                      console.log('🔍 CSV Preview - Total keys after adding defaults:', Object.keys(formDataWithDuration).length);
                                      
                                      setFormData(formDataWithDuration);

                                      // Wait longer for React state to fully update before showing modal
                                      setTimeout(() => {
                                        setIsPdfReady(true);
                                        setShowCsvPopup(false);
                                        setIsCalculating(false);
                                        setVersionOptions({ eng: true, thai: false });
                                        setShowStaticPdf(true);
                                      }, 300);
                                    }, 100);
                                  }}
                                  disabled={!selectedPlans[index]}
                                  onMouseEnter={(e) => {
                                    if (!selectedPlans[index]) {
                                      showCustomTooltip(e, 'Please select a plan first before previewing this report');
                                    }
                                  }}
                                  onMouseLeave={hideCustomTooltip}
                                  style={{
                                    opacity: selectedPlans[index] ? 1 : 0.5,
                                    cursor: selectedPlans[index] ? 'pointer' : 'not-allowed'
                                  }}
                                >
                                  Preview
                                </button>
                              </td>
                              <td
                                style={
                                  reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro'
                                    ? { fontWeight: '500', color: 'var(--text-primary)' }
                                    : {
                                        cursor: 'pointer',
                                        color: 'var(--text-accent)',
                                        textDecoration: 'underline',
                                        fontWeight: '500'
                                      }
                                }
                                onClick={() => {
                                  if (reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro') {
                                    return;
                                  }
                                  setSelectedClientIdIndex(index);
                                  setShowClientIdPopup(true);
                                }}
                                title={
                                  reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro'
                                    ? 'Life stage from age column (years)'
                                    : 'Click to change Client ID type'
                                }
                              >
                                {reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro'
                                  ? (() => {
                                      const rawAge = row.age ?? row.Age;
                                      const stage = petAgeYearsToLifeStageLabel(rawAge);
                                      const num = Number(rawAge);
                                      if (Number.isFinite(num)) {
                                        return `${stage} (${num})`;
                                      }
                                      return stage;
                                    })()
                                  : generatedClientIds[index] || '01-000-000-01'}
                              </td>
                              {(reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro') && (
                                <td style={{ verticalAlign: 'middle', whiteSpace: 'normal' }}>
                                  <PetHealthCsvPreviewCell
                                    row={row}
                                    revealUnhealthyRaw={petHealthCsvColumnShowRaw}
                                  />
                                </td>
                              )}
                              <td>
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                  {(() => {
                                    // Calculate below threshold supplements for this row
                                    const latestStock = calculateUpdatedStock();
                                    const supplements = supplementResults[index] || [];
                                    const belowThresholdSupplements = supplements.filter(supplement => {
                                      const currentStock = latestStock[supplement] || 0;
                                      return currentStock < stockThreshold;
                                    });
                                    
                                    // Check if duration was auto-selected when plan was chosen
                                    const autoSelectedDuration = appliedDurations[index];
                                    const hasAutoDuration = autoSelectedDuration && Object.keys(autoSelectedDuration).length > 0;
                                    
                                    return (
                                      <div style={{ position: 'relative' }}>
                                      <button
                                        ref={el => confirmBtnRefs.current[index] = el}
                                        className="modify-button font_web"
                                        onClick={() => selectedPlans[index] !== 'No supp. Need' && handleSupplementClick(null, index)}
                                        disabled={loadingConfirm[index] || selectedPlans[index] === 'No supp. Need'}
                                          onMouseEnter={() => {
                                            if (selectedPlans[index] !== 'No supp. Need') {
                                              console.log('Hovering button:', index);
                                              setHoveredSelectButton(index);
                                            }
                                          }}
                                          onMouseLeave={() => {
                                            console.log('Leaving button:', index);
                                            setHoveredSelectButton(null);
                                          }}
                                        style={{
                                            backgroundColor: selectedPlans[index] === 'No supp. Need' ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
                                            color: selectedPlans[index] === 'No supp. Need' ? '#999999' : '#213555',
                                            border: 'none',
                                          padding: '8px 12px',
                                          // borderRadius: '4px',
                                          cursor: selectedPlans[index] === 'No supp. Need' ? 'not-allowed' : 'pointer',
                                          fontSize: '14px',
                                          transition: 'all 0.3s ease',
                                          width: '130px',
                                            fontWeight: 'normal',
                                          // boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px'
                                        }}
                                      >
                                        {loadingConfirm[index] ? (
                                          <div className="loading-spinner" style={{
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid #f3f3f3',
                                            borderTop: '2px solid #3498db',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                          }} />
                                        ) : hasAutoDuration ? (
                                          <span style={{ fontSize: '12px', color: 'var(--success-text)' }}>
                                            {selectedPlans[index] === 'No supp. Need' ? 'None' : selectedPlans[index] || 'Select'}
                                          </span>
                                        ) : (
                                            selectedPlans[index] === 'No supp. Need' ? 'None' : selectedPlans[index] || 'Select'
                                        )}
                                      </button>
                                        
                                        {/* Hover Tooltip showing supplement plans */}
                                        {hoveredSelectButton === index && !loadingConfirm[index] && selectedPlans[index] !== 'No supp. Need' && (
                                          <div style={{
                                      position: 'fixed',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                            backgroundColor: 'var(--bg-surface)',
                                            backdropFilter: 'blur(10px)',
                                            WebkitBackdropFilter: 'blur(10px)',
                                        color: 'var(--text-primary)',
                                            padding: '24px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            whiteSpace: 'nowrap',
                                            zIndex: 9999999,
                                            boxShadow: 'var(--sh-lg)',
                                            minWidth: '250px',
                                            maxWidth: '350px',
                                            pointerEvents: 'none',
                                            border: '1px solid var(--border-default)'
                                          }}>
                                      {(() => {
                                              const selectedPlan = selectedPlans[index];
                                              const planAResult = planAResults[index];
                                              const planBResult = planBResults[index];
                                              const autoSelectedDuration = appliedDurations[index];
                                              const hasAutoDuration = autoSelectedDuration && Object.keys(autoSelectedDuration).length > 0;
                                              
                                              if (hasAutoDuration) {
                                                const selectedPlan = selectedPlans[index];
                                                const planAResult = planAResults[index];
                                                const planBResult = planBResults[index];
                                                
                                                // Show selected plan's supplements instead of duration
                                                let supplements = [];
                                                let planName = '';
                                                let planColor = '#3498db';
                                                
                                                if (selectedPlan === 'Plan A') {
                                                  supplements = planAResult?.supplements || [];
                                                  planName = 'Plan A - All Supplements';
                                                  planColor = '#2E7D32';
                                                } else if (selectedPlan === 'Plan B') {
                                                  supplements = planBResult?.supplements || [];
                                                  planName = 'Plan B - Stock-Aware';
                                                  planColor = '#1976D2';
                                                } else if (selectedPlan === 'Plan C') {
                                                  supplements = Object.keys(selectedSupplements).filter(supplement => selectedSupplements[supplement]);
                                                  planName = 'Plan C - Manual Selection';
                                                  planColor = '#E65100';
                                                }
                                                
                                                return (
                                                  <>
                                                    <div style={{
                                                      fontWeight: 'bold', 
                                                      textAlign: 'center',
                                                      fontSize: '14px',
                                                      color: planColor,
                                                      marginBottom: '12px',
                                                      marginLeft: '8px',
                                                      marginRight: '8px'
                                                    }}>
                                                      🎯 {planName}
                                                    </div>
                                                    <div style={{
                                                      textAlign: 'center',
                                                      fontSize: '12px',
                                                      color: 'var(--text-muted)',
                                                      marginBottom: '8px'
                                                    }}>
                                                      <strong>Supplements:</strong> {supplements.length} selected
                                                    </div>
                                                    {supplements.length > 0 && (
                                                      <div style={{
                                                        marginTop: '12px',
                                                        maxHeight: '120px',
                                                        overflowY: 'auto',
                                                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                                        borderRadius: '6px',
                                                        padding: '8px',
                                                        fontSize: '11px'
                                                      }}>
                                                        {supplements.slice(0, 5).map((supplement, idx) => (
                                                          <div key={idx} style={{
                                                            padding: '2px 0',
                                                            borderBottom: idx < Math.min(supplements.length, 5) - 1 ? '1px solid rgba(25, 118, 210, 0.2)' : 'none',
                                                            color: 'var(--text-primary)'
                                                          }}>
                                                            {getSupplementDisplayName(supplement)}
                                                          </div>
                                                        ))}
                                                        {supplements.length > 5 && (
                                                          <div style={{
                                                            padding: '2px 0', 
                                                            fontStyle: 'italic',
                                                            color: 'var(--text-accent)'
                                                          }}>
                                                            +{supplements.length - 5} more...
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                    <div style={{
                                                      textAlign: 'center',
                                                      fontSize: '11px',
                                                      color: 'var(--text-muted)',
                                                      fontStyle: 'italic',
                                                      marginTop: '8px'
                                                    }}>
                                                      Duration auto-filled from CSV
                                                    </div>
                                                  </>
                                                );
                                              } else if (!selectedPlan) {
                                          return (
                                                  <>
                                            <div style={{
                                              fontWeight: 'bold', 
                                              textAlign: 'center',
                                              fontSize: '14px',
                                              color: 'var(--text-accent)',
                                              marginBottom: '12px',
                                              marginLeft: '8px',
                                              marginRight: '8px'
                                            }}>
                                              Need to choose plan first
                                            </div>
                                              <div style={{
                                              fontSize: '12px', 
                                              opacity: 0.9, 
                                              textAlign: 'center',
                                              lineHeight: '1.4',
                                                color: 'var(--text-muted)',
                                              marginLeft: '8px',
                                              marginRight: '8px'
                                              }}>
                                              Click to select supplement plans first
                                              </div>
                                                  </>
                                                );
                                              }
                                              
                                              // Show selected plan's supplements
                                              let supplements = [];
                                              let planName = '';
                                              let planColor = '#3498db';
                                              
                                              if (selectedPlan === 'Plan A') {
                                                supplements = planAResult?.supplements || [];
                                                planName = 'Plan A - All Supplements';
                                                planColor = '#2E7D32';
                                              } else if (selectedPlan === 'Plan B') {
                                                supplements = planBResult?.supplements || [];
                                                planName = 'Plan B - Stock-Aware';
                                                planColor = '#1976D2';
                                              } else if (selectedPlan === 'Plan C') {
                                                // Get manually selected supplements for this row
                                                supplements = Object.keys(selectedSupplements).filter(supplement => selectedSupplements[supplement]);
                                                planName = 'Plan C - Manual Selection';
                                                planColor = '#E65100';
                                        }

                                        return (
                                                <>
                                          <div style={{
                                                    fontWeight: 'bold', 
                                                    textAlign: 'center',
                                                    fontSize: '14px',
                                                    color: planColor,
                                                    marginBottom: '12px',
                                                    marginLeft: '8px',
                                                    marginRight: '8px'
                                                  }}>
                                                    {planName}
                                                  </div>
                                                  <div style={{
                                                    fontSize: '12px',
                                                    opacity: 0.9, 
                                                    textAlign: 'center',
                                                    lineHeight: '1.4',
                                                    color: 'var(--text-muted)',
                                                    marginLeft: '8px',
                                                    marginRight: '8px'
                                                  }}>
                                                    {selectedPlan === 'Plan C' 
                                                      ? `${supplements.length} supplements manually selected`
                                                      : `${supplements.length} supplements selected`
                                                    }
                                                  </div>
                                                  {supplements.length > 0 && (
                                              <div style={{
                                                      marginTop: '12px',
                                                      maxHeight: '120px',
                                                      overflowY: 'auto',
                                                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                                      borderRadius: '6px',
                                                padding: '8px',
                                                      fontSize: '11px'
                                                    }}>
                                                      {supplements.slice(0, 5).map((supplement, idx) => (
                                                        <div key={idx} style={{
                                                          padding: '2px 0',
                                                          borderBottom: idx < Math.min(supplements.length, 5) - 1 ? '1px solid rgba(25, 118, 210, 0.2)' : 'none',
                                                          color: 'var(--text-primary)'
                                                        }}>
                                                          {getSupplementDisplayName(supplement)}
                                                  </div>
                                                      ))}
                                                      {supplements.length > 5 && (
                                                  <div style={{
                                                          padding: '2px 0', 
                                                          fontStyle: 'italic',
                                                          color: 'var(--text-accent)'
                                                        }}>
                                                          +{supplements.length - 5} more...
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                                </>
                                              );
                                            })()}
                                                  </div>
                                                )}
                                              </div>
                                    );
                                  })()}
                                            </div>
                              </td>
                              <td style={{ width: '140px', textAlign: 'center' }}>
                                {(() => {
                                  const selectedPlan = selectedPlans[index];
                                  const planAResult = planAResults[index];
                                  const planBResult = planBResults[index];
                                  const autoSelectedDuration = appliedDurations[index];
                                  const hasAutoDuration = autoSelectedDuration && Object.keys(autoSelectedDuration).length > 0;
                                  
                                  // If we have auto-duration, show it with confirm icon
                                  if (hasAutoDuration) {
                                    const durationKey = Object.values(autoSelectedDuration)[0];
                                    const csvValue = csvPreviewRows[index]['Probiotics (months)'];
                                    const isConfirmed = confirmedRows[index];
                                    
                                                                        return (
                                      <div 
                                        style={{ 
                                          position: 'relative',
                                          cursor: selectedPlans[index] ? 'pointer' : 'not-allowed',
                                          padding: '0px 2px',
                                          borderRadius: '6px',
                                          backgroundColor: isConfirmed ? 'var(--success-soft)' : (selectedPlans[index] ? 'var(--bg-surface-2)' : 'var(--bg-surface-2)'),
                                          border: isConfirmed ? '1px solid #4CAF50' : (selectedPlans[index] ? '1px solid #e0e0e0' : '1px solid #d0d0d0'),
                                          transition: 'all 0.2s ease',
                                          width: '130px',
                                          height: '40px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          opacity: selectedPlans[index] ? 1 : 0.6
                                        }}
                                        onClick={() => selectedPlans[index] && handleAmtOfSuppClick(index)}
                                        onMouseEnter={(e) => {
                                          if (selectedPlans[index]) {
                                            e.target.style.backgroundColor = isConfirmed ? '#e8f5e8' : '#f0f0f0';
                                            e.target.style.transform = 'translateY(-1px)';
                                            e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          if (selectedPlans[index]) {
                                            e.target.style.backgroundColor = isConfirmed ? '#f0f8f0' : '#f8f8f8';
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = 'none';
                                          }
                                        }}
                                      >
                                        <div style={{ 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          gap: '6px',
                                          textAlign: 'center'
                                        }}>
                                          {isConfirmed ? (
                                            <span style={{ 
                                              fontSize: '12px', 
                                              color: 'var(--success-text)', 
                                              fontWeight: 'bold' 
                                            }}>
                                              ✓
                                            </span>
                                          ) : (
                                            <span style={{ fontSize: '12px', color: 'var(--success-text)' }}>
                                              Auto
                                            </span>
                                          )}
                                          <span style={{ fontSize: '11px', color: 'var(--success-text)' }}>
                                            {durationKey}
                                          </span>
                                        </div>
                                        

                                      </div>
                                    );
                                  }
                                  
                                  // Default inventory icon display. Inline SVG (Icon) instead of
                                  // the old inventory.png / done.png so it follows the theme; the
                                  // plan is carried by a class (was a hue-rotate filter).
                                  let iconName = 'box';
                                  let altText = "Inventory";
                                  let supplementCount = 0;
                                  let supplements = [];
                                  let isClickable = !!selectedPlan;
                                  const iconClass = [
                                    'csv-inv',
                                    selectedPlan === 'Plan A' ? 'is-plan-a' :
                                      selectedPlan === 'Plan B' ? 'is-plan-b' :
                                        selectedPlan === 'Plan C' ? 'is-plan-c' : 'is-unset'
                                  ].join(' ');

                                  if (appliedDurations[index] && Object.keys(appliedDurations[index]).length > 0) {
                                    iconName = 'check-circle';
                                    altText = "Done";
                                  } else if (selectedPlan) {
                                    // Get supplements for the selected plan
                                    if (selectedPlan === 'Plan A') {
                                      supplements = planAResult?.supplements || [];
                                      supplementCount = supplements.length;
                                      altText = `Plan A: ${supplementCount} supplements`;
                                    } else if (selectedPlan === 'Plan B') {
                                      supplements = planBResult?.supplements || [];
                                      supplementCount = supplements.length;
                                      altText = `Plan B: ${supplementCount} supplements`;
                                    } else if (selectedPlan === 'Plan C') {
                                      supplements = Object.keys(selectedSupplements).filter(supplement => selectedSupplements[supplement]);
                                      supplementCount = supplements.length;
                                      altText = `Plan C: ${supplementCount} supplements`;
                                    }
                                  } else {
                                    altText = "Please select a plan first";
                                  }
                                              
                                              return (
                                    <div style={{ position: 'relative' }}>
                                      <button
                                        type="button"
                                        className={`${iconClass}${iconName === 'check-circle' ? ' is-done' : ''}`}
                                        title={altText}
                                        aria-label={altText}
                                        onClick={() => {
                                          if (!isClickable) {
                                            // If no plan selected, open the supplement modal to select plan first
                                            handleAmtOfSuppClick(index);
                                            return;
                                          }
                                          handleInventoryClick(index);
                                        }}
                                        onMouseEnter={e => {
                                          if (!isClickable) {
                                            showCustomTooltip(e, 'Click to select plan and edit supplements');
                                            return;
                                          }
                                          if (selectedPlan && supplements.length > 0) {
                                            // Show tooltip with supplement details
                                            const tooltip = document.createElement('div');
                                            tooltip.id = 'inventory-tooltip';
                                            tooltip.style.cssText = `
                                              position: fixed;
                                              top: 50%;
                                              left: 50%;
                                              transform: translate(-50%, -50%);
                                              background-color: rgba(255, 255, 255, 0.95);
                                              backdrop-filter: blur(10px);
                                              -webkit-backdrop-filter: blur(10px);
                                              color: #333;
                                              padding: 16px;
                                              border-radius: 8px;
                                              font-size: 12px;
                                              white-space: nowrap;
                                              z-index: 9999999;
                                              box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
                                              min-width: 200px;
                                              max-width: 300px;
                                              pointer-events: none;
                                              border: 1px solid rgba(255, 255, 255, 0.3);
                                            `;
                                            
                                            const planColor = selectedPlan === 'Plan A' ? '#2E7D32' : 
                                                             selectedPlan === 'Plan B' ? '#1976D2' : '#E65100';
                                            
                                            tooltip.innerHTML = `
                                              <div style="font-weight: bold; text-align: center; font-size: 13px; color: ${planColor}; margin-bottom: 8px;">
                                                ${selectedPlan} - ${supplementCount} Supplements
                                                  </div>
                                              <div style="max-height: 120px; overflow-y: auto; background-color: rgba(25, 118, 210, 0.1); border-radius: 4px; padding: 6px; font-size: 11px;">
                                                ${supplements.slice(0, 8).map(supplement => 
                                                  `<div style="padding: 2px 0; border-bottom: 1px solid rgba(25, 118, 210, 0.2); color: #333; text-align: center;">${getSupplementDisplayName(supplement)}</div>`
                                                ).join('')}
                                                ${supplements.length > 8 ? `<div style="padding: 2px 0; font-style: italic; color: #1976d2; text-align: center;">+${supplements.length - 8} more...</div>` : ''}
                                                  </div>
                                            `;
                                            
                                            document.body.appendChild(tooltip);
                                          }
                                        }}
                                        onMouseLeave={e => {
                                          hideCustomTooltip();
                                          const tooltip = document.getElementById('inventory-tooltip');
                                          if (tooltip) tooltip.remove();
                                        }}
                                      >
                                        <Icon name={iconName} size={16} strokeWidth={2} />
                                      </button>
                                          </div>
                                        );
                                      })()}
                              </td>
                              <td style={{ width: '120px', textAlign: 'center' }}>
                                {row['Partner Name'] || '-'}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="csv-popup-footer">
                      <button
                        type="button"
                        className={`popup-btn primary font_web${allConfirmed ? ' is-done' : ''}`}
                        onClick={handleRecordData}
                        disabled={allConfirmLoading || allConfirmed || !areAllInventoryApplied() || !areAllPlansSelected()}
                        style={{ minWidth: '120px' }}
                      >
                        {allConfirmLoading ? (
                          <span className="hist-spinner" aria-hidden="true" />
                        ) : allConfirmed ? (
                          <>
                            <span style={{ fontSize: '16px' }}>✓</span>
                            <span>Saved All</span>
                          </>
                        ) : !areAllPlansSelected() ? (
                          <>
                            <span style={{ fontSize: '14px' }}>⚠</span>
                            <span>Please Select Plans for All Rows First</span>
                          </>
                        ) : !areAllInventoryApplied() ? (
                          <>
                            <span style={{ fontSize: '14px' }}>⚠</span>
                            <span>Complete Select Amt of Supplements First</span>
                          </>
                        ) : (
                          'Record'
                        )}
                      </button>
                      <button
                        className={`popup-btn small secondary font_web ${!areAllPlansSelected() ? 'disabled' : ''}`}
                        onClick={() => {
                          // Check if all rows have plans selected
                          if (!areAllPlansSelected()) {
                            alert('Please select plans for all rows before downloading all reports.');
                            return;
                          }
                          downloadAllAsZip();
                        }}
                        disabled={!areAllPlansSelected()}
                        onMouseEnter={(e) => {
                          if (!areAllPlansSelected()) {
                            showCustomTooltip(e, 'Please select plans for all rows before downloading all reports');
                          }
                        }}
                        onMouseLeave={hideCustomTooltip}
                        style={{
                          opacity: areAllPlansSelected() ? 1 : 0.5,
                          cursor: areAllPlansSelected() ? 'pointer' : 'not-allowed'
                        }}
                      >
                        Download All
                      </button>
                    </div>

                    {/* Warning Box */}
                    <div className="rdb-note rdb-note--warning">
                      <span className="rdb-note-tag">Notice</span>
                      <span>
                        Use <strong>Google Chrome</strong> for generating reports · fill all required fields · keep a stable connection for server-side PDF generation.
                      </span>
                    </div>

        </div>
      </div>
              )}

              {showCalcPopup && (
                <div className="csv-popup-overlay">
                  <div className="csv-popup-content modern">
                    <div className="csv-popup-header">
                      <h3>Calculation Preview</h3>
                    </div>

                    <div className="csv-popup-body">
                      <table className="csv-popup-table">
                        <thead>
                          <tr>
                            <th>Label</th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(formData).map(([key, value], index) => (
                            <tr key={index}>
                              <td>{key}</td>
                              <td>{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="csv-popup-footer">
                      <button className="popup-btn secondary" onClick={() => setShowCalcPopup(false)}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
        </div>
          ) : (viewingHistory && user?.account_type === 'corporate_client') ? (
            <div key={animationKey} className="main_content fade-in">
              <div className="lab-portal-card">
                <h2 className="history-title">Result from Lab</h2>
                <p style={{ color: 'var(--text-accent)', fontWeight: 600, marginBottom: 12 }}>
                  Partner: {user.partner_name}
                </p>
                <div className="corp-partner-tab-panel">
                  {corpHistoryLoading && <p>Loading...</p>}
                  {corpHistoryError && <p style={{ color: 'red' }}>{corpHistoryError}</p>}
                  {!corpHistoryLoading && !corpHistoryError && (
                    <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#174a5b', color: '#fff' }}>
                          <th style={{ padding: '8px 10px', textAlign: 'left' }}>Full Name</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left' }}>Collection Kit Number</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left' }}>Panel</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left' }}>Collection Date</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left' }}>Report Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {corpHistoryData.length === 0 ? (
                          <tr><td colSpan={5} style={{ padding: 12, textAlign: 'center', color: 'var(--text-muted)' }}>No records found</td></tr>
                        ) : (
                          corpHistoryData.map((row, i) => (
                            <tr key={i} style={{ background: i % 2 === 0 ? 'var(--bg-surface-2)' : 'var(--bg-surface)' }}>
                              <td style={{ padding: '7px 10px' }}>{row['Full Name'] || '-'}</td>
                              <td style={{ padding: '7px 10px' }}>{row['Collection Kit Number'] || '-'}</td>
                              <td style={{ padding: '7px 10px' }}>{row['Panel'] || '-'}</td>
                              <td style={{ padding: '7px 10px' }}>{row['Collection Date'] || '-'}</td>
                              <td style={{ padding: '7px 10px' }}>{row['Report Date'] || '-'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          ) : viewingHistory ? (
            <div key={animationKey} className="main_content fade-in">
              <div className="history-wrapper hist-lab">
                {/* Lab-style header shared with the other views (rdb-* primitives). */}
                <div className="rdb-header">
                  <div className="rdb-header-main">
                    <span className="rdb-microlabel">
                      <span className={`rdb-led${isFiltering ? ' rdb-led--busy' : ''}`} aria-hidden="true" />
                      Reports · {historyPanelCategory === 'biopet' ? `Biopet · ${biopetHistoryHealthTab}` : 'Biomed'} · {historyReportCountTab === 'multiple' ? 'multiple per person' : 'single per person'}
                    </span>
                    <h2 className="rdb-title font_web">All Reports</h2>
                    <p className="rdb-title-sub">grouped by full name · {pagination.limit} people per page</p>
                  </div>
                  <div className="rdb-readouts" aria-label="Report summary">
                    <div className="rdb-readout">
                      <span className="rdb-readout-k">Reports</span>
                      <span className="rdb-readout-v">{Number(pagination.totalRecords || 0).toLocaleString()}</span>
                    </div>
                    <div className="rdb-readout">
                      <span className="rdb-readout-k">Page</span>
                      <span className="rdb-readout-v">{pagination.currentPage}<small> / {pagination.totalPages}</small></span>
                    </div>
                    <div className="rdb-readout">
                      <span className="rdb-readout-k">Selected</span>
                      <span className="rdb-readout-v" style={{ color: selectedReports.size ? 'var(--rdb-brand)' : undefined }}>{selectedReports.size}</span>
                    </div>
                  </div>
                </div>

                {/* Source rack */}
                <div className="rdb-rack history-panel-category-bar" role="tablist" aria-label="Report source">
                  <div className="rdb-rack-group is-active">
                    <span className="rdb-microlabel">Report source</span>
                    <div className="rdb-rack-tabs">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={historyPanelCategory === 'biomed'}
                        className={`rdb-tab${historyPanelCategory === 'biomed' ? ' is-active' : ''}`}
                        onClick={() => {
                          setHistoryPanelCategory('biomed');
                          setPagination((prev) => ({ ...prev, currentPage: 1 }));
                          setSelectedReports(new Set());
                          setSelectAllReports(false);
                          setExpandedGroups(new Set());
                        }}
                        disabled={isFiltering}
                      >
                        Biomed panel
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={historyPanelCategory === 'biopet'}
                        className={`rdb-tab${historyPanelCategory === 'biopet' ? ' is-active' : ''}`}
                        onClick={() => {
                          setHistoryPanelCategory('biopet');
                          setPagination((prev) => ({ ...prev, currentPage: 1 }));
                          setSelectedReports(new Set());
                          setSelectAllReports(false);
                          setExpandedGroups(new Set());
                        }}
                        disabled={isFiltering}
                      >
                        Biopet panel
                      </button>
                    </div>
                  </div>
                  {historyPanelCategory === 'biopet' && (
                    <div className="rdb-rack-group is-active">
                      <span className="rdb-microlabel">Table</span>
                      <div className="rdb-rack-tabs">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={biopetHistoryHealthTab === 'healthy'}
                          className={`rdb-tab${biopetHistoryHealthTab === 'healthy' ? ' is-active' : ''}`}
                          onClick={() => {
                            setBiopetHistoryHealthTab('healthy');
                            setPagination((prev) => ({ ...prev, currentPage: 1 }));
                            setSelectedReports(new Set());
                            setSelectAllReports(false);
                            setExpandedGroups(new Set());
                          }}
                          disabled={isFiltering}
                        >
                          Healthy
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={biopetHistoryHealthTab === 'unhealthy'}
                          className={`rdb-tab${biopetHistoryHealthTab === 'unhealthy' ? ' is-active' : ''}`}
                          onClick={() => {
                            setBiopetHistoryHealthTab('unhealthy');
                            setPagination((prev) => ({ ...prev, currentPage: 1 }));
                            setSelectedReports(new Set());
                            setSelectAllReports(false);
                            setExpandedGroups(new Set());
                          }}
                          disabled={isFiltering}
                        >
                          Unhealthy
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {isFiltering && (
                  <div
                    className="history-loading-banner"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                  >
                    <div className="history-loading-spinner" aria-hidden="true" />
                    <div className="history-loading-banner-text">
                      <p className="history-loading-banner-title">Loading report history</p>
                      <p className="history-loading-banner-sub">
                        Grouping results from the server — this may take a moment for large lists.
                      </p>
                      <div className="history-loading-shimmer" aria-hidden="true" />
                    </div>
                  </div>
                )}

                
                {/* Warning Box */}
                <div className="rdb-note rdb-note--warning">
                  <span className="rdb-note-tag">Notice</span>
                  <span>
                    <strong>Report generation.</strong>
                    <ul>
                      <li>Use <strong>Google Chrome</strong> for generating reports to ensure the best quality and compatibility.</li>
                      <li>Ensure all required fields are filled before generating reports.</li>
                      <li>Make sure you have a stable internet connection for server-side PDF generation.</li>
                    </ul>
                  </span>
                </div>
                
                {/* Filter Section */}
                <div className="filter-section">
                  <div className="filter-container">
                    <div className="filter-header">
                      <img 
                        src={`${process.env.PUBLIC_URL}/filter.png`} 
                        alt="Filter" 
                        className="filter-icon"
                      />
                      <span className="filter-title">Filters</span>
                    </div>
                    
                    <div className="filter-dropdown-container">
                      <button
                        className="filter-dropdown-btn"
                        onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                        disabled={isFiltering}
                      >
                        <span>{selectedFilterColumn}</span>
                        <span className="dropdown-arrow">▼</span>
                      </button>
                      
                      {showFilterDropdown && (
                        <>
                          <div 
                            className="filter-dropdown-overlay"
                            onClick={() => {
                              console.log('🎯 Overlay clicked, closing filter dropdown');
                              setShowFilterDropdown(false);
                            }}
                            style={{
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'rgba(0, 0, 0, 0.01)',
                              zIndex: 2147483647,
                              cursor: 'default',
                              pointerEvents: 'auto'
                            }}
                          />
                          <div className="filter-dropdown-menu">
                            <div className="filter-dropdown-header">
                              {selectedFilterColumn}
                            </div>
                            <div 
                              className={`filter-dropdown-item ${selectedFilterColumn === 'Full Name' ? 'active' : ''}`}
                              onClick={() => handleFilterColumnChange('Full Name')}
                            >
                              Full Name
                            </div>
                            <div 
                              className={`filter-dropdown-item ${selectedFilterColumn === 'Panel' ? 'active' : ''}`}
                              onClick={() => handleFilterColumnChange('Panel')}
                            >
                              Panel
                            </div>
                            <div 
                              className={`filter-dropdown-item ${selectedFilterColumn === 'Report Number' ? 'active' : ''}`}
                              onClick={() => handleFilterColumnChange('Report Number')}
                            >
                              Report Number
                            </div>
                            <div 
                              className={`filter-dropdown-item ${selectedFilterColumn === 'Business Type' ? 'active' : ''}`}
                              onClick={() => handleFilterColumnChange('Business Type')}
                            >
                              Business Type
                            </div>
                            <div 
                              className={`filter-dropdown-item ${selectedFilterColumn === 'Supplement' ? 'active' : ''}`}
                              onClick={() => handleFilterColumnChange('Supplement')}
                            >
                              Supplement
                            </div>
                            <div 
                              className={`filter-dropdown-item ${selectedFilterColumn === 'Result' ? 'active' : ''}`}
                              onClick={() => handleFilterColumnChange('Result')}
                            >
                              Result
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    
                    <input
                      type="text"
                      placeholder={`Enter ${selectedFilterColumn.toLowerCase()}...`}
                      value={filterValue}
                      onChange={(e) => handleHistoryFilterInputChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          applyHistoryFilterSearch();
                        }
                      }}
                      className="filter-input"
                      disabled={isFiltering}
                    />
                    
                    <button
                      type="button"
                      onClick={applyHistoryFilterSearch}
                      className="history-filter-search-btn"
                      disabled={isFiltering}
                    >
                      Search
                    </button>
                    
                    {(filterValue || debouncedFilter) && (
                      <button
                        type="button"
                        onClick={clearHistoryFilter}
                        className="clear-filter-btn"
                        title="Clear filter"
                        disabled={isFiltering}
                      >
                        ✕
                      </button>
                    )}
                    
                    {isFiltering && (
                      <div className="filter-loading">
                        <div className="loading-spinner" style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #007bff',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        <span>Loading...</span>
                      </div>
                    )}
                    
                    <div className="hist-filter-actions">
                    {/* Export CSV Button */}
                    <button
                      type="button"
                      onClick={showPanelSelectionForExport}
                      className="export-csv-btn rdb-btn"
                      title="Select panel to export to CSV"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7,10 12,15 17,10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Export CSV
                    </button>

                    {/* Approve Button — admin/superadmin only */}
                    {(user?.role === 'admin' || user?.role === 'superadmin') && (
                      <button
                        type="button"
                        onClick={() => handleApprove(true)}
                        disabled={selectedReports.size === 0 || approving}
                        className="rdb-btn rdb-btn--success"
                        title="Approve selected reports"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        {approving ? 'Approving…' : `Approve (${selectedReports.size})`}
                      </button>
                    )}

                    {/* Bulk Download Button */}
                    <button
                      type="button"
                      onClick={handleBulkDownload}
                      disabled={selectedReports.size === 0 || bulkDownloadLoading}
                      className="rdb-btn rdb-btn--primary"
                      data-zip={selectedReports.size > 1}
                      title={selectedReports.size === 0 ? 'Select reports to download' : selectedReports.size === 1 ? `Download ${selectedReports.size} report as PDF` : `Download ${selectedReports.size} reports as ZIP file`}
                    >
                      {bulkDownloadLoading ? (
                        <span className="hist-spinner" aria-hidden="true" />
                      ) : (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7,10 12,15 17,10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                      )}
                      {bulkDownloadLoading ? (selectedReports.size === 1 ? 'Processing...' : 'Creating ZIP...') : selectedReports.size === 0 ? 'Download Selected' : selectedReports.size === 1 ? `Download Report (${selectedReports.size})` : `Download as ZIP (${selectedReports.size})`}
                    </button>
                    </div>

                    {/* Helper text for bulk download */}
                    {selectedReports.size > 0 && (
                      <div className="hist-selection">
                        <span>{selectedReports.size} report{selectedReports.size !== 1 ? 's' : ''} selected · {selectedReports.size === 1 ? 'downloads as PDF' : 'downloads as ZIP'}</span>
                        <button
                          type="button"
                          className="rdb-btn rdb-btn--danger rdb-btn--sm"
                          onClick={() => {
                            setSelectedReports(new Set());
                            setSelectAllReports(false);
                          }}
                          title="Clear all selections"
                        >
                          Clear Selection
                        </button>
                      </div>
                    )}

                    {/* Progress indicator for ZIP creation */}
                    {bulkDownloadLoading && selectedReports.size >= 1 && (
                      <div className="hist-selection" style={{ color: 'var(--success-text)' }}>
                        <span className="hist-spinner" aria-hidden="true" />
                        <span>{selectedReports.size === 1 ? 'Processing report...' : `Creating ZIP file with ${selectedReports.size} reports...`}</span>
                      </div>
                    )}
                  </div>
                </div>
                {/* Info message about grouping */}
                <div className="rdb-note rdb-note--info">
                  <span className="rdb-note-tag">Grouping</span>
                  <span>
                    Reports are grouped by Full Name (trimmed; blanks as Unknown). <strong>Single report</strong> / <strong>Multiple reports</strong> are chosen in the database for the current filters. The table shows {pagination.limit} people per page. Expand appears when someone has more than one report.
                  </span>
                </div>
                <div
                  className="hist-count-tabs"
                  role="tablist"
                  aria-label="Filter list by how many reports each person has"
                >
                  <span className="rdb-microlabel">Show</span>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={historyReportCountTab === 'single'}
                    disabled={isFiltering}
                    onClick={() => setHistoryReportCountTab('single')}
                    className={`hist-count-tab${historyReportCountTab === 'single' ? ' is-active' : ''}`}
                  >
                    Single report
                    {historyNewBadgeCounts.single > 0 && (
                      <span
                        className="hist-new-badge"
                        aria-label={`${historyNewBadgeCounts.single} new reports`}
                        title={`${historyNewBadgeCounts.single} new in single-report list (since last 11 PM cutoff)`}
                      >
                        NEW
                        {historyNewBadgeCounts.single > 1 ? (
                          <span>{historyNewBadgeCounts.single > 99 ? '99+' : historyNewBadgeCounts.single}</span>
                        ) : null}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={historyReportCountTab === 'multiple'}
                    disabled={isFiltering}
                    onClick={() => setHistoryReportCountTab('multiple')}
                    className={`hist-count-tab${historyReportCountTab === 'multiple' ? ' is-active' : ''}`}
                  >
                    Multiple reports
                    {historyNewBadgeCounts.multiple > 0 && (
                      <span
                        className="hist-new-badge"
                        aria-label={`${historyNewBadgeCounts.multiple} new reports`}
                        title={`${historyNewBadgeCounts.multiple} new in multiple-report list (since last 11 PM cutoff)`}
                      >
                        NEW
                        {historyNewBadgeCounts.multiple > 1 ? (
                          <span>{historyNewBadgeCounts.multiple > 99 ? '99+' : historyNewBadgeCounts.multiple}</span>
                        ) : null}
                      </span>
                    )}
                  </button>
                  <span className="hist-count-hint">
                    single vs multiple is resolved in the database with your filters
                  </span>
                </div>
                {!isFiltering &&
                  historyReportCountTab === 'multiple' &&
                  Array.isArray(reportHistory) &&
                  reportHistory.length === 0 && (
                    <div className="rdb-note rdb-note--warning">
                      <span className="rdb-note-tag">No match</span>
                      <span>
                        No <strong>multiple-report</strong> names match the current filters. If you expect
                        duplicates: clear the search box above, try another filter column, or confirm both
                        rows use the exact same <code>Full Name</code> in <code>st_report</code> (the server
                        groups by trimmed <code>Full Name</code>, then lowercased).
                      </span>
                    </div>
                  )}
                {pagination.truncated && (
                  <div className="rdb-note rdb-note--warning">
                    <span className="rdb-note-tag">Truncated</span>
                    <span>
                      Large result set: only the most recent {pagination.maxRowsLoaded?.toLocaleString?.() ?? ''} reports were loaded for grouping. Narrow filters or raise REPORT_HISTORY_MAX_ROWS on the server.
                    </span>
                  </div>
                )}

                <div className="history-table-container" style={{ 
                  maxHeight: '800px',
                  overflowY: 'auto',
                  paddingRight: '10px',
                  minHeight: '200px',
                  boxShadow: 'none'
                }}>
                  {isFiltering && (
                    <div className="history-table-loading-overlay" aria-hidden="true">
                      <div className="history-loading-spinner" />
                      <p>Loading reports…</p>
                    </div>
                  )}
                  
                  <table 
                    className={`history-table ${showFilterDropdown ? 'disabled' : ''}`}
                    data-filter-dropdown-open={showFilterDropdown}
                  >
                    <thead>
                      <tr>
                        <th style={{ width: '50px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectAllReports}
                            onChange={handleSelectAllReports}
                            className="hist-check"
                            title="Select all reports"
                          />
                        </th>
                        <th>#</th>
                        <th></th>
                        <th>Full Name</th>
                        <th>Panel</th>
                        <th>Business Type</th>
                        <th>Report Number</th>
                        <th>Supplement</th>
                                                                                   <th>Result</th>
                        <th>Created</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                                            {historyTableGroups.map((group, groupIndex) => {
                        const latestReport = group.reports[0]; // First report is the latest due to sorting
                        const isMultipleReports = group.reports.length > 1;

                        // Check if report is new today (since 11:00 PM yesterday)
                        const reportTime = new Date(latestReport['create_time'] || latestReport['Report Date'] || 0);
                        const currentTime = new Date();
                        
                        // Get today's 11:00 PM
                        const today = new Date(currentTime);
                        today.setHours(23, 0, 0, 0); // 11:00 PM
                        
                        // If current time is before 11:00 PM, use yesterday's 11:00 PM as cutoff
                        // If current time is after 11:00 PM, use today's 11:00 PM as cutoff
                        let cutoffTime;
                        if (currentTime < today) {
                          // Before 11:00 PM today, use yesterday's 11:00 PM
                          cutoffTime = new Date(today);
                          cutoffTime.setDate(cutoffTime.getDate() - 1);
                        } else {
                          // After 11:00 PM today, use today's 11:00 PM
                          cutoffTime = today;
                        }
                        
                        const isNewToday = reportTime >= cutoffTime;
                        
                        // Determine row background color for multiple reports only
                        let rowBackgroundColor = {};
                        if (isMultipleReports) {
                          rowBackgroundColor = { backgroundColor: 'var(--warning-soft)' };
                        }
                        
                        const groupAllSelected = group.reports.every(report => selectedReports.has(report.id));
                        const setGroupSelected = (checked) => {
                          if (checked) {
                            group.reports.forEach(report => {
                              if (!selectedReports.has(report.id)) {
                                handleReportSelection(report.id);
                              }
                            });
                          } else {
                            group.reports.forEach(report => {
                              if (selectedReports.has(report.id)) {
                                handleReportSelection(report.id);
                              }
                            });
                          }
                        };

                        const rows = [
                        <tr
                          key={groupIndex}
                          style={{ ...rowBackgroundColor, cursor: 'pointer' }}
                          onClick={(e) => {
                            if (!isHistoryRowSelectClick(e)) return;
                            setGroupSelected(!groupAllSelected);
                          }}
                        >
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={groupAllSelected}
                              onChange={(e) => setGroupSelected(e.target.checked)}
                              className={`hist-check${!groupAllSelected && group.reports.some(report => selectedReports.has(report.id)) ? ' is-partial' : ''}`}
                              title="Select this report"
                            />
                          </td>
                                                          <td style={{ position: 'relative' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{(pagination.currentPage - 1) * pagination.limit + groupIndex + 1}</span>
                                  </div>
                                  {isMultipleReports && (
                                    <img
                                      src={`${process.env.PUBLIC_URL}/${expandedGroups.has(group.fullName) ? 'up.png' : 'down.png'}`}
                                      alt={expandedGroups.has(group.fullName) ? 'Collapse' : 'Expand'}
                                      onClick={(e) => {
                                        if (showFilterDropdown) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          return;
                                        }
                                        toggleGroup(group.fullName);
                                      }}
                                      style={{
                                        position: 'absolute',
                                        left: '70%',
                                        top: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '40px',
                                        height: '50px',
                                        cursor: showFilterDropdown ? 'not-allowed' : 'pointer',
                                        opacity: showFilterDropdown ? 0.3 : 0.8,
                                        transition: 'opacity 0.2s ease',
                                        zIndex: 10
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!showFilterDropdown) {
                                          e.target.style.opacity = 1;
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!showFilterDropdown) {
                                          e.target.style.opacity = 0.8;
                                        }
                                      }}
                                      title={
                                        showFilterDropdown
                                          ? 'Filter dropdown is open'
                                          : expandedGroups.has(group.fullName)
                                            ? 'Collapse reports'
                                            : 'Expand to see all reports for this person'
                                      }
                                    />
                                  )}
                                </td>
                          <td style={{ textAlign: 'center' }}>
                            {isNewToday && (
                              <span
                                className="hist-badge hist-badge--new"
                                title={`New report created today (since ${cutoffTime.toLocaleTimeString()})`}
                              >
                                NEW
                              </span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {editingNameId === latestReport.id ? (
                                <>
                                  <input
                                    type="text"
                                    value={editingNameValue}
                                    onChange={(e) => setEditingNameValue(e.target.value)}
                                    style={{
                                      padding: '4px 8px',
                                      border: '1px solid #007bff',
                                      borderRadius: '4px',
                                      fontSize: '14px',
                                      width: '150px'
                                    }}
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => handleSaveName(latestReport.id)}
                                    disabled={editingNameLoading}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: editingNameLoading ? 'not-allowed' : 'pointer',
                                      padding: '2px',
                                      opacity: editingNameLoading ? 0.5 : 1
                                    }}
                                    title={editingNameLoading ? 'Saving...' : 'Save'}
                                  >
                                    {editingNameLoading ? (
                                      <div style={{
                                        width: '16px',
                                        height: '16px',
                                        border: '2px solid #28a745',
                                        borderTop: '2px solid transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                      }}></div>
                                    ) : (
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M20 6L9 17l-5-5" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleCancelEdit()}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '2px'
                                    }}
                                    title="Cancel"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M18 6L6 18M6 6l12 12" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                </>
                              ) : (
                                <>
                                  <img
                                    src={`${process.env.PUBLIC_URL}/modify.png`}
                                    alt="Modify"
                                    className="modify-icon"
                                    onClick={() => handleStartEdit(latestReport.id, getReportFullNameRaw(latestReport) || 'Unknown')}
                                    style={{
                                      width: '16px',
                                      height: '16px',
                                      cursor: 'pointer',
                                      opacity: 0.7,
                                      transition: 'opacity 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.opacity = 1}
                                    onMouseLeave={(e) => e.target.style.opacity = 0.7}
                                    title="Modify Full Name"
                                  />
                                  <span style={{ fontWeight: isMultipleReports ? 'bold' : 'normal' }}>
                                    {getReportFullNameRaw(latestReport) || 'Unknown'}
                                    {isMultipleReports && (
                                      <span className="hist-group-count" title={`${group.reports.length} reports for this person`}>
                                        +{group.reports.length - 1}
                                      </span>
                                    )}
                                  </span>
                                </>
                              )}
                            </div>
                          </td>
                          <td>{latestReport['Panel'] || '-'}</td>
                          <td>
                            {(() => {
                              const b2cValue = latestReport['B2C'];
                              if (b2cValue === 1 || b2cValue === '1') {
                                return <span className="hist-badge hist-badge--b2c">B2C</span>;
                              } else if (b2cValue === 0 || b2cValue === '0') {
                                return <span className="hist-badge hist-badge--b2b">B2B</span>;
                              } else {
                                return <span className="hist-badge">Unknown</span>;
                              }
                            })()}
                          </td>
                          <td>{latestReport['Report number'] || '-'}</td>
                          <td>
                            {latestReport['Supplement'] ? (
                              <div style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                                {(() => {

                                  
                                  try {
                                    const supplements = JSON.parse(latestReport['Supplement']);
                                    if (Array.isArray(supplements)) {
                                      // Apply BioGrace age-based filtering for users over 45
                                      const reportPanel = latestReport['Panel'] || reportMode;
                                      const dateOfBirth = latestReport['Date of Birth'];
                                      const filteredSupplements = applyBioGraceFiltering(supplements, dateOfBirth, reportPanel);
                                      
                                      // Use business type-aware supplement display for each supplement
                                      const displaySupplements = filteredSupplements.map(supplement => {
                                        // Check if this is B2B (B2C = 0) or B2C (B2C = 1)
                                        // Make B2C check more robust to handle different data types
                                        const b2cValue = latestReport['B2C'];
                                        const isB2B = b2cValue === 0 || b2cValue === '0' || b2cValue === false || b2cValue === 'false';
                                        
                                        // Get the display name based on business type
                                        // Check for 9P panel (case insensitive) and B2B business type
                                        // Handle both "9P" and "Q9" panel names
                                        const is9PPanel = reportPanel && (
                                          reportPanel === '9P' || 
                                          reportPanel === 'Q9' ||
                                          reportPanel.toLowerCase() === '9p' || 
                                          reportPanel.toLowerCase() === 'q9' ||
                                          reportPanel.includes('9P') || 
                                          reportPanel.includes('9p') ||
                                          reportPanel.includes('Q9') || 
                                          reportPanel.includes('q9')
                                        );
                                        if (is9PPanel && isB2B) {
                                          const nameMappings = {
                                            'BioAll': 'B2B - 8x3SA',
                                            'BioZen': 'B2B - 5x3C',
                                            'BioMeta': 'B2B - 6x3V'
                                          };
                                          return nameMappings[supplement] || supplement;
                                        }
                                        return supplement;
                                      });
                                      
                                      return displaySupplements.join(', ');
                                    } else {
                                      return latestReport['Supplement'];
                                    }
                                  } catch (e) {
                                    return latestReport['Supplement'];
                                  }
                                })()}
                              </div>
                            ) : (
                              <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>No supplements</span>
                            )}
                          </td>
                          <td>
                            {/* Result column - showing colored squares based on panel format */}
                            {(() => {
                              if (latestReport._historyCategory === 'biopet') {
                                const label =
                                  latestReport._biopetDisplayHealth ||
                                  (latestReport._biopetHealth === 'healthy'
                                    ? 'Healthy'
                                    : 'Unhealthy');
                                const green = label === 'Healthy';
                                return (
                                  <span className={`hist-badge ${green ? 'hist-badge--healthy' : 'hist-badge--unhealthy'}`}>
                                    {label}
                                  </span>
                                );
                              }
                              const panel = latestReport['Panel'];
                              if (!panel) return <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>No Panel</span>;
                              
                              // Check if panel is supported
                              if (!isPanelSupported(panel)) {
                                return (
                                  <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>
                                    {panel} (Unsupported)
                                  </span>
                                );
                              }
                              
                              try {
                                // Check if we have detailed report data for real calculations
                                if (!detailedReportData[latestReport.id]) {
                                  // Show loading state; the batch prefetch effect owns ids it
                                  // has claimed — only fetch individually when it hasn't.
                                  if (!reportDataLoading[latestReport.id] && !batchRequestedIdsRef.current.has(latestReport.id)) {
                                    // Trigger data fetch
                                    fetchDetailedReportData(latestReport.id);
                                  }
                                  
                                  // Show loading indicator
                                  return (
                                    <div className="result-squares-container">
                                      <div className="result-squares-row">
                                        <div style={{ 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          gap: '8px',
                                          color: 'var(--text-muted)',
                                          fontSize: '12px'
                                        }}>
                                          <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>
                                          <span>Calculating...</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }

                                                                                                              // We have detailed data; result is memoized per report id (getRowByRowResult)
                                       const detailedData = detailedReportData[latestReport.id];
                                       const result = getRowByRowResult(panel, detailedData, latestReport.id);

                                       if (!result || !result.scoreResults || result.scoreResults.length === 0) {
                                  return (
                                    <div className="result-squares-container">
                                      <div className="result-squares-row">
                                        <div style={{ 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          gap: '8px',
                                          color: 'var(--text-faint)',
                                          fontSize: '12px'
                                        }}>
                                          <span style={{ fontStyle: 'italic' }}>No results available</span>
                                          <button
                                            onClick={() => fetchDetailedReportData(latestReport.id)}
                                            style={{
                                              background: 'none',
                                              border: 'none',
                                              cursor: 'pointer',
                                              padding: '2px',
                                              fontSize: '10px',
                                              color: 'var(--text-muted)'
                                            }}
                                            title="Retry calculation"
                                          >
                                            🔄
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                
                                                                       // Create colored squares based on actual score results
                                       const squares = result.scoreResults.map((scoreResult, index) => {
                                         const color = scoreResult.color || '#ffeb3b';

                                         return (
                                           <div
                                             key={index}
                                             className="result-square"
                                             style={{
                                               backgroundColor: color
                                             }}
                                             title={`${scoreResult.title}: ${scoreResult.score} (${scoreResult.label})`}
                                           />
                                         );
                                       });

                                       return (
                                         <div className="result-squares-container">
                                           {squares}
                                         </div>
                                       );
                                
                              } catch (error) {
                                console.error('Error calculating result for panel:', panel, error);
                                return (
                                  <span style={{ color: 'var(--danger-text)', fontStyle: 'italic' }}>
                                    {panel} (Error)
                                  </span>
                                );
                              }
                            })()}
                          </td>
                          <td>
                            {new Date(latestReport['create_time']).toLocaleString('en-GB', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false,
                              timeZone: 'Asia/Bangkok'
                            }).replace(',', '')}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', alignItems: 'center' }}>
                              <button
                                className="view-btn"
                                title="View Report"
                              onClick={async () => {
                                setPdfPreviewLoading(true);
                                const token = localStorage.getItem('token');
                                try {
                                  const byIdParams = getHistoryByIdParams(latestReport);
                                  const res = await axios.get(
                                    `${process.env.REACT_APP_API_BASE}/api/reports/by-id/${latestReport.id}`,
                                    {
                                      headers: { Authorization: `Bearer ${token}` },
                                      params: byIdParams
                                    }
                                  );
                                  const row = {
                                    ...res.data,
                                    _historyCategory:
                                      latestReport._historyCategory || byIdParams.historyCategory,
                                    _biopetHealth:
                                      latestReport._biopetHealth || byIdParams.biopetHealth
                                  };

                                  if (
                                    latestReport._historyCategory === 'biopet' ||
                                    row.Panel === 'Pet Care Professional'
                                  ) {
                                    reportModeBeforePetHistoryRef.current = reportMode;
                                    setStaticPdfFromHistory(true);
                                    setReportMode('pet-care-pro');
                                    const petForm = { ...row };
                                    if (!petForm.petname && petForm['Full Name']) {
                                      petForm.petname = petForm['Full Name'];
                                    }
                                    let supplements = [];
                                    const supRaw = row.Supplement ?? row.probiotics_select;
                                    if (supRaw) {
                                      try {
                                        const parsed =
                                          typeof supRaw === 'string'
                                            ? JSON.parse(supRaw)
                                            : supRaw;
                                        supplements = Array.isArray(parsed) ? parsed : [];
                                      } catch {
                                        supplements = [];
                                      }
                                    }
                                    setCsvPreviewToggle(false);
                                    setFromCsvPreview(false);
                                    setManualSupplements(
                                      supplements.length ? { 0: supplements } : {}
                                    );
                                    setPreviewRowIndex(0);
                                    setFormData(petForm);
                                    setShowPdfPreview(false);
                                    setPdfPreviewReportMode(null);
                                    setPdfPreviewFormData(null);
                                    setPdfPreviewSupplements([]);
                                    setShowStaticPdf(true);
                                    setIsPdfReady(true);
                                    loadQuartileGrid();
                                    return;
                                  }

                                  // console.log('🔍 Raw database row:', row);
                                  // console.log('🔍 All keys in database row:', Object.keys(row));
                                  // console.log('🔍 Bacteroides genus in raw data:', row['Bacteroides genus']);
                                  // console.log('🔍 Bacteroidetes genus in raw data:', row['Bacteroidetes genus']);
                                  // console.log('🔍 All bacteria-related keys:', Object.keys(row).filter(key => key.toLowerCase().includes('bacteroid')));
                                  // console.log('🔍 Correct Bacteroides genus value:', row['Bacteroides genus']);
                                  // Determine reportMode from Panel
                                  let mode = 'QPT';
                                  if (row.Panel === 'Q9' || row.Panel === '9P') mode = '9P';
                                  else if (row.Panel === 'QA' || row.Panel === 'Allergy and Skin') mode = 'Allergy and Skin';
                                  else if (row.Panel === 'QI' || row.Panel === 'QA(KIDs)' || row.Panel === 'Allergy and Skin_kids') mode = 'Allergy and Skin_kids';
                                  else if (row.Panel === 'QG' || row.Panel === 'Toxin') mode = 'Toxin';
                                  else if (row.Panel === 'QM' || row.Panel === 'oms') mode = 'oms';
                                  else if (row.Panel === 'QBWCA' || row.Panel === 'bwcallergy') mode = 'bwcallergy';
                                  else if (row.Panel === 'QBWCE' || row.Panel === 'bwccore') mode = 'bwccore';
                                  else if (row.Panel === 'QBWCU' || row.Panel === 'bwcdiamond') mode = 'bwcdiamond';
                                  setPdfPreviewReportMode(mode);
                                  // Normalize row to formData
                                  let configInfo;
                                  if (mode === '9P') configInfo = choosing_supplement_9P[0].info;
                                  else if (mode === 'oms') configInfo = choosing_supplement_oms[0].info;
                                  else if (mode === 'Allergy and Skin') configInfo = choosing_supplement_AMG[0].info;
                                  else if (mode === 'Allergy and Skin_kids') configInfo = qpt_choosing_supplement[0].info;
                                  else if (mode === 'Toxin') configInfo = choosing_supplement_toxin[0].info;
                                  else if ([
                                    'bwcallergy', 'bwccore', 'bwcdiamond', 'BWC-A', 'BWC-C', 'BWC-D'
                                  ].includes(mode)) configInfo = choosing_supplement_bwc[0].info;
                                  else configInfo = qpt_choosing_supplement[0].info;
                                  const normalized = normalizeRowKeys(row, configInfo);
                                  // console.log('🔍 Normalized data keys:', Object.keys(normalized));
                                  // console.log('🔍 Bacteroides genus value (correct):', normalized['Bacteroides genus']);
                                  // console.log('🔍 All Bacteroides-related values:', {
                                  //   'Bacteroides genus': normalized['Bacteroides genus'],
                                  //   'Bacteroidetes genus': normalized['Bacteroidetes genus']
                                  // });
                                  
                                  // Debug: Check duration field
                                  console.log('🔍 History PDF - Original row duration:', row['Probiotics (months)']);
                                  console.log('🔍 History PDF - Normalized duration:', normalized['Probiotics (months)']);
                                  console.log('🔍 History PDF - All normalized keys:', Object.keys(normalized));
                                  
                                  setPdfPreviewFormData(normalized);
                                  
                                  // Use stored supplement data if available, otherwise calculate
                                  let supplements = [];
                                  if (row.Supplement) {
                                    try {
                                      const storedSupplements = JSON.parse(row.Supplement);
                                      supplements = Array.isArray(storedSupplements) ? storedSupplements : [];
                                    } catch (e) {
                                      console.warn('Failed to parse stored supplements, calculating instead:', e);
                                      const supplementResult = getRecommendedSupplements(normalized, mode, stock, [], stockThreshold, {});
                                      supplements = supplementResult.supplements || [];
                                    }
                                  } else {
                                    // Calculate supplements if not stored
                                    const supplementResult = getRecommendedSupplements(normalized, mode, stock, [], stockThreshold, {});
                                    supplements = supplementResult.supplements || [];
                                  }
                                  
                                  // Set the business type context for PDF preview
                                  // This ensures B2B reports show B2B images, B2C reports show B2C images
                                  const isB2B = row.B2C === 0 || row.B2C === '0';
                                  console.log('🔍 Setting PDF preview business type:', { 
                                    reportId: row.id, 
                                    b2c: row.B2C, 
                                    isB2B, 
                                    panel: row.Panel 
                                  });
                                  setCsvPreviewToggle(isB2B); // This controls B2B vs B2C mode in PDF

                                  applyDetected9PVersion(mode, supplements);
                                  setPdfPreviewSupplements(supplements);
                                  setShowPdfPreview(true);
                                } catch (err) {
                                  alert('Failed to load report for preview.');
                                } finally {
                                  setPdfPreviewLoading(false);
                                }
                              }}
                            >
                              <img
                                src={`${process.env.PUBLIC_URL}/view-icon.png`}
                                alt="View"
                                style={{ width: '20px', height: '20px' }} />
                            </button>
                            
                            {latestReport.approved ? (
                              <span className="approved-icon" title="Approved">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
                                  <polyline points="9 12 11 14 15 10"/>
                                </svg>
                              </span>
                            ) : null}

                            {user && user.role === 'superadmin' ? (
                              <button
                                onClick={() => handleDeleteReport(latestReport.id)}
                                className="delete-btn"
                                title="Delete Report"
                              >
                                <img
                                  src={`${process.env.PUBLIC_URL}/delete-icon.png`}
                                  alt="Delete"
                                  style={{ width: '20px', height: '20px' }} />
                              </button>
                            ) : null}
                          </div>
                          </td>

                        </tr>];

                        // Add expanded individual reports if the group is expanded
                        if (expandedGroups.has(group.fullName) && isMultipleReports) {
                          // Add header row for individual reports
                          rows.push(
                            <tr key={`${groupIndex}-header`} className="expanded-reports-header">
                              <th colSpan="12" style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #ffeaa7' }}>
                                Individual Reports for{' '}
                                {getReportFullNameRaw(latestReport) || 'Unknown'}
                                <span className="report-count-badge">{group.reports.length} reports</span>
                              </th>
                            </tr>
                          );

                          // Add individual report rows
                          group.reports.forEach((report, reportIndex) => {
                            rows.push(
                              <tr
                                key={`${groupIndex}-${reportIndex}`}
                                className="expanded-individual-report"
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                  if (!isHistoryRowSelectClick(e)) return;
                                  handleReportSelection(report.id);
                                }}
                              >
                                <td style={{ textAlign: 'center' }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedReports.has(report.id)}
                                    onChange={() => handleReportSelection(report.id)}
                                    className="hist-check"
                                    title="Select this report"
                                  />
                                </td>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{reportIndex + 1}</span>
                                  </div>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  {(() => {
                                    const reportTime = new Date(report['create_time'] || report['Report Date'] || 0);
                                    const currentTime = new Date();
                                    
                                    // Get today's 11:00 PM
                                    const today = new Date(currentTime);
                                    today.setHours(23, 0, 0, 0); // 11:00 PM
                                    
                                    // If current time is before 11:00 PM, use yesterday's 11:00 PM as cutoff
                                    // If current time is after 11:00 PM, use today's 11:00 PM as cutoff
                                    let cutoffTime;
                                    if (currentTime < today) {
                                      // Before 11:00 PM today, use yesterday's 11:00 PM
                                      cutoffTime = new Date(today);
                                      cutoffTime.setDate(cutoffTime.getDate() - 1);
                                    } else {
                                      // After 11:00 PM today, use today's 11:00 PM
                                      cutoffTime = today;
                                    }
                                    
                                    const isNewToday = reportTime >= cutoffTime;
                                    
                                    return isNewToday ? (
                                      <span
                                        className="hist-badge hist-badge--new"
                                        title={`New report created today (since ${cutoffTime.toLocaleTimeString()})`}
                                      >
                                        NEW
                                      </span>
                                    ) : null;
                                  })()}
                                </td>
                                <td>{report['Full Name']}</td>
                                <td>{report['Panel'] || '-'}</td>
                                <td>
                                  {(() => {
                                    const b2cValue = report['B2C'];
                                    if (b2cValue === 1 || b2cValue === '1') {
                                      return <span className="hist-badge hist-badge--b2c">B2C</span>;
                                    } else if (b2cValue === 0 || b2cValue === '0') {
                                      return <span className="hist-badge hist-badge--b2b">B2B</span>;
                                    } else {
                                      return <span className="hist-badge">Unknown</span>;
                                    }
                                  })()}
                                </td>
                                <td>{report['Report number'] || '-'}</td>
                                <td>
                                  {report['Supplement'] ? (
                                    <div style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                                      {(() => {
                                        try {
                                          const supplements = JSON.parse(report['Supplement']);
                                          if (Array.isArray(supplements)) {
                                            // Use business type-aware supplement display for each supplement
                                            const displaySupplements = supplements.map(supplement => {
                                              // Check if this is B2B (B2C = 0) or B2C (B2C = 1)
                                              // Make B2C check more robust to handle different data types
                                              const b2cValue = report['B2C'];
                                              const isB2B = b2cValue === 0 || b2cValue === '0' || b2cValue === false || b2cValue === 'false';
                                              const reportPanel = report['Panel'] || reportMode;
                                              
                                              // Get the display name based on business type
                                              // Check for 9P panel (case insensitive) and B2B business type
                                              // Handle both "9P" and "Q9" panel names
                                              const is9PPanel = reportPanel && (
                                                reportPanel === '9P' || 
                                                reportPanel === 'Q9' ||
                                                reportPanel.toLowerCase() === '9p' || 
                                                reportPanel.toLowerCase() === 'q9' ||
                                                reportPanel.includes('9P') || 
                                                reportPanel.includes('9p') ||
                                                reportPanel.includes('Q9') || 
                                                reportPanel.includes('q9')
                                              );
                                              if (is9PPanel && isB2B) {
                                                const nameMappings = {
                                                  'BioAll': 'B2B - 8x3SA',
                                                  'BioZen': 'B2B - 5x3C',
                                                  'BioMeta': 'B2B - 6x3V'
                                                };
                                                return nameMappings[supplement] || supplement;
                                              }
                                              return supplement;
                                            });
                                            
                                            return displaySupplements.join(', ');
                                          } else {
                                            return report['Supplement'];
                                          }
                                        } catch (e) {
                                          return report['Supplement'];
                                        }
                                      })()}
                                    </div>
                                  ) : (
                                    <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>No supplements</span>
                                  )}
                                </td>
                                <td>
                                  {/* Result column for individual reports - using same logic as main table */}
                                  {(() => {
                                    if (report._historyCategory === 'biopet') {
                                      const label =
                                        report._biopetDisplayHealth ||
                                        (report._biopetHealth === 'healthy'
                                          ? 'Healthy'
                                          : 'Unhealthy');
                                      const green = label === 'Healthy';
                                      return (
                                        <span className={`hist-badge ${green ? 'hist-badge--healthy' : 'hist-badge--unhealthy'}`}>
                                          {label}
                                        </span>
                                      );
                                    }
                                    const panel = report['Panel'];
                                    if (!panel) return <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>No Panel</span>;
                                    
                                    // Check if panel is supported
                                    if (!isPanelSupported(panel)) {
                                      return (
                                        <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>
                                          {panel} (Unsupported)
                                        </span>
                                      );
                                    }
                                    
                                    try {
                                      // Check if we have detailed report data for real calculations
                                      if (!detailedReportData[report.id]) {
                                        // Show loading state; the batch prefetch effect owns ids it
                                        // has claimed — only fetch individually when it hasn't.
                                        if (!reportDataLoading[report.id] && !batchRequestedIdsRef.current.has(report.id)) {
                                          // Trigger data fetch
                                          fetchDetailedReportData(report.id);
                                        }
                                        
                                        // Show loading indicator
                                        return (
                                          <div className="result-squares-container">
                                            <div style={{ 
                                              display: 'flex', 
                                              alignItems: 'center', 
                                              gap: '8px',
                                              color: 'var(--text-muted)',
                                              fontSize: '12px'
                                            }}>
                                              <div className="loading-spinner" style={{ width: '16px', height: '16px' }}></div>
                                              <span>Calculating...</span>
                                            </div>
                                          </div>
                                        );
                                      }

                                      // We have detailed data; result is memoized per report id (getRowByRowResult)
                                      const detailedData = detailedReportData[report.id];
                                      const result = getRowByRowResult(panel, detailedData, report.id);
                                      
                                      if (!result || !result.scoreResults || result.scoreResults.length === 0) {
                                        return (
                                          <div className="result-squares-container">
                                            <div style={{ 
                                              display: 'flex', 
                                              alignItems: 'center', 
                                              gap: '8px',
                                              color: 'var(--text-faint)',
                                              fontSize: '12px'
                                            }}>
                                              <span style={{ fontStyle: 'italic' }}>No results available</span>
                                              <button
                                                onClick={() => fetchDetailedReportData(report.id)}
                                                style={{
                                                  background: 'none',
                                                  border: 'none',
                                                  cursor: 'pointer',
                                                  padding: '2px',
                                                  fontSize: '10px',
                                                  color: 'var(--text-muted)'
                                                }}
                                                title="Retry calculation"
                                              >
                                                🔄
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      }
                                      
                                      // Create colored squares based on actual score results
                                      const squares = result.scoreResults.map((scoreResult, index) => {
                                        const color = scoreResult.color || '#ffeb3b';

                                        return (
                                          <div
                                            key={index}
                                            className="result-square"
                                            style={{
                                              backgroundColor: color
                                            }}
                                            title={`${scoreResult.title}: ${scoreResult.score} (${scoreResult.label})`}
                                          />
                                        );
                                      });

                                      return (
                                        <div className="result-squares-container">
                                          {squares}
                                        </div>
                                      );
                                    } catch (error) {
                                      console.error('Error rendering result for report:', report.id, error);
                                      return <span style={{ color: 'var(--danger-text)', fontStyle: 'italic' }}>Error</span>;
                                    }
                                  })()}
                                </td>
                                <td>
                                  {new Date(report['create_time']).toLocaleString('en-GB', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                    hour12: false,
                                    timeZone: 'Asia/Bangkok'
                                  }).replace(',', '')}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
                                    <button
                                      className="view-btn"
                                      title="View Report"
                                      onClick={async () => {
                                        // View functionality for individual reports - same as main table
                                        try {
                                          setPdfPreviewLoading(true);
                                          const token = localStorage.getItem('token');
                                          const byIdParamsNested = getHistoryByIdParams(report);
                                          const res = await axios.get(
                                            `${process.env.REACT_APP_API_BASE}/api/reports/by-id/${report.id}`,
                                            {
                                              headers: { Authorization: `Bearer ${token}` },
                                              params: byIdParamsNested
                                            }
                                          );
                                          const row = {
                                            ...res.data,
                                            _historyCategory:
                                              report._historyCategory ||
                                              byIdParamsNested.historyCategory,
                                            _biopetHealth:
                                              report._biopetHealth ||
                                              byIdParamsNested.biopetHealth
                                          };

                                          if (
                                            report._historyCategory === 'biopet' ||
                                            row.Panel === 'Pet Care Professional'
                                          ) {
                                            reportModeBeforePetHistoryRef.current = reportMode;
                                            setStaticPdfFromHistory(true);
                                            setReportMode('pet-care-pro');
                                            const petForm = { ...row };
                                            if (!petForm.petname && petForm['Full Name']) {
                                              petForm.petname = petForm['Full Name'];
                                            }
                                            let supplements = [];
                                            const supRaw =
                                              row.Supplement ?? row.probiotics_select;
                                            if (supRaw) {
                                              try {
                                                const parsed =
                                                  typeof supRaw === 'string'
                                                    ? JSON.parse(supRaw)
                                                    : supRaw;
                                                supplements = Array.isArray(parsed)
                                                  ? parsed
                                                  : [];
                                              } catch {
                                                supplements = [];
                                              }
                                            }
                                            setCsvPreviewToggle(false);
                                            setFromCsvPreview(false);
                                            setManualSupplements(
                                              supplements.length ? { 0: supplements } : {}
                                            );
                                            setPreviewRowIndex(0);
                                            setFormData(petForm);
                                            setShowPdfPreview(false);
                                            setPdfPreviewReportMode(null);
                                            setPdfPreviewFormData(null);
                                            setPdfPreviewSupplements([]);
                                            setShowStaticPdf(true);
                                            setIsPdfReady(true);
                                            loadQuartileGrid();
                                            return;
                                          }

                                          // Determine reportMode from Panel
                                          let mode = 'QPT';
                                          if (row.Panel === 'Q9' || row.Panel === '9P') mode = '9P';
                                          else if (row.Panel === 'QA' || row.Panel === 'Allergy and Skin') mode = 'Allergy and Skin';
                                          else if (row.Panel === 'QI' || row.Panel === 'QA(KIDs)' || row.Panel === 'Allergy and Skin_kids') mode = 'Allergy and Skin_kids';
                                          else if (row.Panel === 'QG' || row.Panel === 'Toxin') mode = 'Toxin';
                                          else if (row.Panel === 'QM' || row.Panel === 'oms') mode = 'oms';
                                          else if (row.Panel === 'QBWCA' || row.Panel === 'bwcallergy') mode = 'bwcallergy';
                                          else if (row.Panel === 'QBWCE' || row.Panel === 'bwccore') mode = 'bwccore';
                                          else if (row.Panel === 'QBWCU' || row.Panel === 'bwcdiamond') mode = 'bwcdiamond';
                                          setPdfPreviewReportMode(mode);
                                          
                                          // Normalize row to formData
                                          let configInfo;
                                          if (mode === '9P') configInfo = choosing_supplement_9P[0].info;
                                          else if (mode === 'oms') configInfo = choosing_supplement_oms[0].info;
                                          else if (mode === 'Allergy and Skin') configInfo = choosing_supplement_AMG[0].info;
                                          else if (mode === 'Allergy and Skin_kids') configInfo = qpt_choosing_supplement[0].info;
                                          else if (mode === 'Toxin') configInfo = choosing_supplement_toxin[0].info;
                                          else if ([
                                            'bwcallergy', 'bwccore', 'bwcdiamond', 'BWC-A', 'BWC-C', 'BWC-D'
                                          ].includes(mode)) configInfo = choosing_supplement_bwc[0].info;
                                          else configInfo = qpt_choosing_supplement[0].info;
                                          const normalized = normalizeRowKeys(row, configInfo);
                                          setPdfPreviewFormData(normalized);

                                          // Use stored supplement data if available, otherwise calculate
                                          let supplements = [];
                                          if (row.Supplement) {
                                            try {
                                              const storedSupplements = JSON.parse(row.Supplement);
                                              supplements = Array.isArray(storedSupplements) ? storedSupplements : [];
                                            } catch (e) {
                                              console.warn('Failed to parse stored supplements, calculating instead:', e);
                                              const supplementResult = getRecommendedSupplements(normalized, mode, stock, [], stockThreshold, {});
                                              supplements = supplementResult.supplements || [];
                                            }
                                          } else {
                                            // Calculate supplements if not stored
                                            const supplementResult = getRecommendedSupplements(normalized, mode, stock, [], stockThreshold, {});
                                            supplements = supplementResult.supplements || [];
                                          }

                                          // Set the business type context for PDF preview (B2B vs B2C images)
                                          const isB2B = row.B2C === 0 || row.B2C === '0';
                                          console.log('🔍 Setting main table PDF preview business type:', {
                                            reportId: row.id,
                                            b2c: row.B2C,
                                            isB2B,
                                            panel: row.Panel
                                          });
                                          setCsvPreviewToggle(isB2B);
                                          applyDetected9PVersion(mode, supplements);
                                          setPdfPreviewSupplements(supplements);
                                          setShowPdfPreview(true);
                                        } catch (err) {
                                          alert('Failed to load report for preview.');
                                        } finally {
                                          setPdfPreviewLoading(false);
                                        }
                                      }}
                                    >
                                      <img
                                        src={`${process.env.PUBLIC_URL}/view-icon.png`}
                                        alt="View"
                                        style={{ width: '20px', height: '20px' }} />
                                    </button>
                                    
                                    {user && user.role === 'superadmin' ? (
                                      <button
                                        onClick={() => handleDeleteReport(report.id)}
                                        className="delete-btn"
                                        title="Delete Report"
                                      >
                                        <img
                                          src={`${process.env.PUBLIC_URL}/delete-icon.png`}
                                          alt="Delete"
                                          style={{ width: '20px', height: '20px' }} />
                                      </button>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                          

                        }

                        return rows;
                      }).flat()}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination Controls */}
                <div className="pagination-container">
                  <button
                    onClick={() => handlePagination('prev')}
                    disabled={!pagination.hasPrevPage || paginationButtonsDisabled}
                    className="pagination-button"
                    style={{
                      opacity: (!pagination.hasPrevPage || paginationButtonsDisabled) ? 0.5 : 1,
                      cursor: (!pagination.hasPrevPage || paginationButtonsDisabled) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Previous
                  </button>
                  
                  <span className="pagination-info">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </span>
                  
                  <button
                    onClick={() => handlePagination('next')}
                    disabled={!pagination.hasNextPage || paginationButtonsDisabled}
                    className="pagination-button"
                    style={{
                      opacity: (!pagination.hasNextPage || paginationButtonsDisabled) ? 0.5 : 1,
                      cursor: (!pagination.hasNextPage || paginationButtonsDisabled) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next
                  </button>
                  
                  <span className="pagination-total">
                    Total: {pagination.totalRecords} reports
                  </span>
                </div>
              </div>
              
              {/* Success Notification */}
              {showSuccessNotification && (
                <div className="success-notification">
                  <div className="success-notification-content">
                    <svg className="success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                    <span>Name updated successfully!</span>
                  </div>
                </div>
              )}
            </div>
          ) : viewingSetting ? (
            (user?.role === 'admin' || user?.role === 'superadmin') ? (
              <div key={animationKey} className="main_content fade-in">
                <div className="storage-section-custom">
                  <h2 className="history-title font_web">Settings</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <span style={{ color: 'var(--text-primary)', fontSize: '14px' }}>Section:</span>
                    <button
                      className={`mode_btn syndrome-btn ${settingSection === 'biomed' ? 'active' : ''}`}
                      onClick={() => setSettingSection('biomed')}
                    >
                      Biomed
                    </button>
                    <button
                      className={`mode_btn syndrome-btn ${settingSection === 'biopet' ? 'active' : ''}`}
                      onClick={() => setSettingSection('biopet')}
                    >
                      Biopet
                    </button>
                  </div>
                  <div style={{ background: 'var(--bg-surface)', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
                    {settingSection === 'biomed' ? (
                      <div style={{ color: 'var(--text-muted)' }}>Biomed settings section.</div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ color: 'var(--text-muted)' }}>Biopet settings section.</div>
                        <button
                          className="mode_btn syndrome-btn"
                          onClick={async () => {
                            await loadQuartileGrid();
                            setShowQuartileModal(true);
                          }}
                        >
                          Modify Quartile Value
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="main_content fade-in">
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '400px',
                  textAlign: 'center'
                }}>
                  <h2 style={{ color: 'var(--danger-text)', marginBottom: '20px' }}>Access Denied</h2>
                  <p style={{ fontSize: '16px', color: 'var(--text-muted)' }}>
                    You do not have permission to access the Settings page.
                  </p>
                </div>
              </div>
            )
          ) : viewingStorage ? (
            (user?.role === 'admin' || user?.role === 'superadmin') ? (
              <div key={animationKey} className="main_content fade-in">
                <div className="storage-section-custom">
                  {/* Lab-style header shared with the Raw Database view (rdb-* primitives). */}
                  <div className="rdb-header">
                    <div className="rdb-header-main">
                      <span className="rdb-microlabel">
                        <span className={`rdb-led${fdaModifyLoading || stockHistoryLoading ? ' rdb-led--busy' : ''}`} aria-hidden="true" />
                        Storage · {stockSection === 'biopet' ? 'Biopet' : 'Biomed'} · {fdaModifyClicked ? 'Non-FDA selecting' : 'Stock editing'}
                      </span>
                      <h2 className="rdb-title font_web">Storage Supplement Management</h2>
                      <p className="rdb-title-sub">manage supplement stock levels, thresholds and Non-FDA selection</p>
                    </div>
                    <div className="rdb-readouts" aria-label="Stock summary">
                      <div className="rdb-readout">
                        <span className="rdb-readout-k">SKUs</span>
                        <span className="rdb-readout-v">{ssmSummary.tracked}<small> / {ssmSummary.total}</small></span>
                      </div>
                      <div className="rdb-readout">
                        <span className="rdb-readout-k">Low</span>
                        <span className="rdb-readout-v" style={{ color: ssmSummary.low ? 'var(--warning-text)' : undefined }}>{ssmSummary.low}</span>
                      </div>
                      <div className="rdb-readout">
                        <span className="rdb-readout-k">Critical</span>
                        <span className="rdb-readout-v" style={{ color: ssmSummary.critical ? 'var(--danger-text)' : undefined }}>{ssmSummary.critical}</span>
                      </div>
                    </div>
                  </div>

                {/* No inner max-height/overflow here: the section grows with its
                    content and the page scrolls via .main_content, so the SSM
                    page shows a single scrollbar instead of a nested one. */}
                <div className="ssm-body">
                  {/* Section rack */}
                  <div className="rdb-rack" role="tablist" aria-label="Section">
                    <div className="rdb-rack-group is-active">
                      <span className="rdb-microlabel">Section</span>
                      <div className="rdb-rack-tabs">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={stockSection === 'biomed'}
                          className={`rdb-tab${stockSection === 'biomed' ? ' is-active' : ''}`}
                          onClick={() => setStockSection('biomed')}
                        >
                          Biomed
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={stockSection === 'biopet'}
                          className={`rdb-tab${stockSection === 'biopet' ? ' is-active' : ''}`}
                          onClick={() => setStockSection('biopet')}
                        >
                          Biopet
                        </button>
                      </div>
                    </div>
                  </div>
                  <div style={{ position: 'relative' }}>
                    {/* Format legend: hovering a format greys out the groups it does not use.
                        The tooltip reads hoveredMainButton (the state the hover handlers set). */}
                    <div className="rdb-panel rdb-panel--overflow" style={{ display: stockSection === 'biomed' ? 'block' : 'none', marginBottom: 'var(--sp-4)' }}>
                      <div className="rdb-panel-head">
                        <h4>Format</h4>
                        <span className="rdb-microlabel">hover a format to highlight the SKUs it uses</span>
                      </div>
                      <div className="rdb-panel-body">
                        <div className="ssm-legend">
                          {[
                            { key: 'QPT', label: 'QU', tip: 'QPT - Ultimate Biome' },
                            { key: '9P', label: 'Q9', tip: 'Q9 - Tailor-made Probiotics' },
                            { key: 'AS', label: 'QA', tip: 'Allergy and Skin' },
                            { key: 'GTT', label: 'QG', tip: 'Gastrointestinal Tract Toxin' },
                            { key: 'OMs', label: 'QM', tip: 'Obesity and Metabolic syndrome' },
                            { key: 'BWC-A', label: 'QBWCA', tip: 'BWC - Allergy' },
                            { key: 'BWC-C', label: 'QBWCU', tip: 'BWC - Core' },
                            { key: 'BWC-D', label: 'QBWCE', tip: 'BWC - Diamond' }
                          ].map((f) => (
                            <div key={f.key} className="ssm-legend-item">
                              <button
                                type="button"
                                className={`ssm-legend-chip${hoveredMainButton === f.key ? ' is-on' : ''}`}
                                onMouseEnter={() => setHoveredMainButton(f.key)}
                                onMouseLeave={() => setHoveredMainButton(null)}
                                onFocus={() => setHoveredMainButton(f.key)}
                                onBlur={() => setHoveredMainButton(null)}
                                aria-label={f.tip}
                              >
                                {f.label}
                              </button>
                              <div className={`ssm-legend-tip${hoveredMainButton === f.key ? ' is-on' : ''}`} role="tooltip">{f.tip}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="rdb-toolbar" style={{ display: stockSection === 'biomed' ? 'flex' : 'none' }}>
                      <span className="rdb-microlabel">Setting</span>
                      <div className="rdb-actions">
                        <button
                          type="button"
                          className={`rdb-btn ${fdaModifyClicked ? 'rdb-btn--success' : 'rdb-btn--primary'}`}
                          onClick={() => {
                            if (fdaModifyLoading) return; // Prevent clicking during loading
                            if (fdaModifyClicked) {
                              // Save selected buttons to database when going back to "Non FDA Modify"
                              saveSelectedButtonsToDB();
                            }
                            setFdaModifyClicked(!fdaModifyClicked);
                          }}
                          disabled={fdaModifyLoading}
                        >
                          <img
                            src={`${process.env.PUBLIC_URL}/${fdaModifyClicked ? 'saved.png' : 'setting.png'}`}
                            alt=""
                            className={`ssm-btn-icon${fdaModifyLoading ? ' is-spinning' : ''}`}
                          />
                          {fdaModifyLoading ? 'Saving...' : (fdaModifyClicked ? 'Saved Non FDA Selecting' : 'Non FDA Modify')}
                        </button>
                        <button
                          type="button"
                          className="rdb-btn rdb-btn--primary"
                          onClick={() => {
                            setTempStockThreshold(stockThreshold); // Set temp value to current threshold
                            setTempRedThreshold(redThreshold);
                            setTempYellowThreshold(yellowThreshold);
                            setShowStockDecisionModal(true);
                          }}
                        >
                          <img
                            src={`${process.env.PUBLIC_URL}/decision.png`}
                            alt=""
                            className="ssm-btn-icon"
                          />
                          Stock Condition Decision (below: {stockThreshold})
                        </button>
                      </div>
                    </div>
                    {showQptOptions && (
                      <>
                        <div style={{ display: stockSection === 'biomed' ? 'block' : 'none' }}>
                        <div className="ssm-subhead">
                          <h4>Biomed</h4>
                          <small>{fdaModifyClicked ? 'click a SKU to toggle Non-FDA selection' : 'click a SKU to edit stock'}</small>
                        </div>
                        {/* Four legacy SKU groups, each rendered as horizontal cards by
                            renderLegacyStockCard (same thresholds and click behaviour as before). */}
                        <div className="ssm-groups">
                          {[
                            { key: 'qptOptions', label: 'BIO series', items: qptOptions },
                            { key: 'bioGroup', label: 'Bio blends', items: otherButtonGroups[0] },
                            { key: '8x3Group', label: 'Combos', items: otherButtonGroups[1] },
                            { key: 'proGroup', label: 'Pro', items: otherButtonGroups[2] }
                          ].map((group) => (
                            <div key={group.key} className="ssm-group">
                              <span className="ssm-group-label">{group.label}</span>
                              {/* Same horizontal card as the New Formula section (renderLegacyStockCard). */}
                              <div className="newformula-stock-grid">
                                {group.items.map(option => renderLegacyStockCard(option, group.key, group.label))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="ssm-key" aria-label="Stock status key">
                          <span>OK · {yellowThreshold} and above</span>
                          <span className="is-low">Low · below {yellowThreshold}</span>
                          <span className="is-critical">Critical · {redThreshold} and below</span>
                        </div>
                        {/* New-formula single-strain SKUs (shared by 9P/QU/QI/QBWCA/QBWCE/QBWCU) */}
                        <div className="ssm-subhead">
                          <h4>New Formula</h4>
                          <small>single-strain SKUs</small>
                        </div>
                        <div className="newformula-stock-grid">
                          {supplement_9P_skus.map((sku) => renderNewFormulaStockCard(sku))}
                        </div>
                        </div>
                        <div style={{ display: stockSection === 'biopet' ? 'block' : 'none' }}>
                        <div className="rdb-panel" style={{ marginBottom: 'var(--sp-4)' }}>
                          <div className="rdb-panel-head">
                            <h4>Format</h4>
                            <div className="rdb-rack-tabs">
                              <button
                                type="button"
                                className={`rdb-tab${biopetStockFormat === 'professional' ? ' is-active' : ''}`}
                                aria-pressed={biopetStockFormat === 'professional'}
                                onClick={() => setBiopetStockFormat('professional')}
                              >
                                Pet Care Professional Panel
                              </button>
                              <button
                                type="button"
                                className={`rdb-tab${biopetStockFormat === 'essential' ? ' is-active' : ''}`}
                                aria-pressed={biopetStockFormat === 'essential'}
                                onClick={() => setBiopetStockFormat('essential')}
                              >
                                Pet Care Essential Panel
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="rdb-toolbar">
                          <span className="rdb-microlabel">Setting</span>
                          <div className="rdb-actions">
                            <button
                              type="button"
                              className="rdb-btn rdb-btn--primary"
                              onClick={() => {
                                setTempBiopetFallbackMode(biopetFallbackMode);
                                setShowBiopetFallbackModal(true);
                              }}
                            >
                              No Lacking / Autofill Setting
                            </button>
                            <button
                              type="button"
                              className="rdb-btn rdb-btn--primary"
                              onClick={() => setShowBiopetStockDecisionModal(true)}
                            >
                              Stock Condition Decision (Biopet: below {biopetStockThreshold})
                            </button>
                          </div>
                        </div>
                        <div className="ssm-subhead">
                          <h4>Biopet</h4>
                          <small>click a SKU to edit stock</small>
                        </div>
                        <div className="ssm-groups">
                          <div className="ssm-group">
                            <span className="ssm-group-label">Pet care</span>
                            <div className="ssm-tiles">
                              {biopetStockOptions.map(option => {
                                const value = Number(stock[option]);
                                return (
                                  <button
                                    key={option}
                                    type="button"
                                    className={ssmTileClass({ isGrey: false, value, red: biopetRedThreshold, yellow: biopetYellowThreshold, isSelected: false, modify: false })}
                                    onClick={() => {
                                      setEditStockKey(option);
                                      setEditStockValue('');
                                      setShowStockModal(true);
                                    }}
                                  >
                                    <span className="ssm-tile-code">{option}</span>
                                    <span className="ssm-tile-stock">
                                      <small>stock</small>{stock[option] !== undefined ? stock[option] : '—'}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="ssm-key" aria-label="Stock status key">
                          <span>OK · {biopetYellowThreshold} and above</span>
                          <span className="is-low">Low · below {biopetYellowThreshold}</span>
                          <span className="is-critical">Critical · {biopetRedThreshold} and below</span>
                        </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Movement history. Stock changes from three places — partner orders marked
                      delivered, reports being saved, and hand edits here — so the log records
                      which one, and for partner orders, which order reference. */}
                  <div className="ssm-history">
                    <div className="ssm-history-head">
                      <h3 className="ssm-history-title">Stock movement history</h3>
                      <div className="ssm-history-actions">
                        <select
                          className="ssm-history-filter"
                          value={stockHistorySku}
                          onChange={(e) => setStockHistorySku(e.target.value)}
                        >
                          <option value="">All supplements</option>
                          {[...new Set(stockHistory.map((h) => h.sku))].sort().map((sku) => (
                            <option key={sku} value={sku}>{sku}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="rdb-btn"
                          onClick={loadStockHistory}
                          disabled={stockHistoryLoading}
                        >
                          {stockHistoryLoading ? 'Loading…' : 'Refresh'}
                        </button>
                      </div>
                    </div>

                    {stockHistoryError ? (
                      <p className="ssm-history-empty">{stockHistoryError}</p>
                    ) : stockHistory.length === 0 ? (
                      <p className="ssm-history-empty">
                        {stockHistoryLoading ? 'Loading…' : 'No stock movements recorded yet.'}
                      </p>
                    ) : (
                      <div className="ssm-history-table-wrap">
                        <table className="ssm-history-table">
                          <thead>
                            <tr>
                              <th>When</th>
                              <th>Supplement</th>
                              <th>Change</th>
                              <th>Balance after</th>
                              <th>Source</th>
                              <th>Reference</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stockHistory
                              .filter((h) => !stockHistorySku || h.sku === stockHistorySku)
                              .map((h) => (
                                <tr key={h.id}>
                                  <td className="ssm-history-when">
                                    {new Date(h.createdAt).toLocaleString('en-GB')}
                                  </td>
                                  <td className="ssm-history-sku">{h.sku}</td>
                                  <td className={h.delta < 0 ? 'ssm-history-out' : 'ssm-history-in'}>
                                    {h.delta > 0 ? `+${h.delta}` : h.delta}
                                  </td>
                                  <td>{h.balanceAfter ?? '—'}</td>
                                  <td>
                                    <span className={`ssm-history-source ssm-history-source--${h.source}`}>
                                      {STOCK_SOURCE_LABELS[h.source] || h.source}
                                    </span>
                                    {h.actor ? <div className="ssm-history-actor">{h.actor}</div> : null}
                                  </td>
                                  <td>
                                    {h.reference ? <span className="ssm-history-ref">{h.reference}</span> : '—'}
                                    {h.note ? <div className="ssm-history-note">{h.note}</div> : null}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            ) : (
              <div className="main_content fade-in">
                <div style={{
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '400px',
                  textAlign: 'center'
                }}>
                  <h2 style={{ color: 'var(--danger-text)', marginBottom: '20px' }}>Access Denied</h2>
                  <p style={{ fontSize: '16px', color: 'var(--text-muted)' }}>
                    You do not have permission to access the Storage Supplement Management (SSM) page.
                  </p>
                  <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginTop: '10px' }}>
                    Only users with Admin role can access this feature.
                  </p>
                </div>
              </div>
            )
          ) : viewingDatabase ? (
            <div key={animationKey} className="main_content fade-in">
              <div className="database-section-custom">
                {/* Lab-style header: microlabel + status LED, title, mono readouts */}
                <div className="rdb-header">
                  <div className="rdb-header-main">
                    <span className="rdb-microlabel">
                      <span className={`rdb-led${databaseLoading ? ' rdb-led--busy' : databaseError ? ' rdb-led--error' : ''}`} aria-hidden="true" />
                      Raw Database · {databaseLoading ? 'Fetching' : databaseError ? 'Error' : 'Synced'}
                    </span>
                    <h2 className="rdb-title font_web">
                      {RAW_DB_TABLE_UI_LABELS[rawDbActiveButton] || rawDbTable}
                    </h2>
                    <p className="rdb-title-sub">source table <code>{rawDbTable}</code></p>
                  </div>
                  <div className="rdb-readouts" aria-label="Dataset summary">
                    <div className="rdb-readout">
                      <span className="rdb-readout-k">Records</span>
                      <span className="rdb-readout-v">{databaseData.length.toLocaleString()}</span>
                    </div>
                    <div className="rdb-readout">
                      <span className="rdb-readout-k">Filtered</span>
                      <span className="rdb-readout-v">{filteredDatabaseData.length.toLocaleString()}</span>
                    </div>
                    <div className="rdb-readout">
                      <span className="rdb-readout-k">Columns</span>
                      <span className="rdb-readout-v">
                        {visibleColumns.length}<small> / {databaseColumns.length}</small>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Dataset rack: which source table to load */}
                <div className="rdb-rack" role="tablist" aria-label="Dataset">
                  <div className={`rdb-rack-group${(rawDbActiveButton === 'st_report' || rawDbActiveButton === 'partner_codes') ? ' is-active' : ''}`}>
                    <span className="rdb-microlabel">BioMed</span>
                    <div className="rdb-rack-tabs">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={rawDbActiveButton === 'st_report'}
                        className={`rdb-tab${rawDbActiveButton === 'st_report' ? ' is-active' : ''}`}
                        onClick={() => { setRawDbTable('st_report'); setRawDbActiveButton('st_report'); }}
                      >
                        {RAW_DB_TABLE_UI_LABELS.st_report}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={rawDbActiveButton === 'partner_codes'}
                        className={`rdb-tab${rawDbActiveButton === 'partner_codes' ? ' is-active' : ''}`}
                        onClick={() => { setRawDbTable('partner_codes'); setRawDbActiveButton('partner_codes'); }}
                      >
                        {RAW_DB_TABLE_UI_LABELS.partner_codes}
                      </button>
                    </div>
                  </div>

                  <div className={`rdb-rack-group${(rawDbActiveButton === 'biopet_report' || rawDbActiveButton === 'biopet_report_healthy') ? ' is-active' : ''}`}>
                    <span className="rdb-microlabel">BioPet</span>
                    <div className="rdb-rack-tabs">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={rawDbActiveButton === 'biopet_report'}
                        className={`rdb-tab${rawDbActiveButton === 'biopet_report' ? ' is-active' : ''}`}
                        onClick={() => { setRawDbTable('biopet_report'); setRawDbActiveButton('biopet_report'); }}
                      >
                        {RAW_DB_TABLE_UI_LABELS.biopet_report}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={rawDbActiveButton === 'biopet_report_healthy'}
                        className={`rdb-tab${rawDbActiveButton === 'biopet_report_healthy' ? ' is-active' : ''}`}
                        onClick={() => { setRawDbTable('biopet_report_healthy'); setRawDbActiveButton('biopet_report_healthy'); }}
                      >
                        {RAW_DB_TABLE_UI_LABELS.biopet_report_healthy}
                      </button>
                    </div>
                  </div>
                </div>

                {databaseLoading ? (
                  <div className="rdb-state">
                    <div className="loading-spinner" style={{ marginBottom: '12px' }}></div>
                    <p className="rdb-state-title">Loading database data...</p>
                    <p className="rdb-state-hint">once loaded, cell editing is instant</p>
                  </div>
                ) : databaseError ? (
                  <div className="rdb-state">
                    <p className="rdb-state-error">Error: {databaseError}</p>
                    <button type="button" className="rdb-btn rdb-btn--primary" onClick={fetchDatabaseData}>
                      Retry
                    </button>
                  </div>
                ) : databaseData.length === 0 ? (
                  <div className="rdb-state">
                    <p className="rdb-state-title">No data found in {RAW_DB_TABLE_UI_LABELS[rawDbTable] || rawDbTable}</p>
                    <p className="rdb-state-hint">0 records</p>
                  </div>
                ) : (
                  <div className="database-table-container">
                    <div className="rdb-console">
                      {/* Timeline panel */}
                      <div className="rdb-panel">
                        <div className="rdb-panel-head">
                          <h4>Timeline</h4>
                          <div className="rdb-actions">
                            <button
                              type="button"
                              className="rdb-btn rdb-btn--warning"
                              onClick={() => {
                                setDbSelectedYear('');
                                setDbSelectedMonth('');
                              }}
                            >
                              Clear Timeline
                            </button>
                            <button
                              type="button"
                              className="rdb-btn rdb-btn--danger"
                              onClick={() => {
                                setDatabaseSearchTerm('');
                                setDbSelectedYear('');
                                setDbSelectedMonth('');
                                setSelectedColumns([]);
                                setShowNoColumns(false);
                                setDatabaseCurrentPage(1);
                                setEditingCell(null);
                                setEditingValue('');
                                setSavingCell(false);
                              }}
                            >
                              Reset All
                            </button>
                          </div>
                        </div>
                        <div className="rdb-panel-body">
                          <div className="rdb-fields">
                            <div className="rdb-field">
                              <label htmlFor="rdb-year">Year</label>
                              <select
                                id="rdb-year"
                                className="rdb-select"
                                value={dbSelectedYear}
                                onChange={(e) => setDbSelectedYear(e.target.value)}
                              >
                                <option value="">All Years</option>
                                {dbAvailableYears.map(year => (
                                  <option key={year} value={year}>{year}</option>
                                ))}
                              </select>
                            </div>

                            <div className="rdb-field">
                              <label htmlFor="rdb-month">Month</label>
                              <select
                                id="rdb-month"
                                className="rdb-select"
                                value={dbSelectedMonth}
                                onChange={(e) => setDbSelectedMonth(e.target.value)}
                              >
                                <option value="">All Months</option>
                                {dbAvailableMonths.map(month => (
                                  <option key={month} value={month}>
                                    {new Date(2024, parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long' })}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="rdb-inline-readout">
                              rows <b>{filteredDatabaseData.length}</b> / {databaseData.length}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Columns panel */}
                      <div className="rdb-panel">
                        <div className="rdb-panel-head">
                          <h4>Columns</h4>
                          <div className="rdb-actions">
                            <button type="button" className="rdb-btn rdb-btn--success" onClick={selectAllColumns}>
                              ✓ Select All
                            </button>
                            <button type="button" className="rdb-btn rdb-btn--danger" onClick={deselectAllColumns}>
                              ✕ Clear All
                            </button>
                          </div>
                        </div>
                        <div className="rdb-panel-body">
                          <div className="rdb-chips">
                            {databaseColumns.map((column, index) => {
                              const isOn = showNoColumns ? false : (selectedColumns.length === 0 ? true : selectedColumns.includes(column));
                              return (
                                <label key={index} className={`rdb-chip ${isOn ? 'is-on' : 'is-off'}`}>
                                  <input
                                    type="checkbox"
                                    checked={isOn}
                                    onChange={() => handleColumnToggle(column)}
                                  />
                                  <span title={column}>{column}</span>
                                </label>
                              );
                            })}
                          </div>
                          <p className="rdb-chips-note">
                            {showNoColumns
                              ? `No columns selected - table is empty`
                              : selectedColumns.length === 0
                                ? `Showing all ${databaseColumns.length} columns`
                                : `Showing ${selectedColumns.length} of ${databaseColumns.length} columns`
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Toolbar: search + actions */}
                    <div className="rdb-toolbar">
                      <div className="rdb-toolbar-search">
                        <div className="rdb-field">
                          <label htmlFor="rdb-search">Search in table data</label>
                          <input
                            id="rdb-search"
                            className="rdb-input"
                            type="text"
                            placeholder="Search in table..."
                            value={databaseSearchTerm}
                            onChange={(e) => setDatabaseSearchTerm(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                setDatabaseCurrentPage(1); // Reset to first page when searching
                              }
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          className="rdb-btn rdb-btn--primary"
                          style={{ alignSelf: 'flex-end' }}
                          onClick={() => setDatabaseCurrentPage(1)} // Reset to first page when searching
                        >
                          Search
                        </button>
                      </div>
                      <div className="rdb-actions">
                        <button type="button" className="rdb-btn" onClick={fetchDatabaseData}>
                          Refresh
                        </button>
                        <button
                          type="button"
                          className="rdb-btn rdb-btn--success"
                          onClick={() => exportFilteredDataToCSV()}
                          disabled={filteredDatabaseData.length === 0}
                        >
                          Export CSV
                        </button>
                      </div>
                    </div>

                    {/* Editing Instructions */}
                    <div className="rdb-note">
                      <span className="rdb-note-tag">Quick edit</span>
                      <span>
                        Click any cell to open the edit modal. Press Enter to save, Escape to cancel.
                        Changes are saved directly to the database. Page resets automatically when you visit.
                      </span>
                    </div>

                    {/* Horizontal scrollbar sits above the table and stays pinned while scrolling down */}
                    <div className="database-table-topscroll" ref={databaseTopScrollRef} aria-hidden="true">
                      <div className="database-table-topscroll-spacer" ref={databaseTopScrollSpacerRef} />
                    </div>
                    <div className="database-table-wrapper" ref={databaseTableWrapperRef}>
                      <table className="database-table" ref={databaseTableRef}>
                        <thead>
                          <tr>
                            {visibleColumns.map((column, index) => (
                              <th key={index} title={column}>
                                {column}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedDatabaseData.map((row, rowIndex) => (
                            <tr key={rowIndex}
                            ref={(el) => {
                              if (el && rowIndex === 0) {
                                console.log('🎯 First table row rendered at:', performance.now());
                              }
                            }}
                            >
                              {visibleColumns.map((column, colIndex) => (
                                <td
                                  key={colIndex}
                                  title={`Click to edit: ${String(row[column] || '')}`}
                                  onClick={(e) => {
                                    const clickStart = performance.now();
                                    console.log('🖱️ Cell clicked at:', clickStart);

                                    // Test if click is being processed
                                    console.log('🔄 Processing click...');

                                    e.preventDefault();
                                    e.stopPropagation();
                                    
                                    console.log('🔄 Event handled, calling openEditModal...');
                                    const beforeOpen = performance.now();
                                    console.log('⏱️ Time before openEditModal:', beforeOpen - clickStart, 'ms');
                                    
                                    // Force immediate render with flushSync
                                    flushSync(() => {
                                      console.log('🔧 flushSync: Forcing immediate render in click handler');
                                      // Call the function and measure time
                                      const functionStart = performance.now();
                                      openEditModal(rowIndex, column, row[column], row);
                                      const functionEnd = performance.now();
                                      console.log('⚡ openEditModal execution time:', functionEnd - functionStart, 'ms');
                                    });
                                    
                                    const afterOpen = performance.now();
                                    console.log('⏱️ Time after openEditModal:', afterOpen - clickStart, 'ms');
                                    console.log('✅ Click processing completed');
                                  }}
                                >
                                  <div className="rdb-cell">
                                    <span className="rdb-cell-v">
                                      {String(row[column] || '')}
                                    </span>
                                    <span className="rdb-cell-edit" aria-hidden="true">✎</span>
                                  </div>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="rdb-pager">
                        <button
                          type="button"
                          className="rdb-btn rdb-btn--primary"
                          onClick={() => setDatabaseCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={databaseCurrentPage === 1}
                        >
                          ← Previous
                        </button>

                        <span className="rdb-pager-readout">
                          page <b>{databaseCurrentPage}</b> / {totalPages}
                        </span>

                        <button
                          type="button"
                          className="rdb-btn rdb-btn--primary"
                          onClick={() => setDatabaseCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={databaseCurrentPage === totalPages}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : viewingFormScan ? (
            <div key={animationKey} className="main_content fade-in">
              <FormScanPanel />
            </div>
          ) : viewingAnalytics ? (
            <div key={animationKey} className="main_content fade-in">
              <div className="analytics-section-custom">
                <div className="rdb-header">
                  <div className="rdb-header-main">
                    <span className="rdb-microlabel">
                      <span className="rdb-led rdb-led--error" aria-hidden="true" />
                      Analytics · not configured
                    </span>
                    <h2 className="rdb-title font_web">Data Analytics</h2>
                    <p className="rdb-title-sub">charts and trends across reports will appear here</p>
                  </div>
                </div>
                <div className="rdb-state">
                  <p className="rdb-state-title">Analytics dashboard will be displayed here</p>
                  <p className="rdb-state-hint">no data source connected yet</p>
                </div>
              </div>
            </div>
          ) : (
            <div key={animationKey} className='main_content fade-in'>
              <div className='left_content'>
                {/* The assistant takes the whole column. The Create button lives in
                    the rail; the static patch notes were retired with it. */}
                <NavigatorChat user={user} />
              </div>
              <div className='right_content'>
                <section className="home-panel" aria-label="Platform">
                  <div className="home-panel-head">
                    <span className="rdb-microlabel">Platform</span>
                    <span className="home-status">
                      <span className="rdb-led" aria-hidden="true" />
                      <span className="rdb-microlabel" style={{ color: 'inherit' }}>Online</span>
                    </span>
                  </div>
                  <div className="home-panel-body">
                    <h3 className="home-platform-title">Scientific Platform for Gut Health Assessment and Personalized Report Mapping</h3>
                    <dl className="home-meta">
                      <dt>Version</dt><dd>1.1.0</dd>
                      <dt>Updated</dt><dd>10 Dec 2025</dd>
                      <dt>Signed in</dt><dd>{user?.username || '—'} · {user?.role || '—'}</dd>
                    </dl>
                  </div>
                </section>
                {/* Team panel: one row per member — name, role, live online/offline dot
                    (from /api/users → user_sessions.last_activity, refreshed every minute)
                    — and, for a superadmin, a final "Add member" row. The old 4-card grid
                    and the static Description block below it were retired. */}
                <aside className='modern-members-section team-panel' aria-label='Team members'>
                  <div className='members-header'>
                    <div className='members-title'>
                      <Icon name="users" size={18} />
                      <span>Team</span>
                      <span className='team-panel-count'>
                        {members.filter((m) => m.online).length}/{members.length} online
                      </span>
                    </div>
                    {(user?.role === "admin" || user?.role === "superadmin") && (
                      <button
                        type="button"
                        className="manage-members-btn"
                        onClick={() => setShowMemberSettings(true)}
                      >
                        <Icon name="sliders" size={15} />
                        Manage
                      </button>
                    )}
                  </div>

                  <ul className='team-list'>
                    {members.map((member) => (
                      <li
                        key={member.username}
                        className={`team-row ${member.online ? 'is-online' : 'is-offline'}${member.username === user?.username ? ' is-me' : ''}`}
                        title={member.online ? 'Online now' : formatLastSeen(member.lastActivity)}
                      >
                        <span className='team-avatar' aria-hidden='true'>
                          {(member.username || '?').trim().charAt(0).toUpperCase()}
                          <span className='team-dot' />
                        </span>
                        <span className='team-row-main'>
                          <span className='team-name'>
                            {member.username}
                            {member.username === user?.username && <span className='team-me'>you</span>}
                          </span>
                          <span className={`team-role member-role-${String(member.role || '').replace(/[.\s]/g, '-')}`}>
                            {member.role}
                          </span>
                        </span>
                        <span className='team-status'>
                          {member.online ? 'Online' : 'Offline'}
                          {!member.online && member.lastActivity && (
                            <span className='team-status-sub'>{formatLastSeen(member.lastActivity)}</span>
                          )}
                        </span>
                      </li>
                    ))}

                    {user?.role === 'superadmin' && (
                      <li className='team-row team-row--add'>
                        <button type='button' className='team-add-btn' onClick={() => setShowAddModal(true)}>
                          <span className='team-avatar team-avatar--add' aria-hidden='true'>
                            <Icon name="plus" size={16} />
                          </span>
                          <span className='team-name'>Add member</span>
                        </button>
                      </li>
                    )}
                  </ul>

                  {user?.role !== 'superadmin' && (
                    <div className="members-info-text">
                      Contact a Super Admin to manage team members
                    </div>
                  )}
                </aside>
              </div>
            </div>
          )}
        </div>
      </div>

      {showAddModal && (
        <div className="modal-overlay modern-modal-overlay">
          <div className="modern-modal">
            <div className="modern-modal-header">
              <div className="modal-title-section">
                <div className="modal-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9ZM19 9H14V4H5V21H19V9Z" fill="#4F46E5"/>
                  </svg>
                </div>
                <div className="modal-title-content">
                  <h3>Register New Member</h3>
                  <p>Add a new user to the system</p>
                </div>
              </div>
              <button 
                className="modal-close-btn" 
                onClick={() => setShowAddModal(false)}
                aria-label="Close modal"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div className="modern-modal-body">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <div className="input-wrapper">
                  {/* <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z" fill="#9CA3AF"/>
                    <path d="M10 12C6.68629 12 4 14.6863 4 18V20H16V18C16 14.6863 13.3137 12 10 12Z" fill="#9CA3AF"/>
                  </svg> */}
                  <input 
                    id="username"
                    className="modern-input" 
                    type="text" 
                    placeholder="Enter username" 
                    value={newUser.username} 
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} 
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  {/* <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 8H14V6C14 3.79 12.21 2 10 2C7.79 2 6 3.79 6 6V8H5C4.45 8 4 8.45 4 9V17C4 17.55 4.45 18 5 18H15C15.55 18 16 17.55 16 17V9C16 8.45 15.55 8 15 8ZM10 14C9.45 14 9 13.55 9 13C9 12.45 9.45 12 10 12C10.55 12 11 12.45 11 13C11 13.55 10.55 14 10 14ZM12 8H8V6C8 4.9 8.9 4 10 4C11.1 4 12 4.9 12 6V8Z" fill="#9CA3AF"/>
                  </svg> */}
                  <input 
                    id="password"
                    className="modern-input" 
                    type="password" 
                    placeholder="Enter password"
                    value={newUser.password} 
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} 
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="role">Role</label>
                <div className="select-wrapper">
                  <svg className="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 10L10 13L13 10" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <select 
                    id="role"
                    className="modern-select" 
                    value={newUser.role} 
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  >
                    <option value="">Select a role</option>
                    <option value="superadmin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                    <option value="technical support">Technical Support</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="modern-modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleAddUser}
                disabled={!newUser.username || !newUser.password || !newUser.role}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}

      {showMemberSettings && (
        <div className="modal-overlay modern-modal-overlay">
          <div className="modern-modal manage-members-modal">
            <div className="modern-modal-header">
              <div className="modal-title-section">
                <div className="modal-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#4F46E5"/>
                  </svg>
                </div>
                <div className="modal-title-content">
                  <h3>Team Management</h3>
                  <p>Manage system users and permissions</p>
                </div>
              </div>
              <button 
                className="modal-close-btn" 
                onClick={() => setShowMemberSettings(false)}
                aria-label="Close modal"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div className="modern-modal-body">
              <div className="members-stats">
                <div className="stat-card">
                  <div className="stat-number">{members.length}</div>
                  <div className="stat-label">Total Members</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{members.filter(m => m.role === 'superadmin').length}</div>
                  <div className="stat-label">Super Admins</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{members.filter(m => m.role === 'admin').length}</div>
                  <div className="stat-label">Administrators</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{members.filter(m => m.role === 'user').length}</div>
                  <div className="stat-label">Users</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{members.filter(m => m.role === 'tech. support').length}</div>
                  <div className="stat-label">Tech Support</div>
                </div>
              </div>
              
              <div className="members-list">
                {members.length === 0 ? (
                  <div className="empty-state">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#D1D5DB"/>
                    </svg>
                    <h4>No Team Members</h4>
                    <p>Start building your team by adding the first member</p>
                  </div>
                ) : (
                  members.map((m, index) => (
                    <div key={index} className="member-card-enhanced">
                      <div className="member-info">
                        <div className="member-avatar">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#6B7280"/>
                          </svg>
                        </div>
                        <div className="member-details">
                          <span className="member-name">{m.username}</span>
                          <span className={`member-role-badge member-role-${m.role.replace(/[.\s]/g, '-')}`}>{m.role}</span>
                        </div>
                      </div>
                      {user?.role === 'superadmin' && (
                        <div className="member-actions">
                          <div className="role-selector">
                            <label className="role-label">Role:</label>
                            <select 
                              className="modern-select enhanced" 
                              value={m.role} 
                              onChange={(e) => handleRoleChange(m.username, e.target.value)}
                            >
                              <option value="superadmin">Super Admin</option>
                              <option value="admin">Administrator</option>
                              <option value="user">User</option>
                              <option value="technical support">Technical Support</option>
                            </select>
                          </div>
                          <button 
                            className="btn-danger-enhanced" 
                            onClick={() => handleRemoveMember(m.username)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                            </svg>
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
            
            <div className="modern-modal-footer">
              <button 
                className="btn-secondary-enhanced" 
                onClick={() => setShowMemberSettings(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordChangeModal && (
        <div className="modal-overlay modern-modal-overlay">
          <div className="modern-modal">
            <div className="modern-modal-header">
              <div className="modal-title-section">
                <div className="modal-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 8H14V6C14 3.79 12.21 2 10 2C7.79 2 6 3.79 6 6V8H5C4.45 8 4 8.45 4 9V17C4 17.55 4.45 18 5 18H15C15.55 18 16 17.55 16 17V9C16 8.45 15.55 8 15 8ZM10 14C9.45 14 9 13.55 9 13C9 12.45 9.45 12 10 12C10.55 12 11 12.45 11 13C11 13.55 10.55 14 10 14ZM12 8H8V6C8 4.9 8.9 4 10 4C11.1 4 12 4.9 12 6V8Z" fill="#4F46E5"/>
                  </svg>
                </div>
                <div className="modal-title-content">
                  <h3>Change Password</h3>
                  <p>Update your account password</p>
                </div>
              </div>
              <button 
                className="modal-close-btn" 
                onClick={handlePasswordChangeCancel}
                aria-label="Close modal"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <div className="modern-modal-body">
              {passwordChangeError && (
                <div className="error-message" style={{ 
                  backgroundColor: 'var(--danger-soft)', 
                  color: 'var(--danger-text)', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  marginBottom: '16px',
                  border: '1px solid #FECACA'
                }}>
                  {passwordChangeError}
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <div className="input-wrapper">
                  <input 
                    id="currentPassword"
                    className="modern-input" 
                    type="password" 
                    placeholder="Enter your current password" 
                    value={passwordChangeData.currentPassword} 
                    onChange={(e) => setPasswordChangeData({ ...passwordChangeData, currentPassword: e.target.value })} 
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <div className="input-wrapper">
                  <input 
                    id="newPassword"
                    className="modern-input" 
                    type="password" 
                    placeholder="Enter your new password" 
                    value={passwordChangeData.newPassword} 
                    onChange={(e) => setPasswordChangeData({ ...passwordChangeData, newPassword: e.target.value })} 
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <div className="input-wrapper">
                  <input 
                    id="confirmPassword"
                    className="modern-input" 
                    type="password" 
                    placeholder="Confirm your new password" 
                    value={passwordChangeData.confirmPassword} 
                    onChange={(e) => setPasswordChangeData({ ...passwordChangeData, confirmPassword: e.target.value })} 
                  />
                </div>
              </div>
            </div>
            
            <div className="modern-modal-footer">
              <button 
                className="btn-secondary" 
                onClick={handlePasswordChangeCancel}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handlePasswordChangeSubmit}
                disabled={passwordChangeLoading}
              >
                {passwordChangeLoading ? 'Changing Password...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showStaticPdf && isPdfReady && (
        reportMode === 'pet-care-pro' ? (
          <div className="csv-popup-overlay">
            <div className="csv-popup-content modern" style={{ maxWidth: '85%', maxHeight: '90%', overflowY: 'auto' }}>
              <div className="csv-popup-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                <h3>Pet Care Professional Panel</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    className="popup-btn small primary"
                    style={{ cursor: 'default', opacity: 1 }}
                  >
                    ENG
                  </button>
                  <button
                    type="button"
                    className="popup-btn small secondary"
                    disabled
                    title="Thai version is not available yet"
                    style={{ cursor: 'not-allowed', opacity: 0.5 }}
                  >
                    THAI
                  </button>
                  <button
                    type="button"
                    className="popup-btn secondary"
                    onClick={() => {
                      setShowStaticPdf(false);
                      setIsPdfReady(false);
                      if (staticPdfFromHistory) {
                        setStaticPdfFromHistory(false);
                        setManualSupplements({});
                        const prevMode = reportModeBeforePetHistoryRef.current;
                        reportModeBeforePetHistoryRef.current = null;
                        if (prevMode !== undefined) {
                          setReportMode(prevMode);
                        }
                      } else {
                        setShowCsvPopup(true);
                      }
                    }}
                  >
                    Back
                  </button>
                </div>
              </div>
              <div className="csv-popup-body" style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <BiopetReportPages
                    formData={formData}
                    data={biopetReportData}
                    quartileBySpecies={biopetQuartileBySpecies}
                    pageStyle={{
                      marginBottom: '16px',
                      border: '1px solid #ddd',
                      borderRadius: '8px'
                    }}
                  />
                </div>
                <div
                  style={{
                    width: '100%',
                    maxWidth: '360px',
                    minWidth: '320px',
                    position: 'sticky',
                    top: '8px',
                    background: 'var(--bg-surface-2)',
                    border: '1px solid #e1e1e1',
                    borderRadius: '8px',
                    padding: '12px',
                    boxSizing: 'border-box'
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '10px', color: 'var(--text-primary)' }}>
                    Quick Navigation
                  </div>
                  {[
                    { text: 'First Page', page: 1 },
                    { text: 'Microbiota test result', page: 4 },
                    { text: 'Information on Probiotics', page: 5 },
                    { text: 'Information on Core & Harmful bacteria', page: 6 },
                    { text: 'Probiotics supplement advice', page: 8 }
                  ].map((item) => (
                    <button
                      key={item.page}
                      type="button"
                      className="popup-btn secondary"
                      style={{
                        width: '100%',
                        marginBottom: '8px',
                        textAlign: 'left',
                        whiteSpace: 'normal',
                        lineHeight: '1.3'
                      }}
                      onClick={() => {
                        const target = document.getElementById(`pet-professional-page-${item.page}`);
                        if (target) {
                          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                    >
                      {item.text}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <PdfStaticModal
            formData={formData}
            fromCsvPreview={fromCsvPreview}
            useThaiVersion={versionOptions.thai}
            duration={(() => {
              // For CSV preview, use the currently selected duration from appliedDurations
              if (fromCsvPreview && previewRowIndex !== null) {
                const rowDurations = appliedDurations[previewRowIndex] || {};
                const firstSupplement = manualSupplements[previewRowIndex]?.[0] || supplementResults[previewRowIndex]?.[0];
                if (firstSupplement && rowDurations[firstSupplement]) {
                  return rowDurations[firstSupplement];
                }
              }
              // Fallback to original CSV value or null
              return formData?.['duration'] || formData?.['Probiotics (months)'] || null;
            })()}
            pdfBasePath={
              reportMode === '9P'
                ? (versionOptions.thai ? '/9P/thai' : '/9P/eng')
                : versionOptions.thai
                ? '/pdf_QPTthai'
                : '/pdfpages'
            }
            totalPages={reportMode === '9P' ? (versionOptions.thai ? 32 : 22) : 38}
            reportMode={reportMode}
            selectedSupplements={
              manualSupplements[previewRowIndex] && manualSupplements[previewRowIndex].length > 0
                ? manualSupplements[previewRowIndex]
                : (supplementResults[previewRowIndex] || [])
            }
            stock={stock}
            csvPreviewToggle={csvPreviewToggle}
            supplement9PVersion={supplement9PVersion}
            onClose={() => {
              setShowStaticPdf(false);
              setShowCsvPopup(true); // Force back to CSV Import Preview
            }}
            onBackToCsv={() => {
              setShowStaticPdf(false);
              setShowCsvPopup(true); // Force back to CSV Import Preview
            }}
            onToggleLanguage={() =>
              setVersionOptions(prev => ({
                eng: !prev.eng,
                thai: !prev.thai
              }))
            }
          />
        )
      )}

      {singleRowPreview && (
        <div className="csv-popup-overlay font_web">
          <div className="csv-popup-content modern">
            <div className="csv-popup-header">
              <h3>Manual Input Preview</h3>
            </div>

            <div className="csv-popup-body">
              <table className="csv-popup-table">
                <thead>
                  <tr>
                    <th>Full Name</th>
                    <th>Report Number</th>
                    <th>
                      {reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro'
                        ? 'Age'
                        : 'Client ID'}
                    </th>
                    {(reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro') && (
                      <th
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          textDecoration: petHealthCsvColumnShowRaw ? 'underline' : undefined
                        }}
                        title="Click to show raw CSV health for every Unhealthy row; click again to show Unhealthy"
                        onClick={() => setPetHealthCsvColumnShowRaw((v) => !v)}
                      >
                        Health
                        <div
                          style={{
                            fontSize: '10px',
                            color: 'var(--text-muted)',
                            fontWeight: 'normal',
                            marginTop: '2px',
                            lineHeight: 1.2
                          }}
                        >
                          Click header to toggle raw CSV (Unhealthy rows)
                        </div>
                      </th>
                    )}
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{formData['Full Name'] || '-'}</td>
                    <td>{formData['Report Number'] || '-'}</td>
                    <td>
                      {reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro'
                        ? petAgeYearsToLifeStageLabel(formData.age ?? formData.Age)
                        : formData['Client ID'] || '-'}
                    </td>
                    {(reportMode === 'pet-care-pro' || reportMode === 'pet-care-pro') && (
                      <td style={{ verticalAlign: 'middle', whiteSpace: 'normal' }}>
                        <PetHealthCsvPreviewCell
                          row={formData}
                          revealUnhealthyRaw={petHealthCsvColumnShowRaw}
                        />
                      </td>
                    )}
                    <td>
                      <button
                        className={`popup-btn small primary mouse ${!selectedPlans[0] ? 'disabled' : ''}`}
                        onClick={() => {
                          // Check if a plan has been selected for this row
                          if (!selectedPlans[0]) {
                            alert('Please select a plan first before previewing this report.');
                            return;
                          }
                          
                          setFromCsvPreview(false);
                          setIsPdfReady(true);
                          setSingleRowPreview(false);
                          setVersionOptions({ eng: true, thai: false });
                          setShowStaticPdf(true);
                        }}
                        disabled={!selectedPlans[0]}
                        onMouseEnter={(e) => {
                          if (!selectedPlans[0]) {
                            showCustomTooltip(e, 'Please select a plan first before previewing this report');
                          }
                        }}
                        onMouseLeave={hideCustomTooltip}
                        style={{
                          opacity: selectedPlans[0] ? 1 : 0.5,
                          cursor: selectedPlans[0] ? 'pointer' : 'not-allowed'
                        }}
                      >
                        Preview
                      </button>
                    </td>
                    <td>
                      <button
                        className={`popup-btn small secondary font_web ${!selectedPlans[0] ? 'disabled' : ''}`}
                        onClick={async () => {
                          // Check if a plan has been selected for this row
                          if (!selectedPlans[0]) {
                            alert('Please select a plan first before downloading this report.');
                            return;
                          }

                          setLoadingConfirm(prev => ({ ...prev, 0: true }));
                          setHoveredConfirmIndex(null);
                          setCalcStatusText('Generating PDF (English & Thai versions)...');
                          setIsCalculating(true);
                          // ... existing code ...
                          setFdaRecalculatedRows(prev => ({ ...prev, 0: true }));
                        }}
                        disabled={!selectedPlans[0]}
                        onMouseEnter={(e) => {
                          if (!selectedPlans[0]) {
                            showCustomTooltip(e, 'Please select a plan first before downloading this report');
                          }
                        }}
                        onMouseLeave={hideCustomTooltip}
                        style={{
                          opacity: selectedPlans[0] ? 1 : 0.5,
                          cursor: selectedPlans[0] ? 'pointer' : 'not-allowed'
                        }}
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Warning Box */}
            <div style={{
              marginTop: '20px',
              padding: '15px',
              backgroundColor: 'var(--warning-soft)',
              border: '1px solid #ffeaa7',
              borderRadius: '8px',
              borderLeft: '4px solid #f39c12',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <div style={{
                  fontSize: '18px',
                  color: 'var(--warning-text)',
                  marginTop: '2px'
                }}>
                  ⚠️
                </div>
                <div>
                  <div style={{
                    fontWeight: '600',
                    color: 'var(--warning-text)',
                    fontSize: '14px',
                    marginBottom: '5px'
                  }}>
                    Important Notice for Report Generation
                  </div>
                  <div style={{
                    color: 'var(--warning-text)',
                    fontSize: '13px',
                    lineHeight: '1.4'
                  }}>
                                                • Use <strong>Google Chrome browser</strong> for generating reports to ensure the best quality and compatibility<br/>
                            • Ensure all required fields are filled before generating reports<br/>
                            {/* • For optimal PDF quality, avoid generating reports during peak system usage<br/> */}
                            • Make sure you have a stable internet connection for server-side PDF generation
                  </div>
                </div>
              </div>
            </div>

            <div className="csv-popup-footer">
              <button className="popup-btn secondary" onClick={() => setSingleRowPreview(false)}>
                Back
              </button>
              <button
                className="popup-btn primary font_web"
                onClick={async () => {
                  // Handle recording single manual input data
                  if (allConfirmLoading) return;
                  
                  setAllConfirmLoading(true);
                  
                  try {
                    console.log('Recording manual input data to database...');
                    
                    // Clean the form data by replacing missing values with 0
                    const cleanedReport = cleanReportData(formData);
                    
                    // Get supplement data
                    const manual = manualSupplements[0];
                    const auto = supplementResults[0];
                    const supplements = manual && manual.length > 0 ? manual : auto;
                    
                    // Use the Client ID that was generated
                    const clientId = generatedClientIds[0] || '01-000-000-01';
                    
                    const reportToSave = {
                      ...cleanedReport,
                      'Client ID': clientId,
                      'Supplement': supplements && supplements.length > 0 ? JSON.stringify(supplements) : null,
                      'B2C': csvPreviewToggle ? 0 : 1 // 1 = B2C, 0 = B2B
                    };
                    
                    // Debug log to see what data is being sent
                    console.log('Manual Input - Original:', formData);
                    console.log('Manual Input - Cleaned:', cleanedReport);
                    console.log('Manual Input - Final:', reportToSave);
                    
                    // Determine panel name based on report mode
                    let panelName = 'QPT'; // default
                    if (reportMode === '9P') {
                      panelName = 'Q9';
                    } else if (reportMode === 'Allergy and Skin') {
                      panelName = 'QA';
                    } else if (reportMode === 'Allergy and Skin_kids') {
                      panelName = 'QI';
                    } else if (reportMode === 'Toxin') {
                      panelName = 'QG';
                    } else if (reportMode === 'oms') {
                      panelName = 'QM';
                    } else if (reportMode === 'bwcallergy') {
                      panelName = 'QBWCA';
                    } else if (reportMode === 'bwccore') {
                      panelName = 'QBWCE';
                    } else if (reportMode === 'bwcdiamond') {
                      panelName = 'QBWCU';
                    }

                    const token = localStorage.getItem('token');
                    const saveResponse = await axios.post(
                      `${process.env.REACT_APP_API_BASE}/api/reports/save-to-st-report`,
                      {
                        reports: [reportToSave],
                        panel: panelName
                      },
                      {
                        headers: { Authorization: `Bearer ${token}` }
                      }
                    );

                    console.log('Manual input report saved to st_report table:', saveResponse.data);

                    // Update supplement stock in the database if there are supplements
                    if (Object.keys(updatedSupplementStock).length > 0) {
                      console.log('Updating supplement stock:', updatedSupplementStock);
                      await axios.post(`${process.env.REACT_APP_API_BASE}/api/stock/update`, { stock: updatedSupplementStock });
                      
                      // Refresh stock after update
                      const stockRes = await axios.get(`${process.env.REACT_APP_API_BASE}/api/stock`);
                      setStock(stockRes.data);
                    }

                    // Show success message
                    setSuccessMessage(`Successfully recorded manual input report to database with panel: ${panelName}`);
                    setShowSuccessDialog(true);
                    
                    // Auto-close success popup after 3 seconds
                    setTimeout(() => {
                      setShowSuccessDialog(false);
                      setSuccessMessage('');
                    }, 3000);

                    // Close the preview and refresh the total report count
                    setSingleRowPreview(false);
                    fetchSummary();

                  } catch (err) {
                    console.error('Error recording manual input data:', err);
                    alert(`Failed to record manual input data to database!\nError: ${err.response?.data?.message || err.message}`);
                  } finally {
                    setAllConfirmLoading(false);
                  }
                }}
                disabled={allConfirmLoading || !isManualInventoryApplied()}
                style={{
                  backgroundColor: allConfirmLoading ? 'var(--bg-surface-2)' : (!isManualInventoryApplied() ? 'var(--bg-surface-2)' : 'var(--bg-surface)'),
                  color: allConfirmLoading ? 'var(--text-muted)' : (!isManualInventoryApplied() ? 'var(--text-faint)' : 'var(--text-primary)'),
                  cursor: allConfirmLoading ? 'not-allowed' : (!isManualInventoryApplied() ? 'not-allowed' : 'pointer'),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  minWidth: '120px',
                  height: '40px',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '14px',
                  transition: 'all 0.3s ease',
                  fontWeight: '500'
                }}
              >
                {allConfirmLoading ? (
                  <div className="loading-spinner" style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgb(32, 184, 40)',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}/>
                ) : !isManualInventoryApplied() ? (
                  <>
                    <span style={{ fontSize: '14px' }}>⚠</span>
                    <span>Complete Select Amt of Supplements First</span>
                  </>
                ) : (
                  'Record'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showQuartileModal && (
        <div className="csv-popup-overlay" style={{ zIndex: 10005 }}>
          <div
            className="csv-popup-content modern font_web"
            style={{
              maxWidth: Math.min(980, typeof window !== 'undefined' ? window.innerWidth - 48 : 980),
              width: '100%',
              padding: '20px 24px 24px'
            }}
          >
            <div className="csv-popup-header" style={{ marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Modify Quartile Value</h3>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-muted)', fontWeight: 400 }}>
                Each <strong>species</strong> has its own Q1–Q3 grid (columns <strong>Young</strong> &lt;1 y,{' '}
                <strong>Adult</strong> 1–7 y, <strong>Senior</strong> &gt;7 y). One row per species in{' '}
                <code>quartile</code>. Scoring uses Q2/Q3 vs ΔCT per species.
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 }}>
                <strong>Calibrate</strong> uses <code>biopet_report_healthy</code> only. Percentiles are computed{' '}
                <em>per species</em> (not pooled). Unhealthy rows in <code>biopet_report</code> are excluded.
              </p>
            </div>
            {quartileModalLoading ? (
              <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</p>
            ) : (
              <>
                <label className="font_web" style={{ display: 'block', marginBottom: 10, fontSize: 13 }}>
                  Life stage column group
                  <select
                    value={quartileLifeStageFocus}
                    onChange={(e) => {
                      const v = e.target.value;
                      setQuartileLifeStageFocus(v);
                      requestAnimationFrame(() => {
                        document
                          .getElementById(`quartile-modal-stage-${v}`)
                          ?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                      });
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      maxWidth: 280,
                      marginTop: 6,
                      padding: '8px 10px',
                      fontSize: 14,
                      borderRadius: 6,
                      border: '1px solid #ccc'
                    }}
                  >
                    {QUARTILE_MODAL_LIFE_STAGES.map((label) => (
                      <option key={label} value={label}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                    Scrolls the table to that age band. Column colors: blue = Young, green = Adult, purple = Senior.
                  </span>
                </label>
                <div style={{ overflowX: 'auto', marginTop: 4, borderRadius: 8, border: '1px solid #e0e0e0' }}>
                  <table
                    style={{
                      width: 'max-content',
                      minWidth: '100%',
                      borderCollapse: 'separate',
                      borderSpacing: 0
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          rowSpan={2}
                          style={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            background: 'var(--bg-surface)',
                            boxShadow: '4px 0 8px -4px rgba(0,0,0,0.12)',
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            textAlign: 'left',
                            verticalAlign: 'bottom',
                            padding: '10px 12px 10px 8px',
                            minWidth: 160,
                            maxWidth: 220,
                            borderBottom: '1px solid #eee'
                          }}
                        >
                          Species
                        </th>
                        {QUARTILE_MODAL_LIFE_STAGES.map((stage) => {
                          const st = QUARTILE_MODAL_STAGE_STYLES[stage];
                          return (
                          <th
                            key={stage}
                            id={`quartile-modal-stage-${stage}`}
                            colSpan={3}
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: 'var(--text-primary)',
                              textAlign: 'center',
                              padding: '8px 6px',
                              borderBottom: '1px solid #eee',
                              backgroundColor:
                                quartileLifeStageFocus === stage ? st.headerBgFocus : st.headerBg
                            }}
                          >
                            {stage}
                          </th>
                          );
                        })}
                      </tr>
                      <tr>
                        {QUARTILE_MODAL_LIFE_STAGES.flatMap((stage) => {
                          const st = QUARTILE_MODAL_STAGE_STYLES[stage];
                          return ['Q1', 'Q2', 'Q3'].map((q) => (
                            <th
                              key={`${stage}-${q}`}
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: 'var(--text-muted)',
                                textAlign: 'center',
                                padding: '6px 4px',
                                minWidth: 72,
                                backgroundColor:
                                  quartileLifeStageFocus === stage ? st.subHeaderBgFocus : st.subHeaderBg,
                                borderBottom: '1px solid #eee'
                              }}
                            >
                              {q}
                            </th>
                          ));
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {BIOPET_CALIBRATION_SPECIES_COLUMNS.map((speciesName) => {
                        const grid =
                          quartileGridBySpecies[speciesName] ||
                          createEmptyQuartileGridBySpecies()[speciesName];
                        return (
                          <tr key={speciesName}>
                            <th
                              scope="row"
                              title={speciesName}
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 1,
                                background: 'var(--bg-surface)',
                                boxShadow: '4px 0 8px -4px rgba(0,0,0,0.08)',
                                fontSize: 11,
                                fontWeight: 600,
                                color: 'var(--text-primary)',
                                textAlign: 'left',
                                padding: '8px 10px 8px 8px',
                                maxWidth: 220,
                                borderBottom: '1px solid #f0f0f0',
                                whiteSpace: 'normal',
                                lineHeight: 1.25
                              }}
                            >
                              {speciesName}
                            </th>
                            {[0, 1, 2].flatMap((ci) =>
                              [0, 1, 2].map((ri) => {
                                const cell = grid[ri]?.[ci] ?? '0';
                                const stage = QUARTILE_MODAL_LIFE_STAGES[ci];
                                const st = QUARTILE_MODAL_STAGE_STYLES[stage];
                                return (
                                  <td
                                    key={`${speciesName}-c${ci}-r${ri}`}
                                    style={{
                                      padding: 4,
                                      borderBottom: '1px solid #f5f5f5',
                                      backgroundColor:
                                        quartileLifeStageFocus === stage ? st.cellBgFocus : st.cellBg
                                    }}
                                  >
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={cell}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        setQuartileGridBySpecies((prev) => {
                                          const g =
                                            prev[speciesName] ||
                                            createEmptyQuartileGridBySpecies()[speciesName];
                                          const nextGrid = g.map((r, rj) =>
                                            r.map((c, ck) => (rj === ri && ck === ci ? v : c))
                                          );
                                          return { ...prev, [speciesName]: nextGrid };
                                        });
                                      }}
                                      onBlur={() => {
                                        setQuartileGridBySpecies((prev) => {
                                          const g =
                                            prev[speciesName] ||
                                            createEmptyQuartileGridBySpecies()[speciesName];
                                          const nextGrid = g.map((r, rj) =>
                                            r.map((c, ck) => {
                                              if (rj !== ri || ck !== ci) return c;
                                              const n = parseFloat(c);
                                              if (!Number.isFinite(n)) return '0';
                                              return String(Math.round(n * 100) / 100);
                                            })
                                          );
                                          return { ...prev, [speciesName]: nextGrid };
                                        });
                                      }}
                                      style={{
                                        width: '100%',
                                        minWidth: 64,
                                        boxSizing: 'border-box',
                                        padding: '8px 6px',
                                        fontSize: 14,
                                        borderRadius: 4,
                                        border: '1px solid #ccc',
                                        textAlign: 'center'
                                      }}
                                    />
                                  </td>
                                );
                              })
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <div className="csv-popup-footer" style={{ marginTop: 16, justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                className="popup-btn secondary font_web"
                onClick={() => setShowQuartileModal(false)}
                disabled={quartileSaving || quartileCalibrating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="popup-btn secondary font_web"
                disabled={quartileModalLoading || quartileSaving || quartileCalibrating}
                title="Recompute Q1–Q3 from biopet database and save"
                onClick={async () => {
                  setQuartileCalibrating(true);
                  try {
                    const token = localStorage.getItem('token');
                    const res = await axios.post(
                      `${process.env.REACT_APP_API_BASE}/api/reports/quartile/calibrate`,
                      {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const by = res.data?.bySpecies;
                    if (!by || typeof by !== 'object') {
                      showNotification('Calibration returned invalid data.', 'error');
                      return;
                    }
                    setQuartileGridBySpecies(quartileApiResponseToGridBySpecies(res.data));
                    const w = res.data?.warnings;
                    const n = res.data?.rowsScanned;
                    if (Array.isArray(w) && w.length > 0) {
                      showNotification(
                        `${res.data?.message || 'Calibrated.'} (${n ?? '?'} rows). ${w.join(' ')}`,
                        'warning'
                      );
                    } else {
                      showNotification(
                        `${res.data?.message || 'Quartiles calibrated.'} (${n ?? '?'} rows scanned).`,
                        'success'
                      );
                    }
                  } catch (e) {
                    console.error(e);
                    showNotification(
                      e.response?.data?.message || e.message || 'Calibration failed',
                      'error'
                    );
                  } finally {
                    setQuartileCalibrating(false);
                  }
                }}
              >
                {quartileCalibrating ? 'Calibrating…' : 'Calibrate'}
              </button>
              <button
                type="button"
                className="popup-btn primary font_web"
                disabled={quartileModalLoading || quartileSaving || quartileCalibrating}
                onClick={async () => {
                  const bySpecies = {};
                  for (const sp of BIOPET_CALIBRATION_SPECIES_COLUMNS) {
                    const g = quartileGridBySpecies[sp];
                    if (!g) continue;
                    const flat = g.flat().map((x) => {
                      const n = parseFloat(x);
                      return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
                    });
                    if (flat.length === 9) bySpecies[sp] = flat;
                  }
                  const values = meanFlat9FromQuartileGridBySpecies(quartileGridBySpecies);
                  setQuartileSaving(true);
                  try {
                    const token = localStorage.getItem('token');
                    await axios.put(
                      `${process.env.REACT_APP_API_BASE}/api/reports/quartile`,
                      { bySpecies, values },
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    showNotification('Quartile values saved.', 'success');
                    setShowQuartileModal(false);
                  } catch (e) {
                    console.error(e);
                    showNotification(
                      e.response?.data?.message || e.message || 'Failed to save quartile',
                      'error'
                    );
                  } finally {
                    setQuartileSaving(false);
                  }
                }}
              >
                {quartileSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showStockModal && (
        <div className="csv-popup-overlay">
          <div className="csv-popup-content modern" style={{ maxWidth: 400, width: '100%', position: 'relative', paddingBottom: 40 }}>
            <button
              onClick={() => setShowStockModal(false)}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: 'none',
                border: 'none',
                fontSize: 22,
                color: 'var(--text-muted)',
                cursor: 'pointer',
                zIndex: 2
              }}
              aria-label="Close"
            >
              ×
            </button>
            <div className="csv-popup-header">
              <h3 style={{ marginBottom: 12 }}>Manage Stock: <span style={{ color: 'var(--text-accent)' }}>{editStockKey}</span></h3>
            </div>
            <div className="csv-popup-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <input
                type="number"
                value={editStockValue}
                onChange={e => setEditStockValue(e.target.value)}
                placeholder="Enter amount to add"
                style={{
                  padding: '10px',
                  borderRadius: 6,
                  border: '1px solid #ccc',
                  fontSize: 16,
                  marginBottom: 8
                }}
              />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginBottom: 10, marginRight: 5 }}>
                <button
                  className="popup-btnsubmit primary"
                  onClick={async () => {
                    await axios.post(`${process.env.REACT_APP_API_BASE}/api/stock/add`, {
                      key: editStockKey,
                      value: Number(editStockValue)
                    });
                    setShowStockModal(false);
                    axios.get(`${process.env.REACT_APP_API_BASE}/api/stock`).then(res => setStock(res.data));
                  }}
                >
                  + Add
                </button>

                <button
                  className="popup-btncancle secondary"
                  onClick={async () => {
                    await axios.post(`${process.env.REACT_APP_API_BASE}/api/stock/add`, {
                      key: editStockKey,
                      value: -Number(editStockValue)
                    });
                    setShowStockModal(false);
                    axios.get(`${process.env.REACT_APP_API_BASE}/api/stock`).then(res => setStock(res.data));
                  }}
                >
                  - Remove
                </button>

                <button
                  className="popup-btn secondary"
                  onClick={async () => {
                    await axios.post(`${process.env.REACT_APP_API_BASE}/api/stock/reset`, { key: editStockKey });
                    setShowStockModal(false);
                    axios.get(`${process.env.REACT_APP_API_BASE}/api/stock`).then(res => setStock(res.data));
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModifyModal && (
        <div className="csv-popup-overlay">
          <div className="csv-popup-content modern">
            <div className="csv-popup-header">
              <h3>Manual Supplement Selection</h3>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginTop: '5px' }}>
                All supplements for this panel are shown. Manual selection will FORCE USE selected supplements regardless of stock levels.
              </div>
            </div>
            <div className="csv-popup-body">
              {/* Show all supplements for the current format */}
              {(() => {
                // Get latest stock for display purposes
                const latestStock = calculateUpdatedStock();
                const allSupplements = getAllPanelSupplements(reportMode);
                
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {allSupplements.map(supp => {
                      const currentStock = latestStock[supp] || 0;
                      const isLowStock = currentStock < stockThreshold;
                      return (
                        <label key={supp} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          padding: '8px',
                          borderRadius: '4px',
                          backgroundColor: isLowStock ? 'var(--warning-soft)' : 'transparent',
                          border: isLowStock ? '1px solid #ffeaa7' : 'none'
                        }}>
                          <input
                            type="checkbox"
                            checked={manualSupplements[modifyRowIndex]?.includes(supp) || false}
                            onChange={e => {
                              setManualSupplements(prev => {
                                const current = prev[modifyRowIndex] || [];
                                if (e.target.checked) {
                                  return { ...prev, [modifyRowIndex]: [...current, supp] };
                                } else {
                                  return { ...prev, [modifyRowIndex]: current.filter(s => s !== supp) };
                                }
                              });
                            }}
                          />
                          <span style={{ 
                            color: isLowStock ? 'var(--warning-text)' : 'var(--text-primary)',
                            fontWeight: isLowStock ? 'bold' : 'normal'
                          }}>
                            {supp}
                          </span>
                          <span style={{ 
                            fontSize: '12px', 
                            color: isLowStock ? 'var(--warning-text)' : 'var(--text-muted)', 
                            marginLeft: 'auto',
                            fontWeight: isLowStock ? 'bold' : 'normal'
                          }}>
                            Stock: {currentStock}
                            {isLowStock && <span style={{ color: 'var(--danger-text)', marginLeft: '4px' }}>(Low)</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <div className="csv-popup-footer">
              <button className="popup-btn secondary" onClick={() => setShowModifyModal(false)}>Back</button>
              <button 
                className="popup-btn primary" 
                onClick={async () => {
                  const selectedSupplements = manualSupplements[modifyRowIndex] || [];
                  
                  // Force use the manually selected supplements without stock checking
                  console.log('=== MANUAL SUPPLEMENT EDIT - FORCE OVERRIDE ===');
                  console.log('Row Index:', modifyRowIndex);
                  console.log('Manually Selected Supplements (FORCE OVERRIDE):', selectedSupplements);
                  console.log('Stock checking DISABLED for manual selection');
                  console.log('=== END FORCE OVERRIDE ===');
                  
                  // Update supplement results with the exact manual selection (no stock checking)
                  setSupplementResults(prev => ({ ...prev, [modifyRowIndex]: selectedSupplements }));
                  
                  // Calculate and update coverage analysis for the modified row
                  const currentRow = csvPreviewRows[modifyRowIndex];
                  if (currentRow) {
                    // Get the appropriate config based on report mode
                    const configInfo = reportMode === '9P' ? choosing_supplement_9P[0].info :
                      reportMode === 'oms' ? choosing_supplement_oms[0].info :
                      reportMode === 'Allergy and Skin' ? choosing_supplement_AMG[0].info : reportMode === 'Allergy and Skin_kids' ? qpt_choosing_supplement[0].info :
                      reportMode === 'Toxin' ? choosing_supplement_toxin[0].info :
                      ['bwcallergy', 'bwccore', 'bwcdiamond', 'BWC-A', 'BWC-C', 'BWC-D'].includes(reportMode) ? choosing_supplement_bwc[0].info :
                      qpt_choosing_supplement[0].info;
                    
                    const normalizedRow = normalizeRowKeys(currentRow, configInfo);
                    const excludedSupplements = getFdaExcludedSupplements(modifyRowIndex);
                    const durationExclusions = appliedDurations[modifyRowIndex] || {};
                    
                    // Calculate coverage with the manually selected supplements (no stock filtering)
                    const coverageResult = calculateCoverageForManualSupplements(normalizedRow, reportMode, selectedSupplements);
                    
                    // Update coverage results with the new analysis
                    setCoverageResults(prev => ({ ...prev, [modifyRowIndex]: coverageResult }));
                    
                    console.log('Coverage Analysis (FORCE OVERRIDE):', coverageResult);
                  }
                  
                  setConfirmedRows(prev => ({ ...prev, [modifyRowIndex]: true }));
                  setLoadingConfirm(prev => ({ ...prev, [modifyRowIndex]: true }));
                  
                  // Simulate loading for 1 second
                  setTimeout(() => {
                    setLoadingConfirm(prev => ({ ...prev, [modifyRowIndex]: false }));
                    setShowModifyModal(false);
                  }, 1000);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {loadingConfirm[modifyRowIndex] ? (
                  <div className="loading-spinner" style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #ffffff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                ) : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmAllDialog && <ConfirmAllDialog />}
      {showDeleteConfirmDialog && <DeleteConfirmDialog />}
      {showSuccessDialog && <SuccessDialog />}
      {showStockDecisionModal && <StockDecisionModal />}
      {showBiopetFallbackModal && <BiopetFallbackModal />}
      {showBiopetStockDecisionModal && <BiopetStockDecisionModal />}

      {/* Panel Selection Modal for CSV Export */}
      {showPanelSelectionModal && (
        <div className="csv-popup-overlay" style={{ zIndex: 10000 }}>
          <div className="csv-popup-content modern" style={{ maxWidth: '500px' }}>
            <div className="csv-popup-header">
              <h3>Select Panel to Export</h3>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginTop: '5px' }}>
                Choose which panel you want to export to CSV
                {debouncedFilter && (
                  <span style={{ display: 'block', marginTop: '5px', color: 'var(--text-accent)' }}>
                    Current filter: {selectedFilterColumn} = "{debouncedFilter}"
                  </span>
                )}
              </div>
            </div>
            <div className="csv-popup-body" style={{ padding: '20px' }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '12px',
                maxHeight: '300px',
                overflowY: 'auto',
                paddingRight: '8px'
              }}>
                {availablePanels.map(panel => (
                  <button
                    key={panel}
                    onClick={() => handlePanelSelection(panel)}
                    style={{
                      backgroundColor: 'var(--bg-surface-2)',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#e9ecef';
                      e.target.style.borderColor = '#007bff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.borderColor = '#dee2e6';
                    }}
                  >
                    <span style={{ fontWeight: '500' }}>{panel}</span>
                    <img 
                      src={`${process.env.PUBLIC_URL}/export.png`}
                      alt="Export"
                      style={{ width: '24px', height: '24px' }}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div className="csv-popup-footer">
              <button 
                className="popup-btn secondary" 
                onClick={() => setShowPanelSelectionModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Period Selection Modal for CSV Export */}
      {showPeriodSelectionModal && (
        <div className="csv-popup-overlay" style={{ zIndex: 10000 }}>
          <div className="csv-popup-content modern" style={{ maxWidth: '600px' }}>
            <div className="csv-popup-header">
              <h3>Select Period to Export</h3>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginTop: '5px' }}>
                Choose which period you want to export for panel "{selectedPanelForExport}"
                {debouncedFilter && (
                  <span style={{ display: 'block', marginTop: '5px', color: 'var(--text-accent)' }}>
                    Current filter: {selectedFilterColumn} = "{debouncedFilter}"
                  </span>
                )}
              </div>
            </div>
            <div className="csv-popup-body" style={{ 
              padding: '20px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Manual Period Selection */}
                <div style={{ 
                  border: '1px solid #dee2e6', 
                  borderRadius: '6px', 
                  padding: '16px',
                  backgroundColor: 'var(--bg-surface-2)'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    Manual Period Selection
                  </h4>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '500' }}>Year:</label>
                      <input
                        type="number"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '14px',
                          width: '80px'
                        }}
                        min="2000"
                        max="2030"
                        placeholder="2025"
                      />
                    </div>
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '500' }}>Months:</label>
                        <label style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          padding: '6px',
                          fontWeight: '600'
                        }}>
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMonths(['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']);
                              } else {
                                setSelectedMonths([]);
                              }
                            }}
                            style={{ margin: 0 }}
                          />
                          All Months
                        </label>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(3, 1fr)', 
                          gap: '8px',
                          padding: '12px',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          backgroundColor: 'var(--bg-surface)',
                          minWidth: '300px'
                        }}>
                        {[
                          { value: '01', label: 'January' },
                          { value: '02', label: 'February' },
                          { value: '03', label: 'March' },
                          { value: '04', label: 'April' },
                          { value: '05', label: 'May' },
                          { value: '06', label: 'June' },
                          { value: '07', label: 'July' },
                          { value: '08', label: 'August' },
                          { value: '09', label: 'September' },
                          { value: '10', label: 'October' },
                          { value: '11', label: 'November' },
                          { value: '12', label: 'December' }
                        ].map(month => (
                          <label key={month.value} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            fontSize: '13px',
                            cursor: 'pointer',
                            padding: '6px',
                            fontWeight: '500'
                          }}>
                            <input
                              type="checkbox"
                              checked={selectedMonths.includes(month.value)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMonths([...selectedMonths, month.value]);
                                } else {
                                  setSelectedMonths(selectedMonths.filter(m => m !== month.value));
                                }
                              }}
                              style={{ margin: 0 }}
                            />
                            {month.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (selectedYear && selectedMonths.length > 0) {
                          // Export for multiple months with feedback
                          exportMultipleMonthsToCSV(selectedPanelForExport, selectedYear, selectedMonths);
                        } else {
                          alert('Please select both year and at least one month.');
                        }
                      }}
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        marginTop: '16px',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#0056b3';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#007bff';
                      }}
                    >
                      Export
                    </button>
                  </div>
                </div>

                {/* Divider */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  margin: '8px 0'
                }}>
                  <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--bg-surface-2)' }}></div>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '500' }}>OR</span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--bg-surface-2)' }}></div>
                </div>

                {/* Quick Options */}
                <div>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    Quick Options
                  </h4>
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '8px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    paddingRight: '8px'
                  }}>
                    <button
                      onClick={() => exportFilteredReportsToCSV(selectedPanelForExport)}
                      style={{
                        backgroundColor: 'var(--bg-surface-2)',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#e9ecef';
                        e.target.style.borderColor = '#007bff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f8f9fa';
                        e.target.style.borderColor = '#dee2e6';
                      }}
                    >
                      <span style={{ fontWeight: '500' }}>All Periods</span>
                                              <img 
                          src={`${process.env.PUBLIC_URL}/export.png`}
                          alt="Export"
                          style={{ width: '24px', height: '24px' }}
                        />
                    </button>
                    {availablePeriods.map(period => (
                      <button
                        key={period}
                        onClick={() => exportFilteredReportsToCSV(selectedPanelForExport, period)}
                        style={{
                          backgroundColor: 'var(--bg-surface-2)',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px',
                          padding: '12px 16px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          textAlign: 'left',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#e9ecef';
                          e.target.style.borderColor = '#007bff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#f8f9fa';
                          e.target.style.borderColor = '#dee2e6';
                        }}
                      >
                        <span style={{ fontWeight: '500' }}>{period}</span>
                        <img 
                          src={`${process.env.PUBLIC_URL}/export.png`}
                          alt="Export"
                          style={{ width: '24px', height: '24px' }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="csv-popup-footer">
              <button 
                className="popup-btn secondary" 
                onClick={() => {
                  setShowPeriodSelectionModal(false);
                  setShowPanelSelectionModal(true);
                }}
              >
                Back to Panels
              </button>
              <button 
                className="popup-btn secondary" 
                onClick={() => setShowPeriodSelectionModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Success Popup */}
      {showExportSuccessPopup && (
        <div className="csv-popup-overlay" style={{ zIndex: 10001 }}>
          <div className="csv-popup-content modern" style={{ 
            maxWidth: '500px',
            background: 'linear-gradient(to right top, #3167b7, #457eca, #5a96dc, #71afee, #89c7ff)',
            color: 'white',
            border: 'none'
          }}>
            <div className="csv-popup-header" style={{ 
              textAlign: 'center',
              padding: '24px 24px 16px 24px'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px auto',
                fontSize: '28px'
              }}>
                ✓
              </div>
              <h3 style={{ 
                margin: '0 0 8px 0', 
                fontSize: '20px', 
                fontWeight: '600',
                color: 'white'
              }}>
                Export Successful!
              </h3>
              <div style={{ 
                fontSize: '14px', 
                color: 'rgba(255, 255, 255, 0.9)', 
                lineHeight: '1.5',
                whiteSpace: 'pre-line'
              }}>
                {exportSuccessMessage}
              </div>
            </div>
            <div className="csv-popup-footer" style={{ 
              padding: '16px 24px 24px 24px',
              textAlign: 'center'
            }}>
              <button 
                className="popup-btn primary" 
                onClick={() => setShowExportSuccessPopup(false)}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '6px',
                  padding: '10px 24px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {isProcessingCsv && (
        <div className="csv-popup-overlay" style={{ zIndex: 10000 }}>
          <div style={{
            maxWidth: 400,
            margin: '120px auto',
            background: 'var(--bg-surface-2)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '8px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            padding: '32px',
            textAlign: 'center',
            fontSize: '1.2rem',
            color: 'var(--text-primary)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div className="loading-spinner" style={{
                width: '32px',
                height: '32px',
                border: '4px solid rgb(58, 199, 39)',
                borderTop: '4px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span>Processing CSV...</span>
            </div>
            <div style={{
              marginBottom: '16px',
              fontSize: '16px',
              color: 'var(--text-muted)',
              fontWeight: '500'
            }}>
              {csvProgressText}
            </div>
            <div style={{
              width: '100%',
              height: '12px',
              backgroundColor: 'var(--bg-surface-2)',
              borderRadius: '6px',
              overflow: 'hidden',
              marginBottom: '8px'
            }}>
              <div style={{
                width: `${(csvProgress.current / csvProgress.total) * 100}%`,
                height: '100%',
                backgroundColor: '#007bff',
                transition: 'width 0.3s ease',
                borderRadius: '6px'
              }} />
            </div>
            <div style={{
              fontSize: '14px',
              color: 'var(--text-muted)'
            }}>
              {csvProgress.current} of {csvProgress.total} rows processed
            </div>
          </div>
        </div>
      )}

      {pdfPreviewLoading && (
        <div className="csv-popup-overlay" style={{ zIndex: 10000 }}>
          <div style={{
            maxWidth: 350,
            margin: '120px auto',
            background: 'var(--bg-surface-2)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '8px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            padding: '32px',
            textAlign: 'center',
            fontSize: '1.2rem',
            color: 'var(--text-primary)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px'
            }}>
              <div className="loading-spinner" style={{
                width: '32px',
                height: '32px',
                border: '4px solid rgb(58, 199, 39)',
                borderTop: '4px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span>{pdfPreviewThai ? 'Generating Thai version...' : 'Generating English version...'}</span>
            </div>
          </div>
        </div>
      )}

      {showPdfPreview && pdfPreviewFormData && pdfPreviewReportMode && !pdfPreviewLoading && (
        <PdfStaticModal
          formData={pdfPreviewFormData}
          fromCsvPreview={false}
          useThaiVersion={pdfPreviewThai}
          duration={pdfPreviewFormData?.['duration'] || pdfPreviewFormData?.['Probiotics (months)'] || null}
          pdfBasePath={
            pdfPreviewReportMode === '9P'
              ? (pdfPreviewThai ? '/9P/thai' : '/9P/eng')
              : pdfPreviewThai
              ? '/pdf_QPTthai'
              : '/pdfpages'
          }
          totalPages={pdfPreviewReportMode === '9P' ? (pdfPreviewThai ? 32 : 22) : 38}
          reportMode={pdfPreviewReportMode}
          selectedSupplements={pdfPreviewSupplements}
          stock={stock}
          csvPreviewToggle={csvPreviewToggle}
          supplement9PVersion={supplement9PVersion}
          onClose={() => {
            setShowPdfPreview(false);
            setPdfPreviewFormData(null);
            setPdfPreviewReportMode(null);
            setPdfPreviewSupplements([]);
          }}
          onBackToCsv={() => {
            setShowPdfPreview(false);
            setPdfPreviewFormData(null);
            setPdfPreviewReportMode(null);
            setPdfPreviewSupplements([]);
          }}
          onToggleLanguage={() =>
            setPdfPreviewThai((prev) => !prev)
          }
          onlyBackButton={true}
        />
      )}

      {/* Inventory Popup */}
      {showInventoryPopup && (
        <div className="csv-popup-overlay" style={{ zIndex: 10001 }}>
          <div className="csv-popup-content modern" style={{ width: '1080px', maxWidth: '92%', maxHeight: '85vh' }}>
            <div className="csv-popup-header" style={{ position: 'relative' }}>
              <h3>Amount of Supplements Selection</h3>
              <button
                onClick={() => setShowInventoryPopup(false)}
                style={{
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f0f0f0';
                  e.target.style.color = '#333';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#888';
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="csv-popup-body" style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
              {/* Stock Level Checkbox */}
              <div style={{ 
                marginBottom: '15px', 
                padding: '10px', 
                backgroundColor: 'var(--bg-surface-2)', 
                borderRadius: '6px',
                border: '1px solid #dee2e6'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-muted)'
                }}>
                  <input
                    type="checkbox"
                    checked={ignoreStockLevels}
                    onChange={(e) => setIgnoreStockLevels(e.target.checked)}
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer'
                    }}
                  />
                  Ignore stock level conditions (allow selection regardless of available stock)
                </label>
              </div>
              
                <table className="csv-popup-table" style={{ tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '200px', textAlign: 'left' }}>Supplement</th>
                      <th style={{ width: '100px', textAlign: 'center' }}>None</th>
                      {DURATION_OPTIONS.map((opt) => (
                        <th key={opt.key} style={{ width: '110px', textAlign: 'center' }}>{opt.label}</th>
                      ))}
                    </tr>
                  </thead>
                <tbody>
                  {(() => {
                    // Get supplements based on selected plan
                    const selectedPlan = selectedPlans[inventoryRowIndex];
                    let supplements = [];
                    
                    if (selectedPlan === 'Plan A') {
                      supplements = planAResults[inventoryRowIndex]?.supplements || [];
                    } else if (selectedPlan === 'Plan B') {
                      supplements = planBResults[inventoryRowIndex]?.supplements || [];
                    } else if (selectedPlan === 'Plan C') {
                      supplements = Object.keys(selectedSupplements).filter(supplement => selectedSupplements[supplement]);
                    } else {
                      // Fallback to default supplements if no plan is selected
                      supplements = supplementResults[inventoryRowIndex] || [];
                    }
                    
                    const currentDurations = supplementDurations[inventoryRowIndex] || {};
                    
                    return supplements.map((supplement, index) => (
                      <tr key={index}>
                        <td style={{ textAlign: 'left', fontWeight: '500' }}>{getSupplementDisplayName(supplement)}</td>
                        <td style={{ textAlign: 'center' }}>
                          <img
                            src={currentDurations[supplement] === 'None' ? "/ch_box.png" : "/empty_ch.png"}
                            alt="None"
                            onClick={() => handleDurationChange(supplement, 'None')}
                            style={{
                              width: '20px',
                              height: '20px',
                              cursor: 'pointer',
                              transition: 'transform 0.1s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'scale(1)';
                            }}
                          />
                        </td>
                        {DURATION_OPTIONS.map((opt) => {
                          const enabled = hasSufficientStock(supplement, opt.key, popupStock);
                          const selected = currentDurations[supplement] === opt.key;
                          return (
                            <td key={opt.key} style={{ textAlign: 'center' }}>
                              <img
                                src={selected && enabled ? "/ch_box.png" : "/empty_ch.png"}
                                alt={opt.key}
                                onClick={enabled ? () => handleDurationChange(supplement, opt.key) : undefined}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  cursor: enabled ? 'pointer' : 'not-allowed',
                                  transition: 'transform 0.1s ease',
                                  opacity: enabled ? 1 : 0.5
                                }}
                                onMouseEnter={(e) => {
                                  if (enabled) {
                                    e.target.style.transform = 'scale(1.1)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'scale(1)';
                                }}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>

            <div className="csv-popup-footer" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', flex: '1 1 auto', minWidth: 0 }}>
                <button
                  className="popup-btn secondary font_web"
                  onClick={() => handleBulkDurationChange('None')}
                  style={{
                    backgroundColor: 'var(--bg-surface-2)',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    padding: '0 10px',
                    minWidth: '72px',
                    height: '32px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.3s ease',
                    fontWeight: '500'
                  }}
                >
                  None
                </button>
                {DURATION_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    className="popup-btn secondary font_web"
                    onClick={() => handleBulkDurationChange(opt.key)}
                    style={{
                      backgroundColor: 'var(--bg-surface-2)',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      padding: '0 10px',
                      minWidth: '72px',
                      height: '32px',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.3s ease',
                      fontWeight: '500'
                    }}
                  >
                    {opt.months} Months
                  </button>
                ))}
              </div>

              <button
                className="popup-btn primary font_web"
                onClick={() => {
                  // Apply the duration selections
                  setAppliedDurations(prev => ({
                    ...prev,
                    [inventoryRowIndex]: supplementDurations[inventoryRowIndex]
                  }));
                  // console.log('Applied durations for row', inventoryRowIndex, ':', supplementDurations[inventoryRowIndex]);
                  setShowInventoryPopup(false);
                }}
                style={{
                  backgroundColor: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  minWidth: '80px',
                  height: '32px',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '14px',
                  flex: '0 0 auto',
                  marginLeft: 'auto',
                  transition: 'all 0.3s ease',
                  fontWeight: '500'
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {showClientIdPopup && <ClientIdTypePopup />}

      {/* Supplement Plan Selection Modal */}
      {showSupplementPopup && supplementPopupRowIndex !== null && (
        <div className="csv-popup-overlay" style={{ zIndex: 10002 }}>
          <div className="csv-popup-content modern" style={{ width: '900px', maxWidth: '95%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="csv-popup-header" style={{ position: 'relative' }}>
              <h3>Choose Supplement Plan</h3>
              <button
                onClick={() => setShowSupplementPopup(false)}
                style={{
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f0f0f0';
                  e.target.style.color = '#333';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#888';
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className="csv-popup-body">
              {(() => {
                const supplements = supplementResults[supplementPopupRowIndex] || [];
                const coverage = coverageResults[supplementPopupRowIndex];
                const excludedSupplements = getFdaExcludedSupplements(supplementPopupRowIndex);
                
                // Get stored Plan A and Plan B results
                const planAResult = planAResults[supplementPopupRowIndex];
                const planBResult = planBResults[supplementPopupRowIndex];
                
                // Plan A supplements (no stock conditions) - use stored result
                const planASupplements = planAResult ? planAResult.supplements : [];
                
                // Plan B supplements (with stock conditions) - use stored result
                const planBSupplements = planBResult ? planBResult.supplements : [];

                // Use stored coverage results for each plan
                const planACoverage = planAResult ? planAResult.coverage : { covered: 0, total: 0, uncovered: [] };
                const planBCoverage = planBResult ? planBResult.coverage : { covered: 0, total: 0, uncovered: [] };
                

                
                if (loadingConfirm[supplementPopupRowIndex]) {
                  return (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '20px',
                      gap: '10px'
                    }}>
                      <div className="loading-spinner" style={{
                        width: '30px',
                        height: '30px',
                        border: '3px solid #f3f3f3',
                        borderTop: '3px solid #3498db',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      <div style={{
                        color: 'var(--text-muted)',
                        fontSize: '14px'
                      }}>
                        Calculating supplements...
                      </div>
    </div>
  );
                }

                return (
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '15px', justifyContent: 'space-between' }}>
                    
                    {/* Plan A - No Stock Conditions */}
                    <div 
                      style={{
                        border: '2px solid #4CAF50',
                        borderRadius: '8px',
                        padding: '15px',
                        cursor: 'pointer',
                        backgroundColor: 'var(--success-soft)',
                        transition: 'all 0.3s ease',
                        flex: '1',
                        minWidth: '0'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#e8f5e8'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#f8fff8'}
                      onClick={() => {
                        console.log('Plan A selected - No stock conditions');
                        setSelectedPlans(prev => ({ ...prev, [supplementPopupRowIndex]: 'Plan A' }));
                        
                        // Apply Plan A supplements to the CSV preview
                        const planASupplements = planAResults[supplementPopupRowIndex]?.supplements || [];
                        setSupplementResults(prev => ({
                          ...prev,
                          [supplementPopupRowIndex]: planASupplements
                        }));
                        
                        // Auto-fill durations from CSV 'Probiotics (months)' column when plan is selected
                        const csvRow = csvPreviewRows[supplementPopupRowIndex];
                        const probioticsMonths = csvRow?.['Probiotics (months)'];
                        
                        // Handle "none", empty, or invalid values by setting "No Duration"
                        if (probioticsMonths === undefined || probioticsMonths === null || probioticsMonths === '' || 
                            probioticsMonths.toString().toLowerCase() === 'none' || probioticsMonths.toString().toLowerCase() === 'n/a' ||
                            probioticsMonths.toString().toLowerCase() === '0' || probioticsMonths.toString().toLowerCase() === 'none') {
                          
                          // Set "No Duration" for all supplements in this row
                          const planASupplements = planAResults[supplementPopupRowIndex]?.supplements || [];
                          const rowDurations = {};
                          planASupplements.forEach(supplement => {
                            rowDurations[supplement] = 'No Duration';
                          });
                          
                          // Apply the durations
                          setSupplementDurations(prev => ({
                            ...prev,
                            [supplementPopupRowIndex]: rowDurations
                          }));
                          
                          setAppliedDurations(prev => ({
                            ...prev,
                            [supplementPopupRowIndex]: rowDurations
                          }));
                          
                          console.log(`✅ Auto-filled "No Duration" for Plan A based on CSV value: ${probioticsMonths}`);
                          
                          // Auto-confirm the selection since durations are now set
                          setTimeout(() => {
                            console.log(`Auto-confirming Plan A for row ${supplementPopupRowIndex} with No Duration`);
                            setShowSupplementPopup(false);
                            handleAutoConfirm(supplementPopupRowIndex, 'Plan A');
                          }, 100);
                          
                        } else {
                          // Handle numeric values
                          const monthsValue = parseInt(probioticsMonths);
                          if (!isNaN(monthsValue)) {
                            let durationKey = 'None';
                            
                            // Map months to duration keys
                            if (monthsValue === 1) {
                              durationKey = '1 months';
                            } else if (monthsValue === 3) {
                              durationKey = '3 months';
                            } else if (monthsValue === 6) {
                              durationKey = '6 months';
                            } else if (monthsValue === 12) {
                              durationKey = '12 months';
                            }
                            
                            if (durationKey !== 'None') {
                              // Get supplements for this plan
                              const planASupplements = planAResults[supplementPopupRowIndex]?.supplements || [];
                              
                              // Set duration for all supplements in this row
                              const rowDurations = {};
                              planASupplements.forEach(supplement => {
                                rowDurations[supplement] = durationKey;
                              });
                              
                              // Apply the durations
                              setSupplementDurations(prev => ({
                                ...prev,
                                [supplementPopupRowIndex]: rowDurations
                              }));
                              
                              setAppliedDurations(prev => ({
                                ...prev,
                                [supplementPopupRowIndex]: rowDurations
                              }));
                              
                              console.log(`✅ Auto-filled durations for Plan A: ${durationKey} based on CSV value ${monthsValue}`);
                              
                              // Auto-confirm the selection since durations are now set
                              setTimeout(() => {
                                console.log(`Auto-confirming Plan A for row ${supplementPopupRowIndex}`);
                                setShowSupplementPopup(false);
                                handleAutoConfirm(supplementPopupRowIndex, 'Plan A');
                              }, 100);
                            } else {
                              setShowSupplementPopup(false);
                            }
                          } else {
                            setShowSupplementPopup(false);
                          }
                        }
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h4 style={{ margin: '0', color: 'var(--success-text)' }}>Plan A - All Supplements</h4>
                        <span style={{ 
                          backgroundColor: '#4CAF50', 
                          color: 'white', 
                          padding: '4px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }}>
                          {planASupplements ? planASupplements.length : 0} supp.
                        </span>
                      </div>
                      <p style={{ margin: '0 0 10px 0', color: 'var(--text-muted)', fontSize: '13px' }}>
                        <strong>Ignore stock levels</strong>
                      </p>
                      
                      {/* Coverage Information for Plan A */}
                      <div style={{ 
                        backgroundColor: (planACoverage && planACoverage.covered === planACoverage.total) ? 'var(--success-soft)' : 'var(--warning-soft)',
                        border: (planACoverage && planACoverage.covered === planACoverage.total) ? '1px solid #c3e6c3' : '1px solid #ffeaa7',
                        borderRadius: '4px',
                        padding: '8px',
                        marginBottom: '10px',
                        fontSize: '12px'
                      }}>
                        <div style={{ 
                          color: (planACoverage && planACoverage.covered === planACoverage.total) ? '#2d5a2d' : 'var(--warning-text)',
                          fontWeight: 'bold',
                          marginBottom: '4px'
                        }}>
                          {(planACoverage && planACoverage.covered === planACoverage.total) ? '✅ Complete Coverage' : '⚠️ Incomplete Coverage'}
                        </div>
                        <div style={{ color: (planACoverage && planACoverage.covered === planACoverage.total) ? '#2d5a2d' : 'var(--warning-text)' }}>
                          <strong>{planACoverage ? `${planACoverage.covered}/${planACoverage.total}` : '0/0'}</strong> species covered
                        </div>
                        
                        {/* Score Bar for Plan A */}
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '4px'
                          }}>
                            <span style={{ fontSize: '11px', color: (planACoverage && planACoverage.covered === planACoverage.total) ? '#2d5a2d' : 'var(--warning-text)' }}>
                              Coverage Score
                            </span>
                            <span style={{ 
                              fontSize: '11px', 
                              fontWeight: 'bold',
                              color: (planACoverage && planACoverage.covered === planACoverage.total) ? '#2d5a2d' : 'var(--warning-text)'
                            }}>
                              {planACoverage && planACoverage.total > 0 ? Math.round((planACoverage.covered / planACoverage.total) * 100) : 0}%
                            </span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: 'var(--bg-surface-2)',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${planACoverage && planACoverage.total > 0 ? (planACoverage.covered / planACoverage.total) * 100 : 0}%`,
                              height: '100%',
                              backgroundColor: (planACoverage && planACoverage.covered === planACoverage.total) ? '#4CAF50' : '#FF9800',
                              borderRadius: '4px',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>
                        
                        {planACoverage && planACoverage.uncovered && planACoverage.uncovered.length > 0 && (
                          <div style={{ fontSize: '11px', marginTop: '8px', color: 'var(--warning-text)' }}>
                            Missing: {planACoverage.uncovered.join(', ')}
                          </div>
                        )}
                      </div>
                      
                      {/* Plan A Supplements List */}
                      <div style={{ maxHeight: '120px', overflowY: 'auto', backgroundColor: 'var(--bg-surface)', borderRadius: '4px', padding: '8px' }}>
                        {planASupplements && planASupplements.length > 0 ? (
                          planASupplements.map((supplement, idx) => {
                            const currentStock = stock[supplement] || 0;
                            return (
                              <div key={idx} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 0',
                                borderBottom: idx < (planASupplements ? planASupplements.length : 0) - 1 ? '1px solid #eee' : 'none',
                                fontSize: '12px'
                              }}>
                                <span style={{ fontWeight: 'bold', color: 'var(--text-accent)' }}>{getSupplementDisplayName(supplement)}</span>
                                <span style={{ 
                                  color: currentStock < stockThreshold ? 'var(--danger-text)' : '#00b894',
                                  fontSize: '11px',
                                  fontWeight: 'bold'
                                }}>
                                  {currentStock} units
                                </span>
                              </div>
                            );
                          })
                        ) : (
                          <div style={{ color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center' }}>
                            No supplements available
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Plan B - With Stock Conditions */}
                    <div 
                      style={{
                        border: '2px solid #2196F3',
                        borderRadius: '8px',
                        padding: '15px',
                        cursor: 'pointer',
                        backgroundColor: 'var(--accent-soft)',
                        transition: 'all 0.3s ease',
                        flex: '1',
                        minWidth: '0'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#e3f2fd'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#f5f9ff'}
                      onClick={() => {
                        console.log('Plan B selected - With stock conditions');
                        setSelectedPlans(prev => ({ ...prev, [supplementPopupRowIndex]: 'Plan B' }));
                        
                        // Apply Plan B supplements to the CSV preview
                        const planBSupplements = planBResults[supplementPopupRowIndex]?.supplements || [];
                        setSupplementResults(prev => ({
                          ...prev,
                          [supplementPopupRowIndex]: planBSupplements
                        }));
                        
                        // Auto-fill durations from CSV 'Probiotics (months)' column when plan is selected
                        const csvRow = csvPreviewRows[supplementPopupRowIndex];
                        const probioticsMonths = csvRow?.['Probiotics (months)'];
                        
                        // Handle "none", empty, or invalid values by setting "No Duration"
                        if (probioticsMonths === undefined || probioticsMonths === null || probioticsMonths === '' || 
                            probioticsMonths.toString().toLowerCase() === 'none' || probioticsMonths.toString().toLowerCase() === 'n/a' ||
                            probioticsMonths.toString().toLowerCase() === '0' || probioticsMonths.toString().toLowerCase() === 'none') {
                          
                          // Set "No Duration" for all supplements in this row
                          const planBSupplements = planBResults[supplementPopupRowIndex]?.supplements || [];
                          const rowDurations = {};
                          planBSupplements.forEach(supplement => {
                            rowDurations[supplement] = 'No Duration';
                          });
                          
                          // Apply the durations
                          setSupplementDurations(prev => ({
                            ...prev,
                            [supplementPopupRowIndex]: rowDurations
                          }));
                          
                          setAppliedDurations(prev => ({
                            ...prev,
                            [supplementPopupRowIndex]: rowDurations
                          }));
                          
                          console.log(`✅ Auto-filled "No Duration" for Plan B based on CSV value: ${probioticsMonths}`);
                          
                          // Auto-confirm the selection since durations are now set
                          setTimeout(() => {
                            console.log(`Auto-confirming Plan B for row ${supplementPopupRowIndex} with No Duration`);
                            setShowSupplementPopup(false);
                            handleAutoConfirm(supplementPopupRowIndex, 'Plan B');
                          }, 100);
                          
                        } else {
                          // Handle numeric values
                          const monthsValue = parseInt(probioticsMonths);
                          if (!isNaN(monthsValue)) {
                            let durationKey = 'None';
                            
                            // Map months to duration keys
                            if (monthsValue === 1) {
                              durationKey = '1 months';
                            } else if (monthsValue === 3) {
                              durationKey = '3 months';
                            } else if (monthsValue === 6) {
                              durationKey = '6 months';
                            } else if (monthsValue === 12) {
                              durationKey = '12 months';
                            }
                            
                            if (durationKey !== 'None') {
                              // Get supplements for this plan
                              const planBSupplements = planBResults[supplementPopupRowIndex]?.supplements || [];
                              
                              // Set duration for all supplements in this row
                              const rowDurations = {};
                              planBSupplements.forEach(supplement => {
                                rowDurations[supplement] = durationKey;
                              });
                              
                              // Apply the durations
                              setSupplementDurations(prev => ({
                                ...prev,
                                [supplementPopupRowIndex]: rowDurations
                              }));
                              
                              setAppliedDurations(prev => ({
                                ...prev,
                                [supplementPopupRowIndex]: rowDurations
                              }));
                              
                              console.log(`✅ Auto-filled durations for Plan B: ${durationKey} based on CSV value ${monthsValue}`);
                              
                              // Auto-confirm the selection since durations are now set
                              setTimeout(() => {
                                console.log(`Auto-confirming Plan B for row ${supplementPopupRowIndex}`);
                                setShowSupplementPopup(false);
                                handleAutoConfirm(supplementPopupRowIndex, 'Plan B');
                              }, 100);
                            } else {
                              setShowSupplementPopup(false);
                            }
                          } else {
                            setShowSupplementPopup(false);
                          }
                        }
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h4 style={{ margin: '0', color: 'var(--text-accent)' }}>Plan B - Stock-Aware</h4>
                        <span style={{ 
                          backgroundColor: '#2196F3', 
                          color: 'white', 
                          padding: '4px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {planBSupplements ? planBSupplements.length : 0} supp.
                        </span>
                      </div>
                      <p style={{ margin: '0 0 10px 0', color: 'var(--text-muted)', fontSize: '13px' }}>
                        <strong>Supplements with sufficient stock levels</strong>
                      </p>
                      
                      {/* Coverage Information for Plan B */}
                      <div style={{ 
                        backgroundColor: (planBCoverage && planBCoverage.covered === planBCoverage.total) ? 'var(--success-soft)' : 'var(--warning-soft)',
                        border: (planBCoverage && planBCoverage.covered === planBCoverage.total) ? '1px solid #c3e6c3' : '1px solid #ffeaa7',
                        borderRadius: '4px',
                        padding: '8px',
                        marginBottom: '10px',
                        fontSize: '12px'
                      }}>
                        <div style={{ 
                          color: (planBCoverage && planBCoverage.covered === planBCoverage.total) ? '#2d5a2d' : 'var(--warning-text)',
                          fontWeight: 'bold',
                          marginBottom: '4px'
                        }}>
                          {(planBCoverage && planBCoverage.covered === planBCoverage.total) ? '✅ Complete Coverage' : '⚠️ Incomplete Coverage'}
                        </div>
                        <div style={{ color: (planBCoverage && planBCoverage.covered === planBCoverage.total) ? '#2d5a2d' : 'var(--warning-text)' }}>
                          <strong>{planBCoverage ? `${planBCoverage.covered}/${planBCoverage.total}` : '0/0'}</strong> species covered
                        </div>
                        
                        {/* Score Bar for Plan B */}
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '4px'
                          }}>
                            <span style={{ fontSize: '11px', color: (planBCoverage && planBCoverage.covered === planBCoverage.total) ? '#2d5a2d' : 'var(--warning-text)' }}>
                              Coverage Score
                            </span>
                            <span style={{ 
                              fontSize: '11px', 
                              fontWeight: 'bold',
                              color: (planBCoverage && planBCoverage.covered === planBCoverage.total) ? '#2d5a2d' : 'var(--warning-text)'
                            }}>
                              {planBCoverage && planBCoverage.total > 0 ? Math.round((planBCoverage.covered / planBCoverage.total) * 100) : 0}%
                            </span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: 'var(--bg-surface-2)',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${planBCoverage && planBCoverage.total > 0 ? (planBCoverage.covered / planBCoverage.total) * 100 : 0}%`,
                              height: '100%',
                              backgroundColor: (planBCoverage && planBCoverage.covered === planBCoverage.total) ? '#4CAF50' : '#FF9800',
                              borderRadius: '4px',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>
                        
                        {planBCoverage && planBCoverage.uncovered && planBCoverage.uncovered.length > 0 && (
                          <div style={{ fontSize: '11px', marginTop: '8px', color: 'var(--warning-text)' }}>
                            Missing: {planBCoverage.uncovered.join(', ')}
                          </div>
                        )}
                      </div>
                      
                      {/* Plan B Supplements List */}
                      <div style={{ maxHeight: '120px', overflowY: 'auto', backgroundColor: 'var(--bg-surface)', borderRadius: '4px', padding: '8px' }}>
                        {planBSupplements && planBSupplements.length > 0 ? (
                          planBSupplements.map((supplement, idx) => {
                            const currentStock = stock[supplement] || 0;
                            return (
                              <div key={idx} style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 0',
                                borderBottom: idx < (planBSupplements ? planBSupplements.length : 0) - 1 ? '1px solid #eee' : 'none',
                                fontSize: '12px'
                              }}>
                                <span style={{ fontWeight: 'bold', color: 'var(--text-accent)' }}>{getSupplementDisplayName(supplement)}</span>
                                <span style={{ 
                                  color: 'var(--success-text)',
                                  fontSize: '11px',
                                  fontWeight: 'bold'
                                }}>
                                  {currentStock} units
                                </span>
                              </div>
                            );
                          })
                        ) : (
                          <div style={{ color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center' }}>
                            No supplements with sufficient stock
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Plan C - Manual Selection */}
                    <div 
                      style={{
                        border: '2px solid #FF9800',
                        borderRadius: '8px',
                        padding: '15px',
                        cursor: 'pointer',
                        backgroundColor: 'var(--warning-soft)',
                        transition: 'all 0.3s ease',
                        flex: '1',
                        minWidth: '0'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#fff3e0'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#fff8f0'}
                      onClick={() => {
                        // Get the current row data for manual selection
                        const currentRowData = csvPreviewRows[supplementPopupRowIndex];
                        if (currentRowData) {
                          setFormData(currentRowData);
                        }
                        setSelectedPlans(prev => ({ ...prev, [supplementPopupRowIndex]: 'Plan C' }));
                        setShowManualSelection(true);
                        // Reset selected supplements for this new row
                        setSelectedSupplements({});
                        setShowSupplementPopup(false);
                        setSelectedSupplements({}); // Reset to empty state when opening modal
                        setFdaCompliantOnly(false); // Reset FDA checkbox when opening modal
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h4 style={{ margin: '0', color: 'var(--warning-text)' }}>Plan C - Manual Selection</h4>
                        <span style={{ 
                          backgroundColor: '#FF9800', 
                          color: 'white', 
                          padding: '4px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          Custom
                        </span>
                      </div>
                      <p style={{ margin: '0 0 10px 0', color: 'var(--text-muted)', fontSize: '13px' }}>
                        <strong>Choose supplements manually</strong>
                      </p>
                      
                      {/* Coverage Information for Plan C */}
                      <div style={{ 
                        backgroundColor: 'var(--warning-soft)',
                        border: '1px solid #ffeaa7',
                        borderRadius: '4px',
                        padding: '8px',
                        marginBottom: '10px',
                        fontSize: '12px'
                      }}>
                        <div style={{ 
                          color: 'var(--warning-text)',
                          fontWeight: 'bold',
                          marginBottom: '4px'
                        }}>
                          ⚠️ Manual Selection Required
                        </div>
                        <div style={{ color: 'var(--warning-text)' }}>
                          <strong>{coverage ? coverage.total : 0}</strong> total species available for selection
                        </div>
                        
                        {/* Score Bar for Plan C */}
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '4px'
                          }}>
                            <span style={{ fontSize: '11px', color: 'var(--warning-text)' }}>
                              Available Options
                            </span>
                            <span style={{ 
                              fontSize: '11px', 
                              fontWeight: 'bold',
                              color: 'var(--warning-text)'
                            }}>
                              {coverage ? coverage.total : 0} species
                            </span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: 'var(--bg-surface-2)',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: '100%',
                              height: '100%',
                              backgroundColor: '#FF9800',
                              borderRadius: '4px',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>
                        
                        <div style={{ fontSize: '11px', marginTop: '8px', color: 'var(--warning-text)' }}>
                          Click to open manual selection
                        </div>
                      </div>
                    </div>

                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Duration Selection Modal */}
      {showDurationModal && durationModalRowIndex !== null && (
        <div className="csv-popup-overlay" style={{ zIndex: 10004 }}>
          <div className="csv-popup-content modern" style={{ width: '600px', maxWidth: '95%', maxHeight: '60vh', overflowY: 'auto' }}>
            <div className="csv-popup-header" style={{ position: 'relative' }}>
              <h3>Select Supplement Duration</h3>
              <button
                onClick={() => setShowDurationModal(false)}
                style={{
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f0f0f0';
                  e.target.style.color = '#333';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#888';
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className="csv-popup-body">
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                  Choose the duration for supplements in row <strong>{durationModalRowIndex + 1}</strong>
                </p>
                {selectedPlans[durationModalRowIndex] && (
                  <p style={{ color: 'var(--success-text)', fontSize: '13px', fontWeight: 'bold' }}>
                    Plan: {selectedPlans[durationModalRowIndex]}
                  </p>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                {/* No Duration */}
                <button
                  onClick={() => handleBulkDurationChange('None')}
                  style={{
                    padding: '15px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: 'var(--bg-surface-2)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#ccc';
                    e.target.style.backgroundColor = '#f0f0f0';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#e0e0e0';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                >
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--text-muted)', marginBottom: '5px' }}>
                    No Duration
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    No supplements selected
                  </div>
                </button>

                {DURATION_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => handleBulkDurationChange(opt.key)}
                    style={{
                      padding: '15px',
                      border: `2px solid ${opt.color.border}`,
                      borderRadius: '8px',
                      backgroundColor: opt.color.bg,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = opt.color.hoverBorder;
                      e.currentTarget.style.backgroundColor = opt.color.hoverBg;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = opt.color.border;
                      e.currentTarget.style.backgroundColor = opt.color.bg;
                    }}
                  >
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: opt.color.text, marginBottom: '5px', pointerEvents: 'none' }}>
                      {opt.months} {opt.months === 1 ? 'Month' : 'Months'}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', pointerEvents: 'none' }}>
                      {opt.months * 60} units
                    </div>
                  </button>
                ))}
              </div>

              <div style={{ textAlign: 'center', padding: '15px', backgroundColor: 'var(--bg-surface-2)', borderRadius: '8px' }}>
                <p style={{ margin: '0', color: 'var(--text-muted)', fontSize: '13px' }}>
                  <strong>Current Duration:</strong> {appliedDurations[durationModalRowIndex] ? Object.values(appliedDurations[durationModalRowIndex])[0] : 'None'}
                </p>
                {appliedDurations[durationModalRowIndex] && (
                  <p style={{ margin: '5px 0 0 0', color: 'var(--success-text)', fontSize: '12px' }}>
                    ✓ Duration applied to all supplements
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Supplement Selection Modal */}
      {showManualSelection && supplementPopupRowIndex !== null && (
        <div key={manualSelectionKey} className="csv-popup-overlay" style={{ zIndex: 10003 }}>
          <div className="csv-popup-content modern" style={{ width: '800px', maxWidth: '95%', maxHeight: '85vh', overflowY: 'auto' }}>
            <div className="csv-popup-header" style={{ position: 'relative' }}>
              <h3>Manual Supplement Selection</h3>
              <button
                onClick={() => setShowManualSelection(false)}
                style={{
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f0f0f0';
                  e.target.style.color = '#333';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#888';
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className="csv-popup-body">
              {(() => {
                const excludedSupplements = getFdaExcludedSupplementsForManual();
                // Get supplements specific to the current panel
                const getPanelSpecificSupplements = () => {
                  // Single source of truth for panel supplement list.
                  return getAllPanelSupplements(reportMode);
                };
                
                const availableSupplements = getPanelSpecificSupplements();
                
                // Calculate real-time coverage for manual selection - CUSTOM MODE
                const calculateManualCoverage = () => {
                  const selectedSupps = Object.keys(selectedSupplements).filter(key => selectedSupplements[key]);
                  
                  // Get the current row data for this specific row
                  const currentRowData = formData[supplementPopupRowIndex];
                  if (!currentRowData) {
                    return {
                      covered: 0,
                      total: 0,
                      uncovered: [],
                      selectedCount: selectedSupps.length,
                      allDeficientSpecies: []
                    };
                  }
                  
                  // Use the existing function to get the total deficient species count (same as Plan A)
                  const baseCoverageResult = calculateCoverageForManualSupplements(currentRowData, reportMode, []);
                  
                  // If no deficient species found, return empty coverage
                  if (baseCoverageResult.total === 0) {
                    return {
                      covered: 0,
                      total: 0,
                      uncovered: [],
                      selectedCount: selectedSupps.length,
                      allDeficientSpecies: []
                    };
                  }
                  
                  // Now calculate coverage with selected supplements
                  const coverageResult = calculateCoverageForManualSupplements(currentRowData, reportMode, selectedSupps);
                  
                  console.log('=== CUSTOM MODE COVERAGE CALCULATION ===');
                  console.log('Current row data:', currentRowData);
                  console.log('Report mode:', reportMode);
                  console.log('Selected supplements:', selectedSupps);
                  console.log('Base coverage (0 supplements):', baseCoverageResult);
                  console.log('Coverage with selected supplements:', coverageResult);
                  console.log('Progress:', `${coverageResult.covered}/${baseCoverageResult.total} species`);
                  
                  return {
                    covered: coverageResult.covered,
                    total: baseCoverageResult.total, // Same total as Plan A
                    uncovered: coverageResult.uncoveredSpecies || [],
                    selectedCount: selectedSupps.length,
                    allDeficientSpecies: baseCoverageResult.uncoveredSpecies || [] // All deficient species
                  };
                };
                
                // Function to handle manual supplement selection
                const handleManualSupplementSelection = (selectedSupps) => {
                  console.log('Manual supplement selection:', selectedSupps);
                  // Here you can add logic to apply the manual selection
                  // For example, update the supplement results for this row
                  setSupplementResults(prev => ({
                    ...prev,
                    [supplementPopupRowIndex]: selectedSupps
                  }));
                };
                
                const manualCoverage = calculateManualCoverage();
                
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    
                    {/* FDA Compliance Filter */}
                    <div style={{ 
                      backgroundColor: 'var(--warning-soft)',
                      border: '1px solid #ffeaa7',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px'
                    }}>
                      <input
                        type="checkbox"
                        id="fdaCompliant"
                        checked={fdaCompliantOnly}
                        onChange={(e) => {
                          console.log('FDA checkbox changed:', e.target.checked);
                          setFdaCompliantOnly(e.target.checked);
                        }}
                        style={{
                          transform: 'scale(1.2)',
                          margin: '0'
                        }}
                      />
                      <label 
                        htmlFor="fdaCompliant"
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: 'var(--warning-text)',
                          cursor: 'pointer',
                          margin: '0'
                        }}
                      >
                        🏥 FDA Compliant Only
                      </label>
                      <span style={{ 
                        fontSize: '12px', 
                        color: 'var(--warning-text)',
                        marginLeft: 'auto'
                      }}>
                        {fdaCompliantOnly ? 'Excluding FDA excluded supplements' : 'Including all supplements'}
                      </span>
                    </div>
                    
                    {/* Panel Supplements List */}
                    <div>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '15px'
                      }}>
                        <div>
                          <h4 style={{ margin: '0', color: 'var(--text-accent)' }}>Panel Supplements</h4>
                          <div style={{ 
                            fontSize: '12px', 
                            color: 'var(--text-muted)',
                            marginTop: '2px'
                          }}>
                            {(() => {
                              // Map reportMode to display names
                              const panelDisplayNames = {
                                'QPT': 'QU',
                                '9P': '9P',
                                'oms': 'OMS',
                                'Allergy and Skin': 'ALLERGY',
                                'Allergy and Skin_kids': 'QI',
                                'Toxin': 'TOXIN',
                                'bwcallergy': 'BWC-ALLERGY',
                                'bwccore': 'BWC-CORE',
                                'bwcdiamond': 'BWC-DIAMOND'
                              };
                              return `Panel: ${panelDisplayNames[reportMode] || reportMode || 'Unknown'}`;
                            })()}
                          </div>
                        </div>
                        <span style={{ 
                          backgroundColor: '#2196F3', 
                          color: 'white', 
                          padding: '4px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {(() => {
                            const filteredCount = fdaCompliantOnly 
                              ? availableSupplements.filter(supplement => !excludedSupplements.includes(supplement)).length 
                              : availableSupplements.length;
                            return `${filteredCount} supplements`;
                          })()}
                          {fdaCompliantOnly && (
                            <span style={{ 
                              fontSize: '10px', 
                              marginLeft: '4px',
                              opacity: 0.8
                            }}>
                              (FDA compliant only)
                            </span>
                          )}
                        </span>
                      </div>
                      
                      <div style={{ 
                        maxHeight: '300px', 
                        overflowY: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        padding: '10px'
                      }}>
                        {(() => {
                          // Filter supplements based on FDA checkbox - instant filtering
                          const filteredSupplements = fdaCompliantOnly 
                            ? availableSupplements.filter(supplement => !excludedSupplements.includes(supplement))
                            : availableSupplements;
                          
                          // Get current row data for species coverage calculation
                          const currentRowData = formData[supplementPopupRowIndex];
                          
                          // Get supplement map for species coverage
                          let supplementMap;
                          if (reportMode === 'pet-care-pro') {
                            supplementMap = BIOPET_ESSENTIAL_SUPPLEMENT_FORMULA;
                          } else if (reportMode === '9P') {
                            supplementMap = supplement_bacteria_map_9P;
                          } else if (reportMode === 'oms') {
                            supplementMap = supplement_bacteria_map_oms;
                          } else if (reportMode === 'Allergy and Skin') {
                            supplementMap = supplement_bacteria_map_AMG;
                          } else if (reportMode === 'Allergy and Skin_kids') {
                            supplementMap = qpt_supplement_bacteria_map;
                          } else if (reportMode === 'Toxin') {
                            supplementMap = supplement_bacteria_map_toxin;
                          } else if (['bwcallergy', 'bwccore', 'bwcdiamond', 'BWC-A', 'BWC-C', 'BWC-D'].includes(reportMode)) {
                            supplementMap = supplement_bacteria_map_bwc;
                          } else {
                            supplementMap = qpt_supplement_bacteria_map;
                          }
                          
                          // Get deficient species for coverage calculation
                          let config;
                          if (reportMode === 'pet-care-pro') {
                            config = null;
                          } else if (reportMode === '9P') {
                            config = choosing_supplement_9P[0];
                          } else if (reportMode === 'oms') {
                            config = choosing_supplement_oms[0];
                          } else if (reportMode === 'Allergy and Skin') {
                            config = choosing_supplement_AMG[0];
                          } else if (reportMode === 'Allergy and Skin_kids') {
                            config = qpt_choosing_supplement[0];
                          } else if (reportMode === 'Toxin') {
                            config = choosing_supplement_toxin[0];
                          } else if (['bwcallergy', 'bwccore', 'bwcdiamond', 'BWC-A', 'BWC-C', 'BWC-D'].includes(reportMode)) {
                            config = choosing_supplement_bwc[0];
                          } else {
                            config = qpt_choosing_supplement[0];
                          }
                          
                          const deficientSpecies = reportMode === 'pet-care-pro'
                            ? (biopetReportData?.page4Rows?.probiotics || [])
                                .filter((row) => row?.label === 'Deficient' || row?.label === 'Slightly deficient')
                                .map((row) => row.speciesName)
                            : currentRowData && config
                              ? config.info
                                  .filter(({ sp, less }) => {
                                    const value = parseFloat(currentRowData[sp]) || 0;
                                    const calculatedLabel = calculateSpeciesLabel(sp, value, currentRowData);
                                    const isDeficient = Array.isArray(less) ? less.includes(calculatedLabel) : false;
                                    return isDeficient;
                                  })
                                  .map(({ sp }) => sp)
                              : [];
                          
                          return filteredSupplements.map((supplement, idx) => {
                            const currentStock = stock[supplement] || 0;
                            const isSelected = selectedSupplements[supplement] || false;
                            const hasStock = currentStock > 0;
                            
                            // Calculate which deficient species this supplement covers
                            const coveredSpecies = supplementMap[supplement] ? 
                              Object.keys(supplementMap[supplement]).filter(species => 
                                deficientSpecies.includes(species)
                              ) : [];
                            
                            return (
                              <div key={supplement} style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                padding: '12px',
                                borderBottom: idx < filteredSupplements.length - 1 ? '1px solid #eee' : 'none',
                                backgroundColor: isSelected ? 'var(--success-soft)' : 'transparent',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                marginBottom: '4px'
                              }}
                              onClick={() => {
                                console.log('Supplement clicked:', supplement);
                                setSelectedSupplements(prev => {
                                  const newState = {
                                    ...prev,
                                    [supplement]: !prev[supplement]
                                  };
                                  console.log('New selected supplements state:', newState);
                                  return newState;
                                });
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) e.target.style.backgroundColor = '#f8f9fa';
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) e.target.style.backgroundColor = 'transparent';
                              }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                                                   onChange={() => {
                                   console.log('Supplement checkbox changed:', supplement);
                                   setSelectedSupplements(prev => {
                                     const newState = {
                                       ...prev,
                                       [supplement]: !prev[supplement]
                                     };
                                     console.log('New selected supplements state:', newState);
                                     return newState;
                                   });
                                 }}
                                  style={{
                                    marginRight: '12px',
                                    transform: 'scale(1.2)',
                                    marginTop: '2px'
                                  }}
                                />
                                <div style={{ flex: '1' }}>
                                  <div style={{ 
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    marginBottom: '6px'
                                  }}>
                                    <div style={{ flex: '1' }}>
                                      <div style={{ 
                                        fontWeight: 'bold', 
                                        color: hasStock ? '#213555' : 'var(--text-faint)',
                                        fontSize: '14px',
                                        marginBottom: '4px'
                                      }}>
                                        {getSupplementDisplayName(supplement)}
                                        {!hasStock && (
                                          <span style={{ 
                                            fontSize: '10px', 
                                            color: 'var(--text-faint)',
                                            marginLeft: '4px',
                                            fontStyle: 'italic'
                                          }}>
                                            (No Stock)
                                          </span>
                                        )}
                                      </div>
                                      <div style={{ 
                                        color: currentStock < stockThreshold ? 'var(--danger-text)' : '#00b894',
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                      }}>
                                        Stock: {currentStock} units
                                        {currentStock < stockThreshold && currentStock > 0 && (
                                          <span style={{ 
                                            fontSize: '10px', 
                                            color: 'var(--danger-text)',
                                            marginLeft: '4px'
                                          }}>
                                            (Low stock)
                                          </span>
                                        )}
                                        {currentStock === 0 && (
                                          <span style={{ 
                                            fontSize: '10px', 
                                            color: 'var(--text-faint)',
                                            marginLeft: '4px'
                                          }}>
                                            (Out of stock)
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Coverage indicator */}
                                    {coveredSpecies.length > 0 && (
                                      <div style={{
                                        backgroundColor: isSelected ? '#4CAF50' : '#FF9800',
                                        color: 'white',
                                        padding: '4px 8px',
                                        borderRadius: '12px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        marginLeft: '8px'
                                      }}>
                                        Covers {coveredSpecies.length} deficient species
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Species coverage details */}
                                  {coveredSpecies.length > 0 && (
                                    <div style={{
                                      backgroundColor: isSelected ? 'var(--success-soft)' : 'var(--warning-soft)',
                                      border: `1px solid ${isSelected ? '#c8e6c9' : '#ffe0b2'}`,
                                      borderRadius: '4px',
                                      padding: '6px',
                                      marginTop: '6px'
                                    }}>
                                      <div style={{
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        color: isSelected ? '#2e7d32' : 'var(--warning-text)',
                                        marginBottom: '4px'
                                      }}>
                                        Covers deficient species:
                                      </div>
                                      <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '4px'
                                      }}>
                                        {coveredSpecies.map((species, speciesIdx) => (
                                          <span key={species} style={{
                                            backgroundColor: isSelected ? '#4CAF50' : '#FF9800',
                                            color: 'white',
                                            padding: '2px 6px',
                                            borderRadius: '10px',
                                            fontSize: '9px',
                                            fontWeight: 'bold'
                                          }}>
                                            {species}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {coveredSpecies.length === 0 && deficientSpecies.length > 0 && (
                                    <div style={{
                                      backgroundColor: 'var(--bg-surface-2)',
                                      border: '1px solid #e0e0e0',
                                      borderRadius: '4px',
                                      padding: '6px',
                                      marginTop: '6px'
                                    }}>
                                      <div style={{
                                        fontSize: '10px',
                                        color: 'var(--text-muted)',
                                        fontStyle: 'italic'
                                      }}>
                                        Does not cover any deficient species
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'flex-end',
                      gap: '10px',
                      marginTop: '20px'
                    }}>
                      <button
                        onClick={() => setShowManualSelection(false)}
                        style={{
                          backgroundColor: 'var(--bg-surface-2)',
                          color: 'var(--text-muted)',
                          border: '1px solid #dee2e6',
                          padding: '8px 16px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          const selectedSupps = Object.keys(selectedSupplements).filter(key => selectedSupplements[key]);
                          handleManualSupplementSelection(selectedSupps);
                          setShowManualSelection(false);
                        }}
                        style={{
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Apply Selection
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* PDF Progress Modal */}
      {showPdfProgress && (
        <div className="csv-popup-overlay" style={{ zIndex: 10004 }}>
          <div className="csv-popup-content modern" style={{ 
            width: '500px', 
            maxWidth: '90%',
            textAlign: 'center'
          }}>
            <div className="csv-popup-header">
              <h3>Generating PDFs</h3>
            </div>
            
            <div className="csv-popup-body" style={{ padding: '30px 20px' }}>
              {/* Progress Bar */}
              <div style={{ 
                width: '100%', 
                backgroundColor: 'var(--bg-surface-2)', 
                borderRadius: '10px',
                height: '20px',
                marginBottom: '20px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${(pdfProgress.current / pdfProgress.total) * 100}%`,
                  height: '100%',
                  backgroundColor: '#4CAF50',
                  transition: 'width 0.3s ease',
                  borderRadius: '10px'
                }} />
              </div>

              {/* Progress Text */}
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold',
                color: 'var(--text-primary)',
                marginBottom: '10px'
              }}>
                {pdfProgressText}
              </div>

              {/* Progress Details */}
              <div style={{ 
                fontSize: '14px', 
                color: 'var(--text-muted)',
                marginBottom: '20px'
              }}>
                {pdfProgressDetails}
              </div>

              {/* Progress Numbers */}
              <div style={{ 
                fontSize: '12px', 
                color: 'var(--text-muted)',
                marginBottom: '30px'
              }}>
                {pdfProgress.current} of {pdfProgress.total} steps completed
              </div>

              {/* Cancel Button */}
              <button
                onClick={() => {
                  setPdfCancelRequested(true);
                  setShowPdfProgress(false);
                  setPdfProgress({ current: 0, total: 0 });
                  setPdfProgressText('');
                  setPdfProgressDetails('');
                }}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#c82333';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#dc3545';
                }}
              >
                Cancel Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Tooltip */}
      {showTooltip && (
        <div
          style={{
            position: 'fixed',
            top: tooltipPosition.y,
            left: tooltipPosition.x,
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            zIndex: 10005,
            pointerEvents: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            maxWidth: '300px',
            textAlign: 'center'
          }}
        >
          {tooltipText}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid rgba(0, 0, 0, 0.9)'
            }}
          />
        </div>
      )}

            {/* Database Cell Edit Modal */}
      {showDatabaseCellModal && (
        editModalData.rowIndex !== null ? (
          // Full modal content
          <div 
            onAnimationStart={() => console.log('🎬 Modal animation started')}
            onAnimationEnd={() => console.log('✅ Modal animation completed')}
            ref={(el) => {
              if (el) console.log('🎯 Modal DOM element created at:', performance.now());
            }}
            style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'var(--bg-overlay)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10020,
          padding: '16px'
        }}>
          <div             style={{
              background: 'var(--bg-surface)',
              borderRadius: '16px',
              padding: '0',
              width: '480px',
              maxWidth: 'calc(100vw - 32px)',
              minWidth: '320px',
              maxHeight: 'calc(100vh - 32px)',
              boxShadow: 'var(--sh-xl)',
              border: '1px solid var(--border-subtle)',
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: 'var(--font-sans)'
            }}>
            {/* Simplified Header for Performance */}
            <div style={{
              background: 'var(--accent)',
              padding: '20px 24px 16px',
              flexShrink: 0
            }}>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative',
                zIndex: 1,
                gap: '12px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  minWidth: 0,
                  flex: 1
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: 'rgba(255, 255, 255, 0.14)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.22)',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '20px' }}>✏️</span>
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: 'clamp(18px, 4vw, 22px)',
                      fontWeight: '700',
                      color: 'var(--accent-on)',
                      letterSpacing: '-0.5px',
                      lineHeight: '1.2'
                    }}>
                      Edit Data Cell
                    </h3>
                    <p style={{
                      margin: '4px 0 0 0',
                      fontSize: 'clamp(12px, 3vw, 14px)',
                      color: 'rgba(255, 255, 255, 0.82)',
                      fontWeight: '400',
                      lineHeight: '1.3'
                    }}>
                      Update database field value
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={closeEditModal}
                  style={{
                    background: 'rgba(255, 255, 255, 0.14)',
                    border: '1px solid rgba(255, 255, 255, 0.22)',
                    fontSize: '18px',
                    cursor: 'pointer',
                    color: 'var(--accent-on)',
                    padding: '8px',
                    borderRadius: '10px',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s ease',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.26)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.14)';
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Modern Content Body - Responsive */}
            <div style={{ 
              padding: 'clamp(20px, 4vw, 32px)',
              flex: 1,
              overflow: 'auto',
              minHeight: 0
            }}>
              {/* Modern Info Cards - Responsive */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 'clamp(12px, 2vw, 16px)',
                marginBottom: 'clamp(20px, 4vw, 28px)'
              }}>
                <div style={{
                  background: 'var(--accent-soft)',
                  padding: 'clamp(16px, 3vw, 20px)',
                  borderRadius: '12px',
                  border: '1px solid var(--accent-soft-border)'
                }}>
                  <span style={{
                    fontWeight: '600', 
                    color: 'var(--text-accent)',
                    fontSize: 'clamp(11px, 2.5vw, 12px)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Field Name
                  </span>
                  <div style={{ 
                    color: 'var(--text-accent)', 
                    fontWeight: '700',
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    marginTop: '6px',
                    wordBreak: 'break-word'
                  }}>
                    {editModalData.column}
                  </div>
                </div>
                
                <div style={{
                  background: 'var(--info-soft)',
                  padding: 'clamp(16px, 3vw, 20px)',
                  borderRadius: '12px',
                  border: '1px solid var(--border-subtle)'
                }}>
                  <span style={{
                    fontWeight: '600',
                    color: 'var(--info-text)',
                    fontSize: 'clamp(11px, 2.5vw, 12px)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Record ID
                  </span>
                  <div style={{
                    color: 'var(--info-text)',
                    fontWeight: '700',
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    marginTop: '6px'
                  }}>
                    #{editModalData.rowIndex + 1}
                  </div>
                </div>
              </div>

              {/* Current Value Display - Responsive */}
              <div style={{ marginBottom: 'clamp(20px, 4vw, 28px)' }}>
                <label style={{
                  display: 'block',
                  fontSize: 'clamp(13px, 2.5vw, 14px)',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  marginBottom: 'clamp(8px, 2vw, 12px)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Current Value
                </label>
                <div style={{
                  padding: 'clamp(14px, 3vw, 18px)',
                  background: 'var(--bg-inset)',
                  borderRadius: '12px',
                  fontSize: 'clamp(14px, 3vw, 15px)',
                  color: 'var(--text-secondary)',
                  fontFamily: 'monospace',
                  border: '1px solid var(--border-subtle)',
                  borderLeft: '4px solid var(--border-strong)',
                  wordBreak: 'break-word'
                }}>
                  <span style={{ color: editModalData.currentValue ? 'inherit' : 'var(--text-faint)' }}>
                    {editModalData.currentValue || '(empty)'}
                  </span>
                </div>
              </div>

              {/* Modern Input Field with Better Spacing */}
              <div style={{
                marginBottom: 'clamp(20px, 4vw, 28px)',
                padding: 'clamp(18px, 3vw, 22px)',
                background: 'var(--bg-surface-2)',
                borderRadius: '14px',
                border: '1px solid var(--border-subtle)'
              }}>
                <label style={{
                  display: 'block',
                  fontSize: 'clamp(13px, 2.5vw, 14px)',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  marginBottom: 'clamp(10px, 2vw, 14px)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  New Value
                </label>

                <div>
                  {(() => {
                    const columnName = editModalData.column?.toLowerCase() || '';

                    /* One field style for both branches, drawn from the dashboard
                       tokens so the editor matches the rest of the app. */
                    const fieldStyle = {
                      width: '100%',
                      padding: 'clamp(13px, 3vw, 15px) clamp(14px, 3vw, 16px)',
                      border: '1px solid var(--border-default)',
                      borderRadius: '10px',
                      fontSize: 'clamp(14px, 3vw, 15px)',
                      fontFamily: 'inherit',
                      outline: 'none',
                      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                      boxSizing: 'border-box',
                      background: 'var(--bg-surface)',
                      color: 'var(--text-primary)',
                      fontWeight: '500'
                    };
                    const focusField = (e) => {
                      e.target.style.borderColor = 'var(--border-focus)';
                      e.target.style.boxShadow = 'var(--ring-focus)';
                    };
                    const blurField = (e) => {
                      e.target.style.borderColor = 'var(--border-default)';
                      e.target.style.boxShadow = 'none';
                    };

                    // Date columns: manual text entry only, so any format the lab
                    // uses can be typed in verbatim (the DB columns are longtext).
                    if (columnName.includes('date')) {
                      return (
                        <div style={{ width: '100%' }}>
                          <div style={{
                            marginBottom: '8px',
                            fontSize: 'clamp(12px, 2.5vw, 13px)',
                            color: 'var(--text-muted)',
                            fontWeight: '500'
                          }}>
                            ✏️ Manual Entry (Any Format)
                          </div>
                          <input
                            type="text"
                            data-input-type="date-manual"
                            autoFocus
                            placeholder="Type the date: 1/2/22, Jan 2nd, 2022-01-02, etc."
                            defaultValue={editModalData.currentValue || ''}
                            onChange={(e) => {
                              e.target.dataset.currentValue = e.target.value;
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const currentValue = e.target.value;
                                setEditingValue(currentValue);
                                saveCellEdit();
                              }
                              if (e.key === 'Escape') closeEditModal();
                            }}
                            style={fieldStyle}
                            onFocus={focusField}
                            onBlur={blurField}
                          />
                        </div>
                      );
                    }
                    
                    // Regular text input for non-date columns
                    return (
                      <input
                        type="text"
                        data-input-type="main"
                        autoFocus
                        defaultValue={editModalData.currentValue || ''}
                        placeholder="Enter any value — text, numbers, dates (or leave empty to clear)"
                        onChange={(e) => {
                          // Store value locally without triggering re-renders
                          e.target.dataset.currentValue = e.target.value;
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            // Get the current value from the input and save
                            const currentValue = e.target.value;
                            setEditingValue(currentValue);
                            saveCellEdit();
                          }
                          if (e.key === 'Escape') closeEditModal();
                        }}
                        style={fieldStyle}
                        onFocus={focusField}
                        onBlur={blurField}
                      />
                    );
                  })()}
                  
                  {/* Input Helper Text */}
                  <div style={{
                    marginTop: '12px',
                    fontSize: 'clamp(12px, 2.5vw, 13px)',
                    color: 'var(--text-faint)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>💡</span>
                    Press Enter to save, Escape to cancel
                  </div>

                  {/* Format Recommendation */}
                  <div style={{
                    marginTop: '10px',
                    fontSize: 'clamp(11px, 2.5vw, 12px)',
                    color: 'var(--text-muted)',
                    lineHeight: '1.5',
                    padding: '10px 12px',
                    background: 'var(--bg-inset)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-subtle)'
                  }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-accent)' }}>Smart Input:</span> {(() => {
                      const columnName = editModalData.column?.toLowerCase() || '';
                      if (columnName.includes('date')) {
                        return "Type the date in any format you prefer, or leave empty to clear.";
                      }
                      return "Enter any format you prefer - dates, numbers, text, or leave empty to clear. The system will accept whatever you type!";
                    })()}
                  </div>
                </div>
              </div>
            </div>

              {/* Modern Action Buttons with Better Spacing - Responsive */}
              <div style={{
                display: 'flex',
                gap: 'clamp(10px, 2vw, 12px)',
                justifyContent: 'flex-end',
                padding: 'clamp(16px, 3vw, 20px) clamp(20px, 4vw, 32px)',
                background: 'var(--bg-surface-2)',
                borderTop: '1px solid var(--border-subtle)',
                flexShrink: 0
              }}>
                <button
                  onClick={closeEditModal}
                  style={{
                    padding: 'clamp(12px, 2.5vw, 13px) clamp(20px, 4vw, 26px)',
                    background: 'var(--bg-surface)',
                    color: 'var(--text-secondary)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: 'clamp(13px, 2.5vw, 14px)',
                    fontWeight: '600',
                    fontFamily: 'inherit',
                    transition: 'background 0.2s ease, border-color 0.2s ease',
                    minWidth: 'clamp(96px, 20vw, 112px)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--bg-hover)';
                    e.currentTarget.style.borderColor = 'var(--border-strong)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--bg-surface)';
                    e.currentTarget.style.borderColor = 'var(--border-default)';
                  }}
                >
                  Cancel
                </button>
                
                <button
                  onClick={() => {
                    console.log('Save button clicked');
                    console.log('editModalData:', editModalData);
                    console.log('editingValue:', editingValue);
                    saveCellEdit();
                  }}
                  disabled={savingCell}
                  style={{
                    padding: 'clamp(12px, 2.5vw, 13px) clamp(22px, 5vw, 30px)',
                    background: savingCell ? 'var(--text-faint)' : 'var(--accent)',
                    color: 'var(--accent-on)',
                    border: '1px solid transparent',
                    borderRadius: '10px',
                    cursor: savingCell ? 'not-allowed' : 'pointer',
                    fontSize: 'clamp(13px, 2.5vw, 14px)',
                    fontWeight: '600',
                    fontFamily: 'inherit',
                    transition: 'background 0.2s ease, box-shadow 0.2s ease',
                    boxShadow: savingCell ? 'none' : 'var(--sh-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'clamp(8px, 2vw, 10px)',
                    letterSpacing: '0.3px',
                    minWidth: 'clamp(120px, 25vw, 140px)'
                  }}
                  onMouseEnter={(e) => {
                    if (!savingCell) {
                      e.currentTarget.style.background = 'var(--accent-hover)';
                      e.currentTarget.style.boxShadow = 'var(--sh-md)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!savingCell) {
                      e.currentTarget.style.background = 'var(--accent)';
                      e.currentTarget.style.boxShadow = 'var(--sh-sm)';
                    }
                  }}
                >
                  {savingCell ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255,255,255,0.35)',
                        borderTop: '2px solid var(--accent-on)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Processing...
                    </>
                  ) : (
                    <>
                      {/* <span style={{ fontSize: '18px' }}>💾</span> */}
                      Save Changes
                    </>
                  )}
                </button>
              </div>
          </div>
        </div>
        ) : (
          // Loading state while modal is opening
          <div 
            ref={(el) => {
              if (el) console.log('🎯 Loading state DOM created at:', performance.now());
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'var(--bg-overlay)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10020,
              padding: '16px'
              // Removed animation for instant appearance
            }}>
            <div style={{
              background: 'var(--bg-surface)',
              borderRadius: '16px',
              padding: '44px 48px',
              textAlign: 'center',
              boxShadow: 'var(--sh-xl)',
              border: '1px solid var(--border-subtle)',
              fontFamily: 'var(--font-sans)',
              // Removed animation for instant appearance
              minWidth: '200px'
            }}>
              <div
                className="performance-optimized"
                style={{
                  width: '44px',
                  height: '44px',
                  border: '3px solid var(--border-subtle)',
                  borderTop: '3px solid var(--accent)',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  margin: '0 auto 22px'
                }}
              />
              <h3 style={{
                margin: '0 0 8px 0',
                color: 'var(--text-primary)',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                Opening Editor
              </h3>
              <p style={{
                margin: 0,
                color: 'var(--text-muted)',
                fontSize: '14px'
              }}>
                Preparing your data...
              </p>
            </div>
          </div>
        )
      )}

      {/* Modern Notifications */}
      {notifications.map((notification) => (
        <div
          key={notification.id}
          data-notification-id={notification.id}
          className="modern-notification"
          style={{
            position: 'fixed',
            top: `${20 + (notifications.indexOf(notification) * 80)}px`,
            right: '20px',
            backgroundColor: notification.type === 'warning' ? '#ff9800' : 
                             notification.type === 'error' ? '#f44336' :
                             notification.type === 'success' ? '#4caf50' : '#2196F3',
            color: 'white',
            padding: '16px 20px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '500',
            zIndex: 10020,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            maxWidth: '350px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'slideInRight 0.3s ease-out',
            transform: 'translateX(0)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            borderLeft: notification.type === 'success' ? '4px solid #2e7d32' :
                       notification.type === 'error' ? '4px solid #b71c1c' :
                       notification.type === 'warning' ? '4px solid #e65100' : '4px solid #0d47a1'
          }}
          onClick={() => removeNotification(notification.id)}
        >
          {/* Icon */}
          <div style={{
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px'
          }}>
            {notification.type === 'warning' ? '⚠️' : 
             notification.type === 'error' ? '❌' :
             notification.type === 'success' ? '✅' : 'ℹ️'}
          </div>
          
          {/* Message */}
          <span style={{ flex: 1 }}>
            {notification.message}
          </span>
          
          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              removeNotification(notification.id);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '0',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
              transition: 'opacity 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.opacity = 1}
            onMouseLeave={(e) => e.target.style.opacity = 0.8}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

export default DashboardPage;
